/*
 *  Sugar v2.0.4
 *
 *  Freely distributable and licensed under the MIT-style license.
 *  Copyright (c) Andrew Plummer
 *  https://sugarjs.com/
 *
 * ---------------------------- */

(function () {
    'use strict';

    /***
     * @module Core
     * @description Core functionality including the ability to define methods and
     *              extend onto natives.
     *
     ***/

        // The global to export.
    var Sugar;

    // The name of Sugar in the global namespace.
    var SUGAR_GLOBAL = 'Sugar';

    // Natives available on initialization. Letting Object go first to ensure its
    // global is set by the time the rest are checking for chainable Object methods.
    var NATIVE_NAMES = 'Object Number String Array Date RegExp Function';

    // Static method flag
    var STATIC = 0x1;

    // Instance method flag
    var INSTANCE = 0x2;

    // IE8 has a broken defineProperty but no defineProperties so this saves a try/catch.
    var PROPERTY_DESCRIPTOR_SUPPORT = !!(Object.defineProperty && Object.defineProperties);

    // The global context. Rhino uses a different "global" keyword so
    // do an extra check to be sure that it's actually the global context.
    var globalContext = typeof global !== 'undefined' && global.Object === Object ? global : this;

    // Is the environment node?
    var hasExports = typeof module !== 'undefined' && module.exports;

    // Whether object instance methods can be mapped to the prototype.
    var allowObjectPrototype = false;

    // A map from Array to SugarArray.
    var namespacesByName = {};

    // A map from [object Object] to namespace.
    var namespacesByClassString = {};

    // Defining properties.
    var defineProperty = PROPERTY_DESCRIPTOR_SUPPORT ? Object.defineProperty : definePropertyShim;

    // A default chainable class for unknown types.
    var DefaultChainable = getNewChainableClass('Chainable');


    // Global methods

    function setupGlobal() {
        Sugar = globalContext[SUGAR_GLOBAL];
        if (Sugar) {
            // Reuse already defined Sugar global object.
            return;
        }
        Sugar = function (arg) {
            forEachProperty(Sugar, function (sugarNamespace, name) {
                // Although only the only enumerable properties on the global
                // object are Sugar namespaces, environments that can't set
                // non-enumerable properties will step through the utility methods
                // as well here, so use this check to only allow true namespaces.
                if (hasOwn(namespacesByName, name)) {
                    sugarNamespace.extend(arg);
                }
            });
            return Sugar;
        };
        if (hasExports) {
            module.exports = Sugar;
        } else {
            try {
                globalContext[SUGAR_GLOBAL] = Sugar;
            } catch (e) {
                // Contexts such as QML have a read-only global context.
            }
        }
        forEachProperty(NATIVE_NAMES.split(' '), function (name) {
            createNamespace(name);
        });
        setGlobalProperties();
    }

    /***
     * @method createNamespace(name)
     * @returns SugarNamespace
     * @namespace Sugar
     * @short Creates a new Sugar namespace.
     * @extra This method is for plugin developers who want to define methods to be
     *        used with natives that Sugar does not handle by default. The new
     *        namespace will appear on the `Sugar` global with all the methods of
     *        normal namespaces, including the ability to define new methods. When
     *        extended, any defined methods will be mapped to `name` in the global
     *        context.
     *
     * @example
     *
     *   Sugar.createNamespace('Boolean');
     *
     * @param {string} name - The namespace name.
     *
     ***/
    function createNamespace(name) {

        // Is the current namespace Object?
        var isObject = name === 'Object';

        // A Sugar namespace is also a chainable class: Sugar.Array, etc.
        var sugarNamespace = getNewChainableClass(name, true);

        /***
         * @method extend([opts])
         * @returns Sugar
         * @namespace Sugar
         * @short Extends Sugar defined methods onto natives.
         * @extra This method can be called on individual namespaces like
         *        `Sugar.Array` or on the `Sugar` global itself, in which case
         *        [opts] will be forwarded to each `extend` call. For more,
         *        see `extending`.
         *
         * @options
         *
         *   methods           An array of method names to explicitly extend.
         *
         *   except            An array of method names or global namespaces (`Array`,
         *                     `String`) to explicitly exclude. Namespaces should be the
         *                     actual global objects, not strings.
         *
         *   namespaces        An array of global namespaces (`Array`, `String`) to
         *                     explicitly extend. Namespaces should be the actual
         *                     global objects, not strings.
         *
         *   enhance           A shortcut to disallow all "enhance" flags at once
         *                     (flags listed below). For more, see `enhanced methods`.
         *                     Default is `true`.
         *
         *   enhanceString     A boolean allowing String enhancements. Default is `true`.
         *
         *   enhanceArray      A boolean allowing Array enhancements. Default is `true`.
         *
         *   objectPrototype   A boolean allowing Sugar to extend Object.prototype
         *                     with instance methods. This option is off by default
         *                     and should generally not be used except with caution.
         *                     For more, see `object methods`.
         *
         * @example
         *
         *   Sugar.Array.extend();
         *   Sugar.extend();
         *
         * @option {Array<string>} [methods]
         * @option {Array<string|NativeConstructor>} [except]
         * @option {Array<NativeConstructor>} [namespaces]
         * @option {boolean} [enhance]
         * @option {boolean} [enhanceString]
         * @option {boolean} [enhanceArray]
         * @option {boolean} [objectPrototype]
         * @param {ExtendOptions} [opts]
         *
         ***
         * @method extend([opts])
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Extends Sugar defined methods for a specific namespace onto natives.
         * @param {ExtendOptions} [opts]
         *
         ***/
        var extend = function (opts) {

            var nativeClass = globalContext[name], nativeProto = nativeClass.prototype;
            var staticMethods = {}, instanceMethods = {}, methodsByName;

            function objectRestricted(name, target) {
                return isObject && target === nativeProto &&
                    (!allowObjectPrototype || name === 'get' || name === 'set');
            }

            function arrayOptionExists(field, val) {
                var arr = opts[field];
                if (arr) {
                    for (var i = 0, el; el = arr[i]; i++) {
                        if (el === val) {
                            return true;
                        }
                    }
                }
                return false;
            }

            function arrayOptionExcludes(field, val) {
                return opts[field] && !arrayOptionExists(field, val);
            }

            function disallowedByFlags(methodName, target, flags) {
                // Disallowing methods by flag currently only applies if methods already
                // exist to avoid enhancing native methods, as aliases should still be
                // extended (i.e. Array#all should still be extended even if Array#every
                // is being disallowed by a flag).
                if (!target[methodName] || !flags) {
                    return false;
                }
                for (var i = 0; i < flags.length; i++) {
                    if (opts[flags[i]] === false) {
                        return true;
                    }
                }
            }

            function namespaceIsExcepted() {
                return arrayOptionExists('except', nativeClass) ||
                    arrayOptionExcludes('namespaces', nativeClass);
            }

            function methodIsExcepted(methodName) {
                return arrayOptionExists('except', methodName);
            }

            function canExtend(methodName, method, target) {
                return !objectRestricted(methodName, target) &&
                    !disallowedByFlags(methodName, target, method.flags) &&
                    !methodIsExcepted(methodName);
            }

            opts = opts || {};
            methodsByName = opts.methods;

            if (namespaceIsExcepted()) {
                return;
            } else if (isObject && typeof opts.objectPrototype === 'boolean') {
                // Store "objectPrototype" flag for future reference.
                allowObjectPrototype = opts.objectPrototype;
            }

            forEachProperty(methodsByName || sugarNamespace, function (method, methodName) {
                if (methodsByName) {
                    // If we have method names passed in an array,
                    // then we need to flip the key and value here
                    // and find the method in the Sugar namespace.
                    methodName = method;
                    method = sugarNamespace[methodName];
                }
                if (hasOwn(method, 'instance') && canExtend(methodName, method, nativeProto)) {
                    instanceMethods[methodName] = method.instance;
                }
                if (hasOwn(method, 'static') && canExtend(methodName, method, nativeClass)) {
                    staticMethods[methodName] = method;
                }
            });

            // Accessing the extend target each time instead of holding a reference as
            // it may have been overwritten (for example Date by Sinon). Also need to
            // access through the global to allow extension of user-defined namespaces.
            extendNative(nativeClass, staticMethods);
            extendNative(nativeProto, instanceMethods);

            if (!methodsByName) {
                // If there are no method names passed, then
                // all methods in the namespace will be extended
                // to the native. This includes all future defined
                // methods, so add a flag here to check later.
                setProperty(sugarNamespace, 'active', true);
            }
            return sugarNamespace;
        };

        function defineWithOptionCollect(methodName, instance, args) {
            setProperty(sugarNamespace, methodName, function (arg1, arg2, arg3) {
                var opts = collectDefineOptions(arg1, arg2, arg3);
                defineMethods(sugarNamespace, opts.methods, instance, args, opts.last);
                return sugarNamespace;
            });
        }

        /***
         * @method defineStatic(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Defines static methods on the namespace that can later be extended
         *        onto the native globals.
         * @extra Accepts either a single object mapping names to functions, or name
         *        and function as two arguments. If `extend` was previously called
         *        with no arguments, the method will be immediately mapped to its
         *        native when defined.
         *
         * @example
         *
         *   Sugar.Number.defineStatic({
         *     isOdd: function (num) {
         *       return num % 2 === 1;
         *     }
         *   });
         *
         * @signature defineStatic(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        defineWithOptionCollect('defineStatic', STATIC);

        /***
         * @method defineInstance(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Defines methods on the namespace that can later be extended as
         *        instance methods onto the native prototype.
         * @extra Accepts either a single object mapping names to functions, or name
         *        and function as two arguments. All functions should accept the
         *        native for which they are mapped as their first argument, and should
         *        never refer to `this`. If `extend` was previously called with no
         *        arguments, the method will be immediately mapped to its native when
         *        defined.
         *
         *        Methods cannot accept more than 4 arguments in addition to the
         *        native (5 arguments total). Any additional arguments will not be
         *        mapped. If the method needs to accept unlimited arguments, use
         *        `defineInstanceWithArguments`. Otherwise if more options are
         *        required, use an options object instead.
         *
         * @example
         *
         *   Sugar.Number.defineInstance({
         *     square: function (num) {
         *       return num * num;
         *     }
         *   });
         *
         * @signature defineInstance(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        defineWithOptionCollect('defineInstance', INSTANCE);

        /***
         * @method defineInstanceAndStatic(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short A shortcut to define both static and instance methods on the namespace.
         * @extra This method is intended for use with `Object` instance methods. Sugar
         *        will not map any methods to `Object.prototype` by default, so defining
         *        instance methods as static helps facilitate their proper use.
         *
         * @example
         *
         *   Sugar.Object.defineInstanceAndStatic({
         *     isAwesome: function (obj) {
         *       // check if obj is awesome!
         *     }
         *   });
         *
         * @signature defineInstanceAndStatic(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        defineWithOptionCollect('defineInstanceAndStatic', INSTANCE | STATIC);


        /***
         * @method defineStaticWithArguments(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Defines static methods that collect arguments.
         * @extra This method is identical to `defineStatic`, except that when defined
         *        methods are called, they will collect any arguments past `n - 1`,
         *        where `n` is the number of arguments that the method accepts.
         *        Collected arguments will be passed to the method in an array
         *        as the last argument defined on the function.
         *
         * @example
         *
         *   Sugar.Number.defineStaticWithArguments({
         *     addAll: function (num, args) {
         *       for (var i = 0; i < args.length; i++) {
         *         num += args[i];
         *       }
         *       return num;
         *     }
         *   });
         *
         * @signature defineStaticWithArguments(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        defineWithOptionCollect('defineStaticWithArguments', STATIC, true);

        /***
         * @method defineInstanceWithArguments(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Defines instance methods that collect arguments.
         * @extra This method is identical to `defineInstance`, except that when
         *        defined methods are called, they will collect any arguments past
         *        `n - 1`, where `n` is the number of arguments that the method
         *        accepts. Collected arguments will be passed to the method as the
         *        last argument defined on the function.
         *
         * @example
         *
         *   Sugar.Number.defineInstanceWithArguments({
         *     addAll: function (num, args) {
         *       for (var i = 0; i < args.length; i++) {
         *         num += args[i];
         *       }
         *       return num;
         *     }
         *   });
         *
         * @signature defineInstanceWithArguments(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        defineWithOptionCollect('defineInstanceWithArguments', INSTANCE, true);

        /***
         * @method defineStaticPolyfill(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Defines static methods that are mapped onto the native if they do
         *        not already exist.
         * @extra Intended only for use creating polyfills that follow the ECMAScript
         *        spec. Accepts either a single object mapping names to functions, or
         *        name and function as two arguments.
         *
         * @example
         *
         *   Sugar.Object.defineStaticPolyfill({
         *     keys: function (obj) {
         *       // get keys!
         *     }
         *   });
         *
         * @signature defineStaticPolyfill(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        setProperty(sugarNamespace, 'defineStaticPolyfill', function (arg1, arg2, arg3) {
            var opts = collectDefineOptions(arg1, arg2, arg3);
            extendNative(globalContext[name], opts.methods, true, opts.last);
            return sugarNamespace;
        });

        /***
         * @method defineInstancePolyfill(methods)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Defines instance methods that are mapped onto the native prototype
         *        if they do not already exist.
         * @extra Intended only for use creating polyfills that follow the ECMAScript
         *        spec. Accepts either a single object mapping names to functions, or
         *        name and function as two arguments. This method differs from
         *        `defineInstance` as there is no static signature (as the method
         *        is mapped as-is to the native), so it should refer to its `this`
         *        object.
         *
         * @example
         *
         *   Sugar.Array.defineInstancePolyfill({
         *     indexOf: function (arr, el) {
         *       // index finding code here!
         *     }
         *   });
         *
         * @signature defineInstancePolyfill(methodName, methodFn)
         * @param {Object} methods - Methods to be defined.
         * @param {string} methodName - Name of a single method to be defined.
         * @param {Function} methodFn - Function body of a single method to be defined.
         ***/
        setProperty(sugarNamespace, 'defineInstancePolyfill', function (arg1, arg2, arg3) {
            var opts = collectDefineOptions(arg1, arg2, arg3);
            extendNative(globalContext[name].prototype, opts.methods, true, opts.last);
            // Map instance polyfills to chainable as well.
            forEachProperty(opts.methods, function (fn, methodName) {
                defineChainableMethod(sugarNamespace, methodName, fn);
            });
            return sugarNamespace;
        });

        /***
         * @method alias(toName, from)
         * @returns SugarNamespace
         * @namespace SugarNamespace
         * @short Aliases one Sugar method to another.
         *
         * @example
         *
         *   Sugar.Array.alias('all', 'every');
         *
         * @signature alias(toName, fn)
         * @param {string} toName - Name for new method.
         * @param {string|Function} from - Method to alias, or string shortcut.
         ***/
        setProperty(sugarNamespace, 'alias', function (name, source) {
            var method = typeof source === 'string' ? sugarNamespace[source] : source;
            setMethod(sugarNamespace, name, method);
            return sugarNamespace;
        });

        // Each namespace can extend only itself through its .extend method.
        setProperty(sugarNamespace, 'extend', extend);

        // Cache the class to namespace relationship for later use.
        namespacesByName[name] = sugarNamespace;
        namespacesByClassString['[object ' + name + ']'] = sugarNamespace;

        mapNativeToChainable(name);
        mapObjectChainablesToNamespace(sugarNamespace);


        // Export
        return Sugar[name] = sugarNamespace;
    }

    function setGlobalProperties() {
        setProperty(Sugar, 'extend', Sugar);
        setProperty(Sugar, 'toString', toString);
        setProperty(Sugar, 'createNamespace', createNamespace);

        setProperty(Sugar, 'util', {
            'hasOwn': hasOwn,
            'getOwn': getOwn,
            'setProperty': setProperty,
            'classToString': classToString,
            'defineProperty': defineProperty,
            'forEachProperty': forEachProperty,
            'mapNativeToChainable': mapNativeToChainable
        });
    }

    function toString() {
        return SUGAR_GLOBAL;
    }


    // Defining Methods

    function defineMethods(sugarNamespace, methods, type, args, flags) {
        forEachProperty(methods, function (method, methodName) {
            var instanceMethod, staticMethod = method;
            if (args) {
                staticMethod = wrapMethodWithArguments(method);
            }
            if (flags) {
                staticMethod.flags = flags;
            }

            // A method may define its own custom implementation, so
            // make sure that's not the case before creating one.
            if (type & INSTANCE && !method.instance) {
                instanceMethod = wrapInstanceMethod(method, args);
                setProperty(staticMethod, 'instance', instanceMethod);
            }

            if (type & STATIC) {
                setProperty(staticMethod, 'static', true);
            }

            setMethod(sugarNamespace, methodName, staticMethod);

            if (sugarNamespace.active) {
                // If the namespace has been activated (.extend has been called),
                // then map this method as well.
                sugarNamespace.extend(methodName);
            }
        });
    }

    function collectDefineOptions(arg1, arg2, arg3) {
        var methods, last;
        if (typeof arg1 === 'string') {
            methods = {};
            methods[arg1] = arg2;
            last = arg3;
        } else {
            methods = arg1;
            last = arg2;
        }
        return {
            last: last,
            methods: methods
        };
    }

    function wrapInstanceMethod(fn, args) {
        return args ? wrapMethodWithArguments(fn, true) : wrapInstanceMethodFixed(fn);
    }

    function wrapMethodWithArguments(fn, instance) {
        // Functions accepting enumerated arguments will always have "args" as the
        // last argument, so subtract one from the function length to get the point
        // at which to start collecting arguments. If this is an instance method on
        // a prototype, then "this" will be pushed into the arguments array so start
        // collecting 1 argument earlier.
        var startCollect = fn.length - 1 - (instance ? 1 : 0);
        return function () {
            var args = [], collectedArgs = [], len;
            if (instance) {
                args.push(this);
            }
            len = Math.max(arguments.length, startCollect);
            // Optimized: no leaking arguments
            for (var i = 0; i < len; i++) {
                if (i < startCollect) {
                    args.push(arguments[i]);
                } else {
                    collectedArgs.push(arguments[i]);
                }
            }
            args.push(collectedArgs);
            return fn.apply(this, args);
        };
    }

    function wrapInstanceMethodFixed(fn) {
        switch (fn.length) {
            // Wrapped instance methods will always be passed the instance
            // as the first argument, but requiring the argument to be defined
            // may cause confusion here, so return the same wrapped function regardless.
            case 0:
            case 1:
                return function () {
                    return fn(this);
                };
            case 2:
                return function (a) {
                    return fn(this, a);
                };
            case 3:
                return function (a, b) {
                    return fn(this, a, b);
                };
            case 4:
                return function (a, b, c) {
                    return fn(this, a, b, c);
                };
            case 5:
                return function (a, b, c, d) {
                    return fn(this, a, b, c, d);
                };
        }
    }

    // Method helpers

    function extendNative(target, source, polyfill, override) {
        forEachProperty(source, function (method, name) {
            if (polyfill && !override && target[name]) {
                // Method exists, so bail.
                return;
            }
            setProperty(target, name, method);
        });
    }

    function setMethod(sugarNamespace, methodName, method) {
        sugarNamespace[methodName] = method;
        if (method.instance) {
            defineChainableMethod(sugarNamespace, methodName, method.instance, true);
        }
    }


    // Chainables

    function getNewChainableClass(name) {
        var fn = function SugarChainable(obj, arg) {
            if (!(this instanceof fn)) {
                return new fn(obj, arg);
            }
            if (this.constructor !== fn) {
                // Allow modules to define their own constructors.
                obj = this.constructor.apply(obj, arguments);
            }
            this.raw = obj;
        };
        setProperty(fn, 'toString', function () {
            return SUGAR_GLOBAL + name;
        });
        setProperty(fn.prototype, 'valueOf', function () {
            return this.raw;
        });
        return fn;
    }

    function defineChainableMethod(sugarNamespace, methodName, fn) {
        var wrapped = wrapWithChainableResult(fn), existing, collision, dcp;
        dcp = DefaultChainable.prototype;
        existing = dcp[methodName];

        // If the method was previously defined on the default chainable, then a
        // collision exists, so set the method to a disambiguation function that will
        // lazily evaluate the object and find it's associated chainable. An extra
        // check is required to avoid false positives from Object inherited methods.
        collision = existing && existing !== Object.prototype[methodName];

        // The disambiguation function is only required once.
        if (!existing || !existing.disambiguate) {
            dcp[methodName] = collision ? disambiguateMethod(methodName) : wrapped;
        }

        // The target chainable always receives the wrapped method. Additionally,
        // if the target chainable is Sugar.Object, then map the wrapped method
        // to all other namespaces as well if they do not define their own method
        // of the same name. This way, a Sugar.Number will have methods like
        // isEqual that can be called on any object without having to traverse up
        // the prototype chain and perform disambiguation, which costs cycles.
        // Note that the "if" block below actually does nothing on init as Object
        // goes first and no other namespaces exist yet. However it needs to be
        // here as Object instance methods defined later also need to be mapped
        // back onto existing namespaces.
        sugarNamespace.prototype[methodName] = wrapped;
        if (sugarNamespace === Sugar.Object) {
            mapObjectChainableToAllNamespaces(methodName, wrapped);
        }
    }

    function mapObjectChainablesToNamespace(sugarNamespace) {
        forEachProperty(Sugar.Object && Sugar.Object.prototype, function (val, methodName) {
            if (typeof val === 'function') {
                setObjectChainableOnNamespace(sugarNamespace, methodName, val);
            }
        });
    }

    function mapObjectChainableToAllNamespaces(methodName, fn) {
        forEachProperty(namespacesByName, function (sugarNamespace) {
            setObjectChainableOnNamespace(sugarNamespace, methodName, fn);
        });
    }

    function setObjectChainableOnNamespace(sugarNamespace, methodName, fn) {
        var proto = sugarNamespace.prototype;
        if (!hasOwn(proto, methodName)) {
            proto[methodName] = fn;
        }
    }

    function wrapWithChainableResult(fn) {
        return function () {
            return new DefaultChainable(fn.apply(this.raw, arguments));
        };
    }

    function disambiguateMethod(methodName) {
        var fn = function () {
            var raw = this.raw, sugarNamespace, fn;
            if (raw != null) {
                // Find the Sugar namespace for this unknown.
                sugarNamespace = namespacesByClassString[classToString(raw)];
            }
            if (!sugarNamespace) {
                // If no sugarNamespace can be resolved, then default
                // back to Sugar.Object so that undefined and other
                // non-supported types can still have basic object
                // methods called on them, such as type checks.
                sugarNamespace = Sugar.Object;
            }

            fn = new sugarNamespace(raw)[methodName];

            if (fn.disambiguate) {
                // If the method about to be called on this chainable is
                // itself a disambiguation method, then throw an error to
                // prevent infinite recursion.
                throw new TypeError('Cannot resolve namespace for ' + raw);
            }

            return fn.apply(this, arguments);
        };
        fn.disambiguate = true;
        return fn;
    }

    function mapNativeToChainable(name, methodNames) {
        var sugarNamespace = namespacesByName[name],
            nativeProto = globalContext[name].prototype;

        if (!methodNames && ownPropertyNames) {
            methodNames = ownPropertyNames(nativeProto);
        }

        forEachProperty(methodNames, function (methodName) {
            if (nativeMethodProhibited(methodName)) {
                // Sugar chainables have their own constructors as well as "valueOf"
                // methods, so exclude them here. The __proto__ argument should be trapped
                // by the function check below, however simply accessing this property on
                // Object.prototype causes QML to segfault, so pre-emptively excluding it.
                return;
            }
            try {
                var fn = nativeProto[methodName];
                if (typeof fn !== 'function') {
                    // Bail on anything not a function.
                    return;
                }
            } catch (e) {
                // Function.prototype has properties that
                // will throw errors when accessed.
                return;
            }
            defineChainableMethod(sugarNamespace, methodName, fn);
        });
    }

    function nativeMethodProhibited(methodName) {
        return methodName === 'constructor' ||
            methodName === 'valueOf' ||
            methodName === '__proto__';
    }


    // Util

    // Internal references
    var ownPropertyNames = Object.getOwnPropertyNames,
        internalToString = Object.prototype.toString,
        internalHasOwnProperty = Object.prototype.hasOwnProperty;

    // Defining this as a variable here as the ES5 module
    // overwrites it to patch DONTENUM.
    var forEachProperty = function (obj, fn) {
        for (var key in obj) {
            if (!hasOwn(obj, key)) continue;
            if (fn.call(obj, obj[key], key, obj) === false) break;
        }
    };

    function definePropertyShim(obj, prop, descriptor) {
        obj[prop] = descriptor.value;
    }

    function setProperty(target, name, value, enumerable) {
        defineProperty(target, name, {
            value: value,
            enumerable: !!enumerable,
            configurable: true,
            writable: true
        });
    }

    // PERF: Attempts to speed this method up get very Heisenbergy. Quickly
    // returning based on typeof works for primitives, but slows down object
    // types. Even === checks on null and undefined (no typeof) will end up
    // basically breaking even. This seems to be as fast as it can go.
    function classToString(obj) {
        return internalToString.call(obj);
    }

    function hasOwn(obj, prop) {
        return !!obj && internalHasOwnProperty.call(obj, prop);
    }

    function getOwn(obj, prop) {
        if (hasOwn(obj, prop)) {
            return obj[prop];
        }
    }

    setupGlobal();

    /***
     * @module Common
     * @description Internal utility and common methods.
     ***/

        // Flag allowing native methods to be enhanced
    var ENHANCEMENTS_FLAG = 'enhance';

    // For type checking, etc. Excludes object as this is more nuanced.
    var NATIVE_TYPES = 'Boolean Number String Date RegExp Function Array Error Set Map';

    // Do strings have no keys?
    var NO_KEYS_IN_STRING_OBJECTS = !('0' in Object('a'));

    // Prefix for private properties
    var PRIVATE_PROP_PREFIX = '_sugar_';

    // Matches 1..2 style ranges in properties
    var PROPERTY_RANGE_REG = /^(.*?)\[([-\d]*)\.\.([-\d]*)\](.*)$/;

    // WhiteSpace/LineTerminator as defined in ES5.1 plus Unicode characters in the Space, Separator category.
    var TRIM_CHARS = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u180E\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u2028\u2029\u3000\uFEFF';

    // Regex for matching a formatted string
    var STRING_FORMAT_REG = /([{}])\1|\{([^}]*)\}|(%)%|(%(\w*))/g;

    // Common chars
    var HALF_WIDTH_ZERO = 0x30,
        FULL_WIDTH_ZERO = 0xff10,
        HALF_WIDTH_PERIOD = '.',
        FULL_WIDTH_PERIOD = '．',
        HALF_WIDTH_COMMA = ',',
        OPEN_BRACE = '{',
        CLOSE_BRACE = '}';

    // Namespace aliases
    var sugarObject = Sugar.Object,
        sugarArray = Sugar.Array,
        sugarDate = Sugar.Date,
        sugarString = Sugar.String,
        sugarNumber = Sugar.Number,
        sugarFunction = Sugar.Function,
        sugarRegExp = Sugar.RegExp;

    // Class checks
    var isSerializable,
        isBoolean, isNumber, isString,
        isDate, isRegExp, isFunction,
        isArray, isSet, isMap, isError;

    function buildClassChecks() {

        var knownTypes = {};

        function addCoreTypes() {

            var names = spaceSplit(NATIVE_TYPES);

            isBoolean = buildPrimitiveClassCheck(names[0]);
            isNumber = buildPrimitiveClassCheck(names[1]);
            isString = buildPrimitiveClassCheck(names[2]);

            isDate = buildClassCheck(names[3]);
            isRegExp = buildClassCheck(names[4]);

            // Wanted to enhance performance here by using simply "typeof"
            // but Firefox has two major issues that make this impossible,
            // one fixed, the other not, so perform a full class check here.
            //
            // 1. Regexes can be typeof "function" in FF < 3
            //    https://bugzilla.mozilla.org/show_bug.cgi?id=61911 (fixed)
            //
            // 2. HTMLEmbedElement and HTMLObjectElement are be typeof "function"
            //    https://bugzilla.mozilla.org/show_bug.cgi?id=268945 (won't fix)
            isFunction = buildClassCheck(names[5]);


            isArray = Array.isArray || buildClassCheck(names[6]);
            isError = buildClassCheck(names[7]);

            isSet = buildClassCheck(names[8], typeof Set !== 'undefined' && Set);
            isMap = buildClassCheck(names[9], typeof Map !== 'undefined' && Map);

            // Add core types as known so that they can be checked by value below,
            // notably excluding Functions and adding Arguments and Error.
            addKnownType('Arguments');
            addKnownType(names[0]);
            addKnownType(names[1]);
            addKnownType(names[2]);
            addKnownType(names[3]);
            addKnownType(names[4]);
            addKnownType(names[6]);

        }

        function addArrayTypes() {
            var types = 'Int8 Uint8 Uint8Clamped Int16 Uint16 Int32 Uint32 Float32 Float64';
            forEach(spaceSplit(types), function (str) {
                addKnownType(str + 'Array');
            });
        }

        function addKnownType(className) {
            var str = '[object ' + className + ']';
            knownTypes[str] = true;
        }

        function isKnownType(className) {
            return knownTypes[className];
        }

        function buildClassCheck(className, globalObject) {
            if (globalObject && isClass(new globalObject, 'Object')) {
                return getConstructorClassCheck(globalObject);
            } else {
                return getToStringClassCheck(className);
            }
        }

        function getConstructorClassCheck(obj) {
            var ctorStr = String(obj);
            return function (obj) {
                return String(obj.constructor) === ctorStr;
            };
        }

        function getToStringClassCheck(className) {
            return function (obj, str) {
                // perf: Returning up front on instanceof appears to be slower.
                return isClass(obj, className, str);
            };
        }

        function buildPrimitiveClassCheck(className) {
            var type = className.toLowerCase();
            return function (obj) {
                var t = typeof obj;
                return t === type || t === 'object' && isClass(obj, className);
            };
        }

        addCoreTypes();
        addArrayTypes();

        isSerializable = function (obj, className) {
            // Only known objects can be serialized. This notably excludes functions,
            // host objects, Symbols (which are matched by reference), and instances
            // of classes. The latter can arguably be matched by value, but
            // distinguishing between these and host objects -- which should never be
            // compared by value -- is very tricky so not dealing with it here.
            className = className || classToString(obj);
            return isKnownType(className) || isPlainObject(obj, className);
        };

    }

    function isClass(obj, className, str) {
        if (!str) {
            str = classToString(obj);
        }
        return str === '[object ' + className + ']';
    }

    // Wrapping the core's "define" methods to
    // save a few bytes in the minified script.
    function wrapNamespace(method) {
        return function (sugarNamespace, arg1, arg2) {
            sugarNamespace[method](arg1, arg2);
        };
    }

    // Method define aliases
    var alias = wrapNamespace('alias'),
        defineStatic = wrapNamespace('defineStatic'),
        defineInstance = wrapNamespace('defineInstance'),
        defineStaticPolyfill = wrapNamespace('defineStaticPolyfill'),
        defineInstancePolyfill = wrapNamespace('defineInstancePolyfill'),
        defineInstanceAndStatic = wrapNamespace('defineInstanceAndStatic'),
        defineInstanceWithArguments = wrapNamespace('defineInstanceWithArguments');

    function defineInstanceSimilar(sugarNamespace, set, fn, flags) {
        defineInstance(sugarNamespace, collectSimilarMethods(set, fn), flags);
    }

    function defineInstanceAndStaticSimilar(sugarNamespace, set, fn, flags) {
        defineInstanceAndStatic(sugarNamespace, collectSimilarMethods(set, fn), flags);
    }

    function collectSimilarMethods(set, fn) {
        var methods = {};
        if (isString(set)) {
            set = spaceSplit(set);
        }
        forEach(set, function (el, i) {
            fn(methods, el, i);
        });
        return methods;
    }

    // This song and dance is to fix methods to a different length
    // from what they actually accept in order to stay in line with
    // spec. Additionally passing argument length, as some methods
    // throw assertion errors based on this (undefined check is not
    // enough). Fortunately for now spec is such that passing 3
    // actual arguments covers all requirements. Note that passing
    // the argument length also forces the compiler to not rewrite
    // length of the compiled function.
    function fixArgumentLength(fn) {
        var staticFn = function (a) {
            var args = arguments;
            return fn(a, args[1], args[2], args.length - 1);
        };
        staticFn.instance = function (b) {
            var args = arguments;
            return fn(this, b, args[1], args.length);
        };
        return staticFn;
    }

    function defineAccessor(namespace, name, fn) {
        setProperty(namespace, name, fn);
    }

    function defineOptionsAccessor(namespace, defaults) {
        var obj = simpleClone(defaults);

        function getOption(name) {
            return obj[name];
        }

        function setOption(arg1, arg2) {
            var options;
            if (arguments.length === 1) {
                options = arg1;
            } else {
                options = {};
                options[arg1] = arg2;
            }
            forEachProperty(options, function (val, name) {
                if (val === null) {
                    val = defaults[name];
                }
                obj[name] = val;
            });
        }

        defineAccessor(namespace, 'getOption', getOption);
        defineAccessor(namespace, 'setOption', setOption);
        return getOption;
    }

    // For methods defined directly on the prototype like Range
    function defineOnPrototype(ctor, methods) {
        var proto = ctor.prototype;
        forEachProperty(methods, function (val, key) {
            proto[key] = val;
        });
    }

    // Argument helpers

    function assertArgument(exists) {
        if (!exists) {
            throw new TypeError('Argument required');
        }
    }

    function assertCallable(obj) {
        if (!isFunction(obj)) {
            throw new TypeError('Function is not callable');
        }
    }

    function assertArray(obj) {
        if (!isArray(obj)) {
            throw new TypeError('Array required');
        }
    }

    function assertWritable(obj) {
        if (isPrimitive(obj)) {
            // If strict mode is active then primitives will throw an
            // error when attempting to write properties. We can't be
            // sure if strict mode is available, so pre-emptively
            // throw an error here to ensure consistent behavior.
            throw new TypeError('Property cannot be written');
        }
    }

    // Coerces an object to a positive integer.
    // Does not allow Infinity.
    function coercePositiveInteger(n) {
        n = +n || 0;
        if (n < 0 || !isNumber(n) || !isFinite(n)) {
            throw new RangeError('Invalid number');
        }
        return trunc(n);
    }


    // General helpers

    function isDefined(o) {
        return o !== undefined;
    }

    function isUndefined(o) {
        return o === undefined;
    }

    function privatePropertyAccessor(key) {
        var privateKey = PRIVATE_PROP_PREFIX + key;
        return function (obj, val) {
            if (arguments.length > 1) {
                setProperty(obj, privateKey, val);
                return obj;
            }
            return obj[privateKey];
        };
    }

    function setChainableConstructor(sugarNamespace, createFn) {
        sugarNamespace.prototype.constructor = function () {
            return createFn.apply(this, arguments);
        };
    }

    // Fuzzy matching helpers

    function getMatcher(f) {
        if (!isPrimitive(f)) {
            var className = classToString(f);
            if (isRegExp(f, className)) {
                return regexMatcher(f);
            } else if (isDate(f, className)) {
                return dateMatcher(f);
            } else if (isFunction(f, className)) {
                return functionMatcher(f);
            } else if (isPlainObject(f, className)) {
                return fuzzyMatcher(f);
            }
        }
        // Default is standard isEqual
        return defaultMatcher(f);
    }

    function fuzzyMatcher(obj) {
        var matchers = {};
        return function (el, i, arr) {
            var matched = true;
            if (!isObjectType(el)) {
                return false;
            }
            forEachProperty(obj, function (val, key) {
                matchers[key] = getOwn(matchers, key) || getMatcher(val);
                if (matchers[key].call(arr, el[key], i, arr) === false) {
                    matched = false;
                }
                return matched;
            });
            return matched;
        };
    }

    function defaultMatcher(f) {
        return function (el) {
            return isEqual(el, f);
        };
    }

    function regexMatcher(reg) {
        reg = RegExp(reg);
        return function (el) {
            return reg.test(el);
        };
    }

    function dateMatcher(d) {
        var ms = d.getTime();
        return function (el) {
            return !!(el && el.getTime) && el.getTime() === ms;
        };
    }

    function functionMatcher(fn) {
        return function (el, i, arr) {
            // Return true up front if match by reference
            return el === fn || fn.call(arr, el, i, arr);
        };
    }

    // Object helpers

    function getKeys(obj) {
        return Object.keys(obj);
    }

    function deepHasProperty(obj, key, any) {
        return handleDeepProperty(obj, key, any, true);
    }

    function deepGetProperty(obj, key, any) {
        return handleDeepProperty(obj, key, any, false);
    }

    function deepSetProperty(obj, key, val) {
        handleDeepProperty(obj, key, false, false, true, false, val);
        return obj;
    }

    function handleDeepProperty(obj, key, any, has, fill, fillLast, val) {
        var ns, bs, ps, cbi, set, isLast, isPush, isIndex, nextIsIndex, exists;
        ns = obj || undefined;
        if (key == null) return;

        if (isObjectType(key)) {
            // Allow array and array-like accessors
            bs = [key];
        } else {
            key = String(key);
            if (key.indexOf('..') !== -1) {
                return handleArrayIndexRange(obj, key, any, val);
            }
            bs = key.split('[');
        }

        set = isDefined(val);

        for (var i = 0, blen = bs.length; i < blen; i++) {
            ps = bs[i];

            if (isString(ps)) {
                ps = periodSplit(ps);
            }

            for (var j = 0, plen = ps.length; j < plen; j++) {
                key = ps[j];

                // Is this the last key?
                isLast = i === blen - 1 && j === plen - 1;

                // Index of the closing ]
                cbi = key.indexOf(']');

                // Is the key an array index?
                isIndex = cbi !== -1;

                // Is this array push syntax "[]"?
                isPush = set && cbi === 0;

                // If the bracket split was successful and this is the last element
                // in the dot split, then we know the next key will be an array index.
                nextIsIndex = blen > 1 && j === plen - 1;

                if (isPush) {
                    // Set the index to the end of the array
                    key = ns.length;
                } else if (isIndex) {
                    // Remove the closing ]
                    key = key.slice(0, -1);
                }

                // If the array index is less than 0, then
                // add its length to allow negative indexes.
                if (isIndex && key < 0) {
                    key = +key + ns.length;
                }

                // Bracket keys may look like users[5] or just [5], so the leading
                // characters are optional. We can enter the namespace if this is the
                // 2nd part, if there is only 1 part, or if there is an explicit key.
                if (i || key || blen === 1) {

                    exists = any ? key in ns : hasOwn(ns, key);

                    // Non-existent namespaces are only filled if they are intermediate
                    // (not at the end) or explicitly filling the last.
                    if (fill && (!isLast || fillLast) && !exists) {
                        // For our purposes, last only needs to be an array.
                        ns = ns[key] = nextIsIndex || (fillLast && isLast) ? [] : {};
                        continue;
                    }

                    if (has) {
                        if (isLast || !exists) {
                            return exists;
                        }
                    } else if (set && isLast) {
                        assertWritable(ns);
                        ns[key] = val;
                    }

                    ns = exists ? ns[key] : undefined;
                }

            }
        }
        return ns;
    }

    // Get object property with support for 0..1 style range notation.
    function handleArrayIndexRange(obj, key, any, val) {
        var match, start, end, leading, trailing, arr, set;
        match = key.match(PROPERTY_RANGE_REG);
        if (!match) {
            return;
        }

        set = isDefined(val);
        leading = match[1];

        if (leading) {
            arr = handleDeepProperty(obj, leading, any, false, set ? true : false, true);
        } else {
            arr = obj;
        }

        assertArray(arr);

        trailing = match[4];
        start = match[2] ? +match[2] : 0;
        end = match[3] ? +match[3] : arr.length;

        // A range of 0..1 is inclusive, so we need to add 1 to the end. If this
        // pushes the index from -1 to 0, then set it to the full length of the
        // array, otherwise it will return nothing.
        end = end === -1 ? arr.length : end + 1;

        if (set) {
            for (var i = start; i < end; i++) {
                handleDeepProperty(arr, i + trailing, any, false, true, false, val);
            }
        } else {
            arr = arr.slice(start, end);

            // If there are trailing properties, then they need to be mapped for each
            // element in the array.
            if (trailing) {
                if (trailing.charAt(0) === HALF_WIDTH_PERIOD) {
                    // Need to chomp the period if one is trailing after the range. We
                    // can't do this at the regex level because it will be required if
                    // we're setting the value as it needs to be concatentated together
                    // with the array index to be set.
                    trailing = trailing.slice(1);
                }
                return arr.map(function (el) {
                    return handleDeepProperty(el, trailing);
                });
            }
        }
        return arr;
    }

    function getOwnKey(obj, key) {
        if (hasOwn(obj, key)) {
            return key;
        }
    }

    function hasProperty(obj, prop) {
        return !isPrimitive(obj) && prop in obj;
    }

    function isObjectType(obj, type) {
        return !!obj && (type || typeof obj) === 'object';
    }

    function isPrimitive(obj, type) {
        type = type || typeof obj;
        return obj == null || type === 'string' || type === 'number' || type === 'boolean';
    }

    function isPlainObject(obj, className) {
        return isObjectType(obj) &&
            isClass(obj, 'Object', className) &&
            hasValidPlainObjectPrototype(obj) &&
            hasOwnEnumeratedProperties(obj);
    }

    function hasValidPlainObjectPrototype(obj) {
        var hasToString = 'toString' in obj;
        var hasConstructor = 'constructor' in obj;
        // An object created with Object.create(null) has no methods in the
        // prototype chain, so check if any are missing. The additional hasToString
        // check is for false positives on some host objects in old IE which have
        // toString but no constructor. If the object has an inherited constructor,
        // then check if it is Object (the "isPrototypeOf" tapdance here is a more
        // robust way of ensuring this if the global has been hijacked). Note that
        // accessing the constructor directly (without "in" or "hasOwnProperty")
        // will throw a permissions error in IE8 on cross-domain windows.
        return (!hasConstructor && !hasToString) ||
            (hasConstructor && !hasOwn(obj, 'constructor') &&
                hasOwn(obj.constructor.prototype, 'isPrototypeOf'));
    }

    function hasOwnEnumeratedProperties(obj) {
        // Plain objects are generally defined as having enumerated properties
        // all their own, however in early IE environments without defineProperty,
        // there may also be enumerated methods in the prototype chain, so check
        // for both of these cases.
        var objectProto = Object.prototype;
        for (var key in obj) {
            var val = obj[key];
            if (!hasOwn(obj, key) && val !== objectProto[key]) {
                return false;
            }
        }
        return true;
    }

    function simpleRepeat(n, fn) {
        for (var i = 0; i < n; i++) {
            fn(i);
        }
    }

    function simpleClone(obj) {
        return simpleMerge({}, obj);
    }

    function simpleMerge(target, source) {
        forEachProperty(source, function (val, key) {
            target[key] = val;
        });
        return target;
    }

    // Make primtives types like strings into objects.
    function coercePrimitiveToObject(obj) {
        if (isPrimitive(obj)) {
            obj = Object(obj);
        }
        if (NO_KEYS_IN_STRING_OBJECTS && isString(obj)) {
            forceStringCoercion(obj);
        }
        return obj;
    }

    // Force strings to have their indexes set in
    // environments that don't do this automatically.
    function forceStringCoercion(obj) {
        var i = 0, chr;
        while (chr = obj.charAt(i)) {
            obj[i++] = chr;
        }
    }

    // Equality helpers

    function isEqual(a, b, stack) {
        var aClass, bClass;
        if (a === b) {
            // Return quickly up front when matched by reference,
            // but be careful about 0 !== -0.
            return a !== 0 || 1 / a === 1 / b;
        }
        aClass = classToString(a);
        bClass = classToString(b);
        if (aClass !== bClass) {
            return false;
        }

        if (isSerializable(a, aClass) && isSerializable(b, bClass)) {
            return objectIsEqual(a, b, aClass, stack);
        } else if (isSet(a, aClass) && isSet(b, bClass)) {
            return a.size === b.size && isEqual(setToArray(a), setToArray(b), stack);
        } else if (isMap(a, aClass) && isMap(b, bClass)) {
            return a.size === b.size && isEqual(mapToArray(a), mapToArray(b), stack);
        } else if (isError(a, aClass) && isError(b, bClass)) {
            return a.toString() === b.toString();
        }

        return false;
    }

    function objectIsEqual(a, b, aClass, stack) {
        var aType = typeof a, bType = typeof b, propsEqual, count;
        if (aType !== bType) {
            return false;
        }
        if (isObjectType(a.valueOf())) {
            if (a.length !== b.length) {
                // perf: Quickly returning up front for arrays.
                return false;
            }
            count = 0;
            propsEqual = true;
            iterateWithCyclicCheck(a, false, stack, function (key, val, cyc, stack) {
                if (!cyc && (!(key in b) || !isEqual(val, b[key], stack))) {
                    propsEqual = false;
                }
                count++;
                return propsEqual;
            });
            if (!propsEqual || count !== getKeys(b).length) {
                return false;
            }
        }
        // Stringifying the value handles NaN, wrapped primitives, dates, and errors in one go.
        return a.valueOf().toString() === b.valueOf().toString();
    }

    // Serializes an object in a way that will provide a token unique
    // to the type, class, and value of an object. Host objects, class
    // instances etc, are not serializable, and are held in an array
    // of references that will return the index as a unique identifier
    // for the object. This array is passed from outside so that the
    // calling function can decide when to dispose of this array.
    function serializeInternal(obj, refs, stack) {
        var type = typeof obj, className, value, ref;

        // Return quickly for primitives to save cycles
        if (isPrimitive(obj, type) && !isRealNaN(obj)) {
            return type + obj;
        }

        className = classToString(obj);

        if (!isSerializable(obj, className)) {
            ref = indexOf(refs, obj);
            if (ref === -1) {
                ref = refs.length;
                refs.push(obj);
            }
            return ref;
        } else if (isObjectType(obj)) {
            value = serializeDeep(obj, refs, stack) + obj.toString();
        } else if (1 / obj === -Infinity) {
            value = '-0';
        } else if (obj.valueOf) {
            value = obj.valueOf();
        }
        return type + className + value;
    }

    function serializeDeep(obj, refs, stack) {
        var result = '';
        iterateWithCyclicCheck(obj, true, stack, function (key, val, cyc, stack) {
            result += cyc ? 'CYC' : key + serializeInternal(val, refs, stack);
        });
        return result;
    }

    function iterateWithCyclicCheck(obj, sortedKeys, stack, fn) {

        function next(val, key) {
            var cyc = false;

            // Allowing a step into the structure before triggering this check to save
            // cycles on standard JSON structures and also to try as hard as possible to
            // catch basic properties that may have been modified.
            if (stack.length > 1) {
                var i = stack.length;
                while (i--) {
                    if (stack[i] === val) {
                        cyc = true;
                    }
                }
            }

            stack.push(val);
            fn(key, val, cyc, stack);
            stack.pop();
        }

        function iterateWithSortedKeys() {
            // Sorted keys is required for serialization, where object order
            // does not matter but stringified order does.
            var arr = getKeys(obj).sort(), key;
            for (var i = 0; i < arr.length; i++) {
                key = arr[i];
                next(obj[key], arr[i]);
            }
        }

        // This method for checking for cyclic structures was egregiously stolen from
        // the ingenious method by @kitcambridge from the Underscore script:
        // https://github.com/documentcloud/underscore/issues/240
        if (!stack) {
            stack = [];
        }

        if (sortedKeys) {
            iterateWithSortedKeys();
        } else {
            forEachProperty(obj, next);
        }
    }


    // Array helpers

    function isArrayIndex(n) {
        return n >>> 0 == n && n != 0xFFFFFFFF;
    }

    function iterateOverSparseArray(arr, fn, fromIndex, loop) {
        var indexes = getSparseArrayIndexes(arr, fromIndex, loop), index;
        for (var i = 0, len = indexes.length; i < len; i++) {
            index = indexes[i];
            fn.call(arr, arr[index], index, arr);
        }
        return arr;
    }

    // It's unclear whether or not sparse arrays qualify as "simple enumerables".
    // If they are not, however, the wrapping function will be deoptimized, so
    // isolate here (also to share between es5 and array modules).
    function getSparseArrayIndexes(arr, fromIndex, loop, fromRight) {
        var indexes = [], i;
        for (i in arr) {
            if (isArrayIndex(i) && (loop || (fromRight ? i <= fromIndex : i >= fromIndex))) {
                indexes.push(+i);
            }
        }
        indexes.sort(function (a, b) {
            var aLoop = a > fromIndex;
            var bLoop = b > fromIndex;
            if (aLoop !== bLoop) {
                return aLoop ? -1 : 1;
            }
            return a - b;
        });
        return indexes;
    }

    function getEntriesForIndexes(obj, find, loop, isString) {
        var result, length = obj.length;
        if (!isArray(find)) {
            return entryAtIndex(obj, find, length, loop, isString);
        }
        result = new Array(find.length);
        forEach(find, function (index, i) {
            result[i] = entryAtIndex(obj, index, length, loop, isString);
        });
        return result;
    }

    function getNormalizedIndex(index, length, loop) {
        if (index && loop) {
            index = index % length;
        }
        if (index < 0) index = length + index;
        return index;
    }

    function entryAtIndex(obj, index, length, loop, isString) {
        index = getNormalizedIndex(index, length, loop);
        return isString ? obj.charAt(index) : obj[index];
    }

    function mapWithShortcuts(el, f, context, mapArgs) {
        if (!f) {
            return el;
        } else if (f.apply) {
            return f.apply(context, mapArgs || []);
        } else if (isArray(f)) {
            return f.map(function (m) {
                return mapWithShortcuts(el, m, context, mapArgs);
            });
        } else if (isFunction(el[f])) {
            return el[f].call(el);
        } else {
            return deepGetProperty(el, f);
        }
    }

    function spaceSplit(str) {
        return str.split(' ');
    }

    function commaSplit(str) {
        return str.split(HALF_WIDTH_COMMA);
    }

    function periodSplit(str) {
        return str.split(HALF_WIDTH_PERIOD);
    }

    function forEach(arr, fn) {
        for (var i = 0, len = arr.length; i < len; i++) {
            if (!(i in arr)) {
                return iterateOverSparseArray(arr, fn, i);
            }
            fn(arr[i], i);
        }
    }

    function filter(arr, fn) {
        var result = [];
        for (var i = 0, len = arr.length; i < len; i++) {
            var el = arr[i];
            if (i in arr && fn(el, i)) {
                result.push(el);
            }
        }
        return result;
    }

    function map(arr, fn) {
        // perf: Not using fixed array len here as it may be sparse.
        var result = [];
        for (var i = 0, len = arr.length; i < len; i++) {
            if (i in arr) {
                result.push(fn(arr[i], i));
            }
        }
        return result;
    }

    function indexOf(arr, el) {
        for (var i = 0, len = arr.length; i < len; i++) {
            if (i in arr && arr[i] === el) return i;
        }
        return -1;
    }

    // Number helpers

    var trunc = Math.trunc || function (n) {
        if (n === 0 || !isFinite(n)) return n;
        return n < 0 ? ceil(n) : floor(n);
    };

    function isRealNaN(obj) {
        // This is only true of NaN
        return obj != null && obj !== obj;
    }

    function withPrecision(val, precision, fn) {
        var multiplier = pow(10, abs(precision || 0));
        fn = fn || round;
        if (precision < 0) multiplier = 1 / multiplier;
        return fn(val * multiplier) / multiplier;
    }

    function padNumber(num, place, sign, base, replacement) {
        var str = abs(num).toString(base || 10);
        str = repeatString(replacement || '0', place - str.replace(/\.\d+/, '').length) + str;
        if (sign || num < 0) {
            str = (num < 0 ? '-' : '+') + str;
        }
        return str;
    }

    function getOrdinalSuffix(num) {
        if (num >= 11 && num <= 13) {
            return 'th';
        } else {
            switch (num % 10) {
                case 1:
                    return 'st';
                case 2:
                    return 'nd';
                case 3:
                    return 'rd';
                default:
                    return 'th';
            }
        }
    }

    // Fullwidth number helpers
    var fullWidthNumberReg, fullWidthNumberMap, fullWidthNumbers;

    function buildFullWidthNumber() {
        var fwp = FULL_WIDTH_PERIOD, hwp = HALF_WIDTH_PERIOD, hwc = HALF_WIDTH_COMMA, fwn = '';
        fullWidthNumberMap = {};
        for (var i = 0, digit; i <= 9; i++) {
            digit = chr(i + FULL_WIDTH_ZERO);
            fwn += digit;
            fullWidthNumberMap[digit] = chr(i + HALF_WIDTH_ZERO);
        }
        fullWidthNumberMap[hwc] = '';
        fullWidthNumberMap[fwp] = hwp;
        // Mapping this to itself to capture it easily
        // in stringToNumber to detect decimals later.
        fullWidthNumberMap[hwp] = hwp;
        fullWidthNumberReg = allCharsReg(fwn + fwp + hwc + hwp);
        fullWidthNumbers = fwn;
    }

    // Takes into account full-width characters, commas, and decimals.
    function stringToNumber(str, base) {
        var sanitized, isDecimal;
        sanitized = str.replace(fullWidthNumberReg, function (chr) {
            var replacement = getOwn(fullWidthNumberMap, chr);
            if (replacement === HALF_WIDTH_PERIOD) {
                isDecimal = true;
            }
            return replacement;
        });
        return isDecimal ? parseFloat(sanitized) : parseInt(sanitized, base || 10);
    }

    // Math aliases
    var abs = Math.abs,
        pow = Math.pow,
        min = Math.min,
        max = Math.max,
        ceil = Math.ceil,
        floor = Math.floor,
        round = Math.round;


    // String helpers

    var chr = String.fromCharCode;

    function trim(str) {
        return str.trim();
    }

    function repeatString(str, num) {
        var result = '';
        str = str.toString();
        while (num > 0) {
            if (num & 1) {
                result += str;
            }
            if (num >>= 1) {
                str += str;
            }
        }
        return result;
    }

    function simpleCapitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    function createFormatMatcher(bracketMatcher, percentMatcher, precheck) {

        var reg = STRING_FORMAT_REG;
        var compileMemoized = memoizeFunction(compile);

        function getToken(format, match) {
            var get, token, literal, fn;
            var bKey = match[2];
            var pLit = match[3];
            var pKey = match[5];
            if (match[4] && percentMatcher) {
                token = pKey;
                get = percentMatcher;
            } else if (bKey) {
                token = bKey;
                get = bracketMatcher;
            } else if (pLit && percentMatcher) {
                literal = pLit;
            } else {
                literal = match[1] || match[0];
            }
            if (get) {
                assertPassesPrecheck(precheck, bKey, pKey);
                fn = function (obj, opt) {
                    return get(obj, token, opt);
                };
            }
            format.push(fn || getLiteral(literal));
        }

        function getSubstring(format, str, start, end) {
            if (end > start) {
                var sub = str.slice(start, end);
                assertNoUnmatched(sub, OPEN_BRACE);
                assertNoUnmatched(sub, CLOSE_BRACE);
                format.push(function () {
                    return sub;
                });
            }
        }

        function getLiteral(str) {
            return function () {
                return str;
            };
        }

        function assertPassesPrecheck(precheck, bt, pt) {
            if (precheck && !precheck(bt, pt)) {
                throw new TypeError('Invalid token ' + (bt || pt) + ' in format string');
            }
        }

        function assertNoUnmatched(str, chr) {
            if (str.indexOf(chr) !== -1) {
                throw new TypeError('Unmatched ' + chr + ' in format string');
            }
        }

        function compile(str) {
            var format = [], lastIndex = 0, match;
            reg.lastIndex = 0;
            while (match = reg.exec(str)) {
                getSubstring(format, str, lastIndex, match.index);
                getToken(format, match);
                lastIndex = reg.lastIndex;
            }
            getSubstring(format, str, lastIndex, str.length);
            return format;
        }

        return function (str, obj, opt) {
            var format = compileMemoized(str), result = '';
            for (var i = 0; i < format.length; i++) {
                result += format[i](obj, opt);
            }
            return result;
        };
    }

    // Inflection helper

    var Inflections = {};

    function getAcronym(str) {
        return Inflections.acronyms && Inflections.acronyms.find(str);
    }

    function getHumanWord(str) {
        return Inflections.human && Inflections.human.find(str);
    }

    function runHumanRules(str) {
        return Inflections.human && Inflections.human.runRules(str) || str;
    }

    // RegExp helpers

    function allCharsReg(src) {
        return RegExp('[' + src + ']', 'g');
    }

    function getRegExpFlags(reg, add) {
        var flags = '';
        add = add || '';

        function checkFlag(prop, flag) {
            if (prop || add.indexOf(flag) > -1) {
                flags += flag;
            }
        }

        checkFlag(reg.global, 'g');
        checkFlag(reg.ignoreCase, 'i');
        checkFlag(reg.multiline, 'm');
        checkFlag(reg.sticky, 'y');
        return flags;
    }

    function escapeRegExp(str) {
        if (!isString(str)) str = String(str);
        return str.replace(/([\\\/\'*+?|()\[\]{}.^$-])/g, '\\$1');
    }

    // Date helpers

    var _utc = privatePropertyAccessor('utc');

    function callDateGet(d, method) {
        return d['get' + (_utc(d) ? 'UTC' : '') + method]();
    }

    function callDateSet(d, method, value, safe) {
        // "Safe" denotes not setting the date if the value is the same as what is
        // currently set. In theory this should be a noop, however it will cause
        // timezone shifts when in the middle of a DST fallback. This is unavoidable
        // as the notation itself is ambiguous (i.e. there are two "1:00ams" on
        // November 1st, 2015 in northern hemisphere timezones that follow DST),
        // however when advancing or rewinding dates this can throw off calculations
        // so avoiding this unintentional shifting on an opt-in basis.
        if (safe && value === callDateGet(d, method, value)) {
            return;
        }
        d['set' + (_utc(d) ? 'UTC' : '') + method](value);
    }

    // Memoization helpers

    var INTERNAL_MEMOIZE_LIMIT = 1000;

    // Note that attemps to consolidate this with Function#memoize
    // ended up clunky as that is also serializing arguments. Separating
    // these implementations turned out to be simpler.
    function memoizeFunction(fn) {
        var memo = {}, counter = 0;

        return function (key) {
            if (hasOwn(memo, key)) {
                return memo[key];
            }
            if (counter === INTERNAL_MEMOIZE_LIMIT) {
                memo = {};
                counter = 0;
            }
            counter++;
            return memo[key] = fn(key);
        };
    }

    // ES6 helpers

    function setToArray(set) {
        var arr = new Array(set.size), i = 0;
        set.forEach(function (val) {
            arr[i++] = val;
        });
        return arr;
    }

    function mapToArray(map) {
        var arr = new Array(map.size), i = 0;
        map.forEach(function (val, key) {
            arr[i++] = [key, val];
        });
        return arr;
    }

    buildClassChecks();
    buildFullWidthNumber();

    /***
     * @module ES5
     * @description Functions and polyfill methods that fix ES5 functionality. This
     *              module is excluded from default builds, and can be included if
     *              you need legacy browser support (IE8 and below).
     *
     ***/

        // Non-enumerable properties on Object.prototype. In early JScript implementations
        // (< IE9) these will shadow object properties and break for..in loops.
    var DONT_ENUM_PROPS = [
            'valueOf',
            'toString',
            'constructor',
            'isPrototypeOf',
            'hasOwnProperty',
            'toLocaleString',
            'propertyIsEnumerable'
        ];

    /***
     * @fix
     * @short Fixes DontEnum bug for iteration methods in < IE9.
     ***/
    function buildDontEnumFix() {
        if (!({toString: 1}).propertyIsEnumerable('toString')) {
            var forEachEnumerableProperty = forEachProperty;
            forEachProperty = function (obj, fn) {
                forEachEnumerableProperty(obj, fn);
                for (var i = 0, key; key = DONT_ENUM_PROPS[i]; i++) {
                    if (hasOwn(obj, key)) {
                        if (fn.call(obj, obj[key], key, obj) === false) break;
                    }
                }
            };
        }
    }

    /***
     * @fix
     * @short Adds native methods to chainables in < IE9.
     ***/
    function buildChainableNativeMethodsFix() {
        if (!Object.getOwnPropertyNames) {
            defineNativeMethodsOnChainable();
        }
    }

    // Polyfilled methods will automatically be added to the chainable prototype.
    // However, Object.getOwnPropertyNames cannot be shimmed for non-enumerable
    // properties, so if it does not exist, then the only way to access native
    // methods previous to ES5 is to provide them as a list of tokens here.
    function defineNativeMethodsOnChainable() {

        var nativeTokens = {
            'Function': 'apply,call',
            'RegExp': 'compile,exec,test',
            'Number': 'toExponential,toFixed,toLocaleString,toPrecision',
            'Object': 'hasOwnProperty,isPrototypeOf,propertyIsEnumerable,toLocaleString',
            'Array': 'concat,join,pop,push,reverse,shift,slice,sort,splice,toLocaleString,unshift',
            'Date': 'getTime,getTimezoneOffset,setTime,toDateString,toGMTString,toLocaleDateString,toLocaleString,toLocaleTimeString,toTimeString,toUTCString',
            'String': 'anchor,big,blink,bold,charAt,charCodeAt,concat,fixed,fontcolor,fontsize,indexOf,italics,lastIndexOf,link,localeCompare,match,replace,search,slice,small,split,strike,sub,substr,substring,sup,toLocaleLowerCase,toLocaleUpperCase,toLowerCase,toUpperCase'
        };

        var dateTokens = 'FullYear,Month,Date,Hours,Minutes,Seconds,Milliseconds'.split(',');

        function addDateTokens(prefix, arr) {
            for (var i = 0; i < dateTokens.length; i++) {
                arr.push(prefix + dateTokens[i]);
            }
        }

        forEachProperty(nativeTokens, function (str, name) {
            var tokens = str.split(',');
            if (name === 'Date') {
                addDateTokens('get', tokens);
                addDateTokens('set', tokens);
                addDateTokens('getUTC', tokens);
                addDateTokens('setUTC', tokens);
            }
            tokens.push('toString');
            mapNativeToChainable(name, tokens);
        });

    }


    buildDontEnumFix();
    buildChainableNativeMethodsFix();


    /*** @namespace Object ***/

    function assertNonNull(obj) {
        if (obj == null) {
            throw new TypeError('Object required');
        }
    }

    defineStaticPolyfill(sugarObject, {

        'keys': function (obj) {
            var keys = [];
            assertNonNull(obj);
            forEachProperty(coercePrimitiveToObject(obj), function (val, key) {
                keys.push(key);
            });
            return keys;
        }

    });


    /*** @namespace Array ***/

    function arrayIndexOf(arr, search, fromIndex, fromRight) {
        var length = arr.length, defaultFromIndex, index, increment;

        increment = fromRight ? -1 : 1;
        defaultFromIndex = fromRight ? length - 1 : 0;
        fromIndex = trunc(fromIndex);
        if (!fromIndex && fromIndex !== 0) {
            fromIndex = defaultFromIndex;
        }
        if (fromIndex < 0) {
            fromIndex = length + fromIndex;
        }
        if ((!fromRight && fromIndex < 0) || (fromRight && fromIndex >= length)) {
            fromIndex = defaultFromIndex;
        }

        index = fromIndex;

        while ((fromRight && index >= 0) || (!fromRight && index < length)) {
            if (!(index in arr)) {
                return sparseIndexOf(arr, search, fromIndex, fromRight);
            }
            if (isArrayIndex(index) && arr[index] === search) {
                return index;
            }
            index += increment;
        }
        return -1;
    }

    function sparseIndexOf(arr, search, fromIndex, fromRight) {
        var indexes = getSparseArrayIndexes(arr, fromIndex, false, fromRight), index;
        indexes.sort(function (a, b) {
            return fromRight ? b - a : a - b;
        });
        while ((index = indexes.shift()) !== undefined) {
            if (arr[index] === search) {
                return +index;
            }
        }
        return -1;
    }

    function arrayReduce(arr, fn, initialValue, fromRight) {
        var length = arr.length, count = 0, defined = isDefined(initialValue), result, index;
        assertCallable(fn);
        if (length == 0 && !defined) {
            throw new TypeError('Reduce called on empty array with no initial value');
        } else if (defined) {
            result = initialValue;
        } else {
            result = arr[fromRight ? length - 1 : count];
            count++;
        }
        while (count < length) {
            index = fromRight ? length - count - 1 : count;
            if (index in arr) {
                result = fn(result, arr[index], index, arr);
            }
            count++;
        }
        return result;
    }

    defineStaticPolyfill(sugarArray, {

        /***
         *
         * @method isArray(obj)
         * @returns Boolean
         * @polyfill ES5
         * @static
         * @short Returns true if `obj` is an Array.
         *
         * @example
         *
         *   Array.isArray(3)        -> false
         *   Array.isArray(true)     -> false
         *   Array.isArray('wasabi') -> false
         *   Array.isArray([1,2,3])  -> true
         *
         ***/
        'isArray': function (obj) {
            return isArray(obj);
        }

    });

    defineInstancePolyfill(sugarArray, {

        'every': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            var length = this.length, index = 0;
            assertCallable(fn);
            while (index < length) {
                if (index in this && !fn.call(context, this[index], index, this)) {
                    return false;
                }
                index++;
            }
            return true;
        },

        'some': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            var length = this.length, index = 0;
            assertCallable(fn);
            while (index < length) {
                if (index in this && fn.call(context, this[index], index, this)) {
                    return true;
                }
                index++;
            }
            return false;
        },

        'map': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            var length = this.length, index = 0, result = new Array(length);
            assertCallable(fn);
            while (index < length) {
                if (index in this) {
                    result[index] = fn.call(context, this[index], index, this);
                }
                index++;
            }
            return result;
        },

        'filter': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            var length = this.length, index = 0, result = [];
            assertCallable(fn);
            while (index < length) {
                if (index in this && fn.call(context, this[index], index, this)) {
                    result.push(this[index]);
                }
                index++;
            }
            return result;
        },

        /***
         * @method indexOf(search, [fromIndex] = 0)
         * @returns Number
         * @polyfill ES5
         * @short Searches the array and returns the first index where `search` occurs,
         *        or `-1` if the element is not found.
         * @extra [fromIndex] is the index from which to begin the search. This
         *        method performs a simple strict equality comparison on `search`.
         *        Sugar does not enhance this method to support `enhanced matching`.
         *        For such functionality, use the `findIndex` method instead.
         *
         * @example
         *
         *   [1,2,3].indexOf(3) -> 1
         *   [1,2,3].indexOf(7) -> -1
         *
         ***/
        'indexOf': function (search) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, fromIndex = arguments[1];
            if (isString(this)) return this.indexOf(search, fromIndex);
            return arrayIndexOf(this, search, fromIndex);
        },

        /***
         * @method lastIndexOf(search, [fromIndex] = array.length - 1)
         * @returns Number
         * @polyfill ES5
         * @short Searches the array from the end and returns the first index where
         *        `search` occurs, or `-1` if the element is not found.
         * @extra [fromIndex] is the index from which to begin the search. This method
         *        performs a simple strict equality comparison on `search`.
         *        Sugar does not enhance this method to support `enhanced matching`.
         *
         * @example
         *
         *   [1,2,1].lastIndexOf(1) -> 2
         *   [1,2,1].lastIndexOf(7) -> -1
         *
         ***/
        'lastIndexOf': function (search) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, fromIndex = arguments[1];
            if (isString(this)) return this.lastIndexOf(search, fromIndex);
            return arrayIndexOf(this, search, fromIndex, true);
        },

        /***
         * @method forEach([fn], [context])
         * @polyfill ES5
         * @short Iterates over the array, calling [fn] on each loop.
         * @extra [context] becomes the `this` object.
         *
         * @callback fn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   ['a','b','c'].forEach(function(a) {
         *     // Called 3 times: 'a','b','c'
         *   });
         *
         ***/
        'forEach': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            var length = this.length, index = 0;
            assertCallable(fn);
            while (index < length) {
                if (index in this) {
                    fn.call(context, this[index], index, this);
                }
                index++;
            }
        },

        /***
         * @method reduce(fn, [init])
         * @returns Mixed
         * @polyfill ES5
         * @short Reduces the array to a single result.
         * @extra This operation is sometimes called "accumulation", as it takes the
         *        result of the last iteration of `fn` and passes it as the first
         *        argument to the next iteration, "accumulating" that value as it goes.
         *        The return value of this method will be the return value of the final
         *        iteration of `fn`. If [init] is passed, it will be the initial
         *        "accumulator" (the first argument). If [init] is not passed, then it
         *        will take the first element in the array, and `fn` will not be called
         *        for that element.
         *
         * @callback fn
         *
         *   acc  The "accumulator". Either [init], the result of the last iteration
         *        of `fn`, or the first element of the array.
         *   el   The current element for this iteration.
         *   idx  The current index for this iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].reduce(function(a, b) {
         *     return a - b; // 1 - 2 - 3
         *   });
         *
         *   [1,2,3].reduce(function(a, b) {
         *     return a - b; // 100 - 1 - 2 - 3
         *   }, 100);
         *
         ***/
        'reduce': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            return arrayReduce(this, fn, context);
        },

        /***
         * @method reduceRight([fn], [init])
         * @returns Mixed
         * @polyfill ES5
         * @short Similar to `Array#reduce`, but operates on the elements in reverse.
         *
         * @callback fn
         *
         *   acc  The "accumulator", either [init], the result of the last iteration
         *        of `fn`, or the last element of the array.
         *   el   The current element for this iteration.
         *   idx  The current index for this iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].reduceRight(function(a, b) {
         *     return a - b; // 3 - 2 - 1
         *   });
         *
         *   [1,2,3].reduceRight(function(a, b) {
         *     return a - b; // 100 - 3 - 2 - 1
         *   }, 100);
         *
         *
         ***/
        'reduceRight': function (fn) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            return arrayReduce(this, fn, context, true);
        }

    });


    /*** @namespace String ***/

    var TRIM_REG = RegExp('^[' + TRIM_CHARS + ']+|[' + TRIM_CHARS + ']+$', 'g');

    defineInstancePolyfill(sugarString, {
        /***
         * @method trim()
         * @returns String
         * @polyfill ES5
         * @short Removes leading and trailing whitespace from the string.
         * @extra Whitespace is defined as line breaks, tabs, and any character in the
         *        "Space, Separator" Unicode category, conforming to the the ES5 spec.
         *
         * @example
         *
         *   '   wasabi   '.trim()      -> 'wasabi'
         *   '   wasabi   '.trimLeft()  -> 'wasabi   '
         *   '   wasabi   '.trimRight() -> '   wasabi'
         *
         ***/
        'trim': function () {
            return this.toString().replace(TRIM_REG, '');
        }
    });


    /*** @namespace Function ***/

    defineInstancePolyfill(sugarFunction, {

        /***
         * @method bind(context, [arg1], ...)
         * @returns Function
         * @polyfill ES5
         * @short Binds `context` as the `this` object for the function when it is
         *        called. Also allows currying an unlimited number of parameters.
         * @extra "currying" means setting parameters ([arg1], [arg2], etc.) ahead of
         *        time so that they are passed when the function is called later. If
         *        you pass additional parameters when the function is actually called,
         *        they will be added to the end of the curried parameters.
         *
         * @example
         *
         *   logThis.bind('woof')()   -> logs 'woof' as its this object
         *   addArgs.bind(1, 2, 3)()  -> returns 5 with 1 as the this object
         *   addArgs.bind(1)(2, 3, 4) -> returns 9
         *
         ***/
        'bind': function (context) {
            // Optimized: no leaking arguments
            var boundArgs = [];
            for (var $i = 1, $len = arguments.length; $i < $len; $i++) boundArgs.push(arguments[$i]);
            var fn = this, bound;
            assertCallable(this);
            bound = function () {
                // Optimized: no leaking arguments
                var args = [];
                for (var $i = 0, $len = arguments.length; $i < $len; $i++) args.push(arguments[$i]);
                return fn.apply(fn.prototype && this instanceof fn ? this : context, boundArgs.concat(args));
            };
            bound.prototype = this.prototype;
            return bound;
        }

    });


    /*** @namespace Date ***/

    defineStaticPolyfill(sugarDate, {

        /***
         * @method now()
         * @returns String
         * @polyfill ES5
         * @static
         * @short Returns the current time as a Unix timestamp.
         * @extra The number of milliseconds since January 1st, 1970 00:00:00 (UTC).
         *
         * @example
         *
         *   Date.now() -> ex. 1311938296231
         *
         ***/
        'now': function () {
            return new Date().getTime();
        }

    });

    function hasISOSupport() {
        var d = new Date(Date.UTC(2000, 0));
        return !!d.toISOString && d.toISOString() === '2000-01-01T00:00:00.000Z';
    }

    defineInstancePolyfill(sugarDate, {

        /***
         * @method toISOString()
         * @returns String
         * @polyfill ES5
         * @short Formats the string to ISO8601 format.
         * @extra This will always format as UTC time.
         *
         * @example
         *
         *   Date.create().toISOString() -> ex. 2011-07-05 12:24:55.528Z
         *
         ***/
        'toISOString': function () {
            return padNumber(this.getUTCFullYear(), 4) + '-' +
                padNumber(this.getUTCMonth() + 1, 2) + '-' +
                padNumber(this.getUTCDate(), 2) + 'T' +
                padNumber(this.getUTCHours(), 2) + ':' +
                padNumber(this.getUTCMinutes(), 2) + ':' +
                padNumber(this.getUTCSeconds(), 2) + '.' +
                padNumber(this.getUTCMilliseconds(), 3) + 'Z';
        },

        /***
         * @method toJSON([key])
         * @returns String
         * @polyfill ES5
         * @short Returns a JSON representation of the date.
         * @extra This is effectively an alias for `toISOString`. Will always return
         *        the date in UTC time. [key] is ignored.
         *
         * @example
         *
         *   Date.create().toJSON() -> ex. 2011-07-05 12:24:55.528Z
         *
         ***/
        'toJSON': function (key) {
            // Force compiler to respect argument length.
            var argLen = arguments.length;
            return this.toISOString(key);
        }

    }, !hasISOSupport());

    /***
     * @module ES6
     * @description Polyfills that provide basic ES6 compatibility. This module
     *              provides the base for Sugar functionality, but is not a full
     *              polyfill suite.
     *
     ***/


    /*** @namespace String ***/

    function getCoercedStringSubject(obj) {
        if (obj == null) {
            throw new TypeError('String required.');
        }
        return String(obj);
    }

    function getCoercedSearchString(obj) {
        if (isRegExp(obj)) {
            throw new TypeError();
        }
        return String(obj);
    }

    defineInstancePolyfill(sugarString, {

        /***
         * @method includes(search, [pos] = 0)
         * @returns Boolean
         * @polyfill ES6
         * @short Returns true if `search` is contained within the string.
         * @extra Search begins at [pos], which defaults to the beginning of the
         *        string. Sugar enhances this method to allow matching a regex.
         *
         * @example
         *
         *   'jumpy'.includes('py')      -> true
         *   'broken'.includes('ken', 3) -> true
         *   'broken'.includes('bro', 3) -> false
         *
         ***/
        'includes': function (searchString) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, pos = arguments[1];
            var str = getCoercedStringSubject(this);
            searchString = getCoercedSearchString(searchString);
            return str.indexOf(searchString, pos) !== -1;
        },

        /***
         * @method startsWith(search, [pos] = 0)
         * @returns Boolean
         * @polyfill ES6
         * @short Returns true if the string starts with substring `search`.
         * @extra Search begins at [pos], which defaults to the entire string length.
         *
         * @example
         *
         *   'hello'.startsWith('hell')   -> true
         *   'hello'.startsWith('HELL')   -> false
         *   'hello'.startsWith('ell', 1) -> true
         *
         ***/
        'startsWith': function (searchString) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, position = arguments[1];
            var str, start, pos, len, searchLength;
            str = getCoercedStringSubject(this);
            searchString = getCoercedSearchString(searchString);
            pos = +position || 0;
            len = str.length;
            start = min(max(pos, 0), len);
            searchLength = searchString.length;
            if (searchLength + start > len) {
                return false;
            }
            if (str.substr(start, searchLength) === searchString) {
                return true;
            }
            return false;
        },

        /***
         * @method endsWith(search, [pos] = length)
         * @returns Boolean
         * @polyfill ES6
         * @short Returns true if the string ends with substring `search`.
         * @extra Search ends at [pos], which defaults to the entire string length.
         *
         * @example
         *
         *   'jumpy'.endsWith('py')    -> true
         *   'jumpy'.endsWith('MPY')   -> false
         *   'jumpy'.endsWith('mp', 4) -> false
         *
         ***/
        'endsWith': function (searchString) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, endPosition = arguments[1];
            var str, start, end, pos, len, searchLength;
            str = getCoercedStringSubject(this);
            searchString = getCoercedSearchString(searchString);
            len = str.length;
            pos = len;
            if (isDefined(endPosition)) {
                pos = +endPosition || 0;
            }
            end = min(max(pos, 0), len);
            searchLength = searchString.length;
            start = end - searchLength;
            if (start < 0) {
                return false;
            }
            if (str.substr(start, searchLength) === searchString) {
                return true;
            }
            return false;
        },

        /***
         * @method repeat([num] = 0)
         * @returns String
         * @polyfill ES6
         * @short Returns the string repeated [num] times.
         *
         * @example
         *
         *   'jumpy'.repeat(2) -> 'jumpyjumpy'
         *   'a'.repeat(5)     -> 'aaaaa'
         *   'a'.repeat(0)     -> ''
         *
         ***/
        'repeat': function (num) {
            num = coercePositiveInteger(num);
            return repeatString(this, num);
        }

    });


    /*** @namespace Number ***/

    defineStaticPolyfill(sugarNumber, {

        /***
         * @method isNaN(value)
         * @returns Boolean
         * @polyfill ES6
         * @static
         * @short Returns true only if the number is `NaN`.
         * @extra This is differs from the global `isNaN`, which returns true for
         *        anything that is not a number.
         *
         * @example
         *
         *   Number.isNaN(NaN) -> true
         *   Number.isNaN('n') -> false
         *
         ***/
        'isNaN': function (obj) {
            return isRealNaN(obj);
        }

    });


    /*** @namespace Array ***/

    function getCoercedObject(obj) {
        if (obj == null) {
            throw new TypeError('Object required.');
        }
        return coercePrimitiveToObject(obj);
    }

    defineStaticPolyfill(sugarArray, {

        /***
         * @method from(a, [map], [context])
         * @returns Mixed
         * @polyfill ES6
         * @static
         * @short Creates an array from an array-like object.
         * @extra If a function is passed for [map], it will be map each element of
         *        the array. [context] is the `this` object if passed.
         *
         * @callback map
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   Array.from({0:'a',1:'b',length:2}); -> ['a','b']
         *
         ***/
        'from': function (a) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, map = arguments[1], context = arguments[2];
            var len, arr;
            if (isDefined(map)) {
                assertCallable(map);
            }
            a = getCoercedObject(a);
            len = trunc(max(0, a.length || 0));
            if (!isArrayIndex(len)) {
                throw new RangeError('Invalid array length');
            }
            if (isFunction(this)) {
                arr = new this(len);
                arr.length = len;
            } else {
                arr = new Array(len);
            }
            for (var i = 0; i < len; i++) {
                setProperty(arr, i, isDefined(map) ? map.call(context, a[i], i) : a[i], true);
            }
            return arr;
        }

    });

    defineInstancePolyfill(sugarArray, {

        'find': function (f) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            assertCallable(f);
            for (var i = 0, len = this.length; i < len; i++) {
                if (f.call(context, this[i], i, this)) {
                    return this[i];
                }
            }
        },

        'findIndex': function (f) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, context = arguments[1];
            assertCallable(f);
            for (var i = 0, len = this.length; i < len; i++) {
                if (f.call(context, this[i], i, this)) {
                    return i;
                }
            }
            return -1;
        }

    });

    /***
     * @module ES7
     * @description Polyfills that provide basic ES7 compatibility. This module
     *              provides the base for Sugar functionality, but is not a full
     *              polyfill suite.
     *
     ***/


    /*** @namespace Array ***/

    function sameValueZero(a, b) {
        if (isRealNaN(a)) {
            return isRealNaN(b);
        }
        return a === b ? a !== 0 || 1 / a === 1 / b : false;
    }

    defineInstancePolyfill(sugarArray, {

        /***
         * @method includes(search, [fromIndex] = 0)
         * @returns Boolean
         * @polyfill ES7
         * @short Returns true if `search` is contained within the array.
         * @extra Search begins at [fromIndex], which defaults to the beginning of the
         *        array.
         *
         * @example
         *
         *   [1,2,3].includes(2)    -> true
         *   [1,2,3].includes(4)    -> false
         *   [1,2,3].includes(2, 3) -> false
         *
         ***/
        'includes': function (search) {
            // Force compiler to respect argument length.
            var argLen = arguments.length, fromIndex = arguments[1];
            var arr = this, len;
            if (isString(arr)) return arr.includes(search, fromIndex);
            fromIndex = fromIndex ? fromIndex.valueOf() : 0;
            len = arr.length;
            if (fromIndex < 0) {
                fromIndex = max(0, fromIndex + len);
            }
            for (var i = fromIndex; i < len; i++) {
                if (sameValueZero(search, arr[i])) {
                    return true;
                }
            }
            return false;
        }

    });

    /***
     * @module String
     * @description String manupulation, encoding, truncation, and formatting, and more.
     *
     ***/

        // Flag allowing native string methods to be enhanced
    var STRING_ENHANCEMENTS_FLAG = 'enhanceString';

    // Matches non-punctuation characters except apostrophe for capitalization.
    var CAPITALIZE_REG = /[^\u0000-\u0040\u005B-\u0060\u007B-\u007F]+('s)?/g;

    // Regex matching camelCase.
    var CAMELIZE_REG = /(^|_)([^_]+)/g;

    // Regex matching any HTML entity.
    var HTML_ENTITY_REG = /&#?(x)?([\w\d]{0,5});/gi;

    // Very basic HTML escaping regex.
    var HTML_ESCAPE_REG = /[&<>]/g;

    // Special HTML entities.
    var HTMLFromEntityMap = {
        'lt': '<',
        'gt': '>',
        'amp': '&',
        'nbsp': ' ',
        'quot': '"',
        'apos': "'"
    };

    var HTMLToEntityMap;

    // Words that should not be capitalized in titles
    var DOWNCASED_WORDS = [
        'and', 'or', 'nor', 'a', 'an', 'the', 'so', 'but', 'to', 'of', 'at',
        'by', 'from', 'into', 'on', 'onto', 'off', 'out', 'in', 'over',
        'with', 'for'
    ];

    // HTML tags that do not have inner content.
    var HTML_VOID_ELEMENTS = [
        'area', 'base', 'br', 'col', 'command', 'embed', 'hr', 'img',
        'input', 'keygen', 'link', 'meta', 'param', 'source', 'track', 'wbr'
    ];

    var LEFT_TRIM_REG = RegExp('^[' + TRIM_CHARS + ']+');
    var RIGHT_TRIM_REG = RegExp('[' + TRIM_CHARS + ']+$');
    var TRUNC_REG = RegExp('(?=[' + TRIM_CHARS + '])');

    // Reference to native String#includes to enhance later.
    var nativeIncludes = String.prototype.includes;

    // Base64
    var encodeBase64, decodeBase64;

    // Format matcher for String#format.
    var stringFormatMatcher = createFormatMatcher(deepGetProperty);

    function padString(num, padding) {
        return repeatString(isDefined(padding) ? padding : ' ', num);
    }

    function truncateString(str, length, from, ellipsis, split) {
        var str1, str2, len1, len2;
        if (str.length <= length) {
            return str.toString();
        }
        ellipsis = isUndefined(ellipsis) ? '...' : ellipsis;
        switch (from) {
            case 'left':
                str2 = split ? truncateOnWord(str, length, true) : str.slice(str.length - length);
                return ellipsis + str2;
            case 'middle':
                len1 = ceil(length / 2);
                len2 = floor(length / 2);
                str1 = split ? truncateOnWord(str, len1) : str.slice(0, len1);
                str2 = split ? truncateOnWord(str, len2, true) : str.slice(str.length - len2);
                return str1 + ellipsis + str2;
            default:
                str1 = split ? truncateOnWord(str, length) : str.slice(0, length);
                return str1 + ellipsis;
        }
    }

    function stringEach(str, search, fn) {
        var chunks, chunk, reg, result = [];
        if (isFunction(search)) {
            fn = search;
            reg = /[\s\S]/g;
        } else if (!search) {
            reg = /[\s\S]/g;
        } else if (isString(search)) {
            reg = RegExp(escapeRegExp(search), 'gi');
        } else if (isRegExp(search)) {
            reg = RegExp(search.source, getRegExpFlags(search, 'g'));
        }
        // Getting the entire array of chunks up front as we need to
        // pass this into the callback function as an argument.
        chunks = runGlobalMatch(str, reg);

        if (chunks) {
            for (var i = 0, len = chunks.length, r; i < len; i++) {
                chunk = chunks[i];
                result[i] = chunk;
                if (fn) {
                    r = fn.call(str, chunk, i, chunks);
                    if (r === false) {
                        break;
                    } else if (isDefined(r)) {
                        result[i] = r;
                    }
                }
            }
        }
        return result;
    }

    // "match" in < IE9 has enumable properties that will confuse for..in
    // loops, so ensure that the match is a normal array by manually running
    // "exec". Note that this method is also slightly more performant.
    function runGlobalMatch(str, reg) {
        var result = [], match, lastLastIndex;
        while ((match = reg.exec(str)) != null) {
            if (reg.lastIndex === lastLastIndex) {
                reg.lastIndex += 1;
            } else {
                result.push(match[0]);
            }
            lastLastIndex = reg.lastIndex;
        }
        return result;
    }

    function eachWord(str, fn) {
        return stringEach(trim(str), /\S+/g, fn);
    }

    function stringCodes(str, fn) {
        var codes = new Array(str.length), i, len;
        for (i = 0, len = str.length; i < len; i++) {
            var code = str.charCodeAt(i);
            codes[i] = code;
            if (fn) {
                fn.call(str, code, i, str);
            }
        }
        return codes;
    }

    function stringUnderscore(str) {
        var areg = Inflections.acronyms && Inflections.acronyms.reg;
        return str
            .replace(/[-\s]+/g, '_')
            .replace(areg, function (acronym, index) {
                return (index > 0 ? '_' : '') + acronym.toLowerCase();
            })
            .replace(/([A-Z\d]+)([A-Z][a-z])/g, '$1_$2')
            .replace(/([a-z\d])([A-Z])/g, '$1_$2')
            .toLowerCase();
    }

    function stringCamelize(str, upper) {
        str = stringUnderscore(str);
        return str.replace(CAMELIZE_REG, function (match, pre, word, index) {
            var cap = upper !== false || index > 0, acronym;
            acronym = getAcronym(word);
            if (acronym && cap) {
                return acronym;
            }
            return cap ? stringCapitalize(word, true) : word;
        });
    }

    function stringSpacify(str) {
        return stringUnderscore(str).replace(/_/g, ' ');
    }

    function stringCapitalize(str, downcase, all) {
        if (downcase) {
            str = str.toLowerCase();
        }
        return all ? str.replace(CAPITALIZE_REG, simpleCapitalize) : simpleCapitalize(str);
    }

    function stringTitleize(str) {
        var fullStopPunctuation = /[.:;!]$/, lastHadPunctuation;
        str = runHumanRules(str);
        str = stringSpacify(str);
        return eachWord(str, function (word, index, words) {
            word = getHumanWord(word) || word;
            word = getAcronym(word) || word;
            var hasPunctuation, isFirstOrLast;
            var first = index == 0, last = index == words.length - 1;
            hasPunctuation = fullStopPunctuation.test(word);
            isFirstOrLast = first || last || hasPunctuation || lastHadPunctuation;
            lastHadPunctuation = hasPunctuation;
            if (isFirstOrLast || indexOf(DOWNCASED_WORDS, word) === -1) {
                return stringCapitalize(word, false, true);
            } else {
                return word;
            }
        }).join(' ');
    }

    function stringParameterize(str, separator) {
        if (separator === undefined) separator = '-';
        str = str.replace(/[^a-z0-9\-_]+/gi, separator);
        if (separator) {
            var reg = RegExp('^{s}+|{s}+$|({s}){s}+'.split('{s}').join(escapeRegExp(separator)), 'g');
            str = str.replace(reg, '$1');
        }
        return encodeURI(str.toLowerCase());
    }

    function reverseString(str) {
        return str.split('').reverse().join('');
    }

    function truncateOnWord(str, limit, fromLeft) {
        if (fromLeft) {
            return reverseString(truncateOnWord(reverseString(str), limit));
        }
        var words = str.split(TRUNC_REG);
        var count = 0;
        return filter(words, function (word) {
            count += word.length;
            return count <= limit;
        }).join('');
    }

    function unescapeHTML(str) {
        return str.replace(HTML_ENTITY_REG, function (full, hex, code) {
            var special = HTMLFromEntityMap[code];
            return special || chr(hex ? parseInt(code, 16) : +code);
        });
    }

    function tagIsVoid(tag) {
        return indexOf(HTML_VOID_ELEMENTS, tag.toLowerCase()) !== -1;
    }

    function stringReplaceAll(str, f, replace) {
        var i = 0, tokens;
        if (isString(f)) {
            f = RegExp(escapeRegExp(f), 'g');
        } else if (f && !f.global) {
            f = RegExp(f.source, getRegExpFlags(f, 'g'));
        }
        if (!replace) {
            replace = '';
        } else {
            tokens = replace;
            replace = function () {
                var t = tokens[i++];
                return t != null ? t : '';
            };
        }
        return str.replace(f, replace);
    }

    function replaceTags(str, find, replacement, strip) {
        var tags = isString(find) ? [find] : find, reg, src;
        tags = map(tags || [], function (t) {
            return escapeRegExp(t);
        }).join('|');
        src = tags.replace('all', '') || '[^\\s>]+';
        src = '<(\\/)?(' + src + ')(\\s+[^<>]*?)?\\s*(\\/)?>';
        reg = RegExp(src, 'gi');
        return runTagReplacements(str.toString(), reg, strip, replacement);
    }

    function runTagReplacements(str, reg, strip, replacement, fullString) {

        var match;
        var result = '';
        var currentIndex = 0;
        var openTagName;
        var openTagAttributes;
        var openTagCount = 0;

        function processTag(index, tagName, attributes, tagLength, isVoid) {
            var content = str.slice(currentIndex, index), s = '', r = '';
            if (isString(replacement)) {
                r = replacement;
            } else if (replacement) {
                r = replacement.call(fullString, tagName, content, attributes, fullString) || '';
            }
            if (strip) {
                s = r;
            } else {
                content = r;
            }
            if (content) {
                content = runTagReplacements(content, reg, strip, replacement, fullString);
            }
            result += s + content + (isVoid ? '' : s);
            currentIndex = index + (tagLength || 0);
        }

        fullString = fullString || str;
        reg = RegExp(reg.source, 'gi');

        while (match = reg.exec(str)) {

            var tagName = match[2];
            var attributes = (match[3] || '').slice(1);
            var isClosingTag = !!match[1];
            var isSelfClosing = !!match[4];
            var tagLength = match[0].length;
            var isVoid = tagIsVoid(tagName);
            var isOpeningTag = !isClosingTag && !isSelfClosing && !isVoid;
            var isSameAsCurrent = tagName === openTagName;

            if (!openTagName) {
                result += str.slice(currentIndex, match.index);
                currentIndex = match.index;
            }

            if (isOpeningTag) {
                if (!openTagName) {
                    openTagName = tagName;
                    openTagAttributes = attributes;
                    openTagCount++;
                    currentIndex += tagLength;
                } else if (isSameAsCurrent) {
                    openTagCount++;
                }
            } else if (isClosingTag && isSameAsCurrent) {
                openTagCount--;
                if (openTagCount === 0) {
                    processTag(match.index, openTagName, openTagAttributes, tagLength, isVoid);
                    openTagName = null;
                    openTagAttributes = null;
                }
            } else if (!openTagName) {
                processTag(match.index, tagName, attributes, tagLength, isVoid);
            }
        }
        if (openTagName) {
            processTag(str.length, openTagName, openTagAttributes);
        }
        result += str.slice(currentIndex);
        return result;
    }

    function numberOrIndex(str, n, from) {
        if (isString(n)) {
            n = str.indexOf(n);
            if (n === -1) {
                n = from ? str.length : 0;
            }
        }
        return n;
    }

    function buildBase64() {
        var encodeAscii, decodeAscii;

        function catchEncodingError(fn) {
            return function (str) {
                try {
                    return fn(str);
                } catch (e) {
                    return '';
                }
            };
        }

        if (typeof Buffer !== 'undefined') {
            encodeBase64 = function (str) {
                return new Buffer(str).toString('base64');
            };
            decodeBase64 = function (str) {
                return new Buffer(str, 'base64').toString('utf8');
            };
            return;
        }
        if (typeof btoa !== 'undefined') {
            encodeAscii = catchEncodingError(btoa);
            decodeAscii = catchEncodingError(atob);
        } else {
            var key = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
            var base64reg = /[^A-Za-z0-9\+\/\=]/g;
            encodeAscii = function (str) {
                var output = '';
                var chr1, chr2, chr3;
                var enc1, enc2, enc3, enc4;
                var i = 0;
                do {
                    chr1 = str.charCodeAt(i++);
                    chr2 = str.charCodeAt(i++);
                    chr3 = str.charCodeAt(i++);
                    enc1 = chr1 >> 2;
                    enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
                    enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
                    enc4 = chr3 & 63;
                    if (isNaN(chr2)) {
                        enc3 = enc4 = 64;
                    } else if (isNaN(chr3)) {
                        enc4 = 64;
                    }
                    output += key.charAt(enc1);
                    output += key.charAt(enc2);
                    output += key.charAt(enc3);
                    output += key.charAt(enc4);
                    chr1 = chr2 = chr3 = '';
                    enc1 = enc2 = enc3 = enc4 = '';
                } while (i < str.length);
                return output;
            };
            decodeAscii = function (input) {
                var output = '';
                var chr1, chr2, chr3;
                var enc1, enc2, enc3, enc4;
                var i = 0;
                if (input.match(base64reg)) {
                    return '';
                }
                input = input.replace(/[^A-Za-z0-9\+\/\=]/g, '');
                do {
                    enc1 = key.indexOf(input.charAt(i++));
                    enc2 = key.indexOf(input.charAt(i++));
                    enc3 = key.indexOf(input.charAt(i++));
                    enc4 = key.indexOf(input.charAt(i++));
                    chr1 = (enc1 << 2) | (enc2 >> 4);
                    chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
                    chr3 = ((enc3 & 3) << 6) | enc4;
                    output = output + chr(chr1);
                    if (enc3 != 64) {
                        output = output + chr(chr2);
                    }
                    if (enc4 != 64) {
                        output = output + chr(chr3);
                    }
                    chr1 = chr2 = chr3 = '';
                    enc1 = enc2 = enc3 = enc4 = '';
                } while (i < input.length);
                return output;
            };
        }
        encodeBase64 = function (str) {
            return encodeAscii(unescape(encodeURIComponent(str)));
        };
        decodeBase64 = function (str) {
            return decodeURIComponent(escape(decodeAscii(str)));
        };
    }

    function buildEntities() {
        HTMLToEntityMap = {};
        forEachProperty(HTMLFromEntityMap, function (val, key) {
            HTMLToEntityMap[val] = '&' + key + ';';
        });
    }

    function callIncludesWithRegexSupport(str, search, position) {
        if (!isRegExp(search)) {
            return nativeIncludes.call(str, search, position);
        }
        if (position) {
            str = str.slice(position);
        }
        return search.test(str);
    }

    defineInstance(sugarString, {

        // Enhancment to String#includes to allow a regex.
        'includes': fixArgumentLength(callIncludesWithRegexSupport)

    }, [ENHANCEMENTS_FLAG, STRING_ENHANCEMENTS_FLAG]);

    defineInstance(sugarString, {

        /***
         * @method at(index, [loop] = false)
         * @returns Mixed
         * @short Gets the character(s) at a given index.
         * @extra When [loop] is true, overshooting the end of the string will begin
         *        counting from the other end. `index` may be negative. If `index` is
         *        an array, multiple elements will be returned.
         * @example
         *
         *   'jumpy'.at(0)             -> 'j'
         *   'jumpy'.at(2)             -> 'm'
         *   'jumpy'.at(5)             -> ''
         *   'jumpy'.at(5, true)       -> 'j'
         *   'jumpy'.at(-1)            -> 'y'
         *   'lucky charms'.at([2, 4]) -> ['u','k']
         *
         * @param {number|Array<number>} index
         * @param {boolean} [loop]
         *
         ***/
        'at': function (str, index, loop) {
            return getEntriesForIndexes(str, index, loop, true);
        },

        /***
         * @method escapeURL([param] = false)
         * @returns String
         * @short Escapes characters in a string to make a valid URL.
         * @extra If [param] is true, it will also escape valid URL characters. Use
         *        this when the entire string is meant for use in a query string.
         *
         * @example
         *
         *   'a, b, and c'.escapeURL() -> 'a,%20b,%20and%20c'
         *   'http://foo.com/'.escapeURL(true) -> 'http%3A%2F%2Ffoo.com%2F'
         *
         * @param {boolean} [param]
         *
         ***/
        'escapeURL': function (str, param) {
            return param ? encodeURIComponent(str) : encodeURI(str);
        },

        /***
         * @method unescapeURL([partial] = false)
         * @returns String
         * @short Restores escaped characters in a URL escaped string.
         * @extra If [partial] is true, it will only unescape non-valid URL tokens,
         *        and is included here for completeness, but should be rarely needed.
         *
         * @example
         *
         *   'http%3A%2F%2Ffoo.com%2F'.unescapeURL()     -> 'http://foo.com/'
         *   'http%3A%2F%2Ffoo.com%2F'.unescapeURL(true) -> 'http%3A%2F%2Ffoo.com%2F'
         *
         * @param {boolean} [partial]
         *
         ***/
        'unescapeURL': function (str, param) {
            return param ? decodeURI(str) : decodeURIComponent(str);
        },

        /***
         * @method escapeHTML()
         * @returns String
         * @short Converts HTML characters to their entity equivalents.
         *
         * @example
         *
         *   '<p>some text</p>'.escapeHTML() -> '&lt;p&gt;some text&lt;/p&gt;'
         *   'one & two'.escapeHTML()        -> 'one &amp; two'
         *
         ***/
        'escapeHTML': function (str) {
            return str.replace(HTML_ESCAPE_REG, function (chr) {
                return getOwn(HTMLToEntityMap, chr);
            });
        },

        /***
         * @method unescapeHTML()
         * @returns String
         * @short Restores escaped HTML characters.
         *
         * @example
         *
         *   '&lt;p&gt;some text&lt;/p&gt;'.unescapeHTML() -> '<p>some text</p>'
         *   'one &amp; two'.unescapeHTML()                -> 'one & two'
         *
         ***/
        'unescapeHTML': function (str) {
            return unescapeHTML(str);
        },

        /***
         * @method stripTags([tag] = 'all', [replace])
         * @returns String
         * @short Strips HTML tags from the string.
         * @extra [tag] may be an array of tags or 'all', in which case all tags will
         *        be stripped. [replace] will replace what was stripped, and may be a
         *        string or a function to handle replacements. If this function returns
         *        a string, then it will be used for the replacement. If it returns
         *        `undefined`, the tags will be stripped normally.
         *
         * @callback tagReplaceFn
         *
         *   tag     The tag name.
         *   inner   The tag content.
         *   attr    The attributes on the tag, if any, as a string.
         *   outer   The entire matched tag string.
         *
         * @example
         *
         *   '<p>just <b>some</b> text</p>'.stripTags()    -> 'just some text'
         *   '<p>just <b>some</b> text</p>'.stripTags('p') -> 'just <b>some</b> text'
         *   '<p>hi!</p>'.stripTags('p', function(all, content) {
         *     return '|';
         *   }); -> '|hi!|'
         *
         * @param {string} tag
         * @param {string|tagReplaceFn} replace
         * @callbackParam {string} tag
         * @callbackParam {string} inner
         * @callbackParam {string} attr
         * @callbackParam {string} outer
         * @callbackReturns {string} tagReplaceFn
         *
         ***/
        'stripTags': function (str, tag, replace) {
            return replaceTags(str, tag, replace, true);
        },

        /***
         * @method removeTags([tag] = 'all', [replace])
         * @returns String
         * @short Removes HTML tags and their contents from the string.
         * @extra [tag] may be an array of tags or 'all', in which case all tags will
         *        be removed. [replace] will replace what was removed, and may be a
         *        string or a function to handle replacements. If this function returns
         *        a string, then it will be used for the replacement. If it returns
         *        `undefined`, the tags will be removed normally.
         *
         * @callback tagReplaceFn
         *
         *   tag     The tag name.
         *   inner   The tag content.
         *   attr    The attributes on the tag, if any, as a string.
         *   outer   The entire matched tag string.
         *
         * @example
         *
         *   '<p>just <b>some</b> text</p>'.removeTags()    -> ''
         *   '<p>just <b>some</b> text</p>'.removeTags('b') -> '<p>just text</p>'
         *   '<p>hi!</p>'.removeTags('p', function(all, content) {
         *     return 'bye!';
         *   }); -> 'bye!'
         *
         * @param {string} tag
         * @param {string|tagReplaceFn} replace
         * @callbackParam {string} tag
         * @callbackParam {string} inner
         * @callbackParam {string} attr
         * @callbackParam {string} outer
         * @callbackReturns {string} tagReplaceFn
         *
         ***/
        'removeTags': function (str, tag, replace) {
            return replaceTags(str, tag, replace, false);
        },

        /***
         * @method encodeBase64()
         * @returns String
         * @short Encodes the string into base64 encoding.
         * @extra This method wraps native methods when available, and uses a custom
         *        implementation when not available. It can also handle Unicode
         *        string encodings.
         *
         * @example
         *
         *   'gonna get encoded!'.encodeBase64()  -> 'Z29ubmEgZ2V0IGVuY29kZWQh'
         *   'http://twitter.com/'.encodeBase64() -> 'aHR0cDovL3R3aXR0ZXIuY29tLw=='
         *
         ***/
        'encodeBase64': function (str) {
            return encodeBase64(str);
        },

        /***
         * @method decodeBase64()
         * @returns String
         * @short Decodes the string from base64 encoding.
         * @extra This method wraps native methods when available, and uses a custom
         *        implementation when not available. It can also handle Unicode string
         *        encodings.
         *
         * @example
         *
         *   'aHR0cDovL3R3aXR0ZXIuY29tLw=='.decodeBase64() -> 'http://twitter.com/'
         *   'anVzdCBnb3QgZGVjb2RlZA=='.decodeBase64()     -> 'just got decoded!'
         *
         ***/
        'decodeBase64': function (str) {
            return decodeBase64(str);
        },

        /***
         * @method forEach([search], [callback])
         * @returns Array
         * @short Runs callback [fn] against every character in the string, or every
         *        every occurence of [search] if it is provided.
         * @extra Returns an array of matches. [search] may be either a string or
         *        regex, and defaults to every character in the string. If [fn]
         *        returns false at any time it will break out of the loop.
         *
         * @callback stringEachFn
         *
         *   match  The current match.
         *   i      The current index.
         *   arr    An array of all matches.
         *
         * @example
         *
         *   'jumpy'.forEach(log)     -> ['j','u','m','p','y']
         *   'jumpy'.forEach(/[r-z]/) -> ['u','y']
         *   'jumpy'.forEach(/mp/)    -> ['mp']
         *   'jumpy'.forEach(/[r-z]/, function(m) {
         *     // Called twice: "u", "y"
         *   });
         *
         * @signature forEach(callback)
         * @param {string|RegExp} [search]
         * @param {stringEachFn} [callback]
         * @callbackParam {string} match
         * @callbackParam {number} i
         * @callbackParam {Array<string>} arr
         *
         ***/
        'forEach': function (str, search, fn) {
            return stringEach(str, search, fn);
        },

        /***
         * @method chars([callback])
         * @returns Array
         * @short Runs [fn] against each character in the string, and returns an array.
         *
         * @callback eachCharFn
         *
         *   char  The current character.
         *   i     The current index.
         *   arr   An array of all characters.
         *
         * @example
         *
         *   'jumpy'.chars() -> ['j','u','m','p','y']
         *   'jumpy'.chars(function(c) {
         *     // Called 5 times: "j","u","m","p","y"
         *   });
         *
         * @param {eachCharFn} [callback]
         * @callbackParam {string} char
         * @callbackParam {number} i
         * @callbackParam {Array<string>} arr
         *
         ***/
        'chars': function (str, search, fn) {
            return stringEach(str, search, fn);
        },

        /***
         * @method words([callback])
         * @returns Array
         * @short Runs [fn] against each word in the string, and returns an array.
         * @extra A "word" is defined as any sequence of non-whitespace characters.
         *
         * @callback eachWordFn
         *
         *   word  The current word.
         *   i     The current index.
         *   arr   An array of all words.
         *
         * @example
         *
         *   'broken wear'.words() -> ['broken','wear']
         *   'broken wear'.words(function(w) {
         *     // Called twice: "broken", "wear"
         *   });
         *
         * @param {eachWordFn} [callback]
         * @callbackParam {string} word
         * @callbackParam {number} i
         * @callbackParam {Array<string>} arr
         *
         ***/
        'words': function (str, fn) {
            return stringEach(trim(str), /\S+/g, fn);
        },

        /***
         * @method lines([callback])
         * @returns Array
         * @short Runs [fn] against each line in the string, and returns an array.
         *
         * @callback eachLineFn
         *
         *   line  The current line.
         *   i     The current index.
         *   arr   An array of all lines.
         *
         * @example
         *
         *   lineText.lines() -> array of lines
         *   lineText.lines(function(l) {
         *     // Called once per line
         *   });
         *
         * @param {eachLineFn} [callback]
         * @callbackParam {string} line
         * @callbackParam {number} i
         * @callbackParam {Array<string>} arr
         *
         ***/
        'lines': function (str, fn) {
            return stringEach(trim(str), /^.*$/gm, fn);
        },

        /***
         * @method codes([callback])
         * @returns Array
         * @short Runs callback [fn] against each character code in the string.
         *        Returns an array of character codes.
         *
         * @callback eachCodeFn
         *
         *   code  The current character code.
         *   i     The current index.
         *   str   The string being operated on.
         *
         * @example
         *
         *   'jumpy'.codes() -> [106,117,109,112,121]
         *   'jumpy'.codes(function(c) {
         *     // Called 5 times: 106, 117, 109, 112, 121
         *   });
         *
         * @param {eachCodeFn} [callback]
         * @callbackParam {number} code
         * @callbackParam {number} i
         * @callbackParam {string} str
         *
         ***/
        'codes': function (str, fn) {
            return stringCodes(str, fn);
        },

        /***
         * @method shift(n)
         * @returns Array
         * @short Shifts each character in the string `n` places in the character map.
         *
         * @example
         *
         *   'a'.shift(1)  -> 'b'
         *   'ク'.shift(1) -> 'グ'
         *
         * @param {number} n
         *
         ***/
        'shift': function (str, n) {
            var result = '';
            n = n || 0;
            stringCodes(str, function (c) {
                result += chr(c + n);
            });
            return result;
        },

        /***
         * @method isBlank()
         * @returns Boolean
         * @short Returns true if the string has length 0 or contains only whitespace.
         *
         * @example
         *
         *   ''.isBlank()      -> true
         *   '   '.isBlank()   -> true
         *   'noway'.isBlank() -> false
         *
         ***/
        'isBlank': function (str) {
            return trim(str).length === 0;
        },

        /***
         * @method isEmpty()
         * @returns Boolean
         * @short Returns true if the string has length 0.
         *
         * @example
         *
         *   ''.isEmpty()  -> true
         *   'a'.isBlank() -> false
         *   ' '.isBlank() -> false
         *
         ***/
        'isEmpty': function (str) {
            return str.length === 0;
        },

        /***
         * @method insert(str, [index] = length)
         * @returns String
         * @short Adds `str` at [index]. Allows negative values.
         *
         * @example
         *
         *   'dopamine'.insert('e', 3)       -> dopeamine
         *   'spelling eror'.insert('r', -3) -> spelling error
         *
         * @param {string} str
         * @param {number} [index]
         *
         ***/
        'insert': function (str, substr, index) {
            index = isUndefined(index) ? str.length : index;
            return str.slice(0, index) + substr + str.slice(index);
        },

        /***
         * @method remove(f)
         * @returns String
         * @short Removes the first occurrence of `f` in the string.
         * @extra `f` can be a either case-sensitive string or a regex. In either case
         *        only the first match will be removed. To remove multiple occurrences,
         *        use `removeAll`.
         *
         * @example
         *
         *   'schfifty five'.remove('f')      -> 'schifty five'
         *   'schfifty five'.remove(/[a-f]/g) -> 'shfifty five'
         *
         * @param {string|RegExp} f
         *
         ***/
        'remove': function (str, f) {
            return str.replace(f, '');
        },

        /***
         * @method removeAll(f)
         * @returns String
         * @short Removes any occurences of `f` in the string.
         * @extra `f` can be either a case-sensitive string or a regex. In either case
         *        all matches will be removed. To remove only a single occurence, use
         *        `remove`.
         *
         * @example
         *
         *   'schfifty five'.removeAll('f')     -> 'schity ive'
         *   'schfifty five'.removeAll(/[a-f]/) -> 'shity iv'
         *
         * @param {string|RegExp} f
         *
         ***/
        'removeAll': function (str, f) {
            return stringReplaceAll(str, f);
        },

        /***
         * @method reverse()
         * @returns String
         * @short Reverses the string.
         *
         * @example
         *
         *   'jumpy'.reverse()        -> 'ypmuj'
         *   'lucky charms'.reverse() -> 'smrahc ykcul'
         *
         ***/
        'reverse': function (str) {
            return reverseString(str);
        },

        /***
         * @method compact()
         * @returns String
         * @short Compacts whitespace in the string to a single space and trims the ends.
         *
         * @example
         *
         *   'too \n much \n space'.compact() -> 'too much space'
         *   'enough \n '.compact()           -> 'enought'
         *
         ***/
        'compact': function (str) {
            return trim(str).replace(/([\r\n\s　])+/g, function (match, whitespace) {
                return whitespace === '　' ? whitespace : ' ';
            });
        },

        /***
         * @method from([index] = 0)
         * @returns String
         * @short Returns a section of the string starting from [index].
         *
         * @example
         *
         *   'lucky charms'.from()   -> 'lucky charms'
         *   'lucky charms'.from(7)  -> 'harms'
         *
         * @param {number} [index]
         *
         ***/
        'from': function (str, from) {
            return str.slice(numberOrIndex(str, from, true));
        },

        /***
         * @method to([index] = end)
         * @returns String
         * @short Returns a section of the string ending at [index].
         *
         * @example
         *
         *   'lucky charms'.to()   -> 'lucky charms'
         *   'lucky charms'.to(7)  -> 'lucky ch'
         *
         * @param {number} [index]
         *
         ***/
        'to': function (str, to) {
            if (isUndefined(to)) to = str.length;
            return str.slice(0, numberOrIndex(str, to));
        },

        /***
         * @method dasherize()
         * @returns String
         * @short Converts underscores and camel casing to hypens.
         *
         * @example
         *
         *   'a_farewell_to_arms'.dasherize() -> 'a-farewell-to-arms'
         *   'capsLock'.dasherize()           -> 'caps-lock'
         *
         ***/
        'dasherize': function (str) {
            return stringUnderscore(str).replace(/_/g, '-');
        },

        /***
         * @method underscore()
         * @returns String
         * @short Converts hyphens and camel casing to underscores.
         *
         * @example
         *
         *   'a-farewell-to-arms'.underscore() -> 'a_farewell_to_arms'
         *   'capsLock'.underscore()           -> 'caps_lock'
         *
         ***/
        'underscore': function (str) {
            return stringUnderscore(str);
        },

        /***
         * @method camelize([upper] = true)
         * @returns String
         * @short Converts underscores and hyphens to camel case.
         * @extra If [upper] is true, the string will be UpperCamelCase. If the
         *        inflections module is included, acronyms can also be defined that
         *        will be used when camelizing.
         *
         * @example
         *
         *   'caps_lock'.camelize()              -> 'CapsLock'
         *   'moz-border-radius'.camelize()      -> 'MozBorderRadius'
         *   'moz-border-radius'.camelize(false) -> 'mozBorderRadius'
         *   'http-method'.camelize()            -> 'HTTPMethod'
         *
         * @param {boolean} [upper]
         *
         ***/
        'camelize': function (str, upper) {
            return stringCamelize(str, upper);
        },

        /***
         * @method spacify()
         * @returns String
         * @short Converts camelcase, underscores, and hyphens to spaces.
         *
         * @example
         *
         *   'camelCase'.spacify()                         -> 'camel case'
         *   'an-ugly-string'.spacify()                    -> 'an ugly string'
         *   'oh-no_youDid-not'.spacify().capitalize(true) -> 'something else'
         *
         ***/
        'spacify': function (str) {
            return stringSpacify(str);
        },

        /***
         * @method titleize()
         * @returns String
         * @short Creates a title version of the string.
         * @extra Capitalizes all the words and replaces some characters in the string
         *        to create a nicer looking title. String#titleize is meant for
         *        creating pretty output.
         *
         * @example
         *
         *   'man from the boondocks'.titleize() -> 'Man from the Boondocks'
         *   'x-men: apocalypse'.titleize() -> 'X Men: Apocalypse'
         *   'TheManWithoutAPast'.titleize() -> 'The Man Without a Past'
         *   'raiders_of_the_lost_ark'.titleize() -> 'Raiders of the Lost Ark'
         *
         ***/
        'titleize': function (str) {
            return stringTitleize(str);
        },

        /***
         * @method parameterize()
         * @returns String
         * @short Replaces special characters in a string so that it may be used as
         *        part of a pretty URL.
         *
         * @example
         *
         *   'hell, no!'.parameterize() -> 'hell-no'
         *
         ***/
        'parameterize': function (str, separator) {
            return stringParameterize(str, separator);
        },

        /***
         * @method truncate(length, [from] = 'right', [ellipsis] = '...')
         * @returns String
         * @short Truncates a string.
         * @extra [from] can be `'right'`, `'left'`, or `'middle'`. If the string is
         *        shorter than `length`, [ellipsis] will not be added.
         *
         * @example
         *
         *   'sittin on the dock'.truncate(10)           -> 'sittin on ...'
         *   'sittin on the dock'.truncate(10, 'left')   -> '...n the dock'
         *   'sittin on the dock'.truncate(10, 'middle') -> 'sitti... dock'
         *
         * @param {number} length
         * @param {string} [from]
         * @param {string} [ellipsis]
         *
         ***/
        'truncate': function (str, length, from, ellipsis) {
            return truncateString(str, length, from, ellipsis);
        },

        /***
         * @method truncateOnWord(length, [from] = 'right', [ellipsis] = '...')
         * @returns String
         * @short Truncates a string without splitting up words.
         * @extra [from] can be `'right'`, `'left'`, or `'middle'`. If the string is
         *        shorter than `length`, [ellipsis] will not be added. A "word" is
         *        defined as any sequence of non-whitespace characters.
         *
         * @example
         *
         *   'here we go'.truncateOnWord(5)         -> 'here...'
         *   'here we go'.truncateOnWord(5, 'left') -> '...we go'
         *
         * @param {number} length
         * @param {string} [from]
         * @param {string} [ellipsis]
         *
         ***/
        'truncateOnWord': function (str, length, from, ellipsis) {
            return truncateString(str, length, from, ellipsis, true);
        },

        /***
         * @method pad(num, [padding] = ' ')
         * @returns String
         * @short Pads the string out with [padding] to be exactly `num` characters.
         *
         * @example
         *
         *   'wasabi'.pad(8)      -> ' wasabi '
         *   'wasabi'.pad(8, '-') -> '-wasabi-'
         *
         * @param {number} num
         * @param {string} [padding]
         *
         ***/
        'pad': function (str, num, padding) {
            var half, front, back;
            num = coercePositiveInteger(num);
            half = max(0, num - str.length) / 2;
            front = floor(half);
            back = ceil(half);
            return padString(front, padding) + str + padString(back, padding);
        },

        /***
         * @method padLeft(num, [padding] = ' ')
         * @returns String
         * @short Pads the string out from the left with [padding] to be exactly
         *        `num` characters.
         *
         * @example
         *
         *   'wasabi'.padLeft(8)      -> '  wasabi'
         *   'wasabi'.padLeft(8, '-') -> '--wasabi'
         *
         * @param {number} num
         * @param {string} [padding]
         *
         ***/
        'padLeft': function (str, num, padding) {
            num = coercePositiveInteger(num);
            return padString(max(0, num - str.length), padding) + str;
        },

        /***
         * @method padRight(num, [padding] = ' ')
         * @returns String
         * @short Pads the string out from the right with [padding] to be exactly
         *        `num` characters.
         *
         * @example
         *
         *   'wasabi'.padRight(8)      -> 'wasabi  '
         *   'wasabi'.padRight(8, '-') -> 'wasabi--'
         *
         * @param {number} num
         * @param {string} [padding]
         *
         ***/
        'padRight': function (str, num, padding) {
            num = coercePositiveInteger(num);
            return str + padString(max(0, num - str.length), padding);
        },

        /***
         * @method first([n] = 1)
         * @returns String
         * @short Returns the first [n] characters of the string.
         *
         * @example
         *
         *   'lucky charms'.first()  -> 'l'
         *   'lucky charms'.first(3) -> 'luc'
         *
         * @param {number} [n]
         *
         ***/
        'first': function (str, num) {
            if (isUndefined(num)) num = 1;
            return str.substr(0, num);
        },

        /***
         * @method last([n] = 1)
         * @returns String
         * @short Returns the last [n] characters of the string.
         *
         * @example
         *
         *   'lucky charms'.last()  -> 's'
         *   'lucky charms'.last(3) -> 'rms'
         *
         * @param {number} [n]
         *
         ***/
        'last': function (str, num) {
            if (isUndefined(num)) num = 1;
            var start = str.length - num < 0 ? 0 : str.length - num;
            return str.substr(start);
        },

        /***
         * @method toNumber([base] = 10)
         * @returns Number
         * @short Converts the string into a number.
         * @extra Any value with a "." fill be converted to a floating point value,
         *        otherwise an integer.
         *
         * @example
         *
         *   '153'.toNumber()    -> 153
         *   '12,000'.toNumber() -> 12000
         *   '10px'.toNumber()   -> 10
         *   'ff'.toNumber(16)   -> 255
         *
         * @param {number} [base]
         *
         ***/
        'toNumber': function (str, base) {
            return stringToNumber(str, base);
        },

        /***
         * @method capitalize([lower] = false, [all] = false)
         * @returns String
         * @short Capitalizes the first character of the string.
         * @extra If [lower] is true, the remainder of the string will be downcased.
         *        If [all] is true, all words in the string will be capitalized.
         *
         * @example
         *
         *   'hello'.capitalize()           -> 'Hello'
         *   'HELLO'.capitalize(true)       -> 'Hello'
         *   'hello kitty'.capitalize()     -> 'Hello kitty'
         *   'hEllO kItTy'.capitalize(true, true) -> 'Hello Kitty'
         *
         * @param {boolean} [lower]
         * @param {boolean} [all]
         *
         ***/
        'capitalize': function (str, lower, all) {
            return stringCapitalize(str, lower, all);
        },

        /***
         * @method trimLeft()
         * @returns String
         * @short Removes leading whitespace from the string.
         * @extra Whitespace is defined as line breaks, tabs, and any character in the
         *        "Space, Separator" Unicode category, conforming to the the ES5 `trim`
         *        spec.
         *
         * @example
         *
         *   '   wasabi   '.trimLeft()  -> 'wasabi   '
         *
         ***/
        'trimLeft': function (str) {
            return str.replace(LEFT_TRIM_REG, '');
        },

        /***
         * @method trimRight()
         * @returns String
         * @short Removes trailing whitespace from the string.
         * @extra Whitespace is defined as line breaks, tabs, and any character in the
         *        "Space, Separator" Unicode category, conforming to the the ES5 `trim`
         *        spec.
         *
         * @example
         *
         *   '   wasabi   '.trimRight() -> '   wasabi'
         *
         ***/
        'trimRight': function (str) {
            return str.replace(RIGHT_TRIM_REG, '');
        }

    });

    defineInstanceWithArguments(sugarString, {

        /***
         * @method replaceAll(f, [str1], [str2], ...)
         * @returns String
         * @short Replaces all occurences of `f` with arguments passed.
         * @extra This method is intended to be a quick way to perform multiple string
         *        replacements quickly when the replacement token differs depending on
         *        position. `f` can be either a case-sensitive string or a regex.
         *        In either case all matches will be replaced.
         *
         * @example
         *
         *   '-x -y -z'.replaceAll('-', 1, 2, 3)               -> '1x 2y 3z'
         *   'one and two'.replaceAll(/one|two/, '1st', '2nd') -> '1st and 2nd'
         *
         * @param {string|RegExp} f
         * @param {string} [str1]
         * @param {string} [str2]
         *
         ***/
        'replaceAll': function (str, f, args) {
            return stringReplaceAll(str, f, args);
        },

        /***
         * @method format(obj1, [obj2], ...)
         * @returns String
         * @short Replaces `{}` tokens in the string with arguments or properties.
         * @extra Tokens support `deep properties`. If a single object is passed, its
         *        properties can be accessed by keywords such as `{name}`. If multiple
         *        objects or a non-object are passed, they can be accessed by the
         *        argument position like `{0}`. Literal braces in the string can be
         *        escaped by repeating them.
         *
         * @example
         *
         *   'Welcome, {name}.'.format({ name: 'Bill' }) -> 'Welcome, Bill.'
         *   'You are {0} years old today.'.format(5)    -> 'You are 5 years old today.'
         *   '{0.name} and {1.name}'.format(users)       -> logs first two users' names
         *   '${currencies.usd.balance}'.format(Harry)   -> "$500"
         *   ''.format('Hello')                 -> "{Hello}"
         *
         * @param {any} [obj1]
         * @param {any} [obj2]
         *
         ***/
        'format': function (str, args) {
            var arg1 = args[0] && args[0].valueOf();
            // Unwrap if a single object is passed in.
            if (args.length === 1 && isObjectType(arg1)) {
                args = arg1;
            }
            return stringFormatMatcher(str, args);
        }

    });

    buildBase64();
    buildEntities();

    /***
     * @module Number
     * @description Number formatting, precision rounding, Math aliases, and more.
     *
     ***/


    var NUMBER_OPTIONS = {
        'decimal': HALF_WIDTH_PERIOD,
        'thousands': HALF_WIDTH_COMMA
    };

    // Abbreviation Units
    var BASIC_UNITS = '|kmbt',
        MEMORY_UNITS = '|KMGTPE',
        MEMORY_BINARY_UNITS = '|,Ki,Mi,Gi,Ti,Pi,Ei',
        METRIC_UNITS_SHORT = 'nμm|k',
        METRIC_UNITS_FULL = 'yzafpnμm|KMGTPEZY';


    /***
     * @method getOption(name)
     * @returns Mixed
     * @accessor
     * @short Gets an option used interally by Number.
     * @example
     *
     *   Sugar.Number.getOption('thousands');
     *
     * @param {string} name
     *
     ***
     * @method setOption(name, value)
     * @accessor
     * @short Sets an option used interally by Number.
     * @extra If `value` is `null`, the default value will be restored.
     * @options
     *
     *   decimal     A string used as the decimal marker by `format`, `abbr`,
     *               `metric`, and `bytes`. Default is `.`.
     *
     *   thousands   A string used as the thousands marker by `format`, `abbr`,
     *               `metric`, and `bytes`. Default is `,`.
     *
     *
     * @example
     *
     *   Sugar.Number.setOption('decimal', ',');
     *   Sugar.Number.setOption('thousands', ' ');
     *
     * @signature setOption(options)
     * @param {NumberOptions} options
     * @param {string} name
     * @param {any} value
     * @option {string} decimal
     * @option {string} thousands
     *
     ***/
    var _numberOptions = defineOptionsAccessor(sugarNumber, NUMBER_OPTIONS);


    function abbreviateNumber(num, precision, ustr, bytes) {
        var fixed = num.toFixed(20),
            decimalPlace = fixed.search(/\./),
            numeralPlace = fixed.search(/[1-9]/),
            significant = decimalPlace - numeralPlace,
            units, unit, mid, i, divisor;
        if (significant > 0) {
            significant -= 1;
        }
        units = commaSplit(ustr);
        if (units.length === 1) {
            units = ustr.split('');
        }
        mid = units.indexOf('|');
        if (mid === -1) {
            // Skipping the placeholder means the units should start from zero,
            // otherwise assume they end at zero.
            mid = units[0] === '_' ? 0 : units.length;
        }
        i = max(min(floor(significant / 3), units.length - mid - 1), -mid);
        unit = units[i + mid];
        while (unit === '_') {
            i += i < 0 ? -1 : 1;
            unit = units[i + mid];
        }
        if (unit === '|') {
            unit = '';
        }
        if (significant < -9) {
            precision = abs(significant) - 9;
        }
        divisor = bytes ? pow(2, 10 * i) : pow(10, i * 3);
        return numberFormat(withPrecision(num / divisor, precision || 0)) + unit;
    }

    function numberFormat(num, place) {
        var result = '', thousands, decimal, fraction, integer, split, str;

        decimal = _numberOptions('decimal');
        thousands = _numberOptions('thousands');

        if (isNumber(place)) {
            str = withPrecision(num, place || 0).toFixed(max(place, 0));
        } else {
            str = num.toString();
        }

        str = str.replace(/^-/, '');
        split = periodSplit(str);
        integer = split[0];
        fraction = split[1];
        if (/e/.test(str)) {
            result = str;
        } else {
            for (var i = integer.length; i > 0; i -= 3) {
                if (i < integer.length) {
                    result = thousands + result;
                }
                result = integer.slice(max(0, i - 3), i) + result;
            }
        }
        if (fraction) {
            result += decimal + repeatString('0', (place || 0) - fraction.length) + fraction;
        }
        return (num < 0 ? '-' : '') + result;
    }

    function isInteger(n) {
        return n % 1 === 0;
    }

    function isMultipleOf(n1, n2) {
        return n1 % n2 === 0;
    }

    function createRoundingFunction(fn) {
        return function (n, precision) {
            return precision ? withPrecision(n, precision, fn) : fn(n);
        };
    }

    defineStatic(sugarNumber, {

        /***
         * @method random([n1], [n2])
         * @returns Number
         * @static
         * @short Returns a random integer from [n1] to [n2] (both inclusive).
         * @extra If only 1 number is passed, the other will be 0. If none are passed,
         *        the number will be either 0 or 1.
         *
         * @example
         *
         *   Number.random(50, 100) -> ex. 85
         *   Number.random(50)      -> ex. 27
         *   Number.random()        -> ex. 0
         *
         * @param {number} [n1]
         * @param {number} [n2]
         *
         ***/
        'random': function (n1, n2) {
            var minNum, maxNum;
            if (arguments.length == 1) n2 = n1, n1 = 0;
            minNum = min(n1 || 0, isUndefined(n2) ? 1 : n2);
            maxNum = max(n1 || 0, isUndefined(n2) ? 1 : n2) + 1;
            return trunc((Math.random() * (maxNum - minNum)) + minNum);
        }

    });

    defineInstance(sugarNumber, {

        /***
         * @method isInteger()
         * @returns Boolean
         * @short Returns true if the number has no trailing decimal.
         *
         * @example
         *
         *   (420).isInteger() -> true
         *   (4.5).isInteger() -> false
         *
         ***/
        'isInteger': function (n) {
            return isInteger(n);
        },

        /***
         * @method isOdd()
         * @returns Boolean
         * @short Returns true if the number is odd.
         *
         * @example
         *
         *   (3).isOdd()  -> true
         *   (18).isOdd() -> false
         *
         ***/
        'isOdd': function (n) {
            return isInteger(n) && !isMultipleOf(n, 2);
        },

        /***
         * @method isEven()
         * @returns Boolean
         * @short Returns true if the number is even.
         *
         * @example
         *
         *   (6).isEven()  -> true
         *   (17).isEven() -> false
         *
         ***/
        'isEven': function (n) {
            return isMultipleOf(n, 2);
        },

        /***
         * @method isMultipleOf(num)
         * @returns Boolean
         * @short Returns true if the number is a multiple of `num`.
         *
         * @example
         *
         *   (6).isMultipleOf(2)  -> true
         *   (17).isMultipleOf(2) -> false
         *   (32).isMultipleOf(4) -> true
         *   (34).isMultipleOf(4) -> false
         *
         * @param {number} num
         *
         ***/
        'isMultipleOf': function (n, num) {
            return isMultipleOf(n, num);
        },

        /***
         * @method log([base] = Math.E)
         * @returns Number
         * @short Returns the logarithm of the number with `base`, or the natural
         *        logarithm of the number if `base` is undefined.
         *
         * @example
         *
         *   (64).log(2) -> 6
         *   (9).log(3)  -> 2
         *   (5).log()   -> 1.6094379124341003
         *
         * @param {number} [base]
         *
         ***/
        'log': function (n, base) {
            return Math.log(n) / (base ? Math.log(base) : 1);
        },

        /***
         * @method abbr([precision] = 0)
         * @returns String
         * @short Returns an abbreviated form of the number ("k" for thousand, "m"
         *        for million, etc).
         * @extra [precision] will round to the given precision. `thousands` and
         *        `decimal` allow custom separators to be used.
         *
         * @example
         *
         *   (1000).abbr()    -> "1k"
         *   (1000000).abbr() -> "1m"
         *   (1280).abbr(1)   -> "1.3k"
         *
         * @param {number} [precision]
         *
         ***/
        'abbr': function (n, precision) {
            return abbreviateNumber(n, precision, BASIC_UNITS);
        },

        /***
         * @method metric([precision] = 0, [units] = "nμm|k")
         * @returns String
         * @short Returns the number as a string in metric notation.
         * @extra [precision] will round to the given precision (can be negative).
         *        [units] is a string that determines both the unit notation and the
         *        min/max unit allowed. The default is natural notation for common
         *        units (meters, grams, etc). "all" can be passed for [units] and is a
         *        shortcut to all standard SI units. The token `,` if present separates
         *        units, otherwise each character is a unit. The token `|` if present
         *        marks where fractional units end, otherwise no fractional units are
         *        used. Finally, the token `_` if present is a placeholder for no unit.
         *
         * @example
         *
         *   (1000).metric()        -> "1k"
         *   (1000000).metric()     -> "1,000k"
         *   (1249).metric(2) + 'g' -> "1.25kg"
         *   (0.025).metric() + 'm' -> "25mm"
         *   (1000000).metric(0, 'nμm|kM') -> "1M"
         *
         * @param {number} [precision]
         * @param {string} [units]
         *
         ***/
        'metric': function (n, precision, units) {
            if (units === 'all') {
                units = METRIC_UNITS_FULL;
            } else if (!units) {
                units = METRIC_UNITS_SHORT;
            }
            return abbreviateNumber(n, precision, units);
        },

        /***
         * @method bytes([precision] = 0, [binary] = false, [units] = 'si')
         * @returns String
         * @short Returns an abbreviated form of the number, with 'B' on the end for "bytes".
         * @extra [precision] will round to the given precision. If [binary] is `true`,
         *        powers of 1024 will be used instead of 1000, and units will default
         *        to the binary units "KiB", "MiB", etc. Units can be overridden by
         *        passing "si" or "binary" for [units], or further customized by
         *        passing a unit string. See `metric` for more.
         *
         * @example
         *
         *   (1000).bytes()                 -> "1KB"
         *   (1289).bytes(2)                -> "1.29KB"
         *   (1000).bytes(2, true)          -> "0.98KiB"
         *   (1000).bytes(2, true, 'si')    -> "0.98KB"
         *
         * @param {number} [precision]
         * @param {boolean} [binary]
         * @param {string} [units]
         *
         ***/
        'bytes': function (n, precision, binary, units) {
            if (units === 'binary' || (!units && binary)) {
                units = MEMORY_BINARY_UNITS;
            } else if (units === 'si' || !units) {
                units = MEMORY_UNITS;
            }
            return abbreviateNumber(n, precision, units, binary) + 'B';
        },

        /***
         * @method format([place] = 0)
         * @returns String
         * @short Formats the number to a readable string.
         * @extra If [place] is `undefined`, the place will automatically be determined.
         *        `thousands` and `decimal` allow custom markers to be used.
         *
         * @example
         *
         *   (56782).format()    -> '56,782'
         *   (56782).format(2)   -> '56,782.00'
         *   (4388.43).format(2) -> '4,388.43'
         *
         * @param {number} [place]
         *
         ***/
        'format': function (n, place) {
            return numberFormat(n, place);
        },

        /***
         * @method hex([pad] = 1)
         * @returns String
         * @short Converts the number to hexidecimal.
         * @extra [pad] will pad the resulting string to that many places.
         *
         * @example
         *
         *   (255).hex()   -> 'ff';
         *   (255).hex(4)  -> '00ff';
         *   (23654).hex() -> '5c66';
         *
         * @param {number} [pad]
         *
         ***/
        'hex': function (n, pad) {
            return padNumber(n, pad || 1, false, 16);
        },

        /***
         * @method times(fn)
         * @returns Mixed
         * @short Calls `fn` a number of times equivalent to the number.
         * @extra Any non-undefined return values of `fn` will be collected and
         *        returned in an array.
         *
         * @callback indexMapFn
         *
         *   i   The index of the current iteration.
         *
         * @example
         *
         *   (8).times(logHello) -> logs "hello" 8 times
         *   (7).times(function(n) {
         *     return Math.pow(2, n);
         *   });
         *
         * @callbackParam {number} i
         * @callbackReturns {any} indexMapFn
         * @param {indexMapFn} fn
         *
         ***/
        'times': function (n, fn) {
            var arr, result;
            for (var i = 0; i < n; i++) {
                result = fn.call(n, i);
                if (isDefined(result)) {
                    if (!arr) {
                        arr = [];
                    }
                    arr.push(result);
                }
            }
            return arr;
        },

        /***
         * @method chr()
         * @returns String
         * @short Returns a string at the code point of the number.
         *
         * @example
         *
         *   (65).chr() -> "A"
         *   (75).chr() -> "K"
         *
         ***/
        'chr': function (n) {
            return chr(n);
        },

        /***
         * @method pad([place] = 0, [sign] = false, [base] = 10)
         * @returns String
         * @short Pads a number with "0" to `place`.
         * @extra [sign] allows you to force the sign as well (+05, etc). [base] can
         *        change the base for numeral conversion.
         *
         * @example
         *
         *   (5).pad(2)        -> '05'
         *   (-5).pad(4)       -> '-0005'
         *   (82).pad(3, true) -> '+082'
         *
         * @param {number} place
         * @param {boolean} [sign]
         * @param {number} [base]
         *
         ***/
        'pad': function (n, place, sign, base) {
            return padNumber(n, place, sign, base);
        },

        /***
         * @method ordinalize()
         * @returns String
         * @short Returns an ordinalized English string, i.e. "1st", "2nd", etc.
         *
         * @example
         *
         *   (1).ordinalize() -> '1st';
         *   (2).ordinalize() -> '2nd';
         *   (8).ordinalize() -> '8th';
         *
         ***/
        'ordinalize': function (n) {
            var num = abs(n), last = +num.toString().slice(-2);
            return n + getOrdinalSuffix(last);
        },

        /***
         * @method toNumber()
         * @returns Number
         * @short Identity function for compatibilty.
         *
         * @example
         *
         *   (420).toNumber() -> 420
         *
         ***/
        'toNumber': function (n) {
            return n.valueOf();
        },

        /***
         * @method round([precision] = 0)
         * @returns Number
         * @short Shortcut for `Math.round` that also allows a `precision`.
         *
         * @example
         *
         *   (3.241).round()  -> 3
         *   (-3.841).round() -> -4
         *   (3.241).round(2) -> 3.24
         *   (3748).round(-2) -> 3800
         *
         * @param {number} [precision]
         *
         ***/
        'round': createRoundingFunction(round),

        /***
         * @method ceil([precision] = 0)
         * @returns Number
         * @short Shortcut for `Math.ceil` that also allows a `precision`.
         *
         * @example
         *
         *   (3.241).ceil()  -> 4
         *   (-3.241).ceil() -> -3
         *   (3.241).ceil(2) -> 3.25
         *   (3748).ceil(-2) -> 3800
         *
         * @param {number} [precision]
         *
         ***/
        'ceil': createRoundingFunction(ceil),

        /***
         * @method floor([precision] = 0)
         * @returns Number
         * @short Shortcut for `Math.floor` that also allows a `precision`.
         *
         * @example
         *
         *   (3.241).floor()  -> 3
         *   (-3.841).floor() -> -4
         *   (3.241).floor(2) -> 3.24
         *   (3748).floor(-2) -> 3700
         *
         * @param {number} [precision]
         *
         ***/
        'floor': createRoundingFunction(floor)

    });

    /***
     * @method [math]()
     * @returns Number
     * @short Math related functions are mapped as shortcuts to numbers and are
     *        identical. Note that `log` provides some special defaults.
     *
     * @set
     *   abs
     *   sin
     *   asin
     *   cos
     *   acos
     *   tan
     *   atan
     *   sqrt
     *   exp
     *   pow
     *
     * @example
     *
     *   (3).pow(3) -> 27
     *   (-3).abs() -> 3
     *   (1024).sqrt() -> 32
     *
     ***/
    function buildMathAliases() {
        defineInstanceSimilar(sugarNumber, 'abs pow sin asin cos acos tan atan exp pow sqrt', function (methods, name) {
            methods[name] = function (n, arg) {
                // Note that .valueOf() here is only required due to a
                // very strange bug in iOS7 that only occurs occasionally
                // in which Math.abs() called on non-primitive numbers
                // returns a completely different number (Issue #400)
                return Math[name](n.valueOf(), arg);
            };
        });
    }

    buildMathAliases();

    /***
     * @module Array
     * @description Array manipulation and traversal, alphanumeric sorting and collation.
     *
     ***/

    var HALF_WIDTH_NINE = 0x39;
    var FULL_WIDTH_NINE = 0xff19;

    // Undefined array elements in < IE8 will not be visited by concat
    // and so will not be copied. This means that non-sparse arrays will
    // become sparse, so detect for this here.
    var HAS_CONCAT_BUG = !('0' in [].concat(undefined).concat());

    var ARRAY_OPTIONS = {
        'sortIgnore': null,
        'sortNatural': true,
        'sortIgnoreCase': true,
        'sortOrder': getSortOrder(),
        'sortCollate': collateStrings,
        'sortEquivalents': getSortEquivalents()
    };

    /***
     * @method getOption(name)
     * @returns Mixed
     * @accessor
     * @short Gets an option used interally by Array.
     * @extra Options listed below. Current options are for sorting strings with
     *        `sortBy`.
     *
     * @example
     *
     *   Sugar.Array.getOption('sortNatural')
     *
     * @param {string} name
     *
     ***
     * @method setOption(name, value)
     * @accessor
     * @short Sets an option used interally by Array.
     * @extra Options listed below. Current options are for sorting strings with
     *        `sortBy`. If `value` is `null`, the default value will be restored.
     *
     * @options
     *
     *   sortIgnore        A regex to ignore when sorting. An example usage of this
     *                     option would be to ignore numbers in a list to instead
     *                     sort by the first text that appears. Default is `null`.
     *
     *   sortIgnoreCase    A boolean that ignores case when sorting.
     *                     Default is `true`.
     *
     *   sortNatural       A boolean that turns on natural sorting. "Natural" means
     *                     that numerals like "10" will be sorted after "9" instead
     *                     of after "1". Default is `true`.
     *
     *   sortOrder         A string of characters to use as the base sort order. The
     *                     default is an order natural to most major world languages.
     *
     *   sortEquivalents   A table of equivalent characters used when sorting. The
     *                     default produces a natural sort order for most world
     *                     languages, however can be modified for others. For
     *                     example, setting "ä" and "ö" to `null` in the table would
     *                     produce a Scandanavian sort order. Note that setting this
     *                     option to `null` will restore the default table, but any
     *                     mutations made to that table will persist.
     *
     *   sortCollate       The collation function used when sorting strings. The
     *                     default function produces a natural sort order that can
     *                     be customized with the other "sort" options. Overriding
     *                     the function directly here will also override these
     *                     options.
     *
     * @example
     *
     *   Sugar.Array.setOption('sortIgnore', /^\d+\./)
     *   Sugar.Array.setOption('sortIgnoreCase', false)
     *
     * @signature setOption(options)
     * @param {ArrayOptions} options
     * @param {string} name
     * @param {any} value
     * @option {RegExp} [sortIgnore]
     * @option {boolean} [sortIgnoreCase]
     * @option {boolean} [sortNatural]
     * @option {string} [sortOrder]
     * @option {Object} [sortEquivalents]
     * @option {Function} [sortCollate]
     *
     ***/
    var _arrayOptions = defineOptionsAccessor(sugarArray, ARRAY_OPTIONS);


    function setArrayChainableConstructor() {
        setChainableConstructor(sugarArray, arrayCreate);
    }

    function isArrayOrInherited(obj) {
        return obj && obj.constructor && isArray(obj.constructor.prototype);
    }

    function arrayCreate(obj, clone) {
        var arr;
        if (isArrayOrInherited(obj)) {
            arr = clone ? arrayClone(obj) : obj;
        } else if (isObjectType(obj) || isString(obj)) {
            arr = Array.from(obj);
        } else if (isDefined(obj)) {
            arr = [obj];
        }
        return arr || [];
    }

    function arrayClone(arr) {
        var clone = new Array(arr.length);
        forEach(arr, function (el, i) {
            clone[i] = el;
        });
        return clone;
    }

    function arrayConcat(arr1, arr2) {
        if (HAS_CONCAT_BUG) {
            return arraySafeConcat(arr1, arr2);
        }
        return arr1.concat(arr2);
    }

    // Avoids issues with [undefined] in < IE9
    function arrayWrap(obj) {
        var arr = [];
        arr.push(obj);
        return arr;
    }

    // Avoids issues with concat in < IE8
    function arraySafeConcat(arr, arg) {
        var result = arrayClone(arr), len = result.length, arr2;
        arr2 = isArray(arg) ? arg : [arg];
        result.length += arr2.length;
        forEach(arr2, function (el, i) {
            result[len + i] = el;
        });
        return result;
    }


    function arrayAppend(arr, el, index) {
        var spliceArgs;
        index = +index;
        if (isNaN(index)) {
            index = arr.length;
        }
        spliceArgs = [index, 0];
        if (isDefined(el)) {
            spliceArgs = spliceArgs.concat(el);
        }
        arr.splice.apply(arr, spliceArgs);
        return arr;
    }

    function arrayRemove(arr, f) {
        var matcher = getMatcher(f), i = 0;
        while (i < arr.length) {
            if (matcher(arr[i], i, arr)) {
                arr.splice(i, 1);
            } else {
                i++;
            }
        }
        return arr;
    }

    function arrayExclude(arr, f) {
        var result = [], matcher = getMatcher(f);
        for (var i = 0; i < arr.length; i++) {
            if (!matcher(arr[i], i, arr)) {
                result.push(arr[i]);
            }
        }
        return result;
    }

    function arrayUnique(arr, map) {
        var result = [], obj = {}, refs = [];
        forEach(arr, function (el, i) {
            var transformed = map ? mapWithShortcuts(el, map, arr, [el, i, arr]) : el;
            var key = serializeInternal(transformed, refs);
            if (!hasOwn(obj, key)) {
                result.push(el);
                obj[key] = true;
            }
        });
        return result;
    }

    function arrayFlatten(arr, level, current) {
        var result = [];
        level = level || Infinity;
        current = current || 0;
        forEach(arr, function (el) {
            if (isArray(el) && current < level) {
                result = result.concat(arrayFlatten(el, level, current + 1));
            } else {
                result.push(el);
            }
        });
        return result;
    }

    function arrayCompact(arr, all) {
        return filter(arr, function (el) {
            return el || (!all && el != null && el.valueOf() === el.valueOf());
        });
    }

    function arrayShuffle(arr) {
        arr = arrayClone(arr);
        var i = arr.length, j, x;
        while (i) {
            j = (Math.random() * i) | 0;
            x = arr[--i];
            arr[i] = arr[j];
            arr[j] = x;
        }
        return arr;
    }

    function arrayGroupBy(arr, map, fn) {
        var result = {}, key;
        forEach(arr, function (el, i) {
            key = mapWithShortcuts(el, map, arr, [el, i, arr]);
            if (!hasOwn(result, key)) {
                result[key] = [];
            }
            result[key].push(el);
        });
        if (fn) {
            forEachProperty(result, fn);
        }
        return result;
    }

    function arrayIntersectOrSubtract(arr1, arr2, subtract) {
        var result = [], obj = {}, refs = [];
        if (!isArray(arr2)) {
            arr2 = arrayWrap(arr2);
        }
        forEach(arr2, function (el) {
            obj[serializeInternal(el, refs)] = true;
        });
        forEach(arr1, function (el) {
            var key = serializeInternal(el, refs);
            if (hasOwn(obj, key) !== subtract) {
                delete obj[key];
                result.push(el);
            }
        });
        return result;
    }

    // Collation helpers

    function compareValue(aVal, bVal) {
        var cmp, i, collate;
        if (isString(aVal) && isString(bVal)) {
            collate = _arrayOptions('sortCollate');
            return collate(aVal, bVal);
        } else if (isArray(aVal) && isArray(bVal)) {
            if (aVal.length < bVal.length) {
                return -1;
            } else if (aVal.length > bVal.length) {
                return 1;
            } else {
                for (i = 0; i < aVal.length; i++) {
                    cmp = compareValue(aVal[i], bVal[i]);
                    if (cmp !== 0) {
                        return cmp;
                    }
                }
                return 0;
            }
        }
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }

    function codeIsNumeral(code) {
        return (code >= HALF_WIDTH_ZERO && code <= HALF_WIDTH_NINE) ||
            (code >= FULL_WIDTH_ZERO && code <= FULL_WIDTH_NINE);
    }

    function collateStrings(a, b) {
        var aValue, bValue, aChar, bChar, aEquiv, bEquiv, index = 0, tiebreaker = 0;

        var sortOrder = _arrayOptions('sortOrder');
        var sortIgnore = _arrayOptions('sortIgnore');
        var sortNatural = _arrayOptions('sortNatural');
        var sortIgnoreCase = _arrayOptions('sortIgnoreCase');
        var sortEquivalents = _arrayOptions('sortEquivalents');

        a = getCollationReadyString(a, sortIgnore, sortIgnoreCase);
        b = getCollationReadyString(b, sortIgnore, sortIgnoreCase);

        do {

            aChar = getCollationCharacter(a, index, sortEquivalents);
            bChar = getCollationCharacter(b, index, sortEquivalents);
            aValue = getSortOrderIndex(aChar, sortOrder);
            bValue = getSortOrderIndex(bChar, sortOrder);

            if (aValue === -1 || bValue === -1) {
                aValue = a.charCodeAt(index) || null;
                bValue = b.charCodeAt(index) || null;
                if (sortNatural && codeIsNumeral(aValue) && codeIsNumeral(bValue)) {
                    aValue = stringToNumber(a.slice(index));
                    bValue = stringToNumber(b.slice(index));
                }
            } else {
                aEquiv = aChar !== a.charAt(index);
                bEquiv = bChar !== b.charAt(index);
                if (aEquiv !== bEquiv && tiebreaker === 0) {
                    tiebreaker = aEquiv - bEquiv;
                }
            }
            index += 1;
        } while (aValue != null && bValue != null && aValue === bValue);
        if (aValue === bValue) return tiebreaker;
        return aValue - bValue;
    }

    function getCollationReadyString(str, sortIgnore, sortIgnoreCase) {
        if (!isString(str)) str = String(str);
        if (sortIgnoreCase) {
            str = str.toLowerCase();
        }
        if (sortIgnore) {
            str = str.replace(sortIgnore, '');
        }
        return str;
    }

    function getCollationCharacter(str, index, sortEquivalents) {
        var chr = str.charAt(index);
        return getOwn(sortEquivalents, chr) || chr;
    }

    function getSortOrderIndex(chr, sortOrder) {
        if (!chr) {
            return null;
        } else {
            return sortOrder.indexOf(chr);
        }
    }

    function getSortOrder() {
        var order = 'AÁÀÂÃĄBCĆČÇDĎÐEÉÈĚÊËĘFGĞHıIÍÌİÎÏJKLŁMNŃŇÑOÓÒÔPQRŘSŚŠŞTŤUÚÙŮÛÜVWXYÝZŹŻŽÞÆŒØÕÅÄÖ';
        return map(order.split(''), function (str) {
            return str + str.toLowerCase();
        }).join('');
    }

    function getSortEquivalents() {
        var equivalents = {};
        forEach(spaceSplit('AÁÀÂÃÄ CÇ EÉÈÊË IÍÌİÎÏ OÓÒÔÕÖ Sß UÚÙÛÜ'), function (set) {
            var first = set.charAt(0);
            forEach(set.slice(1).split(''), function (chr) {
                equivalents[chr] = first;
                equivalents[chr.toLowerCase()] = first.toLowerCase();
            });
        });
        return equivalents;
    }

    defineStatic(sugarArray, {

        /***
         *
         * @method create([obj], [clone] = false)
         * @returns Array
         * @static
         * @short Creates an array from an unknown object.
         * @extra This method is similar to native `Array.from` but is faster when
         *        `obj` is already an array. When [clone] is true, the array will be
         *        shallow cloned. Additionally, it will not fail on `undefined`,
         *        `null`, or numbers, producing an empty array in the case of
         *        `undefined` and wrapping `obj` otherwise.
         *
         * @example
         *
         *   Array.create()          -> []
         *   Array.create(8)         -> [8]
         *   Array.create('abc')     -> ['a','b','c']
         *   Array.create([1,2,3])   -> [1, 2, 3]
         *   Array.create(undefined) -> []
         *
         * @param {number|ArrayLike<T>} [obj]
         * @param {boolean} [clone]
         *
         ***/
        'create': function (obj, clone) {
            return arrayCreate(obj, clone);
        },

        /***
         *
         * @method construct(n, map)
         * @returns Array
         * @static
         * @short Constructs an array of `n` length from the values of `map`.
         * @extra This function is essentially a shortcut for using `Array.from` with
         *        `new Array(n)`.
         *
         * @callback indexMapFn
         *
         *   i   The index of the current iteration.
         *
         * @example
         *
         *   Array.construct(4, function(i) {
         *     return i * i;
         *   }); -> [0, 1, 4]
         *
         * @param {number} n
         * @param {indexMapFn} map
         * @callbackParam {number} i
         * @callbackReturns {any} indexMapFn
         *
         ***/
        'construct': function (n, fn) {
            n = coercePositiveInteger(n);
            return Array.from(new Array(n), function (el, i) {
                return fn && fn(i);
            });
        }

    });

    defineInstance(sugarArray, {

        /***
         * @method isEmpty()
         * @returns Boolean
         * @short Returns true if the array has a length of zero.
         *
         * @example
         *
         *   [].isEmpty()    -> true
         *   ['a'].isEmpty() -> false
         *
         ***/
        'isEmpty': function (arr) {
            return arr.length === 0;
        },

        /***
         * @method isEqual(arr)
         * @returns Boolean
         * @short Returns true if the array is equal to `arr`.
         * @extra Objects in the array are considered equal if they are not obserably
         *        distinguishable. This method is an instance alias for
         *        `Object.isEqual()`.
         *
         * @example
         *
         *   ['a','b'].isEqual(['a','b']) -> true
         *   ['a','b'].isEqual(['a','c']) -> false
         *   [{a:'a'}].isEqual([{a:'a'}]) -> true
         *   [5].isEqual([Object(5)])     -> false
         *
         * @param {Array} arr
         *
         ***/
        'isEqual': function (a, b) {
            return isEqual(a, b);
        },

        /***
         * @method clone()
         * @returns Array
         * @short Creates a shallow clone of the array.
         *
         * @example
         *
         *   [1,2,3].clone() -> [1,2,3]
         *
         ***/
        'clone': function (arr) {
            return arrayClone(arr);
        },

        /***
         * @method at(index, [loop] = false)
         * @returns ArrayElement
         * @short Gets the element(s) at `index`.
         * @extra When [loop] is true, overshooting the end of the array will begin
         *        counting from the other end. `index` may be negative. If `index` is
         *        an array, multiple elements will be returned.
         *
         * @example
         *
         *   [1,2,3].at(0)       -> 1
         *   [1,2,3].at(2)       -> 3
         *   [1,2,3].at(4)       -> undefined
         *   [1,2,3].at(4, true) -> 2
         *   [1,2,3].at(-1)      -> 3
         *   [1,2,3].at([0, 1])  -> [1, 2]
         *
         * @param {number|number[]} index
         * @param {boolean} [loop]
         *
         ***/
        'at': function (arr, index, loop) {
            return getEntriesForIndexes(arr, index, loop);
        },

        /***
         * @method add(item, [index])
         * @returns Array
         * @short Adds `item` to the array and returns the result as a new array.
         * @extra If `item` is also an array, it will be concatenated instead of
         *        inserted. [index] will control where `item` is added. Use `append`
         *        to modify the original array.
         *
         * @example
         *
         *   [1,2,3,4].add(5)       -> [1,2,3,4,5]
         *   [1,2,3,4].add(8, 1)    -> [1,8,2,3,4]
         *   [1,2,3,4].add([5,6,7]) -> [1,2,3,4,5,6,7]
         *
         * @param {ArrayElement|Array} item
         * @param {number} [index]
         *
         ***/
        'add': function (arr, item, index) {
            return arrayAppend(arrayClone(arr), item, index);
        },

        /***
         * @method subtract(item)
         * @returns Array
         * @short Subtracts `item` from the array and returns the result as a new array.
         * @extra If `item` is also an array, all elements in it will be removed. In
         *        addition to primitives, this method will also deep-check objects for
         *        equality.
         *
         * @example
         *
         *   [1,3,5].subtract([5,7,9])     -> [1,3]
         *   ['a','b'].subtract(['b','c']) -> ['a']
         *   [1,2,3].subtract(2)           -> [1,3]
         *
         * @param {ArrayElement|Array} item
         *
         ***/
        'subtract': function (arr, item) {
            return arrayIntersectOrSubtract(arr, item, true);
        },

        /***
         * @method append(item, [index])
         * @returns Array
         * @short Appends `item` to the array.
         * @extra If `item` is also an array, it will be concatenated instead of
         *        inserted. This method modifies the array! Use `add` to create a new
         *        array. Additionally, `insert` is provided as an alias that reads
         *        better when using an index.
         *
         * @example
         *
         *   [1,2,3,4].append(5)       -> [1,2,3,4,5]
         *   [1,2,3,4].append([5,6,7]) -> [1,2,3,4,5,6,7]
         *   [1,2,3,4].append(8, 1)    -> [1,8,2,3,4]
         *
         * @param {ArrayElement|Array} item
         * @param {number} index
         *
         ***/
        'append': function (arr, item, index) {
            return arrayAppend(arr, item, index);
        },

        /***
         * @method removeAt(start, [end])
         * @returns Array
         * @short Removes element at `start`. If [end] is specified, removes the range
         *        between `start` and [end]. This method will modify the array!
         *
         * @example
         *
         *   ['a','b','c'].removeAt(0) -> ['b','c']
         *   [1,2,3,4].removeAt(1, 2)  -> [1, 4]
         *
         * @param {number} start
         * @param {number} [end]
         *
         ***/
        'removeAt': function (arr, start, end) {
            if (isUndefined(start)) return arr;
            if (isUndefined(end)) end = start;
            arr.splice(start, end - start + 1);
            return arr;
        },

        /***
         * @method unique([map])
         * @returns Array
         * @short Removes all duplicate elements in the array.
         * @extra [map] may be a function returning the value to be uniqued or a
         *        string acting as a shortcut. This is most commonly used when you
         *        only need to check a single field that can ensure the object's
         *        uniqueness (such as an `id` field). If [map] is not passed, then
         *        objects will be deep checked for equality. Supports
         *        `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,2,3].unique()            -> [1,2,3]
         *   [{a:'a'},{a:'a'}].unique()    -> [{a:'a'}]
         *
         *   users.unique(function(user) {
         *     return user.id;
         *   }); -> users array uniqued by id
         *
         *   users.unique('id')            -> users array uniqued by id
         *
         * @param {string|mapFn} map
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'unique': function (arr, map) {
            return arrayUnique(arr, map);
        },

        /***
         * @method flatten([limit] = Infinity)
         * @returns Array
         * @short Returns a flattened, one-dimensional copy of the array.
         * @extra You can optionally specify a [limit], which will only flatten to
         *        that depth.
         *
         * @example
         *
         *   [[1], 2, [3]].flatten() -> [1,2,3]
         *   [[1],[],2,3].flatten()  -> [1,2,3]
         *
         * @param {number} [limit]
         *
         ***/
        'flatten': function (arr, limit) {
            return arrayFlatten(arr, limit);
        },

        /***
         * @method first([num] = 1)
         * @returns Mixed
         * @short Returns the first element(s) in the array.
         * @extra When `num` is passed, returns the first `num` elements in the array.
         *
         * @example
         *
         *   [1,2,3].first()  -> 1
         *   [1,2,3].first(2) -> [1,2]
         *
         * @param {number} [num]
         *
         ***/
        'first': function (arr, num) {
            if (isUndefined(num)) return arr[0];
            if (num < 0) num = 0;
            return arr.slice(0, num);
        },

        /***
         * @method last([num] = 1)
         * @returns Mixed
         * @short Returns the last element(s) in the array.
         * @extra When `num` is passed, returns the last `num` elements in the array.
         *
         * @example
         *
         *   [1,2,3].last()  -> 3
         *   [1,2,3].last(2) -> [2,3]
         *
         * @param {number} [num]
         *
         ***/
        'last': function (arr, num) {
            if (isUndefined(num)) return arr[arr.length - 1];
            var start = arr.length - num < 0 ? 0 : arr.length - num;
            return arr.slice(start);
        },

        /***
         * @method from(index)
         * @returns Array
         * @short Returns a slice of the array from `index`.
         *
         * @example
         *
         *   ['a','b','c'].from(1) -> ['b','c']
         *   ['a','b','c'].from(2) -> ['c']
         *
         * @param {number} [index]
         *
         ***/
        'from': function (arr, num) {
            return arr.slice(num);
        },

        /***
         * @method to(index)
         * @returns Array
         * @short Returns a slice of the array up to `index`.
         *
         * @example
         *
         *   ['a','b','c'].to(1) -> ['a']
         *   ['a','b','c'].to(2) -> ['a','b']
         *
         * @param {number} [index]
         *
         ***/
        'to': function (arr, num) {
            if (isUndefined(num)) num = arr.length;
            return arr.slice(0, num);
        },

        /***
         * @method compact([all] = false)
         * @returns Array
         * @short Removes all instances of `undefined`, `null`, and `NaN` from the array.
         * @extra If [all] is `true`, all "falsy" elements will be removed. This
         *        includes empty strings, `0`, and `false`.
         *
         * @example
         *
         *   [1,null,2,undefined,3].compact() -> [1,2,3]
         *   [1,'',2,false,3].compact()       -> [1,'',2,false,3]
         *   [1,'',2,false,3].compact(true)   -> [1,2,3]
         *   [null, [null, 'bye']].compact()  -> ['hi', [null, 'bye']]
         *
         * @param {boolean} [all]
         *
         ***/
        'compact': function (arr, all) {
            return arrayCompact(arr, all);
        },

        /***
         * @method groupBy(map, [fn])
         * @returns Object
         * @short Groups the array by `map`.
         * @extra Will return an object whose keys are the mapped from `map`, which
         *        may be a mapping function, or a string acting as a shortcut. `map`
         *        supports `deep properties`. Optionally calls [fn] for each group.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @callback groupFn
         *
         *   arr  The current group as an array.
         *   key  The unique key of the current group.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   ['a','aa','aaa'].groupBy('length') -> { 1: ['a'], 2: ['aa'], 3: ['aaa'] }
         *
         *   users.groupBy(function(n) {
         *     return n.age;
         *   }); -> users array grouped by age
         *
         *   users.groupBy('age', function(age, users) {
         *     // iterates each grouping
         *   });
         *
         * @param {string|mapFn} map
         * @param {groupFn} fn
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'groupBy': function (arr, map, fn) {
            return arrayGroupBy(arr, map, fn);
        },

        /***
         * @method inGroups(num, [padding])
         * @returns Array
         * @short Groups the array into `num` arrays.
         * @extra If specified, [padding] will be added to the last array to be of
         *        equal length.
         *
         * @example
         *
         *   [1,2,3,4,5,6,7].inGroups(3)    -> [[1,2,3],[4,5,6],[7]]
         *   [1,2,3,4,5,6,7].inGroups(3, 0) -> [[1,2,3],[4,5,6],[7,0,0]]
         *
         * @param {number} num
         * @param {any} [padding]
         *
         ***/
        'inGroups': function (arr, num, padding) {
            var pad = isDefined(padding);
            var result = new Array(num);
            var divisor = ceil(arr.length / num);
            simpleRepeat(num, function (i) {
                var index = i * divisor;
                var group = arr.slice(index, index + divisor);
                if (pad && group.length < divisor) {
                    simpleRepeat(divisor - group.length, function () {
                        group.push(padding);
                    });
                }
                result[i] = group;
            });
            return result;
        },

        /***
         * @method inGroupsOf(num, [padding] = null)
         * @returns Array
         * @short Groups the array into arrays of `num` elements each.
         * @extra [padding] will be added to the last array to be of equal length.
         *
         * @example
         *
         *   [1,2,3,4,5,6,7].inGroupsOf(4)    -> [ [1,2,3,4], [5,6,7] ]
         *   [1,2,3,4,5,6,7].inGroupsOf(4, 0) -> [ [1,2,3,4], [5,6,7,0] ]
         *
         * @param {number} num
         * @param {any} [padding]
         *
         ***/
        'inGroupsOf': function (arr, num, padding) {
            var result = [], len = arr.length, group;
            if (len === 0 || num === 0) return arr;
            if (isUndefined(num)) num = 1;
            if (isUndefined(padding)) padding = null;
            simpleRepeat(ceil(len / num), function (i) {
                group = arr.slice(num * i, num * i + num);
                while (group.length < num) {
                    group.push(padding);
                }
                result.push(group);
            });
            return result;
        },

        /***
         * @method shuffle()
         * @returns Array
         * @short Returns a copy of the array with the elements randomized.
         * @extra Uses Fisher-Yates algorithm.
         *
         * @example
         *
         *   [1,2,3,4].shuffle()  -> [?,?,?,?]
         *
         ***/
        'shuffle': function (arr) {
            return arrayShuffle(arr);
        },

        /***
         * @method sample([num] = 1, [remove] = false)
         * @returns Mixed
         * @short Returns a random element from the array.
         * @extra If [num] is passed, will return an array of [num] elements. If
         *        [remove] is true, sampled elements will also be removed from the
         *        array. [remove] can also be passed in place of [num].
         *
         * @example
         *
         *   [1,2,3,4,5].sample()  -> // Random element
         *   [1,2,3,4,5].sample(1) -> // Array of 1 random element
         *   [1,2,3,4,5].sample(3) -> // Array of 3 random elements
         *
         * @param {number} [num]
         * @param {boolean} [remove]
         *
         ***/
        'sample': function (arr, arg1, arg2) {
            var result = [], num, remove, single;
            if (isBoolean(arg1)) {
                remove = arg1;
            } else {
                num = arg1;
                remove = arg2;
            }
            if (isUndefined(num)) {
                num = 1;
                single = true;
            }
            if (!remove) {
                arr = arrayClone(arr);
            }
            num = min(num, arr.length);
            for (var i = 0, index; i < num; i++) {
                index = trunc(Math.random() * arr.length);
                result.push(arr[index]);
                arr.splice(index, 1);
            }
            return single ? result[0] : result;
        },

        /***
         * @method sortBy([map], [desc] = false)
         * @returns Array
         * @short Enhanced sorting function that will sort the array by `map`.
         * @extra `map` may be a function, a string acting as a shortcut, an array
         *        (comparison by multiple values), or blank (direct comparison of
         *        array values). `map` supports `deep properties`. [desc] will sort
         *        the array in descending order. When the field being sorted on is
         *        a string, the resulting order will be determined by an internal
         *        collation algorithm that is optimized for major Western languages,
         *        but can be customized using sorting accessors such as `sortIgnore`.
         *        This method will modify the array!
         *
         * @callback sortMapFn
         *
         *   el   An array element.
         *
         * @example
         *
         *   ['world','a','new'].sortBy('length')       -> ['a','new','world']
         *   ['world','a','new'].sortBy('length', true) -> ['world','new','a']
         *   users.sortBy(function(n) {
         *     return n.age;
         *   }); -> users array sorted by age
         *   users.sortBy('age') -> users array sorted by age
         *
         * @param {string|sortMapFn} [map]
         * @param {boolean} [desc]
         * @callbackParam {ArrayElement} el
         * @callbackReturns {NewArrayElement} sortMapFn
         *
         ***/
        'sortBy': function (arr, map, desc) {
            arr.sort(function (a, b) {
                var aProperty = mapWithShortcuts(a, map, arr, [a]);
                var bProperty = mapWithShortcuts(b, map, arr, [b]);
                return compareValue(aProperty, bProperty) * (desc ? -1 : 1);
            });
            return arr;
        },

        /***
         * @method remove(search)
         * @returns Array
         * @short Removes any element in the array that matches `search`.
         * @extra This method will modify the array! Use `exclude` for a
         *        non-destructive alias. This method implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].remove(3)         -> [1,2]
         *   ['a','b','c'].remove(/b/) -> ['a','c']
         *   [{a:1},{b:2}].remove(function(n) {
         *     return n['a'] == 1;
         *   }); -> [{b:2}]
         *
         * @param {ArrayElement|searchFn} search
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'remove': function (arr, f) {
            return arrayRemove(arr, f);
        },

        /***
         * @method exclude(search)
         * @returns Array
         * @short Returns a new array with every element that does not match `search`.
         * @extra This method can be thought of as the inverse of `Array#filter`. It
         *        will not modify the original array, Use `remove` to modify the
         *        array in place. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].exclude(3)         -> [1,2]
         *   ['a','b','c'].exclude(/b/) -> ['a','c']
         *   [{a:1},{b:2}].exclude(function(n) {
         *     return n['a'] == 1;
         *   }); -> [{b:2}]
         *
         * @param {ArrayElement|searchFn} search
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'exclude': function (arr, f) {
            return arrayExclude(arr, f);
        },

        /***
         * @method union(arr)
         * @returns Array
         * @short Returns a new array containing elements in both arrays with
         *        duplicates removed.
         * @extra In addition to primitives, this method will also deep-check objects
         *        for equality.
         *
         * @example
         *
         *   [1,3,5].union([5,7,9])     -> [1,3,5,7,9]
         *   ['a','b'].union(['b','c']) -> ['a','b','c']
         *
         * @param {Array} arr
         *
         ***/
        'union': function (arr1, arr2) {
            return arrayUnique(arrayConcat(arr1, arr2));
        },

        /***
         * @method intersect(arr)
         * @returns Array
         * @short Returns a new array containing any elements that both arrays have in
         *        common.
         * @extra In addition to primitives, this method will also deep-check objects
         *        for equality.
         *
         * @example
         *
         *   [1,3,5].intersect([5,7,9])     -> [5]
         *   ['a','b'].intersect(['b','c']) -> ['b']
         *
         * @param {Array} arr
         *
         ***/
        'intersect': function (arr1, arr2) {
            return arrayIntersectOrSubtract(arr1, arr2, false);
        }

    });

    defineInstanceWithArguments(sugarArray, {

        /***
         * @method zip([arr1], [arr2], ...)
         * @returns Array
         * @short Merges multiple arrays together.
         * @extra This method "zips up" smaller arrays into one large whose elements
         *        are "all elements at index 0", "all elements at index 1", etc.
         *        Useful when you have associated data that is split over separated
         *        arrays. If the arrays passed have more elements than the original
         *        array, they will be discarded. If they have fewer elements, the
         *        missing elements will filled with `null`.
         *
         * @example
         *
         *   [1,2,3].zip([4,5,6]) -> [[1,2], [3,4], [5,6]]
         *
         * @param {Array} arr1
         * @param {Array} arr2
         *
         ***/
        'zip': function (arr, args) {
            return map(arr, function (el, i) {
                return [el].concat(map(args, function (k) {
                    return (i in k) ? k[i] : null;
                }));
            });
        }

    });

    /***
     * @method insert(item, [index])
     * @returns Array
     * @short Appends `item` to the array at [index].
     * @extra This method is simply a more readable alias for `append` when passing
     *        an index. If `el` is an array it will be joined. This method modifies
     *        the array! Use `add` as a non-destructive alias.
     *
     * @example
     *
     *   [1,3,4,5].insert(2, 1)     -> [1,2,3,4,5]
     *   [1,4,5,6].insert([2,3], 1) -> [1,2,3,4,5,6]
     *
     * @param {ArrayElement|Array} item
     * @param {number} [index]
     *
     ***/
    alias(sugarArray, 'insert', 'append');

    setArrayChainableConstructor();

    /***
     * @module Enumerable
     * @description Counting, mapping, and finding methods on both arrays and objects.
     *
     ***/

    function sum(obj, map) {
        var sum = 0;
        enumerateWithMapping(obj, map, function (val) {
            sum += val;
        });
        return sum;
    }

    function average(obj, map) {
        var sum = 0, count = 0;
        enumerateWithMapping(obj, map, function (val) {
            sum += val;
            count++;
        });
        // Prevent divide by 0
        return sum / (count || 1);
    }

    function median(obj, map) {
        var result = [], middle, len;
        enumerateWithMapping(obj, map, function (val) {
            result.push(val);
        });
        len = result.length;
        if (!len) return 0;
        result.sort(function (a, b) {
            // IE7 will throw errors on non-numbers!
            return (a || 0) - (b || 0);
        });
        middle = trunc(len / 2);
        return len % 2 ? result[middle] : (result[middle - 1] + result[middle]) / 2;
    }

    function getMinOrMax(obj, arg1, arg2, max, asObject) {
        var result = [], pushVal, edge, all, map;
        if (isBoolean(arg1)) {
            all = arg1;
            map = arg2;
        } else {
            map = arg1;
        }
        enumerateWithMapping(obj, map, function (val, key) {
            if (isUndefined(val)) {
                throw new TypeError('Cannot compare with undefined');
            }
            pushVal = asObject ? key : obj[key];
            if (val === edge) {
                result.push(pushVal);
            } else if (isUndefined(edge) || (max && val > edge) || (!max && val < edge)) {
                result = [pushVal];
                edge = val;
            }
        });
        return getReducedMinMaxResult(result, obj, all, asObject);
    }

    function getLeastOrMost(obj, arg1, arg2, most, asObject) {
        var group = {}, refs = [], minMaxResult, result, all, map;
        if (isBoolean(arg1)) {
            all = arg1;
            map = arg2;
        } else {
            map = arg1;
        }
        enumerateWithMapping(obj, map, function (val, key) {
            var groupKey = serializeInternal(val, refs);
            var arr = getOwn(group, groupKey) || [];
            arr.push(asObject ? key : obj[key]);
            group[groupKey] = arr;
        });
        minMaxResult = getMinOrMax(group, !!all, 'length', most, true);
        if (all) {
            result = [];
            // Flatten result
            forEachProperty(minMaxResult, function (val) {
                result = result.concat(val);
            });
        } else {
            result = getOwn(group, minMaxResult);
        }
        return getReducedMinMaxResult(result, obj, all, asObject);
    }


    // Support

    function getReducedMinMaxResult(result, obj, all, asObject) {
        if (asObject && all) {
            // The method has returned an array of keys so use this array
            // to build up the resulting object in the form we want it in.
            return result.reduce(function (o, key) {
                o[key] = obj[key];
                return o;
            }, {});
        } else if (result && !all) {
            result = result[0];
        }
        return result;
    }

    function enumerateWithMapping(obj, map, fn) {
        var arrayIndexes = isArray(obj);
        forEachProperty(obj, function (val, key) {
            if (arrayIndexes) {
                if (!isArrayIndex(key)) {
                    return;
                }
                key = +key;
            }
            var mapped = mapWithShortcuts(val, map, obj, [val, key, obj]);
            fn(mapped, key);
        });
    }

    /*** @namespace Array ***/

        // Flag allowing native array methods to be enhanced
    var ARRAY_ENHANCEMENTS_FLAG = 'enhanceArray';

    // Enhanced map function
    var enhancedMap = buildEnhancedMapping('map');

    // Enhanced matcher methods
    var enhancedFind = buildEnhancedMatching('find'),
        enhancedSome = buildEnhancedMatching('some'),
        enhancedEvery = buildEnhancedMatching('every'),
        enhancedFilter = buildEnhancedMatching('filter'),
        enhancedFindIndex = buildEnhancedMatching('findIndex');

    function arrayNone() {
        return !enhancedSome.apply(this, arguments);
    }

    function arrayCount(arr, f) {
        if (isUndefined(f)) {
            return arr.length;
        }
        return enhancedFilter.apply(this, arguments).length;
    }

    // Enhanced methods

    function buildEnhancedMapping(name) {
        return wrapNativeArrayMethod(name, enhancedMapping);
    }


    function buildEnhancedMatching(name) {
        return wrapNativeArrayMethod(name, enhancedMatching);
    }

    function enhancedMapping(map, context) {
        if (isFunction(map)) {
            return map;
        } else if (map) {
            return function (el, i, arr) {
                return mapWithShortcuts(el, map, context, [el, i, arr]);
            };
        }
    }

    function enhancedMatching(f) {
        var matcher;
        if (isFunction(f)) {
            return f;
        }
        matcher = getMatcher(f);
        return function (el, i, arr) {
            return matcher(el, i, arr);
        };
    }

    function wrapNativeArrayMethod(methodName, wrapper) {
        var nativeFn = Array.prototype[methodName];
        return function (arr, f, context, argsLen) {
            var args = new Array(2);
            assertArgument(argsLen > 0);
            args[0] = wrapper(f, context);
            args[1] = context;
            return nativeFn.apply(arr, args);
        };
    }


    /***
     * @method [fn]FromIndex(startIndex, [loop], ...)
     * @returns Mixed
     * @short Runs native array functions beginning from `startIndex`.
     * @extra If [loop] is `true`, once the end of the array has been reached,
     *        iteration will continue from the start of the array up to
     *        `startIndex - 1`. If [loop] is false it can be omitted. Standard
     *        arguments are then passed which will be forwarded to the native
     *        methods. When available, methods are always `enhanced`. This includes
     *        `deep properties` for `map`, and `enhanced matching` for `some`,
     *        `every`, `filter`, `find`, and `findIndex`. Note also that
     *        `forEachFromIndex` is optimized for sparse arrays and may be faster
     *        than native `forEach`.
     *
     * @set
     *   mapFromIndex
     *   forEachFromIndex
     *   filterFromIndex
     *   someFromIndex
     *   everyFromIndex
     *   reduceFromIndex
     *   reduceRightFromIndex
     *   findFromIndex
     *   findIndexFromIndex
     *
     * @example
     *
     *   users.mapFromIndex(2, 'name');
     *   users.mapFromIndex(2, true, 'name');
     *   names.forEachFromIndex(10, log);
     *   names.everyFromIndex(15, /^[A-F]/);
     *
     * @signature [fn]FromIndex(startIndex, ...)
     * @param {number} startIndex
     * @param {boolean} loop
     *
     ***/
    function buildFromIndexMethods() {

        var methods = {
            'forEach': {
                base: forEachAsNative
            },
            'map': {
                wrapper: enhancedMapping
            },
            'some every': {
                wrapper: enhancedMatching
            },
            'findIndex': {
                wrapper: enhancedMatching,
                result: indexResult
            },
            'reduce': {
                apply: applyReduce
            },
            'filter find': {
                wrapper: enhancedMatching
            },
            'reduceRight': {
                apply: applyReduce,
                slice: sliceArrayFromRight,
                clamp: clampStartIndexFromRight
            }
        };

        forEachProperty(methods, function (opts, key) {
            forEach(spaceSplit(key), function (baseName) {
                var methodName = baseName + 'FromIndex';
                var fn = createFromIndexWithOptions(baseName, opts);
                defineInstanceWithArguments(sugarArray, methodName, fn);
            });
        });

        function forEachAsNative(fn) {
            forEach(this, fn);
        }

        // Methods like filter and find have a direct association between the value
        // returned by the callback and the element of the current iteration. This
        // means that when looping, array elements must match the actual index for
        // which they are being called, so the array must be sliced. This is not the
        // case for methods like forEach and map, which either do not use return
        // values or use them in a way that simply getting the element at a shifted
        // index will not affect the final return value. However, these methods will
        // still fail on sparse arrays, so always slicing them here. For example, if
        // "forEachFromIndex" were to be called on [1,,2] from index 1, although the
        // actual index 1 would itself would be skipped, when the array loops back to
        // index 0, shifting it by adding 1 would result in the element for that
        // iteration being undefined. For shifting to work, all gaps in the array
        // between the actual index and the shifted index would have to be accounted
        // for. This is infeasible and is easily solved by simply slicing the actual
        // array instead so that gaps align. Note also that in the case of forEach,
        // we are using the internal function which handles sparse arrays in a way
        // that does not increment the index, and so is highly optimized compared to
        // the others here, which are simply going through the native implementation.
        function sliceArrayFromLeft(arr, startIndex, loop) {
            var result = arr;
            if (startIndex) {
                result = arr.slice(startIndex);
                if (loop) {
                    result = result.concat(arr.slice(0, startIndex));
                }
            }
            return result;
        }

        // When iterating from the right, indexes are effectively shifted by 1.
        // For example, iterating from the right from index 2 in an array of 3
        // should also include the last element in the array. This matches the
        // "lastIndexOf" method which also iterates from the right.
        function sliceArrayFromRight(arr, startIndex, loop) {
            if (!loop) {
                startIndex += 1;
                arr = arr.slice(0, max(0, startIndex));
            }
            return arr;
        }

        function clampStartIndex(startIndex, len) {
            return min(len, max(0, startIndex));
        }

        // As indexes are shifted by 1 when starting from the right, clamping has to
        // go down to -1 to accommodate the full range of the sliced array.
        function clampStartIndexFromRight(startIndex, len) {
            return min(len, max(-1, startIndex));
        }

        function applyReduce(arr, startIndex, fn, context, len, loop) {
            return function (acc, val, i) {
                i = getNormalizedIndex(i + startIndex, len, loop);
                return fn.call(arr, acc, val, i, arr);
            };
        }

        function applyEach(arr, startIndex, fn, context, len, loop) {
            return function (el, i) {
                i = getNormalizedIndex(i + startIndex, len, loop);
                return fn.call(context, arr[i], i, arr);
            };
        }

        function indexResult(result, startIndex, len) {
            if (result !== -1) {
                result = (result + startIndex) % len;
            }
            return result;
        }

        function createFromIndexWithOptions(methodName, opts) {

            var baseFn = opts.base || Array.prototype[methodName],
                applyCallback = opts.apply || applyEach,
                sliceArray = opts.slice || sliceArrayFromLeft,
                clampIndex = opts.clamp || clampStartIndex,
                getResult = opts.result,
                wrapper = opts.wrapper;

            return function (arr, startIndex, args) {
                var callArgs = [], argIndex = 0, lastArg, result, len, loop, fn;
                len = arr.length;
                if (isBoolean(args[0])) {
                    loop = args[argIndex++];
                }
                fn = args[argIndex++];
                lastArg = args[argIndex];
                if (startIndex < 0) {
                    startIndex += len;
                }
                startIndex = clampIndex(startIndex, len);
                assertArgument(args.length);
                fn = wrapper ? wrapper(fn, lastArg) : fn;
                callArgs.push(applyCallback(arr, startIndex, fn, lastArg, len, loop));
                if (lastArg) {
                    callArgs.push(lastArg);
                }
                result = baseFn.apply(sliceArray(arr, startIndex, loop), callArgs);
                if (getResult) {
                    result = getResult(result, startIndex, len);
                }
                return result;
            };
        }
    }

    defineInstance(sugarArray, {

        /***
         * @method map(map, [context])
         * @returns New Array
         * @polyfill ES5
         * @short Maps the array to another array whose elements are the values
         *        returned by the `map` callback.
         * @extra [context] is the `this` object. Sugar enhances this method to accept
         *        a string for `map`, which is a shortcut for a function that gets
         *        a property or invokes a function on each element.
         *        Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].map(function(n) {
         *     return n * 3;
         *   }); -> [3,6,9]
         *
         *   ['a','aa','aaa'].map('length') -> [1,2,3]
         *   ['A','B','C'].map('toLowerCase') -> ['a','b','c']
         *   users.map('name') -> array of user names
         *
         * @param {string|mapFn} map
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'map': fixArgumentLength(enhancedMap),

        /***
         * @method some(search, [context])
         * @returns Boolean
         * @polyfill ES5
         * @short Returns true if `search` is true for any element in the array.
         * @extra [context] is the `this` object. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   ['a','b','c'].some(function(n) {
         *     return n == 'a';
         *   });
         *   ['a','b','c'].some(function(n) {
         *     return n == 'd';
         *   });
         *   ['a','b','c'].some('a')    -> true
         *   [{a:2},{b:5}].some({a:2})  -> true
         *   users.some({ name: /^H/ }) -> true if any have a name starting with H
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'some': fixArgumentLength(enhancedSome),

        /***
         * @method every(search, [context])
         * @returns Boolean
         * @polyfill ES5
         * @short Returns true if `search` is true for all elements of the array.
         * @extra [context] is the `this` object. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   ['a','a','a'].every(function(n) {
         *     return n == 'a';
         *   });
         *   ['a','a','a'].every('a')   -> true
         *   [{a:2},{a:2}].every({a:2}) -> true
         *   users.every({ name: /^H/ }) -> true if all have a name starting with H
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'every': fixArgumentLength(enhancedEvery),

        /***
         * @method filter(search, [context])
         * @returns Array
         * @polyfill ES5
         * @short Returns any elements in the array that match `search`.
         * @extra [context] is the `this` object. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].filter(function(n) {
         *     return n > 1;
         *   });
         *   [1,2,2,4].filter(2) -> 2
         *   users.filter({ name: /^H/ }) -> all users with a name starting with H
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'filter': fixArgumentLength(enhancedFilter),

        /***
         * @method find(search, [context])
         * @returns Mixed
         * @polyfill ES6
         * @short Returns the first element in the array that matches `search`.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   users.find(function(user) {
         *     return user.name = 'Harry';
         *   }); -> harry!
         *
         *   users.find({ name: 'Harry' }); -> harry!
         *   users.find({ name: /^[A-H]/ });  -> First user with name starting with A-H
         *   users.find({ titles: ['Ms', 'Dr'] }); -> not harry!
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'find': fixArgumentLength(enhancedFind),

        /***
         * @method findIndex(search, [context])
         * @returns Number
         * @polyfill ES6
         * @short Returns the index of the first element in the array that matches
         *        `search`, or `-1` if none.
         * @extra [context] is the `this` object. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3,4].findIndex(function(n) {
         *     return n % 2 == 0;
         *   }); -> 1
         *   ['a','b','c'].findIndex('c');        -> 2
         *   ['cuba','japan','canada'].find(/^c/) -> 0
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'findIndex': fixArgumentLength(enhancedFindIndex)

    }, [ENHANCEMENTS_FLAG, ARRAY_ENHANCEMENTS_FLAG]);


    defineInstance(sugarArray, {

        /***
         * @method none(search, [context])
         *
         * @returns Boolean
         * @short Returns true if none of the elements in the array match `search`.
         * @extra [context] is the `this` object. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].none(5)         -> true
         *   ['a','b','c'].none(/b/) -> false
         *   users.none(function(user) {
         *     return user.name == 'Wolverine';
         *   }); -> probably true
         *   users.none({ name: 'Wolverine' }); -> same as above
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'none': fixArgumentLength(arrayNone),

        /***
         * @method count(search, [context])
         * @returns Number
         * @short Counts all elements in the array that match `search`.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   ['a','b','a'].count('a') -> 2
         *   ['a','b','c'].count(/b/) -> 1
         *   users.count(function(user) {
         *     return user.age > 30;
         *   }); -> number of users older than 30
         *
         * @param {ArrayElement|searchFn} search
         * @param {any} context
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'count': fixArgumentLength(arrayCount),

        /***
         * @method min([all] = false, [map])
         * @returns Mixed
         * @short Returns the element in the array with the lowest value.
         * @extra [map] may be passed in place of [all], and is a function mapping the
         *        value to be checked or a string acting as a shortcut. If [all] is
         *        true, multiple elements will be returned. Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].min()                          -> 1
         *   ['fee','fo','fum'].min('length')       -> 'fo'
         *   ['fee','fo','fum'].min(true, 'length') -> ['fo']
         *   users.min('age')                       -> youngest guy!
         *
         *   ['fee','fo','fum'].min(true, function(n) {
         *     return n.length;
         *   }); -> ['fo']
         *
         * @signature min([map])
         * @param {string|mapFn} map
         * @param {boolean} all
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'min': function (arr, all, map) {
            return getMinOrMax(arr, all, map);
        },

        /***
         * @method max([all] = false, [map])
         * @returns Mixed
         * @short Returns the element in the array with the greatest value.
         * @extra [map] may be passed in place of [all], and is a function mapping the
         *        value to be checked or a string acting as a shortcut. If [all] is
         *        true, multiple elements will be returned. Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3].max()                          -> 3
         *   ['fee','fo','fum'].max('length')       -> 'fee'
         *   ['fee','fo','fum'].max(true, 'length') -> ['fee','fum']
         *   users.max('age')                       -> oldest guy!
         *
         *   ['fee','fo','fum'].max(true, function(n) {
         *     return n.length;
         *   }); -> ['fee', 'fum']
         *
         * @signature max([map])
         * @param {string|mapFn} map
         * @param {boolean} all
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'max': function (arr, all, map) {
            return getMinOrMax(arr, all, map, true);
        },

        /***
         * @method least([all] = false, [map])
         * @returns Array
         * @short Returns the elements in the array with the least commonly occuring value.
         * @extra [map] may be passed in place of [all], and is a function mapping the
         *        value to be checked or a string acting as a shortcut. If [all] is
         *        true, will return multiple values in an array.
         *        Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [3,2,2].least() -> 3
         *   ['fe','fo','fum'].least(true, 'length') -> ['fum']
         *   users.least('profile.type')             -> (user with least commonly occurring type)
         *   users.least(true, 'profile.type')       -> (users with least commonly occurring type)
         *
         * @signature least([map])
         * @param {string|mapFn} map
         * @param {boolean} all
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'least': function (arr, all, map) {
            return getLeastOrMost(arr, all, map);
        },

        /***
         * @method most([all] = false, [map])
         * @returns Array
         * @short Returns the elements in the array with the most commonly occuring value.
         * @extra [map] may be passed in place of [all], and is a function mapping the
         *        value to be checked or a string acting as a shortcut. If [all] is
         *        true, will return multiple values in an array.
         *        Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [3,2,2].most(2) -> 2
         *   ['fe','fo','fum'].most(true, 'length') -> ['fe','fo']
         *   users.most('profile.type')             -> (user with most commonly occurring type)
         *   users.most(true, 'profile.type')       -> (users with most commonly occurring type)
         *
         * @signature most([map])
         * @param {string|mapFn} map
         * @param {boolean} all
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'most': function (arr, all, map) {
            return getLeastOrMost(arr, all, map, true);
        },

        /***
         * @method sum([map])
         * @returns Number
         * @short Sums all values in the array.
         * @extra [map] may be a function mapping the value to be summed or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,2].sum() -> 5
         *   users.sum(function(user) {
         *     return user.votes;
         *   }); -> total votes!
         *   users.sum('votes') -> total votes!
         *
         * @param {string|mapFn} map
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'sum': function (arr, map) {
            return sum(arr, map);
        },

        /***
         * @method average([map])
         * @returns Number
         * @short Gets the mean average for all values in the array.
         * @extra [map] may be a function mapping the value to be averaged or a string
         *        acting as a shortcut. Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,3,4].average() -> 2
         *   users.average(function(user) {
         *     return user.age;
         *   }); -> average user age
         *   users.average('age') -> average user age
         *   users.average('currencies.usd.balance') -> average USD balance
         *
         * @param {string|mapFn} map
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'average': function (arr, map) {
            return average(arr, map);
        },

        /***
         * @method median([map])
         * @returns Number
         * @short Gets the median average for all values in the array.
         * @extra [map] may be a function mapping the value to be averaged or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   arr  A reference to the array.
         *
         * @example
         *
         *   [1,2,2].median() -> 2
         *   [{a:1},{a:2},{a:2}].median('a') -> 2
         *   users.median('age') -> median user age
         *   users.median('currencies.usd.balance') -> median USD balance
         *
         * @param {string|mapFn} map
         * @callbackParam {ArrayElement} el
         * @callbackParam {number} i
         * @callbackParam {Array} arr
         * @callbackReturns {NewArrayElement} mapFn
         *
         ***/
        'median': function (arr, map) {
            return median(arr, map);
        }

    });


    /*** @namespace Object ***/

        // Object matchers
    var objectSome = wrapObjectMatcher('some'),
        objectFind = wrapObjectMatcher('find'),
        objectEvery = wrapObjectMatcher('every');

    function objectForEach(obj, fn) {
        assertCallable(fn);
        forEachProperty(obj, function (val, key) {
            fn(val, key, obj);
        });
        return obj;
    }

    function objectMap(obj, map) {
        var result = {};
        forEachProperty(obj, function (val, key) {
            result[key] = mapWithShortcuts(val, map, obj, [val, key, obj]);
        });
        return result;
    }

    function objectReduce(obj, fn, acc) {
        var init = isDefined(acc);
        forEachProperty(obj, function (val, key) {
            if (!init) {
                acc = val;
                init = true;
                return;
            }
            acc = fn(acc, val, key, obj);
        });
        return acc;
    }

    function objectNone(obj, f) {
        return !objectSome(obj, f);
    }

    function objectFilter(obj, f) {
        var matcher = getMatcher(f), result = {};
        forEachProperty(obj, function (val, key) {
            if (matcher(val, key, obj)) {
                result[key] = val;
            }
        });
        return result;
    }

    function objectCount(obj, f) {
        var matcher = getMatcher(f), count = 0;
        forEachProperty(obj, function (val, key) {
            if (matcher(val, key, obj)) {
                count++;
            }
        });
        return count;
    }

    // Support

    function wrapObjectMatcher(name) {
        var nativeFn = Array.prototype[name];
        return function (obj, f) {
            var matcher = getMatcher(f);
            return nativeFn.call(getKeys(obj), function (key) {
                return matcher(obj[key], key, obj);
            });
        };
    }

    defineInstanceAndStatic(sugarObject, {

        /***
         * @method forEach(fn)
         * @returns Object
         * @short Runs `fn` against each property in the object.
         * @extra Does not iterate over inherited or non-enumerable properties.
         *
         * @callback eachFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.forEach({a:'b'}, function(val, key) {
         *     // val = 'b', key = a
         *   });
         *
         * @param {eachFn} fn
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         *
         ***/
        'forEach': function (obj, fn) {
            return objectForEach(obj, fn);
        },

        /***
         * @method map(map)
         * @returns Object
         * @short Maps the object to another object whose properties are the values
         *        returned by `map`.
         * @extra `map` can also be a string, which is a shortcut for a function that
         *        gets that property (or invokes a function) on each element.
         *        Supports `deep properties`.
         *
         * @callback mapFn
         *
         *   val  The value of the current property.
         *   key  The key of the current property.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   data.map(function(val, key) {
         *     return key;
         *   }); -> {a:'b'}
         *   users.map('age');
         *
         * @param {string|mapFn} map
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'map': function (obj, map) {
            return objectMap(obj, map);
        },

        /***
         * @method some(search)
         * @returns Boolean
         * @short Returns true if `search` is true for any property in the object.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.some({a:1,b:2}, function(val) {
         *     return val == 1;
         *   }); -> true
         *   Object.some({a:1,b:2}, 1); -> true
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'some': objectSome,

        /***
         * @method every(search)
         * @returns Boolean
         * @short Returns true if `search` is true for all properties in the object.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.every({a:1,b:2}, function(val) {
         *     return val > 0;
         *   }); -> true
         *   Object.every({a:'a',b:'b'}, /[a-z]/); -> true
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'every': objectEvery,

        /***
         * @method filter(search)
         * @returns Array
         * @short Returns a new object with properties that match `search`.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.filter({a:1,b:2}, function(val) {
         *     return val == 1;
         *   }); -> {a:1}
         *   Object.filter({a:'a',z:'z'}, /[a-f]/); -> {a:'a'}
         *   Object.filter(usersByName, /^H/); -> all users with names starting with H
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'filter': function (obj, f) {
            return objectFilter(obj, f);
        },

        /***
         * @method reduce(reduceFn, [init])
         * @returns Mixed
         * @short Reduces the object to a single result.
         * @extra This operation is sometimes called "accumulation", as it takes the
         *        result of the last iteration of `fn` and passes it as the first
         *        argument to the next iteration, "accumulating" that value as it goes.
         *        The return value of this method will be the return value of the final
         *        iteration of `fn`. If [init] is passed, it will be the initial
         *        "accumulator" (the first argument). If [init] is not passed, then a
         *        property of the object will be used instead and `fn` will not be
         *        called for that property. Note that object properties have no order,
         *        and this may lead to bugs (for example if performing division or
         *        subtraction operations on a value). If order is important, use an
         *        array instead!
         *
         * @callback reduceFn
         *
         *   acc  The "accumulator", either [init], the result of the last iteration
         *        of `fn`, or a property of `obj`.
         *   val  The value of the current property called for `fn`.
         *   key  The key of the current property called for `fn`.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.reduce({a:2,b:4}, function(a, b) {
         *     return a * b;
         *   }); -> 8
         *
         *   Object.reduce({a:2,b:4}, function(a, b) {
         *     return a * b;
         *   }, 10); -> 80
         *
         *
         * @param {reduceFn} reduceFn
         * @param {any} [init]
         * @callbackParam {Property} acc
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         *
         ***/
        'reduce': function (obj, fn, init) {
            return objectReduce(obj, fn, init);
        },

        /***
         * @method find(search)
         * @returns Boolean
         * @short Returns the first key whose value matches `search`.
         * @extra Implements `enhanced matching`. Note that "first" is
         *        implementation-dependent. If order is important an array should be
         *        used instead.
         *
         * @callback searchFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.find({a:1,b:2}, function(val) {
         *     return val == 2;
         *   }); -> 'b'
         *   Object.find({a:'a',b:'b'}, /[a-z]/); -> 'a'
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'find': objectFind,

        /***
         * @method count(search)
         * @returns Number
         * @short Counts all properties in the object that match `search`.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.count({a:'a',b:'b',c:'a'}, 'a') -> 2
         *   Object.count(usersByName, function(user) {
         *     return user.age > 30;
         *   }); -> number of users older than 30
         *   Object.count(usersByName, { name: /^[H-Z]/ });
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'count': function (obj, f) {
            return objectCount(obj, f);
        },

        /***
         * @method none(search)
         * @returns Boolean
         * @short Returns true if none of the properties in the object match `search`.
         * @extra Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.none({a:1,b:2}, 3); -> true
         *   Object.none(usersByName, function(user) {
         *     return user.name == 'Wolverine';
         *   }); -> probably true
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'none': function (obj, f) {
            return objectNone(obj, f);
        },

        /***
         * @method sum([map])
         * @returns Number
         * @short Sums all properties in the object.
         * @extra [map] may be a function mapping the value to be summed or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.sum({a:35,b:13}); -> 48
         *   Object.sum(usersByName, function(user) {
         *     return user.votes;
         *   }); -> total user votes
         *
         * @param {string|mapFn} map
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'sum': function (obj, map) {
            return sum(obj, map);
        },

        /***
         * @method average([map])
         * @returns Number
         * @short Gets the mean average of all properties in the object.
         * @extra [map] may be a function mapping the value to be averaged or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.average({a:35,b:11}); -> 23
         *   Object.average(usersByName, 'age'); -> average user age
         *   Object.average(usersByName, 'currencies.usd.balance'); -> USD mean balance
         *
         * @param {string|mapFn} map
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'average': function (obj, map) {
            return average(obj, map);
        },

        /***
         * @method median([map])
         * @returns Number
         * @short Gets the median average of all properties in the object.
         * @extra [map] may be a function mapping the value to be averaged or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.median({a:1,b:2,c:2}) -> 2
         *   Object.median(usersByName, 'age'); -> median user age
         *   Object.median(usersByName, 'currencies.usd.balance'); -> USD median balance
         *
         * @param {string|mapFn} map
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'median': function (obj, map) {
            return median(obj, map);
        },

        /***
         * @method min([all] = false, [map])
         * @returns Mixed
         * @short Returns the key of the property in the object with the lowest value.
         * @extra If [all] is true, will return an object with all properties in the
         *        object with the lowest value. [map] may be passed in place of [all]
         *        and is a function mapping the value to be checked or a string acting
         *        as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.min({a:1,b:2,c:3})                    -> 'a'
         *   Object.min({a:'aaa',b:'bb',c:'c'}, 'length') -> 'c'
         *   Object.min({a:1,b:1,c:3}, true)              -> {a:1,b:1}
         *
         * @signature min([map])
         * @param {string|mapFn} map
         * @param {boolean} [all]
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'min': function (obj, all, map) {
            return getMinOrMax(obj, all, map, false, true);
        },

        /***
         * @method max([all] = false, [map])
         * @returns Mixed
         * @short Returns the key of the property in the object with the highest value.
         * @extra If [all] is true, will return an object with all properties in the
         *        object with the highest value. [map] may be passed in place of [all]
         *        and is a function mapping the value to be checked or a string acting
         *        as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.max({a:1,b:2,c:3})                    -> 'c'
         *   Object.max({a:'aaa',b:'bb',c:'c'}, 'length') -> 'a'
         *   Object.max({a:1,b:3,c:3}, true)              -> {b:3,c:3}
         *
         * @signature max([map])
         * @param {string|mapFn} map
         * @param {boolean} [all]
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'max': function (obj, all, map) {
            return getMinOrMax(obj, all, map, true, true);
        },

        /***
         * @method least([all] = false, [map])
         * @returns Mixed
         * @short Returns the key of the property in the object with the least commonly
         *        occuring value.
         * @extra If [all] is true, will return an object with all properties in the
         *        object with the least common value. [map] may be passed in place of
         *        [all] and is a function mapping the value to be checked or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.least({a:1,b:3,c:3})                   -> 'a'
         *   Object.least({a:'aa',b:'bb',c:'c'}, 'length') -> 'c'
         *   Object.least({a:1,b:3,c:3}, true)             -> {a:1}
         *
         * @signature least([map])
         * @param {string|mapFn} map
         * @param {boolean} [all]
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'least': function (obj, all, map) {
            return getLeastOrMost(obj, all, map, false, true);
        },

        /***
         * @method most([all] = false, [map])
         * @returns Mixed
         * @short Returns the key of the property in the object with the most commonly
         *        occuring value.
         * @extra If [all] is true, will return an object with all properties in the
         *        object with the most common value. [map] may be passed in place of
         *        [all] and is a function mapping the value to be checked or a string
         *        acting as a shortcut.
         *
         * @callback mapFn
         *
         *   val  The value of the current iteration.
         *   key  The key of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.most({a:1,b:3,c:3})                   -> 'b'
         *   Object.most({a:'aa',b:'bb',c:'c'}, 'length') -> 'a'
         *   Object.most({a:1,b:3,c:3}, true)             -> {b:3,c:3}
         *
         * @signature most([map])
         * @param {string|mapFn} map
         * @param {boolean} [all]
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} mapFn
         *
         ***/
        'most': function (obj, all, map) {
            return getLeastOrMost(obj, all, map, true, true);
        }

    });


    buildFromIndexMethods();

    /***
     * @module Object
     * @description Object creation, manipulation, comparison, type checking, and more.
     *
     * Much thanks to kangax for his informative aricle about how problems with
     * instanceof and constructor: http://bit.ly/1Qds27W
     *
     ***/

        // Matches bracket-style query strings like user[name]
    var DEEP_QUERY_STRING_REG = /^(.+?)(\[.*\])$/;

    // Matches any character not allowed in a decimal number.
    var NON_DECIMAL_REG = /[^\d.-]/;

    // Native methods for merging by descriptor when available.
    var getOwnPropertyNames = Object.getOwnPropertyNames;
    var getOwnPropertySymbols = Object.getOwnPropertySymbols;
    var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

    // Basic Helpers

    function isArguments(obj, className) {
        className = className || classToString(obj);
        // .callee exists on Arguments objects in < IE8
        return hasProperty(obj, 'length') && (className === '[object Arguments]' || !!obj.callee);
    }

    // Query Strings | Creating

    function toQueryStringWithOptions(obj, opts) {
        opts = opts || {};
        if (isUndefined(opts.separator)) {
            opts.separator = '_';
        }
        return toQueryString(obj, opts.deep, opts.transform, opts.prefix || '', opts.separator);
    }

    function toQueryString(obj, deep, transform, prefix, separator) {
        if (isArray(obj)) {
            return collectArrayAsQueryString(obj, deep, transform, prefix, separator);
        } else if (isObjectType(obj) && obj.toString === internalToString) {
            return collectObjectAsQueryString(obj, deep, transform, prefix, separator);
        } else if (prefix) {
            return getURIComponentValue(obj, prefix, transform);
        }
        return '';
    }

    function collectArrayAsQueryString(arr, deep, transform, prefix, separator) {
        var el, qc, key, result = [];
        // Intentionally treating sparse arrays as dense here by avoiding map,
        // otherwise indexes will shift during the process of serialization.
        for (var i = 0, len = arr.length; i < len; i++) {
            el = arr[i];
            key = prefix + (prefix && deep ? '[]' : '');
            if (!key && !isObjectType(el)) {
                // If there is no key, then the values of the array should be
                // considered as null keys, so use them instead;
                qc = sanitizeURIComponent(el);
            } else {
                qc = toQueryString(el, deep, transform, key, separator);
            }
            result.push(qc);
        }
        return result.join('&');
    }

    function collectObjectAsQueryString(obj, deep, transform, prefix, separator) {
        var result = [];
        forEachProperty(obj, function (val, key) {
            var fullKey;
            if (prefix && deep) {
                fullKey = prefix + '[' + key + ']';
            } else if (prefix) {
                fullKey = prefix + separator + key;
            } else {
                fullKey = key;
            }
            result.push(toQueryString(val, deep, transform, fullKey, separator));
        });
        return result.join('&');
    }

    function getURIComponentValue(obj, prefix, transform) {
        var value;
        if (transform) {
            value = transform(obj, prefix);
        } else if (isDate(obj)) {
            value = obj.getTime();
        } else {
            value = obj;
        }
        return sanitizeURIComponent(prefix) + '=' + sanitizeURIComponent(value);
    }

    function sanitizeURIComponent(obj) {
        // undefined, null, and NaN are represented as a blank string,
        // while false and 0 are stringified.
        return !obj && obj !== false && obj !== 0 ? '' : encodeURIComponent(obj);
    }


    // Query Strings | Parsing

    function fromQueryStringWithOptions(obj, opts) {
        var str = String(obj || '').replace(/^.*?\?/, ''), result = {}, auto;
        opts = opts || {};
        if (str) {
            forEach(str.split('&'), function (p) {
                var split = p.split('=');
                var key = decodeURIComponent(split[0]);
                var val = split.length === 2 ? decodeURIComponent(split[1]) : '';
                auto = opts.auto !== false;
                parseQueryComponent(result, key, val, opts.deep, auto, opts.separator, opts.transform);
            });
        }
        return result;
    }

    function parseQueryComponent(obj, key, val, deep, auto, separator, transform) {
        var match;
        if (separator) {
            key = mapQuerySeparatorToKeys(key, separator);
            deep = true;
        }
        if (deep === true && (match = key.match(DEEP_QUERY_STRING_REG))) {
            parseDeepQueryComponent(obj, match, val, deep, auto, separator, transform);
        } else {
            setQueryProperty(obj, key, val, auto, transform);
        }
    }

    function parseDeepQueryComponent(obj, match, val, deep, auto, separator, transform) {
        var key = match[1];
        var inner = match[2].slice(1, -1).split('][');
        forEach(inner, function (k) {
            if (!hasOwn(obj, key)) {
                obj[key] = k ? {} : [];
            }
            obj = getOwn(obj, key);
            key = k ? k : obj.length.toString();
        });
        setQueryProperty(obj, key, val, auto, transform);
    }

    function mapQuerySeparatorToKeys(key, separator) {
        var split = key.split(separator), result = split[0];
        for (var i = 1, len = split.length; i < len; i++) {
            result += '[' + split[i] + ']';
        }
        return result;
    }

    function setQueryProperty(obj, key, val, auto, transform) {
        var fnValue;
        if (transform) {
            fnValue = transform(val, key, obj);
        }
        if (isDefined(fnValue)) {
            val = fnValue;
        } else if (auto) {
            val = getQueryValueAuto(obj, key, val);
        }
        obj[key] = val;
    }

    function getQueryValueAuto(obj, key, val) {
        if (!val) {
            return null;
        } else if (val === 'true') {
            return true;
        } else if (val === 'false') {
            return false;
        }
        var num = +val;
        if (!isNaN(num) && stringIsDecimal(val)) {
            return num;
        }
        var existing = getOwn(obj, key);
        if (val && existing) {
            return isArray(existing) ? existing.concat(val) : [existing, val];
        }
        return val;
    }

    function stringIsDecimal(str) {
        return str !== '' && !NON_DECIMAL_REG.test(str);
    }


    // Object Merging

    function mergeWithOptions(target, source, opts) {
        opts = opts || {};
        return objectMerge(target, source, opts.deep, opts.resolve, opts.hidden, opts.descriptor);
    }

    function defaults(target, sources, opts) {
        opts = opts || {};
        opts.resolve = opts.resolve || false;
        return mergeAll(target, sources, opts);
    }

    function mergeAll(target, sources, opts) {
        if (!isArray(sources)) {
            sources = [sources];
        }
        forEach(sources, function (source) {
            return mergeWithOptions(target, source, opts);
        });
        return target;
    }

    function iterateOverProperties(hidden, obj, fn) {
        if (getOwnPropertyNames && hidden) {
            iterateOverKeys(getOwnPropertyNames, obj, fn, hidden);
        } else {
            forEachProperty(obj, fn);
        }
        if (getOwnPropertySymbols) {
            iterateOverKeys(getOwnPropertySymbols, obj, fn, hidden);
        }
    }

    // "keys" may include symbols
    function iterateOverKeys(getFn, obj, fn, hidden) {
        var keys = getFn(obj), desc;
        for (var i = 0, key; key = keys[i]; i++) {
            desc = getOwnPropertyDescriptor(obj, key);
            if (desc.enumerable || hidden) {
                fn(obj[key], key);
            }
        }
    }

    function mergeByPropertyDescriptor(target, source, prop, sourceVal) {
        var descriptor = getOwnPropertyDescriptor(source, prop);
        if (isDefined(descriptor.value)) {
            descriptor.value = sourceVal;
        }
        defineProperty(target, prop, descriptor);
    }

    function objectMerge(target, source, deep, resolve, hidden, descriptor) {
        var resolveByFunction = isFunction(resolve), resolveConflicts = resolve !== false;

        if (isUndefined(target)) {
            target = getNewObjectForMerge(source);
        } else if (resolveConflicts && isDate(target) && isDate(source)) {
            // A date's timestamp is a property that can only be reached through its
            // methods, so actively set it up front if both are dates.
            target.setTime(source.getTime());
        }

        if (isPrimitive(target)) {
            // Will not merge into a primitive type, so simply override.
            return source;
        }

        // If the source object is a primitive
        // type then coerce it into an object.
        if (isPrimitive(source)) {
            source = coercePrimitiveToObject(source);
        }

        iterateOverProperties(hidden, source, function (val, key) {
            var sourceVal, targetVal, resolved, goDeep, result;

            sourceVal = source[key];

            // We are iterating over properties of the source, so hasOwnProperty on
            // it is guaranteed to always be true. However, the target may happen to
            // have properties in its prototype chain that should not be considered
            // as conflicts.
            targetVal = getOwn(target, key);

            if (resolveByFunction) {
                result = resolve(key, targetVal, sourceVal, target, source);
                if (isUndefined(result)) {
                    // Result is undefined so do not merge this property.
                    return;
                } else if (isDefined(result) && result !== Sugar) {
                    // If the source returns anything except undefined, then the conflict
                    // has been resolved, so don't continue traversing into the object. If
                    // the returned value is the Sugar global object, then allowing Sugar
                    // to resolve the conflict, so continue on.
                    sourceVal = result;
                    resolved = true;
                }
            } else if (isUndefined(sourceVal)) {
                // Will not merge undefined.
                return;
            }

            // Regex properties are read-only, so intentionally disallowing deep
            // merging for now. Instead merge by reference even if deep.
            goDeep = !resolved && deep && isObjectType(sourceVal) && !isRegExp(sourceVal);

            if (!goDeep && !resolveConflicts && isDefined(targetVal)) {
                return;
            }

            if (goDeep) {
                sourceVal = objectMerge(targetVal, sourceVal, deep, resolve, hidden, descriptor);
            }

            // getOwnPropertyNames is standing in as
            // a test for property descriptor support
            if (getOwnPropertyNames && descriptor) {
                mergeByPropertyDescriptor(target, source, key, sourceVal);
            } else {
                target[key] = sourceVal;
            }

        });
        return target;
    }

    function getNewObjectForMerge(source) {
        var klass = classToString(source);
        // Primitive types, dates, and regexes have no "empty" state. If they exist
        // at all, then they have an associated value. As we are only creating new
        // objects when they don't exist in the target, these values can come alone
        // for the ride when created.
        if (isArray(source, klass)) {
            return [];
        } else if (isPlainObject(source, klass)) {
            return {};
        } else if (isDate(source, klass)) {
            return new Date(source.getTime());
        } else if (isRegExp(source, klass)) {
            return RegExp(source.source, getRegExpFlags(source));
        } else if (isPrimitive(source && source.valueOf())) {
            return source;
        }
        // If the object is not of a known type, then simply merging its
        // properties into a plain object will result in something different
        // (it will not respond to instanceof operator etc). Similarly we don't
        // want to call a constructor here as we can't know for sure what the
        // original constructor was called with (Events etc), so throw an
        // error here instead. Non-standard types can be handled if either they
        // already exist and simply have their properties merged, if the merge
        // is not deep so their references will simply be copied over, or if a
        // resolve function is used to assist the merge.
        throw new TypeError('Must be a basic data type');
    }

    function clone(source, deep) {
        var target = getNewObjectForMerge(source);
        return objectMerge(target, source, deep, true, true, true);
    }


    // Keys/Values

    function objectSize(obj) {
        return getKeysWithObjectCoercion(obj).length;
    }

    function getKeysWithObjectCoercion(obj) {
        return getKeys(coercePrimitiveToObject(obj));
    }

    function getValues(obj) {
        var values = [];
        forEachProperty(obj, function (val) {
            values.push(val);
        });
        return values;
    }

    function tap(obj, arg) {
        var fn = arg;
        if (!isFunction(arg)) {
            fn = function () {
                if (arg) obj[arg]();
            };
        }
        fn.call(obj, obj);
        return obj;
    }

    // Select/Reject

    function objectSelect(obj, f) {
        return selectFromObject(obj, f, true);
    }

    function objectReject(obj, f) {
        return selectFromObject(obj, f, false);
    }

    function selectFromObject(obj, f, select) {
        var match, result = {};
        f = [].concat(f);
        forEachProperty(obj, function (val, key) {
            match = false;
            for (var i = 0; i < f.length; i++) {
                if (matchInObject(f[i], key)) {
                    match = true;
                }
            }
            if (match === select) {
                result[key] = val;
            }
        });
        return result;
    }

    function matchInObject(match, key) {
        if (isRegExp(match)) {
            return match.test(key);
        } else if (isObjectType(match)) {
            return key in match;
        } else {
            return key === String(match);
        }
    }

    // Remove/Exclude

    function objectRemove(obj, f) {
        var matcher = getMatcher(f);
        forEachProperty(obj, function (val, key) {
            if (matcher(val, key, obj)) {
                delete obj[key];
            }
        });
        return obj;
    }

    function objectExclude(obj, f) {
        var result = {};
        var matcher = getMatcher(f);
        forEachProperty(obj, function (val, key) {
            if (!matcher(val, key, obj)) {
                result[key] = val;
            }
        });
        return result;
    }

    function objectIntersectOrSubtract(obj1, obj2, subtract) {
        if (!isObjectType(obj1)) {
            return subtract ? obj1 : {};
        }
        obj2 = coercePrimitiveToObject(obj2);

        function resolve(key, val, val1) {
            var exists = key in obj2 && isEqual(val1, obj2[key]);
            if (exists !== subtract) {
                return val1;
            }
        }

        return objectMerge({}, obj1, false, resolve);
    }

    /***
     * @method is[Type]()
     * @returns Boolean
     * @short Returns true if the object is an object of that type.
     *
     * @set
     *   isArray
     *   isBoolean
     *   isDate
     *   isError
     *   isFunction
     *   isMap
     *   isNumber
     *   isRegExp
     *   isSet
     *   isString
     *
     * @example
     *
     *   Object.isArray([3]) -> true
     *   Object.isNumber(3)  -> true
     *   Object.isString(8)  -> false
     *
     ***/
    function buildClassCheckMethods() {
        var checks = [isBoolean, isNumber, isString, isDate, isRegExp, isFunction, isArray, isError, isSet, isMap];
        defineInstanceAndStaticSimilar(sugarObject, NATIVE_TYPES, function (methods, name, i) {
            methods['is' + name] = checks[i];
        });
    }

    defineStatic(sugarObject, {

        /***
         * @method fromQueryString(str, [options])
         * @returns Object
         * @static
         * @short Converts the query string of a URL into an object.
         * @extra Options can be passed with [options] for more control over the result.
         *
         * @options
         *
         *   deep        If the string contains "deep" syntax `foo[]`, this will
         *               be automatically converted to an array. (Default `false`)
         *
         *   auto        If `true`, booleans `"true"` and `"false"`, numbers, and arrays
         *               (repeated keys) will be automatically cast to native
         *               values. (Default `true`)
         *
         *   transform   A function whose return value becomes the final value. If
         *               the function returns `undefined`, then the original value
         *               will be used. This allows the function to intercept only
         *               certain keys or values. (Default `undefined`)
         *
         *   separator   If passed, keys will be split on this string to extract
         *               deep values. (Default `''`)
         *
         * @callback queryStringTransformFn
         *
         *   key   The key component of the query string (before `=`).
         *   val   The value component of the query string (after `=`).
         *   obj   A reference to the object being built.
         *
         * @example
         *
         *   Object.fromQueryString('a=1&b=2')                 -> {a:1,b:2}
         *   Object.fromQueryString('a[]=1&a[]=2',{deep:true}) -> {a:['1','2']}
         *   Object.fromQueryString('a_b=c',{separator:'_'})   -> {a:{b:'c'}}
         *   Object.fromQueryString('id=123', {transform:idToNumber});
         *
         * @param {string} str
         * @param {QueryStringParseOptions} options
         * @callbackParam {string} key
         * @callbackParam {Property} val
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} queryStringTransformFn
         * @option {boolean} [deep]
         * @option {boolean} [auto]
         * @option {string} [separator]
         * @option {queryStringTransformFn} [transform]
         *
         ***/
        'fromQueryString': function (obj, options) {
            return fromQueryStringWithOptions(obj, options);
        }

    });

    defineInstanceAndStatic(sugarObject, {

        /***
         * @method has(key, [inherited] = false)
         * @returns Boolean
         * @short Checks if the object has property `key`.
         * @extra Supports `deep properties`. If [inherited] is `true`,
         *        properties defined in the prototype chain will also return `true`.
         *        The default of `false` for this argument makes this method suited
         *        to working with objects as data stores by default.
         *
         * @example
         *
         *   Object.has(usersByName, 'Harry')     -> true
         *   Object.has(data, 'users[1].profile') -> true
         *   Object.has([], 'forEach')            -> false
         *   Object.has([], 'forEach', true)      -> true
         *
         * @param {string} key
         * @param {boolean} [inherited]
         *
         ***/
        'has': function (obj, key, any) {
            return deepHasProperty(obj, key, any);
        },

        /***
         * @method get(key, [inherited] = false)
         * @returns Mixed
         * @short Gets a property of the object.
         * @extra Supports `deep properties`. If [inherited] is `true`,
         *        properties defined in the prototype chain will also be returned.
         *        The default of `false` for this argument makes this method suited
         *        to working with objects as data stores by default.
         *
         * @example
         *
         *   Object.get(Harry, 'name');           -> 'Harry'
         *   Object.get(Harry, 'profile.likes');  -> Harry's likes
         *   Object.get(data, 'users[3].name')    -> User 3's name
         *   Object.get(data, 'users[1..2]')      -> Users 1 and 2
         *   Object.get(data, 'users[1..2].name') -> Names of users 1 and 2
         *   Object.get(data, 'users[-2..-1]')    -> Last 2 users
         *
         * @param {string} key
         * @param {boolean} [inherited]
         *
         ***/
        'get': function (obj, key, any) {
            return deepGetProperty(obj, key, any);
        },

        /***
         * @method set(key, val)
         * @returns Object
         * @short Sets a property on the object.
         * @extra Using a dot or square bracket in `key` is considered "deep" syntax,
         *        and will attempt to traverse into the object to set the property,
         *        creating properties that do not exist along the way. If the missing
         *        property is referenced using square brackets, an empty array will be
         *        created, otherwise an empty object. A special `[]` carries the
         *        meaning of "the last index + 1", and will effectively push `val`
         *        onto the end of the array. Lastly, a `..` separator inside the
         *        brackets is "range" notation, and will set properties on all
         *        elements in the specified range. Range members may be negative,
         *        which will be offset from the end of the array.
         *
         * @example
         *
         *   Object.set({}, 'name', 'Harry');         -> {name:'Harry'}
         *   Object.set({}, 'user.name', 'Harry');    -> {user:{name:'Harry'}}
         *   Object.set({}, 'users[].name', 'Bob')    -> {users:[{name:'Bob'}}
         *   Object.set({}, 'users[1].name','Bob')    -> {users:[undefined, {name:'Bob'}]}
         *   Object.set({}, 'users[0..1].name','Bob') -> {users:[{name:'Bob'},{name:'Bob'}]}
         *
         * @param {string} key
         * @param {Property} val
         *
         ***/
        'set': function (obj, key, val) {
            return deepSetProperty(obj, key, val);
        },

        /***
         * @method size()
         * @returns Number
         * @short Returns the number of properties in the object.
         *
         * @example
         *
         *   Object.size({foo:'bar'}) -> 1
         *
         ***/
        'size': function (obj) {
            return objectSize(obj);
        },

        /***
         * @method isEmpty()
         * @returns Boolean
         * @short Returns true if the number of properties in the object is zero.
         *
         * @example
         *
         *   Object.isEmpty({})    -> true
         *   Object.isEmpty({a:1}) -> false
         *
         ***/
        'isEmpty': function (obj) {
            return objectSize(obj) === 0;
        },

        /***
         * @method toQueryString([options])
         * @returns Object
         * @short Converts the object into a query string.
         * @extra Accepts deep objects and arrays. [options] can be passed for more
         *        control over the result.
         *
         * @options
         *
         *   deep        If `true`, non-standard "deep" syntax `foo[]` will be
         *               used for output. Note that `separator` will be ignored,
         *               as this option overrides shallow syntax. (Default `false`)
         *
         *   prefix      If passed, this string will be prefixed to all keys,
         *               separated by the `separator`. (Default `''`).
         *
         *   transform   A function whose return value becomes the final value
         *               in the string. (Default `undefined`)
         *
         *   separator   A string that is used to separate keys, either for deep
         *               objects, or when `prefix` is passed.(Default `_`).
         *
         * @callback queryStringTransformFn
         *
         *   key  The key of the current iteration.
         *   val  The value of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.toQueryString({foo:'bar'})                  -> 'foo=bar'
         *   Object.toQueryString({foo:['a','b']})              -> 'foo=a&foo=b'
         *   Object.toQueryString({foo:['a','b']}, {deep:true}) -> 'foo[]=a&foo[]=b'
         *
         * @param {Object} obj
         * @param {QueryStringOptions} [options]
         * @callbackParam {string} key
         * @callbackParam {Property} val
         * @callbackParam {Object} obj
         * @callbackReturns {NewProperty} queryStringTransformFn
         * @option {boolean} [deep]
         * @option {string} [prefix]
         * @option {string} [separator]
         * @option {queryStringTransformFn} [transform]
         *
         ***/
        'toQueryString': function (obj, options) {
            return toQueryStringWithOptions(obj, options);
        },

        /***
         * @method isEqual(obj)
         * @returns Boolean
         * @short Returns true if `obj` is equivalent to the object.
         * @extra If both objects are built-in types, they will be considered
         *        equivalent if they are not "observably distinguishable". This means
         *        that primitives and object types, `0` and `-0`, and sparse and
         *        dense arrays are all not equal. Functions and non-built-ins like
         *        instances of user-defined classes and host objects like Element and
         *        Event are strictly compared `===`, and will only be equal if they
         *        are the same reference. Plain objects as well as Arrays will be
         *        traversed into and deeply checked by their non-inherited, enumerable
         *        properties. Other allowed types include Typed Arrays, Sets, Maps,
         *        Arguments, Dates, Regexes, and Errors.
         *
         * @example
         *
         *   Object.isEqual({a:2}, {a:2})         -> true
         *   Object.isEqual({a:2}, {a:3})         -> false
         *   Object.isEqual(5, Object(5))         -> false
         *   Object.isEqual(Object(5), Object(5)) -> true
         *   Object.isEqual(NaN, NaN)             -> false
         *
         * @param {Object} obj
         *
         ***/
        'isEqual': function (obj1, obj2) {
            return isEqual(obj1, obj2);
        },

        /***
         * @method merge(source, [options])
         * @returns Object
         * @short Merges properties from `source` into the object.
         * @extra This method will modify the object! Use `add` for a non-destructive
         *        alias.
         *
         * @options
         *
         *   deep         If `true` deep properties are merged recursively.
         *                (Default `false`)
         *
         *   hidden       If `true`, non-enumerable properties will be merged as well.
         *                (Default `false`)
         *
         *   descriptor   If `true`, properties will be merged by property descriptor.
         *                Use this option to merge getters or setters, or to preserve
         *                `enumerable`, `configurable`, etc.
         *                (Default `false`)
         *
         *   resolve      Determines which property wins in the case of conflicts.
         *                If `true`, `source` wins. If `false`, the original property wins.
         *                If a function is passed, its return value will decide the result.
         *                Any non-undefined return value will resolve the conflict
         *                for that property (will not continue if `deep`). Returning
         *                `undefined` will do nothing (no merge). Finally, returning
         *                the global object `Sugar` will allow Sugar to handle the
         *                merge as normal. (Default `true`)
         *
         * @callback resolveFn
         *
         *   key        The key of the current iteration.
         *   targetVal  The current value for the key in the target.
         *   sourceVal  The current value for the key in `source`.
         *   target     The target object.
         *   source     The source object.
         *
         * @example
         *
         *   Object.merge({one:1},{two:2})                 -> {one:1,two:2}
         *   Object.merge({one:1},{one:9,two:2})           -> {one:9,two:2}
         *   Object.merge({x:{a:1}},{x:{b:2}},{deep:true}) -> {x:{a:1,b:2}}
         *   Object.merge({a:1},{a:2},{resolve:mergeAdd})  -> {a:3}
         *
         * @param {Object} source
         * @param {ObjectMergeOptions} [options]
         * @callbackParam {string} key
         * @callbackParam {Property} targetVal
         * @callbackParam {Property} sourceVal
         * @callbackParam {Object} target
         * @callbackParam {Object} source
         * @callbackReturns {boolean} resolveFn
         * @option {boolean} [deep]
         * @option {boolean} [hidden]
         * @option {boolean} [descriptor]
         * @option {boolean|resolveFn} [resolve]
         *
         ***/
        'merge': function (target, source, opts) {
            return mergeWithOptions(target, source, opts);
        },

        /***
         * @method add(obj, [options])
         * @returns Object
         * @short Adds properties in `obj` and returns a new object.
         * @extra This method will not modify the original object. See `merge` for options.
         *
         * @example
         *
         *   Object.add({one:1},{two:2})                 -> {one:1,two:2}
         *   Object.add({one:1},{one:9,two:2})           -> {one:9,two:2}
         *   Object.add({x:{a:1}},{x:{b:2}},{deep:true}) -> {x:{a:1,b:2}}
         *   Object.add({a:1},{a:2},{resolve:mergeAdd})  -> {a:3}
         *
         * @param {Object} obj
         * @param {ObjectMergeOptions} [options]
         *
         ***/
        'add': function (obj1, obj2, opts) {
            return mergeWithOptions(clone(obj1), obj2, opts);
        },

        /***
         * @method mergeAll(sources, [options])
         * @returns Object
         * @short Merges properties from an array of `sources`.
         * @extra This method will modify the object! Use `addAll` for a non-destructive
         *        alias. See `merge` for options.
         *
         * @example
         *
         *   Object.mergeAll({one:1},[{two:2},{three:3}]) -> {one:1,two:2,three:3}
         *   Object.mergeAll({x:{a:1}},[{x:{b:2}},{x:{c:3}}],{deep:true}) -> {x:{a:1,b:2,c:3}}
         *
         * @param {Array<Object>} sources
         * @param {ObjectMergeOptions} [options]
         *
         ***/
        'mergeAll': function (target, sources, opts) {
            return mergeAll(target, sources, opts);
        },

        /***
         * @method addAll(sources, [options])
         * @returns Object
         * @short Adds properties from an array of `sources` and returns a new object.
         * @extra This method will not modify the object. See `merge` for options.
         *
         * @example
         *
         *   Object.addAll({one:1},[{two:2},{three:3}]) -> {one:1,two:2,three:3}
         *   Object.addAll({x:{a:1}},[{x:{b:2}},{x:{c:3}}],{deep:true}) -> {x:{a:1,b:2,c:3}}
         *
         * @param {Array<Object>} sources
         * @param {ObjectMergeOptions} [options]
         *
         ***/
        'addAll': function (obj, sources, opts) {
            return mergeAll(clone(obj), sources, opts);
        },

        /***
         * @method defaults(sources, [options])
         * @returns Object
         * @short Merges properties from one or multiple `sources` while preserving
         *        the object's defined properties.
         * @extra This method modifies the object! See `merge` for options.
         *
         * @example
         *
         *   Object.defaults({one:1},[{one:9},{two:2}])                   -> {one:1,two:2}
         *   Object.defaults({x:{a:1}},[{x:{a:9}},{x:{b:2}}],{deep:true}) -> {x:{a:1,b:2}}
         *
         * @param {Array<Object>} sources
         * @param {ObjectMergeOptions} [options]
         *
         ***/
        'defaults': function (target, sources, opts) {
            return defaults(target, sources, opts);
        },

        /***
         * @method intersect(obj)
         * @returns Object
         * @short Returns a new object whose properties are those that the object has
         *        in common both with `obj`.
         * @extra If both key and value do not match, then the property will not be included.
         *
         * @example
         *
         *   Object.intersect({a:'a'},{b:'b'}) -> {}
         *   Object.intersect({a:'a'},{a:'b'}) -> {}
         *   Object.intersect({a:'a',b:'b'},{b:'b',z:'z'}) -> {b:'b'}
         *
         * @param {Object} obj
         *
         ***/
        'intersect': function (obj1, obj2) {
            return objectIntersectOrSubtract(obj1, obj2, false);
        },

        /***
         * @method subtract(obj)
         * @returns Object
         * @short Returns a clone of the object with any properties shared with `obj` excluded.
         * @extra If both key and value do not match, then the property will not be excluded.
         *
         * @example
         *
         *   Object.subtract({a:'a',b:'b'},{b:'b'}) -> {a:'a'}
         *   Object.subtract({a:'a',b:'b'},{a:'b'}) -> {a:'a',b:'b'}
         *
         * @param {Object} obj
         *
         ***/
        'subtract': function (obj1, obj2) {
            return objectIntersectOrSubtract(obj1, obj2, true);
        },

        /***
         * @method clone([deep] = false)
         * @returns Object
         * @short Creates a clone of the object.
         * @extra Default is a shallow clone, unless [deep] is true.
         *
         * @example
         *
         *   Object.clone({foo:'bar'})       -> creates shallow clone
         *   Object.clone({foo:'bar'}, true) -> creates a deep clone
         *
         * @param {boolean} [deep]
         *
         ***/
        'clone': function (obj, deep) {
            return clone(obj, deep);
        },

        /***
         * @method values()
         * @returns Array
         * @short Returns an array containing the values in the object.
         * @extra Values are in no particular order. Does not include inherited or
         *        non-enumerable properties.
         *
         * @example
         *
         *   Object.values({a:'a',b:'b'}) -> ['a','b']
         *
         ***/
        'values': function (obj) {
            return getValues(obj);
        },

        /***
         * @method invert([multi] = false)
         * @returns Object
         * @short Creates a new object with the keys and values swapped.
         * @extra If [multi] is true, values will be an array of all keys, othewise
         *        collisions will be overwritten.
         *
         * @example
         *
         *   Object.invert({foo:'bar'})     -> {bar:'foo'}
         *   Object.invert({a:1,b:1}, true) -> {1:['a','b']}
         *
         * @param {boolean} [multi]
         *
         ***/
        'invert': function (obj, multi) {
            var result = {};
            multi = multi === true;
            forEachProperty(obj, function (val, key) {
                if (hasOwn(result, val) && multi) {
                    result[val].push(key);
                } else if (multi) {
                    result[val] = [key];
                } else {
                    result[val] = key;
                }
            });
            return result;
        },

        /***
         * @method tap(fn)
         * @returns Object
         * @short Runs `fn` and returns the object.
         * @extra A string can also be used as a shortcut to a method. This method is
         *        designed to run an intermediary function that "taps into" a method
         *        chain. As such, it is fairly useless as a static method. However it
         *        can be quite useful when combined with chainables.
         *
         * @callback tapFn
         *
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Sugar.Array([1,4,9]).map(Math.sqrt).tap('pop') -> [1,2]
         *   Sugar.Object({a:'a'}).tap(logArgs).merge({b:'b'})  -> {a:'a',b:'b'}
         *
         * @param {tapFn} fn
         * @callbackParam {Object} obj
         * @callbackReturns {any} tapFn
         *
         ***/
        'tap': function (obj, arg) {
            return tap(obj, arg);
        },

        /***
         * @method isArguments()
         * @returns Boolean
         * @short Returns true if the object is an arguments object.
         *
         * @example
         *
         *   Object.isArguments([1]) -> false
         *
         ***/
        'isArguments': function (obj) {
            return isArguments(obj);
        },

        /***
         * @method isObject()
         * @returns Boolean
         * @short Returns true if the object is a "plain" object.
         * @extra Plain objects do not include instances of classes or "host" objects,
         *        such as Elements, Events, etc.
         *
         * @example
         *
         *   Object.isObject({ broken:'wear' }) -> true
         *
         ***/
        'isObject': function (obj) {
            return isPlainObject(obj);
        },

        /***
         * @method remove(search)
         * @returns Object
         * @short Deletes all properties in the object matching `search`.
         * @extra This method will modify the object!. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   key  The key of the current iteration.
         *   val  The value of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.remove({a:'a',b:'b'}, 'a');           -> {b:'b'}
         *   Object.remove({a:'a',b:'b',z:'z'}, /[a-f]/); -> {z:'z'}
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'remove': function (obj, f) {
            return objectRemove(obj, f);
        },

        /***
         * @method exclude(search)
         * @returns Object
         * @short Returns a new object with all properties matching `search` removed.
         * @extra This is a non-destructive version of `remove` and will not modify
         *        the object. Implements `enhanced matching`.
         *
         * @callback searchFn
         *
         *   key  The key of the current iteration.
         *   val  The value of the current iteration.
         *   obj  A reference to the object.
         *
         * @example
         *
         *   Object.exclude({a:'a',b:'b'}, 'a');           -> {b:'b'}
         *   Object.exclude({a:'a',b:'b',z:'z'}, /[a-f]/); -> {z:'z'}
         *
         * @param {Property|searchFn} search
         * @callbackParam {Property} val
         * @callbackParam {string} key
         * @callbackParam {Object} obj
         * @callbackReturns {boolean} searchFn
         *
         ***/
        'exclude': function (obj, f) {
            return objectExclude(obj, f);
        },

        /***
         * @method select(find)
         * @returns Object
         * @short Builds a new object containing the keys specified in `find`.
         * @extra When `find` is a string, a single key will be selected. Arrays or
         *        objects match multiple keys, and a regex will match keys by regex.
         *
         * @example
         *
         *   Object.select({a:1,b:2}, 'a')           -> {a:1}
         *   Object.select({a:1,b:2}, ['a', 'b'])    -> {a:1,b:2}
         *   Object.select({a:1,b:2}, /[a-z]/)       -> {a:1,b:2}
         *   Object.select({a:1,b:2}, {a:'a',b:'b'}) -> {a:1,b:2}
         *
         * @param {string|RegExp|Array<string>|Object} find
         *
         ***/
        'select': function (obj, f) {
            return objectSelect(obj, f);
        },

        /***
         * @method reject(find)
         * @returns Object
         * @short Builds a new object containing all keys except those in `find`.
         * @extra When `find` is a string, a single key will be rejected. Arrays or
         *        objects match multiple keys, and a regex will match keys by regex.
         *
         * @example
         *
         *   Object.reject({a:1,b:2}, 'a')        -> {b:2}
         *   Object.reject({a:1,b:2}, /[a-z]/)    -> {}
         *   Object.reject({a:1,b:2}, {a:'a'})    -> {b:2}
         *   Object.reject({a:1,b:2}, ['a', 'b']) -> {}
         *
         * @param {string|RegExp|Array<string>|Object} find
         *
         ***/
        'reject': function (obj, f) {
            return objectReject(obj, f);
        }

    });

    // TODO: why is this here?
    defineInstance(sugarObject, {

        /***
         * @method keys()
         * @returns Array
         * @polyfill ES5
         * @short Returns an array containing the keys of all of the non-inherited,
         *        enumerable properties of the object.
         *
         * @example
         *
         *   Object.keys({a:'a',b:'b'}) -> ['a','b']
         *
         ***/
        'keys': function (obj) {
            return getKeys(obj);
        }

    });

    buildClassCheckMethods();

    /***
     * @module Date
     * @description Date parsing and formatting, relative formats, number shortcuts,
     *              and locale support with default English locales.
     *
     ***/

    var DATE_OPTIONS = {
        'newDateInternal': defaultNewDate
    };

    var LOCALE_ARRAY_FIELDS = [
        'months', 'weekdays', 'units', 'numerals', 'placeholders',
        'articles', 'tokens', 'timeMarkers', 'ampm', 'timeSuffixes',
        'parse', 'timeParse', 'timeFrontParse', 'modifiers'
    ];

    // Regex for stripping Timezone Abbreviations
    var TIMEZONE_ABBREVIATION_REG = /(\w{3})[()\s\d]*$/;

    // One minute in milliseconds
    var MINUTES = 60 * 1000;

    // Date unit indexes
    var HOURS_INDEX = 3,
        DAY_INDEX = 4,
        WEEK_INDEX = 5,
        MONTH_INDEX = 6,
        YEAR_INDEX = 7;

    // ISO Defaults
    var ISO_FIRST_DAY_OF_WEEK = 1,
        ISO_FIRST_DAY_OF_WEEK_YEAR = 4;

    var ParsingTokens = {
        'yyyy': {
            param: 'year',
            src: '\\d{4}'
        },
        'MM': {
            param: 'month',
            src: '[01]?\\d'
        },
        'dd': {
            param: 'date',
            src: '[0123]?\\d'
        },
        'hh': {
            param: 'hour',
            src: '[0-2]?\\d'
        },
        'mm': {
            param: 'minute',
            src: '[0-5]\\d'
        },
        'ss': {
            param: 'second',
            src: '[0-5]\\d(?:[,.]\\d+)?'
        },
        'yy': {
            param: 'year',
            src: '\\d{2}'
        },
        'y': {
            param: 'year',
            src: '\\d'
        },
        'yearSign': {
            src: '[+-]',
            sign: true
        },
        'tzHour': {
            src: '[0-1]\\d'
        },
        'tzMinute': {
            src: '[0-5]\\d'
        },
        'tzSign': {
            src: '[+−-]',
            sign: true
        },
        'ihh': {
            param: 'hour',
            src: '[0-2]?\\d(?:[,.]\\d+)?'
        },
        'imm': {
            param: 'minute',
            src: '[0-5]\\d(?:[,.]\\d+)?'
        },
        'GMT': {
            param: 'utc',
            src: 'GMT',
            val: 1
        },
        'Z': {
            param: 'utc',
            src: 'Z',
            val: 1
        },
        'timestamp': {
            src: '\\d+'
        }
    };

    var LocalizedParsingTokens = {
        'year': {
            base: 'yyyy',
            requiresSuffix: true
        },
        'month': {
            base: 'MM',
            requiresSuffix: true
        },
        'date': {
            base: 'dd',
            requiresSuffix: true
        },
        'hour': {
            base: 'hh',
            requiresSuffixOr: ':'
        },
        'minute': {
            base: 'mm'
        },
        'second': {
            base: 'ss'
        },
        'num': {
            src: '\\d+',
            requiresNumerals: true
        }
    };

    var CoreParsingFormats = [
        {
            // 12-1978
            // 08-1978 (MDY)
            src: '{MM}[-.\\/]{yyyy}'
        },
        {
            // 12/08/1978
            // 08/12/1978 (MDY)
            time: true,
            src: '{dd}[-.\\/]{MM}(?:[-.\\/]{yyyy|yy|y})?',
            mdy: '{MM}[-.\\/]{dd}(?:[-.\\/]{yyyy|yy|y})?'
        },
        {
            // 1975-08-25
            time: true,
            src: '{yyyy}[-.\\/]{MM}(?:[-.\\/]{dd})?'
        },
        {
            // .NET JSON
            src: '\\\\/Date\\({timestamp}(?:[+-]\\d{4,4})?\\)\\\\/'
        },
        {
            // ISO-8601
            src: '{yearSign?}{yyyy}(?:-?{MM}(?:-?{dd}(?:T{ihh}(?::?{imm}(?::?{ss})?)?)?)?)?{tzOffset?}'
        }
    ];

    var CoreOutputFormats = {
        'ISO8601': '{yyyy}-{MM}-{dd}T{HH}:{mm}:{ss}.{SSS}{Z}',
        'RFC1123': '{Dow}, {dd} {Mon} {yyyy} {HH}:{mm}:{ss} {ZZ}',
        'RFC1036': '{Weekday}, {dd}-{Mon}-{yy} {HH}:{mm}:{ss} {ZZ}'
    };

    var FormatTokensBase = [
        {
            ldml: 'Dow',
            strf: 'a',
            lowerToken: 'dow',
            get: function (d, localeCode) {
                return localeManager.get(localeCode).getWeekdayName(getWeekday(d), 2);
            }
        },
        {
            ldml: 'Weekday',
            strf: 'A',
            lowerToken: 'weekday',
            allowAlternates: true,
            get: function (d, localeCode, alternate) {
                return localeManager.get(localeCode).getWeekdayName(getWeekday(d), alternate);
            }
        },
        {
            ldml: 'Mon',
            strf: 'b h',
            lowerToken: 'mon',
            get: function (d, localeCode) {
                return localeManager.get(localeCode).getMonthName(getMonth(d), 2);
            }
        },
        {
            ldml: 'Month',
            strf: 'B',
            lowerToken: 'month',
            allowAlternates: true,
            get: function (d, localeCode, alternate) {
                return localeManager.get(localeCode).getMonthName(getMonth(d), alternate);
            }
        },
        {
            strf: 'C',
            get: function (d) {
                return getYear(d).toString().slice(0, 2);
            }
        },
        {
            ldml: 'd date day',
            strf: 'd',
            strfPadding: 2,
            ldmlPaddedToken: 'dd',
            ordinalToken: 'do',
            get: function (d) {
                return getDate(d);
            }
        },
        {
            strf: 'e',
            get: function (d) {
                return padNumber(getDate(d), 2, false, 10, ' ');
            }
        },
        {
            ldml: 'H 24hr',
            strf: 'H',
            strfPadding: 2,
            ldmlPaddedToken: 'HH',
            get: function (d) {
                return getHours(d);
            }
        },
        {
            ldml: 'h hours 12hr',
            strf: 'I',
            strfPadding: 2,
            ldmlPaddedToken: 'hh',
            get: function (d) {
                return getHours(d) % 12 || 12;
            }
        },
        {
            ldml: 'D',
            strf: 'j',
            strfPadding: 3,
            ldmlPaddedToken: 'DDD',
            get: function (d) {
                var s = setUnitAndLowerToEdge(cloneDate(d), MONTH_INDEX);
                return getDaysSince(d, s) + 1;
            }
        },
        {
            ldml: 'M',
            strf: 'm',
            strfPadding: 2,
            ordinalToken: 'Mo',
            ldmlPaddedToken: 'MM',
            get: function (d) {
                return getMonth(d) + 1;
            }
        },
        {
            ldml: 'm minutes',
            strf: 'M',
            strfPadding: 2,
            ldmlPaddedToken: 'mm',
            get: function (d) {
                return callDateGet(d, 'Minutes');
            }
        },
        {
            ldml: 'Q',
            get: function (d) {
                return ceil((getMonth(d) + 1) / 3);
            }
        },
        {
            ldml: 'TT',
            strf: 'p',
            get: function (d, localeCode) {
                return getMeridiemToken(d, localeCode);
            }
        },
        {
            ldml: 'tt',
            strf: 'P',
            get: function (d, localeCode) {
                return getMeridiemToken(d, localeCode).toLowerCase();
            }
        },
        {
            ldml: 'T',
            lowerToken: 't',
            get: function (d, localeCode) {
                return getMeridiemToken(d, localeCode).charAt(0);
            }
        },
        {
            ldml: 's seconds',
            strf: 'S',
            strfPadding: 2,
            ldmlPaddedToken: 'ss',
            get: function (d) {
                return callDateGet(d, 'Seconds');
            }
        },
        {
            ldml: 'S ms',
            strfPadding: 3,
            ldmlPaddedToken: 'SSS',
            get: function (d) {
                return callDateGet(d, 'Milliseconds');
            }
        },
        {
            ldml: 'e',
            strf: 'u',
            ordinalToken: 'eo',
            get: function (d) {
                return getWeekday(d) || 7;
            }
        },
        {
            strf: 'U',
            strfPadding: 2,
            get: function (d) {
                // Sunday first, 0-53
                return getWeekNumber(d, false, 0);
            }
        },
        {
            ldml: 'W',
            strf: 'V',
            strfPadding: 2,
            ordinalToken: 'Wo',
            ldmlPaddedToken: 'WW',
            get: function (d) {
                // Monday first, 1-53 (ISO8601)
                return getWeekNumber(d, true);
            }
        },
        {
            strf: 'w',
            get: function (d) {
                return getWeekday(d);
            }
        },
        {
            ldml: 'w',
            ordinalToken: 'wo',
            ldmlPaddedToken: 'ww',
            get: function (d, localeCode) {
                // Locale dependent, 1-53
                var loc = localeManager.get(localeCode),
                    dow = loc.getFirstDayOfWeek(localeCode),
                    doy = loc.getFirstDayOfWeekYear(localeCode);
                return getWeekNumber(d, true, dow, doy);
            }
        },
        {
            strf: 'W',
            strfPadding: 2,
            get: function (d) {
                // Monday first, 0-53
                return getWeekNumber(d, false);
            }
        },
        {
            ldmlPaddedToken: 'gggg',
            ldmlTwoDigitToken: 'gg',
            get: function (d, localeCode) {
                return getWeekYear(d, localeCode);
            }
        },
        {
            strf: 'G',
            strfPadding: 4,
            strfTwoDigitToken: 'g',
            ldmlPaddedToken: 'GGGG',
            ldmlTwoDigitToken: 'GG',
            get: function (d, localeCode) {
                return getWeekYear(d, localeCode, true);
            }
        },
        {
            ldml: 'year',
            ldmlPaddedToken: 'yyyy',
            ldmlTwoDigitToken: 'yy',
            strf: 'Y',
            strfPadding: 4,
            strfTwoDigitToken: 'y',
            get: function (d) {
                return getYear(d);
            }
        },
        {
            ldml: 'ZZ',
            strf: 'z',
            get: function (d) {
                return getUTCOffset(d);
            }
        },
        {
            ldml: 'X',
            get: function (d) {
                return trunc(d.getTime() / 1000);
            }
        },
        {
            ldml: 'x',
            get: function (d) {
                return d.getTime();
            }
        },
        {
            ldml: 'Z',
            get: function (d) {
                return getUTCOffset(d, true);
            }
        },
        {
            ldml: 'z',
            strf: 'Z',
            get: function (d) {
                // Note that this is not accurate in all browsing environments!
                // https://github.com/moment/moment/issues/162
                // It will continue to be supported for Node and usage with the
                // understanding that it may be blank.
                var match = d.toString().match(TIMEZONE_ABBREVIATION_REG);
                return match ? match[1] : '';
            }
        },
        {
            strf: 'D',
            alias: '%m/%d/%y'
        },
        {
            strf: 'F',
            alias: '%Y-%m-%d'
        },
        {
            strf: 'r',
            alias: '%I:%M:%S %p'
        },
        {
            strf: 'R',
            alias: '%H:%M'
        },
        {
            strf: 'T',
            alias: '%H:%M:%S'
        },
        {
            strf: 'x',
            alias: '{short}'
        },
        {
            strf: 'X',
            alias: '{time}'
        },
        {
            strf: 'c',
            alias: '{stamp}'
        }
    ];

    var DateUnits = [
        {
            name: 'millisecond',
            method: 'Milliseconds',
            multiplier: 1,
            start: 0,
            end: 999
        },
        {
            name: 'second',
            method: 'Seconds',
            multiplier: 1000,
            start: 0,
            end: 59
        },
        {
            name: 'minute',
            method: 'Minutes',
            multiplier: 60 * 1000,
            start: 0,
            end: 59
        },
        {
            name: 'hour',
            method: 'Hours',
            multiplier: 60 * 60 * 1000,
            start: 0,
            end: 23
        },
        {
            name: 'day',
            alias: 'date',
            method: 'Date',
            ambiguous: true,
            multiplier: 24 * 60 * 60 * 1000,
            start: 1,
            end: function (d) {
                return getDaysInMonth(d);
            }
        },
        {
            name: 'week',
            method: 'ISOWeek',
            ambiguous: true,
            multiplier: 7 * 24 * 60 * 60 * 1000
        },
        {
            name: 'month',
            method: 'Month',
            ambiguous: true,
            multiplier: 30.4375 * 24 * 60 * 60 * 1000,
            start: 0,
            end: 11
        },
        {
            name: 'year',
            method: 'FullYear',
            ambiguous: true,
            multiplier: 365.25 * 24 * 60 * 60 * 1000,
            start: 0
        }
    ];

    /***
     * @method getOption(name)
     * @returns Mixed
     * @accessor
     * @short Gets an option used interally by Date.
     * @example
     *
     *   Sugar.Date.getOption('newDateInternal');
     *
     * @param {string} name
     *
     ***
     * @method setOption(name, value)
     * @accessor
     * @short Sets an option used interally by Date.
     * @extra If `value` is `null`, the default value will be restored.
     * @options
     *
     *   newDateInternal   Sugar's internal date constructor. Date methods often
     *                     construct a `new Date()` internally as a reference point
     *                     (`isToday`, relative formats like `tomorrow`, etc). This
     *                     can be overridden if you need it to be something else.
     *                     Most commonly, this allows you to return a shifted date
     *                     to simulate a specific timezone, as dates in Javascript
     *                     are always local.
     *
     * @example
     *
     *   Sugar.Date.setOption('newDateInternal', function() {
     *     var d = new Date(), offset;
     *     offset = (d.getTimezoneOffset() - 600) * 60 * 1000; // Hawaii time!
     *     d.setTime(d.getTime() + offset);
     *     return d;
     *   });
     *
     * @signature setOption(options)
     * @param {DateOptions} options
     * @param {string} name
     * @param {any} value
     * @option {Function} newDateInternal
     *
     ***/
    var _dateOptions = defineOptionsAccessor(sugarDate, DATE_OPTIONS);

    function setDateChainableConstructor() {
        setChainableConstructor(sugarDate, createDate);
    }

    // General helpers

    function getNewDate() {
        return _dateOptions('newDateInternal')();
    }

    function defaultNewDate() {
        return new Date;
    }

    function cloneDate(d) {
        // Rhino environments have a bug where new Date(d) truncates
        // milliseconds so need to call getTime() here.
        var clone = new Date(d.getTime());
        _utc(clone, !!_utc(d));
        return clone;
    }

    function dateIsValid(d) {
        return !isNaN(d.getTime());
    }

    function assertDateIsValid(d) {
        if (!dateIsValid(d)) {
            throw new TypeError('Date is not valid');
        }
    }

    function getHours(d) {
        return callDateGet(d, 'Hours');
    }

    function getWeekday(d) {
        return callDateGet(d, 'Day');
    }

    function getDate(d) {
        return callDateGet(d, 'Date');
    }

    function getMonth(d) {
        return callDateGet(d, 'Month');
    }

    function getYear(d) {
        return callDateGet(d, 'FullYear');
    }

    function setDate(d, val) {
        callDateSet(d, 'Date', val);
    }

    function setMonth(d, val) {
        callDateSet(d, 'Month', val);
    }

    function setYear(d, val) {
        callDateSet(d, 'FullYear', val);
    }

    function getDaysInMonth(d) {
        return 32 - callDateGet(new Date(getYear(d), getMonth(d), 32), 'Date');
    }

    function setWeekday(d, dow, dir) {
        if (!isNumber(dow)) return;
        var currentWeekday = getWeekday(d);
        if (dir) {
            // Allow a "direction" parameter to determine whether a weekday can
            // be set beyond the current weekday in either direction.
            var ndir = dir > 0 ? 1 : -1;
            var offset = dow % 7 - currentWeekday;
            if (offset && offset / abs(offset) !== ndir) {
                dow += 7 * ndir;
            }
        }
        setDate(d, getDate(d) + dow - currentWeekday);
        return d.getTime();
    }

    // Normal callDateSet method with ability
    // to handle ISOWeek setting as well.
    function callDateSetWithWeek(d, method, value, safe) {
        if (method === 'ISOWeek') {
            setISOWeekNumber(d, value);
        } else {
            callDateSet(d, method, value, safe);
        }
    }

    // UTC helpers

    function isUTC(d) {
        return !!_utc(d) || tzOffset(d) === 0;
    }

    function getUTCOffset(d, iso) {
        var offset = _utc(d) ? 0 : tzOffset(d), hours, mins, colon;
        colon = iso === true ? ':' : '';
        if (!offset && iso) return 'Z';
        hours = padNumber(trunc(-offset / 60), 2, true);
        mins = padNumber(abs(offset % 60), 2);
        return hours + colon + mins;
    }

    function tzOffset(d) {
        return d.getTimezoneOffset();
    }

    // Argument helpers

    function collectDateArguments(args, allowDuration) {
        var arg1 = args[0], arg2 = args[1];
        if (allowDuration && isString(arg1)) {
            arg1 = getDateParamsFromString(arg1);
        } else if (isNumber(arg1) && isNumber(arg2)) {
            arg1 = collectDateParamsFromArguments(args);
            arg2 = null;
        } else {
            if (isObjectType(arg1)) {
                arg1 = simpleClone(arg1);
            }
        }
        return [arg1, arg2];
    }

    function collectDateParamsFromArguments(args) {
        var params = {}, index = 0;
        walkUnitDown(YEAR_INDEX, function (unit) {
            var arg = args[index++];
            if (isDefined(arg)) {
                params[unit.name] = arg;
            }
        });
        return params;
    }

    function getDateParamsFromString(str) {
        var match, num, params = {};
        match = str.match(/^(-?\d*[\d.]\d*)?\s?(\w+?)s?$/i);
        if (match) {
            if (isUndefined(num)) {
                num = +match[1];
                if (isNaN(num)) {
                    num = 1;
                }
            }
            params[match[2].toLowerCase()] = num;
        }
        return params;
    }

    // Iteration helpers

    // Years -> Milliseconds
    function iterateOverDateUnits(fn, startIndex, endIndex) {
        endIndex = endIndex || 0;
        if (isUndefined(startIndex)) {
            startIndex = YEAR_INDEX;
        }
        for (var index = startIndex; index >= endIndex; index--) {
            if (fn(DateUnits[index], index) === false) {
                break;
            }
        }
    }

    // Years -> Milliseconds using getLower/Higher methods
    function walkUnitDown(unitIndex, fn) {
        while (unitIndex >= 0) {
            if (fn(DateUnits[unitIndex], unitIndex) === false) {
                break;
            }
            unitIndex = getLowerUnitIndex(unitIndex);
        }
    }

    // Moving lower from specific unit
    function getLowerUnitIndex(index) {
        if (index === MONTH_INDEX) {
            return DAY_INDEX;
        } else if (index === WEEK_INDEX) {
            return HOURS_INDEX;
        }
        return index - 1;
    }

    // Moving higher from specific unit
    function getHigherUnitIndex(index) {
        return index === DAY_INDEX ? MONTH_INDEX : index + 1;
    }

    // Years -> Milliseconds checking all date params including "weekday"
    function iterateOverDateParams(params, fn, startIndex, endIndex) {

        function run(name, unit, i) {
            var val = getDateParam(params, name);
            if (isDefined(val)) {
                fn(name, val, unit, i);
            }
        }

        iterateOverDateUnits(function (unit, i) {
            var result = run(unit.name, unit, i);
            if (result !== false && i === DAY_INDEX) {
                // Check for "weekday", which has a distinct meaning
                // in the context of setting a date, but has the same
                // meaning as "day" as a unit of time.
                result = run('weekday', unit, i);
            }
            return result;
        }, startIndex, endIndex);

    }

    // Years -> Days
    function iterateOverHigherDateParams(params, fn) {
        iterateOverDateParams(params, fn, YEAR_INDEX, DAY_INDEX);
    }

    // Advancing helpers

    function advanceDate(d, unit, num, reset) {
        var set = {};
        set[unit] = num;
        return updateDate(d, set, reset, 1);
    }

    function advanceDateWithArgs(d, args, dir) {
        args = collectDateArguments(args, true);
        return updateDate(d, args[0], args[1], dir);
    }

    // Edge helpers

    function resetTime(d) {
        return setUnitAndLowerToEdge(d, HOURS_INDEX);
    }

    function resetLowerUnits(d, unitIndex) {
        return setUnitAndLowerToEdge(d, getLowerUnitIndex(unitIndex));
    }

    function moveToBeginningOfWeek(d, firstDayOfWeek) {
        setWeekday(d, floor((getWeekday(d) - firstDayOfWeek) / 7) * 7 + firstDayOfWeek);
        return d;
    }

    function moveToEndOfWeek(d, firstDayOfWeek) {
        var target = firstDayOfWeek - 1;
        setWeekday(d, ceil((getWeekday(d) - target) / 7) * 7 + target);
        return d;
    }

    function moveToBeginningOfUnit(d, unitIndex, localeCode) {
        if (unitIndex === WEEK_INDEX) {
            moveToBeginningOfWeek(d, localeManager.get(localeCode).getFirstDayOfWeek());
        }
        return setUnitAndLowerToEdge(d, getLowerUnitIndex(unitIndex));
    }

    function moveToEndOfUnit(d, unitIndex, localeCode, stopIndex) {
        if (unitIndex === WEEK_INDEX) {
            moveToEndOfWeek(d, localeManager.get(localeCode).getFirstDayOfWeek());
        }
        return setUnitAndLowerToEdge(d, getLowerUnitIndex(unitIndex), stopIndex, true);
    }

    function setUnitAndLowerToEdge(d, startIndex, stopIndex, end) {
        walkUnitDown(startIndex, function (unit, i) {
            var val = end ? unit.end : unit.start;
            if (isFunction(val)) {
                val = val(d);
            }
            callDateSet(d, unit.method, val);
            return !isDefined(stopIndex) || i > stopIndex;
        });
        return d;
    }

    // Param helpers

    function getDateParamKey(params, key) {
        return getOwnKey(params, key) ||
            getOwnKey(params, key + 's') ||
            (key === 'day' && getOwnKey(params, 'date'));
    }

    function getDateParam(params, key) {
        return getOwn(params, getDateParamKey(params, key));
    }

    function deleteDateParam(params, key) {
        delete params[getDateParamKey(params, key)];
    }

    function getUnitIndexForParamName(name) {
        var params = {}, unitIndex;
        params[name] = 1;
        iterateOverDateParams(params, function (name, val, unit, i) {
            unitIndex = i;
            return false;
        });
        return unitIndex;
    }

    // Time distance helpers

    function getDaysSince(d1, d2) {
        return getTimeDistanceForUnit(d1, d2, DateUnits[DAY_INDEX]);
    }

    function getTimeDistanceForUnit(d1, d2, unit) {
        var fwd = d2 > d1, num, tmp;
        if (!fwd) {
            tmp = d2;
            d2 = d1;
            d1 = tmp;
        }
        num = d2 - d1;
        if (unit.multiplier > 1) {
            num = trunc(num / unit.multiplier);
        }
        // For higher order with potential ambiguity, use the numeric calculation
        // as a starting point, then iterate until we pass the target date.
        if (unit.ambiguous) {
            d1 = cloneDate(d1);
            if (num) {
                advanceDate(d1, unit.name, num);
            }
            while (d1 < d2) {
                advanceDate(d1, unit.name, 1);
                if (d1 > d2) {
                    break;
                }
                num += 1;
            }
        }
        return fwd ? -num : num;
    }

    // Parsing helpers

    function getParsingTokenValue(token, str) {
        var val;
        if (token.val) {
            val = token.val;
        } else if (token.sign) {
            val = str === '+' ? 1 : -1;
        } else if (token.bool) {
            val = !!val;
        } else {
            val = +str.replace(/,/, '.');
        }
        if (token.param === 'month') {
            val -= 1;
        }
        return val;
    }

    function getYearFromAbbreviation(str, d, prefer) {
        // Following IETF here, adding 1900 or 2000 depending on the last two digits.
        // Note that this makes no accordance for what should happen after 2050, but
        // intentionally ignoring this for now. https://www.ietf.org/rfc/rfc2822.txt
        var val = +str, delta;
        val += val < 50 ? 2000 : 1900;
        if (prefer) {
            delta = val - getYear(d);
            if (delta / abs(delta) !== prefer) {
                val += prefer * 100;
            }
        }
        return val;
    }

    // Week number helpers

    function setISOWeekNumber(d, num) {
        if (isNumber(num)) {
            // Intentionally avoiding updateDate here to prevent circular dependencies.
            var isoWeek = cloneDate(d), dow = getWeekday(d);
            moveToFirstDayOfWeekYear(isoWeek, ISO_FIRST_DAY_OF_WEEK, ISO_FIRST_DAY_OF_WEEK_YEAR);
            setDate(isoWeek, getDate(isoWeek) + 7 * (num - 1));
            setYear(d, getYear(isoWeek));
            setMonth(d, getMonth(isoWeek));
            setDate(d, getDate(isoWeek));
            setWeekday(d, dow || 7);
        }
        return d.getTime();
    }

    function getWeekNumber(d, allowPrevious, firstDayOfWeek, firstDayOfWeekYear) {
        var isoWeek, n = 0;
        if (isUndefined(firstDayOfWeek)) {
            firstDayOfWeek = ISO_FIRST_DAY_OF_WEEK;
        }
        if (isUndefined(firstDayOfWeekYear)) {
            firstDayOfWeekYear = ISO_FIRST_DAY_OF_WEEK_YEAR;
        }
        // Moving to the end of the week allows for forward year traversal, ie
        // Dec 29 2014 is actually week 01 of 2015.
        isoWeek = moveToEndOfWeek(cloneDate(d), firstDayOfWeek);
        moveToFirstDayOfWeekYear(isoWeek, firstDayOfWeek, firstDayOfWeekYear);
        if (allowPrevious && d < isoWeek) {
            // If the date is still before the start of the year, then it should be
            // the last week of the previous year, ie Jan 1 2016 is actually week 53
            // of 2015, so move to the beginning of the week to traverse the year.
            isoWeek = moveToBeginningOfWeek(cloneDate(d), firstDayOfWeek);
            moveToFirstDayOfWeekYear(isoWeek, firstDayOfWeek, firstDayOfWeekYear);
        }
        while (isoWeek <= d) {
            // Doing a very simple walk to get the week number.
            setDate(isoWeek, getDate(isoWeek) + 7);
            n++;
        }
        return n;
    }

    // Week year helpers

    function getWeekYear(d, localeCode, iso) {
        var year, month, firstDayOfWeek, firstDayOfWeekYear, week, loc;
        year = getYear(d);
        month = getMonth(d);
        if (month === 0 || month === 11) {
            if (!iso) {
                loc = localeManager.get(localeCode);
                firstDayOfWeek = loc.getFirstDayOfWeek(localeCode);
                firstDayOfWeekYear = loc.getFirstDayOfWeekYear(localeCode);
            }
            week = getWeekNumber(d, false, firstDayOfWeek, firstDayOfWeekYear);
            if (month === 0 && week === 0) {
                year -= 1;
            } else if (month === 11 && week === 1) {
                year += 1;
            }
        }
        return year;
    }

    function moveToFirstDayOfWeekYear(d, firstDayOfWeek, firstDayOfWeekYear) {
        setUnitAndLowerToEdge(d, MONTH_INDEX);
        setDate(d, firstDayOfWeekYear);
        moveToBeginningOfWeek(d, firstDayOfWeek);
    }

    // Relative helpers

    function dateRelative(d, dRelative, arg1, arg2) {
        var adu, format, type, localeCode, fn;
        assertDateIsValid(d);
        if (isFunction(arg1)) {
            fn = arg1;
        } else {
            localeCode = arg1;
            fn = arg2;
        }
        adu = getAdjustedUnitForDate(d, dRelative);
        if (fn) {
            format = fn.apply(d, adu.concat(localeManager.get(localeCode)));
            if (format) {
                return dateFormat(d, format, localeCode);
            }
        }
        // Adjust up if time is in ms, as this doesn't
        // look very good for a standard relative date.
        if (adu[1] === 0) {
            adu[1] = 1;
            adu[0] = 1;
        }
        if (dRelative) {
            type = 'duration';
        } else if (adu[2] > 0) {
            type = 'future';
        } else {
            type = 'past';
        }
        return localeManager.get(localeCode).getRelativeFormat(adu, type);
    }

    // Gets an "adjusted date unit" which is a way of representing
    // the largest possible meaningful unit. In other words, if passed
    // 3600000, this will return an array which represents "1 hour".
    function getAdjustedUnit(ms, fn) {
        var unitIndex = 0, value = 0;
        iterateOverDateUnits(function (unit, i) {
            value = abs(fn(unit));
            if (value >= 1) {
                unitIndex = i;
                return false;
            }
        });
        return [value, unitIndex, ms];
    }

    // Gets the adjusted unit based on simple division by
    // date unit multiplier.
    function getAdjustedUnitForNumber(ms) {
        return getAdjustedUnit(ms, function (unit) {
            return trunc(withPrecision(ms / unit.multiplier, 1));
        });
    }

    // Gets the adjusted unit using the unitsFromNow methods,
    // which use internal date methods that neatly avoid vaguely
    // defined units of time (days in month, leap years, etc).
    // Reserving dRelative to allow another date to be relative to.
    function getAdjustedUnitForDate(d, dRelative) {
        var ms;
        if (!dRelative) {
            dRelative = getNewDate();
            if (d > dRelative) {
                // If our date is greater than the one that we got from getNewDate, it
                // means that we are finding the unit for a date that is in the future
                // relative to now. However, often the incoming date was created in
                // the same cycle as our comparison, but our "now" date will have been
                // created an instant after it, creating situations where "5 minutes from
                // now" becomes "4 minutes from now" in the same tick. To prevent this,
                // subtract a buffer of 10ms to compensate.
                dRelative = new Date(dRelative.getTime() - 10);
            }
        }
        ms = d - dRelative;
        return getAdjustedUnit(ms, function (u) {
            return abs(getTimeDistanceForUnit(d, dRelative, u));
        });
    }

    // Foramtting helpers

    // Formatting tokens
    var ldmlTokens, strfTokens;

    function dateFormat(d, format, localeCode) {
        assertDateIsValid(d);
        format = CoreOutputFormats[format] || format || '{long}';
        return dateFormatMatcher(format, d, localeCode);
    }

    function getMeridiemToken(d, localeCode) {
        var hours = getHours(d);
        return localeManager.get(localeCode).ampm[trunc(hours / 12)] || '';
    }

    function buildDateFormatTokens() {

        function addFormats(target, tokens, fn) {
            if (tokens) {
                forEach(spaceSplit(tokens), function (token) {
                    target[token] = fn;
                });
            }
        }

        function buildLowercase(get) {
            return function (d, localeCode) {
                return get(d, localeCode).toLowerCase();
            };
        }

        function buildOrdinal(get) {
            return function (d, localeCode) {
                var n = get(d, localeCode);
                return n + localeManager.get(localeCode).getOrdinal(n);
            };
        }

        function buildPadded(get, padding) {
            return function (d, localeCode) {
                return padNumber(get(d, localeCode), padding);
            };
        }

        function buildTwoDigits(get) {
            return function (d, localeCode) {
                return get(d, localeCode) % 100;
            };
        }

        function buildAlias(alias) {
            return function (d, localeCode) {
                return dateFormatMatcher(alias, d, localeCode);
            };
        }

        function buildAlternates(f) {
            for (var n = 1; n <= 5; n++) {
                buildAlternate(f, n);
            }
        }

        function buildAlternate(f, n) {
            var alternate = function (d, localeCode) {
                return f.get(d, localeCode, n);
            };
            addFormats(ldmlTokens, f.ldml + n, alternate);
            if (f.lowerToken) {
                ldmlTokens[f.lowerToken + n] = buildLowercase(alternate);
            }
        }

        function getIdentityFormat(name) {
            return function (d, localeCode) {
                var loc = localeManager.get(localeCode);
                return dateFormatMatcher(loc[name], d, localeCode);
            };
        }

        ldmlTokens = {};
        strfTokens = {};

        forEach(FormatTokensBase, function (f) {
            var get = f.get, getPadded;
            if (f.lowerToken) {
                ldmlTokens[f.lowerToken] = buildLowercase(get);
            }
            if (f.ordinalToken) {
                ldmlTokens[f.ordinalToken] = buildOrdinal(get, f);
            }
            if (f.ldmlPaddedToken) {
                ldmlTokens[f.ldmlPaddedToken] = buildPadded(get, f.ldmlPaddedToken.length);
            }
            if (f.ldmlTwoDigitToken) {
                ldmlTokens[f.ldmlTwoDigitToken] = buildPadded(buildTwoDigits(get), 2);
            }
            if (f.strfTwoDigitToken) {
                strfTokens[f.strfTwoDigitToken] = buildPadded(buildTwoDigits(get), 2);
            }
            if (f.strfPadding) {
                getPadded = buildPadded(get, f.strfPadding);
            }
            if (f.alias) {
                get = buildAlias(f.alias);
            }
            if (f.allowAlternates) {
                buildAlternates(f);
            }
            addFormats(ldmlTokens, f.ldml, get);
            addFormats(strfTokens, f.strf, getPadded || get);
        });

        forEachProperty(CoreOutputFormats, function (src, name) {
            addFormats(ldmlTokens, name, buildAlias(src));
        });

        defineInstanceSimilar(sugarDate, 'short medium long full', function (methods, name) {
            var fn = getIdentityFormat(name);
            addFormats(ldmlTokens, name, fn);
            methods[name] = fn;
        });

        addFormats(ldmlTokens, 'time', getIdentityFormat('time'));
        addFormats(ldmlTokens, 'stamp', getIdentityFormat('stamp'));
    }

    // Format matcher

    var dateFormatMatcher;

    function buildDateFormatMatcher() {

        function getLdml(d, token, localeCode) {
            return getOwn(ldmlTokens, token)(d, localeCode);
        }

        function getStrf(d, token, localeCode) {
            return getOwn(strfTokens, token)(d, localeCode);
        }

        function checkDateToken(ldml, strf) {
            return hasOwn(ldmlTokens, ldml) || hasOwn(strfTokens, strf);
        }

        // Format matcher for LDML or STRF tokens.
        dateFormatMatcher = createFormatMatcher(getLdml, getStrf, checkDateToken);
    }

    // Comparison helpers

    function fullCompareDate(date, d, margin) {
        var tmp;
        if (!dateIsValid(date)) return;
        if (isString(d)) {
            d = trim(d).toLowerCase();
            switch (true) {
                case d === 'future':
                    return date.getTime() > getNewDate().getTime();
                case d === 'past':
                    return date.getTime() < getNewDate().getTime();
                case d === 'today':
                    return compareDay(date);
                case d === 'tomorrow':
                    return compareDay(date, 1);
                case d === 'yesterday':
                    return compareDay(date, -1);
                case d === 'weekday':
                    return getWeekday(date) > 0 && getWeekday(date) < 6;
                case d === 'weekend':
                    return getWeekday(date) === 0 || getWeekday(date) === 6;

                case (isDefined(tmp = English.weekdayMap[d])):
                    return getWeekday(date) === tmp;
                case (isDefined(tmp = English.monthMap[d])):
                    return getMonth(date) === tmp;
            }
        }
        return compareDate(date, d, margin);
    }

    function compareDate(date, d, margin, localeCode, options) {
        var loMargin = 0, hiMargin = 0, timezoneShift, compareEdges, override, min, max, p, t;

        function getTimezoneShift() {
            // If there is any specificity in the date then we're implicitly not
            // checking absolute time, so ignore timezone shifts.
            if (p.set && p.set.specificity) {
                return 0;
            }
            return (tzOffset(p.date) - tzOffset(date)) * MINUTES;
        }

        function addSpecificUnit() {
            var unit = DateUnits[p.set.specificity];
            return advanceDate(cloneDate(p.date), unit.name, 1).getTime() - 1;
        }

        if (_utc(date)) {
            options = options || {};
            options.fromUTC = true;
            options.setUTC = true;
        }

        p = getExtendedDate(null, d, options, true);

        if (margin > 0) {
            loMargin = hiMargin = margin;
            override = true;
        }
        if (!dateIsValid(p.date)) return false;
        if (p.set && p.set.specificity) {
            if (isDefined(p.set.edge) || isDefined(p.set.shift)) {
                compareEdges = true;
                moveToBeginningOfUnit(p.date, p.set.specificity, localeCode);
            }
            if (compareEdges || p.set.specificity === MONTH_INDEX) {
                max = moveToEndOfUnit(cloneDate(p.date), p.set.specificity, localeCode).getTime();
            } else {
                max = addSpecificUnit();
            }
            if (!override && isDefined(p.set.sign) && p.set.specificity) {
                // If the time is relative, there can occasionally be an disparity between
                // the relative date and "now", which it is being compared to, so set an
                // extra margin to account for this.
                loMargin = 50;
                hiMargin = -50;
            }
        }
        t = date.getTime();
        min = p.date.getTime();
        max = max || min;
        timezoneShift = getTimezoneShift();
        if (timezoneShift) {
            min -= timezoneShift;
            max -= timezoneShift;
        }
        return t >= (min - loMargin) && t <= (max + hiMargin);
    }

    function compareDay(d, shift) {
        var comp = getNewDate();
        if (shift) {
            setDate(comp, getDate(comp) + shift);
        }
        return getYear(d) === getYear(comp) &&
            getMonth(d) === getMonth(comp) &&
            getDate(d) === getDate(comp);
    }

    // Create helpers

    function createDate(d, options, forceClone) {
        return getExtendedDate(null, d, options, forceClone).date;
    }

    function createDateWithContext(contextDate, d, options, forceClone) {
        return getExtendedDate(contextDate, d, options, forceClone).date;
    }

    function getExtendedDate(contextDate, d, opt, forceClone) {

        var date, set, loc, options, afterCallbacks, relative, weekdayDir;

        afterCallbacks = [];
        options = getDateOptions(opt);

        function getDateOptions(opt) {
            var options = isString(opt) ? {locale: opt} : opt || {};
            options.prefer = +!!getOwn(options, 'future') - +!!getOwn(options, 'past');
            return options;
        }

        function getFormatParams(match, dif) {
            var set = getOwn(options, 'params') || {};
            forEach(dif.to, function (field, i) {
                var str = match[i + 1], token, val;
                if (!str) return;
                if (field === 'yy' || field === 'y') {
                    field = 'year';
                    val = getYearFromAbbreviation(str, date, getOwn(options, 'prefer'));
                } else if (token = getOwn(ParsingTokens, field)) {
                    field = token.param || field;
                    val = getParsingTokenValue(token, str);
                } else {
                    val = loc.getTokenValue(field, str);
                }
                set[field] = val;
            });
            return set;
        }

        // Clone date will set the utc flag, but it will
        // be overriden later, so set option flags instead.
        function cloneDateByFlag(d, clone) {
            if (_utc(d) && !isDefined(getOwn(options, 'fromUTC'))) {
                options.fromUTC = true;
            }
            if (_utc(d) && !isDefined(getOwn(options, 'setUTC'))) {
                options.setUTC = true;
            }
            if (clone) {
                d = new Date(d.getTime());
            }
            return d;
        }

        function afterDateSet(fn) {
            afterCallbacks.push(fn);
        }

        function fireCallbacks() {
            forEach(afterCallbacks, function (fn) {
                fn.call();
            });
        }

        function parseStringDate(str) {

            str = str.toLowerCase();

            // The act of getting the locale will initialize
            // if it is missing and add the required formats.
            loc = localeManager.get(getOwn(options, 'locale'));

            for (var i = 0, dif, match; dif = loc.compiledFormats[i]; i++) {
                match = str.match(dif.reg);
                if (match) {

                    // Note that caching the format will modify the compiledFormats array
                    // which is not a good idea to do inside its for loop, however we
                    // know at this point that we have a matched format and that we will
                    // break out below, so simpler to do it here.
                    loc.cacheFormat(dif, i);

                    set = getFormatParams(match, dif);

                    if (isDefined(set.timestamp)) {
                        str = set.timestamp;
                        set = null;
                        break;
                    }

                    if (isDefined(set.ampm)) {
                        handleAmpm(set.ampm);
                    }

                    if (set.utc || isDefined(set.tzHour)) {
                        handleTimezoneOffset(set.tzHour, set.tzMinute, set.tzSign);
                    }

                    if (isDefined(set.shift) && isUndefined(set.unit)) {
                        // "next january", "next monday", etc
                        handleUnitlessShift();
                    }

                    if (isDefined(set.num) && isUndefined(set.unit)) {
                        // "the second of January", etc
                        handleUnitlessNum(set.num);
                    }

                    if (set.midday) {
                        // "noon" and "midnight"
                        handleMidday(set.midday);
                    }

                    if (isDefined(set.day)) {
                        // Relative day localizations such as "today" and "tomorrow".
                        handleRelativeDay(set.day);
                    }

                    if (isDefined(set.unit)) {
                        // "3 days ago", etc
                        handleRelativeUnit(set.unit);
                    }

                    if (set.edge) {
                        // "the end of January", etc
                        handleEdge(set.edge, set);
                    }

                    if (set.yearSign) {
                        set.year *= set.yearSign;
                    }

                    break;
                }
            }

            if (!set) {
                // Fall back to native parsing
                date = new Date(str);
                if (getOwn(options, 'fromUTC')) {
                    // Falling back to system date here which cannot be parsed as UTC,
                    // so if we're forcing UTC then simply add the offset.
                    date.setTime(date.getTime() + (tzOffset(date) * MINUTES));
                }
            } else if (relative) {
                updateDate(date, set, false, 1);
            } else {
                if (_utc(date)) {
                    // UTC times can traverse into other days or even months,
                    // so preemtively reset the time here to prevent this.
                    resetTime(date);
                }
                updateDate(date, set, true, 0, getOwn(options, 'prefer'), weekdayDir);
            }
            fireCallbacks();
            return date;
        }

        function handleAmpm(ampm) {
            if (ampm === 1 && set.hour < 12) {
                // If the time is 1pm-11pm advance the time by 12 hours.
                set.hour += 12;
            } else if (ampm === 0 && set.hour === 12) {
                // If it is 12:00am then set the hour to 0.
                set.hour = 0;
            }
        }

        function handleTimezoneOffset(tzHour, tzMinute, tzSign) {
            // Adjust for timezone offset
            _utc(date, true);
            var offset = (tzSign || 1) * ((tzHour || 0) * 60 + (tzMinute || 0));
            if (offset) {
                set.minute = (set.minute || 0) - offset;
            }
        }

        function handleUnitlessShift() {
            if (isDefined(set.month)) {
                // "next January"
                set.unit = YEAR_INDEX;
            } else if (isDefined(set.weekday)) {
                // "next Monday"
                set.unit = WEEK_INDEX;
            }
        }

        function handleUnitlessNum(num) {
            if (isDefined(set.weekday)) {
                // "The second Tuesday of March"
                setOrdinalWeekday(num);
            } else if (isDefined(set.month)) {
                // "The second of March"
                set.date = set.num;
            }
        }

        function handleMidday(hour) {
            set.hour = hour % 24;
            if (hour > 23) {
                // If the date has hours past 24, we need to prevent it from traversing
                // into a new day as that would make it being part of a new week in
                // ambiguous dates such as "Monday".
                afterDateSet(function () {
                    advanceDate(date, 'date', trunc(hour / 24));
                });
            }
        }

        function handleRelativeDay() {
            resetTime(date);
            if (isUndefined(set.unit)) {
                set.unit = DAY_INDEX;
                set.num = set.day;
                delete set.day;
            }
        }

        function handleRelativeUnit(unitIndex) {
            var num = isDefined(set.num) ? set.num : 1;

            // If a weekday is defined, there are 3 possible formats being applied:
            //
            // 1. "the day after monday": unit is days
            // 2. "next monday": short for "next week monday", unit is weeks
            // 3. "the 2nd monday of next month": unit is months
            //
            // In the first case, we need to set the weekday up front, as the day is
            // relative to it. The second case also needs to be handled up front for
            // formats like "next monday at midnight" which will have its weekday reset
            // if not set up front. The last case will set up the params necessary to
            // shift the weekday and allow separateAbsoluteUnits below to handle setting
            // it after the date has been shifted.
            if (isDefined(set.weekday)) {
                if (unitIndex === MONTH_INDEX) {
                    setOrdinalWeekday(num);
                    num = 1;
                } else {
                    updateDate(date, {weekday: set.weekday}, true);
                    delete set.weekday;
                }
            }

            if (set.half) {
                // Allow localized "half" as a standalone colloquialism. Purposely avoiding
                // the locale number system to reduce complexity. The units "month" and
                // "week" are purposely excluded in the English date formats below, as
                // "half a week" and "half a month" are meaningless as exact dates.
                num *= set.half;
            }

            if (isDefined(set.shift)) {
                // Shift and unit, ie "next month", "last week", etc.
                num *= set.shift;
            } else if (set.sign) {
                // Unit and sign, ie "months ago", "weeks from now", etc.
                num *= set.sign;
            }

            if (isDefined(set.day)) {
                // "the day after tomorrow"
                num += set.day;
                delete set.day;
            }

            // Formats like "the 15th of last month" or "6:30pm of next week"
            // contain absolute units in addition to relative ones, so separate
            // them here, remove them from the params, and set up a callback to
            // set them after the relative ones have been set.
            separateAbsoluteUnits(unitIndex);

            // Finally shift the unit.
            set[English.units[unitIndex]] = num;
            relative = true;
        }

        function handleEdge(edge, params) {
            var edgeIndex = params.unit, weekdayOfMonth;
            if (!edgeIndex) {
                // If we have "the end of January", then we need to find the unit index.
                iterateOverHigherDateParams(params, function (unitName, val, unit, i) {
                    if (unitName === 'weekday' && isDefined(params.month)) {
                        // If both a month and weekday exist, then we have a format like
                        // "the last tuesday in November, 2012", where the "last" is still
                        // relative to the end of the month, so prevent the unit "weekday"
                        // from taking over.
                        return;
                    }
                    edgeIndex = i;
                });
            }
            if (edgeIndex === MONTH_INDEX && isDefined(params.weekday)) {
                // If a weekday in a month exists (as described above),
                // then set it up to be set after the date has been shifted.
                weekdayOfMonth = params.weekday;
                delete params.weekday;
            }
            afterDateSet(function () {
                var stopIndex;
                // "edge" values that are at the very edge are "2" so the beginning of the
                // year is -2 and the end of the year is 2. Conversely, the "last day" is
                // actually 00:00am so it is 1. -1 is reserved but unused for now.
                if (edge < 0) {
                    moveToBeginningOfUnit(date, edgeIndex, getOwn(options, 'locale'));
                } else if (edge > 0) {
                    if (edge === 1) {
                        stopIndex = DAY_INDEX;
                        moveToBeginningOfUnit(date, DAY_INDEX);
                    }
                    moveToEndOfUnit(date, edgeIndex, getOwn(options, 'locale'), stopIndex);
                }
                if (isDefined(weekdayOfMonth)) {
                    setWeekday(date, weekdayOfMonth, -edge);
                    resetTime(date);
                }
            });
            if (edgeIndex === MONTH_INDEX) {
                params.specificity = DAY_INDEX;
            } else {
                params.specificity = edgeIndex - 1;
            }
        }

        function setOrdinalWeekday(num) {
            // If we have "the 2nd Tuesday of June", then pass the "weekdayDir"
            // flag along to updateDate so that the date does not accidentally traverse
            // into the previous month. This needs to be independent of the "prefer"
            // flag because we are only ensuring that the weekday is in the future, not
            // the entire date.
            set.weekday = 7 * (num - 1) + set.weekday;
            set.date = 1;
            weekdayDir = 1;
        }

        function separateAbsoluteUnits(unitIndex) {
            var params;

            iterateOverDateParams(set, function (name, val, unit, i) {
                // If there is a time unit set that is more specific than
                // the matched unit we have a string like "5:30am in 2 minutes",
                // which is meaningless, so invalidate the date...
                if (i >= unitIndex) {
                    date.setTime(NaN);
                    return false;
                } else if (i < unitIndex) {
                    // ...otherwise set the params to set the absolute date
                    // as a callback after the relative date has been set.
                    params = params || {};
                    params[name] = val;
                    deleteDateParam(set, name);
                }
            });
            if (params) {
                afterDateSet(function () {
                    updateDate(date, params, true, false, getOwn(options, 'prefer'), weekdayDir);
                });
                if (set.edge) {
                    // "the end of March of next year"
                    handleEdge(set.edge, params);
                    delete set.edge;
                }
            }
        }

        if (contextDate && d) {
            // If a context date is passed ("get" and "unitsFromNow"),
            // then use it as the starting point.
            date = cloneDateByFlag(contextDate, true);
        } else {
            date = getNewDate();
        }

        _utc(date, getOwn(options, 'fromUTC'));

        if (isString(d)) {
            date = parseStringDate(d);
        } else if (isDate(d)) {
            date = cloneDateByFlag(d, hasOwn(options, 'clone') || forceClone);
        } else if (isObjectType(d)) {
            set = simpleClone(d);
            updateDate(date, set, true);
        } else if (isNumber(d) || d === null) {
            date.setTime(d);
        }
        // A date created by parsing a string presumes that the format *itself* is
        // UTC, but not that the date, once created, should be manipulated as such. In
        // other words, if you are creating a date object from a server time
        // "2012-11-15T12:00:00Z", in the majority of cases you are using it to create
        // a date that will, after creation, be manipulated as local, so reset the utc
        // flag here unless "setUTC" is also set.
        _utc(date, !!getOwn(options, 'setUTC'));
        return {
            set: set,
            date: date
        };
    }

    function updateDate(d, params, reset, advance, prefer, weekdayDir) {
        var upperUnitIndex;

        function setUpperUnit(unitName, unitIndex) {
            if (prefer && !upperUnitIndex) {
                if (unitName === 'weekday') {
                    upperUnitIndex = WEEK_INDEX;
                } else {
                    upperUnitIndex = getHigherUnitIndex(unitIndex);
                }
            }
        }

        function setSpecificity(unitIndex) {
            // Other functions may preemptively set the specificity before arriving
            // here so concede to them if they have already set more specific units.
            if (unitIndex > params.specificity) {
                return;
            }
            params.specificity = unitIndex;
        }

        function canDisambiguate() {
            if (!upperUnitIndex || upperUnitIndex > YEAR_INDEX) {
                return;
            }
            switch (prefer) {
                case -1:
                    return d > getNewDate();
                case  1:
                    return d < getNewDate();
            }
        }

        function disambiguateHigherUnit() {
            var unit = DateUnits[upperUnitIndex];
            advance = prefer;
            setUnit(unit.name, 1, unit, upperUnitIndex);
        }

        function handleFraction(unit, unitIndex, fraction) {
            if (unitIndex) {
                var lowerUnit = DateUnits[getLowerUnitIndex(unitIndex)];
                var val = round(unit.multiplier / lowerUnit.multiplier * fraction);
                params[lowerUnit.name] = val;
            }
        }

        function monthHasShifted(d, targetMonth) {
            if (targetMonth < 0) {
                targetMonth = targetMonth % 12 + 12;
            }
            return targetMonth % 12 !== getMonth(d);
        }

        function setUnit(unitName, value, unit, unitIndex) {
            var method = unit.method, checkMonth, fraction;

            setUpperUnit(unitName, unitIndex);
            setSpecificity(unitIndex);

            fraction = value % 1;
            if (fraction) {
                handleFraction(unit, unitIndex, fraction);
                value = trunc(value);
            }

            if (unitName === 'weekday') {
                if (!advance) {
                    // Weekdays are always considered absolute units so simply set them
                    // here even if it is an "advance" operation. This is to help avoid
                    // ambiguous meanings in "advance" as well as to neatly allow formats
                    // like "Wednesday of next week" without more complex logic.
                    setWeekday(d, value, weekdayDir);
                }
                return;
            }
            checkMonth = unitIndex === MONTH_INDEX && getDate(d) > 28;

            // If we are advancing or rewinding, then we need we need to set the
            // absolute time if the unit is "hours" or less. This is due to the fact
            // that setting by method is ambiguous during DST shifts. For example,
            // 1:00am on November 1st 2015 occurs twice in North American timezones
            // with DST, the second time being after the clocks are rolled back at
            // 2:00am. When springing forward this is automatically handled as there
            // is no 2:00am so the date automatically jumps to 3:00am. However, when
            // rolling back, setHours(2) will always choose the first "2am" even if
            // the date is currently set to the second, causing unintended jumps.
            // This ambiguity is unavoidable when setting dates as the notation is
            // ambiguous. However when advancing, we clearly want the resulting date
            // to be an acutal hour ahead, which can only be accomplished by setting
            // the absolute time. Conversely, any unit higher than "hours" MUST use
            // the internal set methods, as they are ambiguous as absolute units of
            // time. Years may be 365 or 366 days depending on leap years, months are
            // all over the place, and even days may be 23-25 hours depending on DST
            // shifts. Finally, note that the kind of jumping described above will
            // occur when calling ANY "set" method on the date and will occur even if
            // the value being set is identical to the one currently set (i.e.
            // setHours(2) on a date at 2am may not be a noop). This is precarious,
            // so avoiding this situation in callDateSet by checking up front that
            // the value is not the same before setting.
            if (advance && !unit.ambiguous) {
                d.setTime(d.getTime() + (value * advance * unit.multiplier));
                return;
            } else if (advance) {
                if (unitIndex === WEEK_INDEX) {
                    value *= 7;
                    method = DateUnits[DAY_INDEX].method;
                }
                value = (value * advance) + callDateGet(d, method);
            }
            callDateSetWithWeek(d, method, value, advance);
            if (checkMonth && monthHasShifted(d, value)) {
                // As we are setting the units in reverse order, there is a chance that
                // our date may accidentally traverse into a new month, such as setting
                // { month: 1, date 15 } on January 31st. Check for this here and reset
                // the date to the last day of the previous month if this has happened.
                setDate(d, 0);
            }
        }

        if (isNumber(params) && advance) {
            // If param is a number and advancing, the number is in milliseconds.
            params = {millisecond: params};
        } else if (isNumber(params)) {
            // Otherwise just set the timestamp and return.
            d.setTime(params);
            return d;
        }

        iterateOverDateParams(params, setUnit);

        if (reset && params.specificity) {
            resetLowerUnits(d, params.specificity);
        }

        // If past or future is preferred, then the process of "disambiguation" will
        // ensure that an ambiguous time/date ("4pm", "thursday", "June", etc.) will
        // be in the past or future. Weeks are only considered ambiguous if there is
        // a weekday, i.e. "thursday" is an ambiguous week, but "the 4th" is an
        // ambiguous month.
        if (canDisambiguate()) {
            disambiguateHigherUnit();
        }
        return d;
    }

    // Locales

    // Locale helpers
    var English, localeManager;

    function getEnglishVariant(v) {
        return simpleMerge(simpleClone(EnglishLocaleBaseDefinition), v);
    }

    function arrayToRegAlternates(arr) {
        var joined = arr.join('');
        if (!arr || !arr.length) {
            return '';
        }
        if (joined.length === arr.length) {
            return '[' + joined + ']';
        }
        // map handles sparse arrays so no need to compact the array here.
        return map(arr, escapeRegExp).join('|');
    }

    function getRegNonCapturing(src, opt) {
        if (src.length > 1) {
            src = '(?:' + src + ')';
        }
        if (opt) {
            src += '?';
        }
        return src;
    }

    function getParsingTokenWithSuffix(field, src, suffix) {
        var token = LocalizedParsingTokens[field];
        if (token.requiresSuffix) {
            src = getRegNonCapturing(src + getRegNonCapturing(suffix));
        } else if (token.requiresSuffixOr) {
            src += getRegNonCapturing(token.requiresSuffixOr + '|' + suffix);
        } else {
            src += getRegNonCapturing(suffix, true);
        }
        return src;
    }

    function getArrayWithOffset(arr, n, alternate, offset) {
        var val;
        if (alternate > 1) {
            val = arr[n + (alternate - 1) * offset];
        }
        return val || arr[n];
    }

    function buildLocales() {

        function LocaleManager(loc) {
            this.locales = {};
            this.add(loc);
        }

        LocaleManager.prototype = {

            get: function (code, fallback) {
                var loc = this.locales[code];
                if (!loc && LazyLoadedLocales[code]) {
                    loc = this.add(code, LazyLoadedLocales[code]);
                } else if (!loc && code) {
                    loc = this.locales[code.slice(0, 2)];
                }
                return loc || fallback === false ? loc : this.current;
            },

            getAll: function () {
                return this.locales;
            },

            set: function (code) {
                var loc = this.get(code, false);
                if (!loc) {
                    throw new TypeError('Invalid Locale: ' + code);
                }
                return this.current = loc;
            },

            add: function (code, def) {
                if (!def) {
                    def = code;
                    code = def.code;
                } else {
                    def.code = code;
                }
                var loc = def.compiledFormats ? def : getNewLocale(def);
                this.locales[code] = loc;
                if (!this.current) {
                    this.current = loc;
                }
                return loc;
            },

            remove: function (code) {
                if (this.current.code === code) {
                    this.current = this.get('en');
                }
                return delete this.locales[code];
            }

        };

        // Sorry about this guys...
        English = getNewLocale(AmericanEnglishDefinition);
        localeManager = new LocaleManager(English);
    }

    function getNewLocale(def) {

        function Locale(def) {
            this.init(def);
        }

        Locale.prototype = {

            getMonthName: function (n, alternate) {
                if (this.monthSuffix) {
                    return (n + 1) + this.monthSuffix;
                }
                return getArrayWithOffset(this.months, n, alternate, 12);
            },

            getWeekdayName: function (n, alternate) {
                return getArrayWithOffset(this.weekdays, n, alternate, 7);
            },

            getTokenValue: function (field, str) {
                var map = this[field + 'Map'], val;
                if (map) {
                    val = map[str];
                }
                if (isUndefined(val)) {
                    val = this.getNumber(str);
                    if (field === 'month') {
                        // Months are the only numeric date field
                        // whose value is not the same as its number.
                        val -= 1;
                    }
                }
                return val;
            },

            getNumber: function (str) {
                var num = this.numeralMap[str];
                if (isDefined(num)) {
                    return num;
                }
                // The unary plus operator here show better performance and handles
                // every format that parseFloat does with the exception of trailing
                // characters, which are guaranteed not to be in our string at this point.
                num = +str.replace(/,/, '.');
                if (!isNaN(num)) {
                    return num;
                }
                num = this.getNumeralValue(str);
                if (!isNaN(num)) {
                    this.numeralMap[str] = num;
                    return num;
                }
                return num;
            },

            getNumeralValue: function (str) {
                var place = 1, num = 0, lastWasPlace, isPlace, numeral, digit, arr;
                // Note that "numerals" that need to be converted through this method are
                // all considered to be single characters in order to handle CJK. This
                // method is by no means unique to CJK, but the complexity of handling
                // inflections in non-CJK languages adds too much overhead for not enough
                // value, so avoiding for now.
                arr = str.split('');
                for (var i = arr.length - 1; numeral = arr[i]; i--) {
                    digit = getOwn(this.numeralMap, numeral);
                    if (isUndefined(digit)) {
                        digit = getOwn(fullWidthNumberMap, numeral) || 0;
                    }
                    isPlace = digit > 0 && digit % 10 === 0;
                    if (isPlace) {
                        if (lastWasPlace) {
                            num += place;
                        }
                        if (i) {
                            place = digit;
                        } else {
                            num += digit;
                        }
                    } else {
                        num += digit * place;
                        place *= 10;
                    }
                    lastWasPlace = isPlace;
                }
                return num;
            },

            getOrdinal: function (n) {
                var suffix = this.ordinalSuffix;
                return suffix || getOrdinalSuffix(n);
            },

            getRelativeFormat: function (adu, type) {
                return this.convertAdjustedToFormat(adu, type);
            },

            getDuration: function (ms) {
                return this.convertAdjustedToFormat(getAdjustedUnitForNumber(max(0, ms)), 'duration');
            },

            getFirstDayOfWeek: function () {
                var val = this.firstDayOfWeek;
                return isDefined(val) ? val : ISO_FIRST_DAY_OF_WEEK;
            },

            getFirstDayOfWeekYear: function () {
                return this.firstDayOfWeekYear || ISO_FIRST_DAY_OF_WEEK_YEAR;
            },

            convertAdjustedToFormat: function (adu, type) {
                var sign, unit, mult,
                    num = adu[0],
                    u = adu[1],
                    ms = adu[2],
                    format = this[type] || this.relative;
                if (isFunction(format)) {
                    return format.call(this, num, u, ms, type);
                }
                mult = !this.plural || num === 1 ? 0 : 1;
                unit = this.units[mult * 8 + u] || this.units[u];
                sign = this[ms > 0 ? 'fromNow' : 'ago'];
                return format.replace(/\{(.*?)\}/g, function (full, match) {
                    switch (match) {
                        case 'num':
                            return num;
                        case 'unit':
                            return unit;
                        case 'sign':
                            return sign;
                    }
                });
            },

            cacheFormat: function (dif, i) {
                this.compiledFormats.splice(i, 1);
                this.compiledFormats.unshift(dif);
            },

            addFormat: function (src, to) {
                var loc = this;

                function getTokenSrc(str) {
                    var suffix, src, val,
                        opt = str.match(/\?$/),
                        nc = str.match(/^(\d+)\??$/),
                        slice = str.match(/(\d)(?:-(\d))?/),
                        key = str.replace(/[^a-z]+$/i, '');

                    // Allowing alias tokens such as {time}
                    if (val = getOwn(loc.parsingAliases, key)) {
                        src = replaceParsingTokens(val);
                        if (opt) {
                            src = getRegNonCapturing(src, true);
                        }
                        return src;
                    }

                    if (nc) {
                        src = loc.tokens[nc[1]];
                    } else if (val = getOwn(ParsingTokens, key)) {
                        src = val.src;
                    } else {
                        val = getOwn(loc.parsingTokens, key) || getOwn(loc, key);

                        // Both the "months" array and the "month" parsing token can be accessed
                        // by either {month} or {months}, falling back as necessary, however
                        // regardless of whether or not a fallback occurs, the final field to
                        // be passed to addRawFormat must be normalized as singular.
                        key = key.replace(/s$/, '');

                        if (!val) {
                            val = getOwn(loc.parsingTokens, key) || getOwn(loc, key + 's');
                        }

                        if (isString(val)) {
                            src = val;
                            suffix = loc[key + 'Suffix'];
                        } else {
                            if (slice) {
                                val = filter(val, function (m, i) {
                                    var mod = i % (loc.units ? 8 : val.length);
                                    return mod >= slice[1] && mod <= (slice[2] || slice[1]);
                                });
                            }
                            src = arrayToRegAlternates(val);
                        }
                    }
                    if (!src) {
                        return '';
                    }
                    if (nc) {
                        // Non-capturing tokens like {0}
                        src = getRegNonCapturing(src);
                    } else {
                        // Capturing group and add to parsed tokens
                        to.push(key);
                        src = '(' + src + ')';
                    }
                    if (suffix) {
                        // Date/time suffixes such as those in CJK
                        src = getParsingTokenWithSuffix(key, src, suffix);
                    }
                    if (opt) {
                        src += '?';
                    }
                    return src;
                }

                function replaceParsingTokens(str) {

                    // Make spaces optional
                    str = str.replace(/ /g, ' ?');

                    return str.replace(/\{([^,]+?)\}/g, function (match, token) {
                        var tokens = token.split('|'), src;
                        if (tokens.length > 1) {
                            src = getRegNonCapturing(map(tokens, getTokenSrc).join('|'));
                        } else {
                            src = getTokenSrc(token);
                        }
                        return src;
                    });
                }

                if (!to) {
                    to = [];
                    src = replaceParsingTokens(src);
                }

                loc.addRawFormat(src, to);
            },

            addRawFormat: function (format, to) {
                this.compiledFormats.unshift({
                    reg: RegExp('^ *' + format + ' *$', 'i'),
                    to: to
                });
            },

            init: function (def) {
                var loc = this;

                // -- Initialization helpers

                function initFormats() {
                    loc.compiledFormats = [];
                    loc.parsingAliases = {};
                    loc.parsingTokens = {};
                }

                function initDefinition() {
                    simpleMerge(loc, def);
                }

                function initArrayFields() {
                    forEach(LOCALE_ARRAY_FIELDS, function (name) {
                        var val = loc[name];
                        if (isString(val)) {
                            loc[name] = commaSplit(val);
                        } else if (!val) {
                            loc[name] = [];
                        }
                    });
                }

                // -- Value array build helpers

                function buildValueArray(name, mod, map, fn) {
                    var field = name, all = [], setMap;
                    if (!loc[field]) {
                        field += 's';
                    }
                    if (!map) {
                        map = {};
                        setMap = true;
                    }
                    forAllAlternates(field, function (alt, j, i) {
                        var idx = j * mod + i, val;
                        val = fn ? fn(i) : i;
                        map[alt] = val;
                        map[alt.toLowerCase()] = val;
                        all[idx] = alt;
                    });
                    loc[field] = all;
                    if (setMap) {
                        loc[name + 'Map'] = map;
                    }
                }

                function forAllAlternates(field, fn) {
                    forEach(loc[field], function (str, i) {
                        forEachAlternate(str, function (alt, j) {
                            fn(alt, j, i);
                        });
                    });
                }

                function forEachAlternate(str, fn) {
                    var arr = map(str.split('+'), function (split) {
                        return split.replace(/(.+):(.+)$/, function (full, base, suffixes) {
                            return map(suffixes.split('|'), function (suffix) {
                                return base + suffix;
                            }).join('|');
                        });
                    }).join('|');
                    forEach(arr.split('|'), fn);
                }

                function buildNumerals() {
                    var map = {};
                    buildValueArray('numeral', 10, map);
                    buildValueArray('article', 1, map, function () {
                        return 1;
                    });
                    buildValueArray('placeholder', 4, map, function (n) {
                        return pow(10, n + 1);
                    });
                    loc.numeralMap = map;
                }

                function buildTimeFormats() {
                    loc.parsingAliases['time'] = getTimeFormat();
                    loc.parsingAliases['tzOffset'] = getTZOffsetFormat();
                }

                function getTimeFormat() {
                    var src;
                    if (loc.ampmFront) {
                        // "ampmFront" exists mostly for CJK locales, which also presume that
                        // time suffixes exist, allowing this to be a simpler regex.
                        src = '{ampm?} {hour} (?:{minute} (?::?{second})?)?';
                    } else if (loc.ampm.length) {
                        src = '{hour}(?:[.:]{minute}(?:[.:]{second})? {ampm?}| {ampm})';
                    } else {
                        src = '{hour}(?:[.:]{minute}(?:[.:]{second})?)';
                    }
                    return src;
                }

                function getTZOffsetFormat() {
                    return '(?:{Z}|{GMT?}(?:{tzSign}{tzHour}(?::?{tzMinute}(?: \\([\\w\\s]+\\))?)?)?)?';
                }

                function buildParsingTokens() {
                    forEachProperty(LocalizedParsingTokens, function (token, name) {
                        var src, arr;
                        src = token.base ? ParsingTokens[token.base].src : token.src;
                        if (token.requiresNumerals || loc.numeralUnits) {
                            src += getNumeralSrc();
                        }
                        arr = loc[name + 's'];
                        if (arr && arr.length) {
                            src += '|' + arrayToRegAlternates(arr);
                        }
                        loc.parsingTokens[name] = src;
                    });
                }

                function getNumeralSrc() {
                    var all, src = '';
                    all = loc.numerals.concat(loc.placeholders).concat(loc.articles);
                    if (loc.allowsFullWidth) {
                        all = all.concat(fullWidthNumbers.split(''));
                    }
                    if (all.length) {
                        src = '|(?:' + arrayToRegAlternates(all) + ')+';
                    }
                    return src;
                }

                function buildTimeSuffixes() {
                    iterateOverDateUnits(function (unit, i) {
                        var token = loc.timeSuffixes[i];
                        if (token) {
                            loc[(unit.alias || unit.name) + 'Suffix'] = token;
                        }
                    });
                }

                function buildModifiers() {
                    forEach(loc.modifiers, function (modifier) {
                        var name = modifier.name, mapKey = name + 'Map', map;
                        map = loc[mapKey] || {};
                        forEachAlternate(modifier.src, function (alt, j) {
                            var token = getOwn(loc.parsingTokens, name), val = modifier.value;
                            map[alt] = val;
                            loc.parsingTokens[name] = token ? token + '|' + alt : alt;
                            if (modifier.name === 'sign' && j === 0) {
                                // Hooking in here to set the first "fromNow" or "ago" modifier
                                // directly on the locale, so that it can be reused in the
                                // relative format.
                                loc[val === 1 ? 'fromNow' : 'ago'] = alt;
                            }
                        });
                        loc[mapKey] = map;
                    });
                }

                // -- Format adding helpers

                function addCoreFormats() {
                    forEach(CoreParsingFormats, function (df) {
                        var src = df.src;
                        if (df.mdy && loc.mdy) {
                            // Use the mm/dd/yyyy variant if it
                            // exists and the locale requires it
                            src = df.mdy;
                        }
                        if (df.time) {
                            // Core formats that allow time require the time
                            // reg on both sides, so add both versions here.
                            loc.addFormat(getFormatWithTime(src, true));
                            loc.addFormat(getFormatWithTime(src));
                        } else {
                            loc.addFormat(src);
                        }
                    });
                    loc.addFormat('{time}');
                }

                function addLocaleFormats() {
                    addFormatSet('parse');
                    addFormatSet('timeParse', true);
                    addFormatSet('timeFrontParse', true, true);
                }

                function addFormatSet(field, allowTime, timeFront) {
                    forEach(loc[field], function (format) {
                        if (allowTime) {
                            format = getFormatWithTime(format, timeFront);
                        }
                        loc.addFormat(format);
                    });
                }

                function getFormatWithTime(baseFormat, timeBefore) {
                    if (timeBefore) {
                        return getTimeBefore() + baseFormat;
                    }
                    return baseFormat + getTimeAfter();
                }

                function getTimeBefore() {
                    return getRegNonCapturing('{time}[,\\s\\u3000]', true);
                }

                function getTimeAfter() {
                    var markers = ',?[\\s\\u3000]', localized;
                    localized = arrayToRegAlternates(loc.timeMarkers);
                    if (localized) {
                        markers += '| (?:' + localized + ') ';
                    }
                    markers = getRegNonCapturing(markers, loc.timeMarkerOptional);
                    return getRegNonCapturing(markers + '{time}', true);
                }

                initFormats();
                initDefinition();
                initArrayFields();

                buildValueArray('month', 12);
                buildValueArray('weekday', 7);
                buildValueArray('unit', 8);
                buildValueArray('ampm', 2);

                buildNumerals();
                buildTimeFormats();
                buildParsingTokens();
                buildTimeSuffixes();
                buildModifiers();

                // The order of these formats is important. Order is reversed so formats
                // that are initialized later will take precedence. Generally, this means
                // that more specific formats should come later.
                addCoreFormats();
                addLocaleFormats();

            }

        };

        return new Locale(def);
    }


    /***
     * @method [units]Since(d, [options])
     * @returns Number
     * @short Returns the time since [d].
     * @extra [d] will accept a date object, timestamp, or string. If not specified,
     *        [d] is assumed to be now. `unitsAgo` is provided as an alias to make
     *        this more readable when [d] is assumed to be the current date.
     *        [options] can be an object or a locale code as a string. See `create`
     *        for more.
     *
     * @set
     *   millisecondsSince
     *   secondsSince
     *   minutesSince
     *   hoursSince
     *   daysSince
     *   weeksSince
     *   monthsSince
     *   yearsSince
     *
     * @example
     *
     *   new Date().millisecondsSince('1 hour ago') -> 3,600,000
     *   new Date().daysSince('1 week ago')         -> 7
     *   new Date().yearsSince('15 years ago')      -> 15
     *   lastYear.yearsAgo()                 -> 1
     *
     * @param {string|number|Date} d
     * @param {DateCreateOptions} options
     *
     ***
     * @method [units]Ago()
     * @returns Number
     * @short Returns the time ago in the appropriate unit.
     *
     * @set
     *   millisecondsAgo
     *   secondsAgo
     *   minutesAgo
     *   hoursAgo
     *   daysAgo
     *   weeksAgo
     *   monthsAgo
     *   yearsAgo
     *
     * @example
     *
     *   lastYear.millisecondsAgo() -> 3,600,000
     *   lastYear.daysAgo()         -> 7
     *   lastYear.yearsAgo()        -> 15
     *
     ***
     * @method [units]Until([d], [options])
     * @returns Number
     * @short Returns the time until [d].
     * @extra [d] will accept a date object, timestamp, or string. If not specified,
     *        [d] is assumed to be now. `unitsFromNow` is provided as an alias to
     *        make this more readable when [d] is assumed to be the current date.
     *        [options] can be an object or a locale code as a string. See `create`
     *        for more.
     *
     *
     * @set
     *   millisecondsUntil
     *   secondsUntil
     *   minutesUntil
     *   hoursUntil
     *   daysUntil
     *   weeksUntil
     *   monthsUntil
     *   yearsUntil
     *
     * @example
     *
     *   new Date().millisecondsUntil('1 hour from now') -> 3,600,000
     *   new Date().daysUntil('1 week from now')         -> 7
     *   new Date().yearsUntil('15 years from now')      -> 15
     *   nextYear.yearsFromNow()                  -> 1
     *
     * @param {string|number|Date} d
     * @param {DateCreateOptions} options
     *
     ***
     * @method [units]FromNow()
     * @returns Number
     * @short Returns the time from now in the appropriate unit.
     *
     * @set
     *   millisecondsFromNow
     *   secondsFromNow
     *   minutesFromNow
     *   hoursFromNow
     *   daysFromNow
     *   weeksFromNow
     *   monthsFromNow
     *   yearsFromNow
     *
     * @example
     *
     *   nextYear.millisecondsFromNow() -> 3,600,000
     *   nextYear.daysFromNow()         -> 7
     *   nextYear.yearsFromNow()        -> 15
     *
     ***
     * @method add[Units](n, [reset] = false)
     * @returns Date
     * @short Adds `n` units to the date. If [reset] is true, all lower units will
     *        be reset.
     * @extra This method modifies the date! Note that in the case of `addMonths`,
     *        the date may fall on a date that doesn't exist (i.e. February 30). In
     *        this case the date will be shifted to the last day of the month. Don't
     *        use `addMonths` if you need precision.
     *
     * @set
     *   addMilliseconds
     *   addSeconds
     *   addMinutes
     *   addHours
     *   addDays
     *   addWeeks
     *   addMonths
     *   addYears
     *
     * @example
     *
     *   new Date().addYears(5)        -> current time + 5 years
     *   new Date().addDays(5)         -> current time + 5 days
     *   new Date().addDays(5, true)   -> current time + 5 days (time reset)
     *
     * @param {number} n
     * @param {boolean} [reset]
     *
     ***
     * @method isLast[Unit]([localeCode])
     * @returns Boolean
     * @short Returns true if the date is last week, month, or year.
     * @extra This method takes an optional locale code for `isLastWeek`, which is
     *        locale dependent. The default locale code is `en`, which places
     *        Sunday at the beginning of the week. You can pass `en-GB` as a quick
     *        way to force Monday as the beginning of the week.
     *
     * @set
     *   isLastWeek
     *   isLastMonth
     *   isLastYear
     *
     * @example
     *
     *   yesterday.isLastWeek()  -> true or false?
     *   yesterday.isLastMonth() -> probably not...
     *   yesterday.isLastYear()  -> even less likely...
     *
     * @param {string} [localeCode]
     *
     ***
     * @method isThis[Unit]([localeCode])
     * @returns Boolean
     * @short Returns true if the date is this week, month, or year.
     * @extra This method takes an optional locale code for `isThisWeek`, which is
     *        locale dependent. The default locale code is `en`, which places
     *        Sunday at the beginning of the week. You can pass `en-GB` as a quick
     *        way to force Monday as the beginning of the week.
     *
     * @set
     *   isThisWeek
     *   isThisMonth
     *   isThisYear
     *
     * @example
     *
     *   tomorrow.isThisWeek()  -> true or false?
     *   tomorrow.isThisMonth() -> probably...
     *   tomorrow.isThisYear()  -> signs point to yes...
     *
     * @param {string} [localeCode]
     *
     ***
     * @method isNext[Unit]([localeCode])
     * @returns Boolean
     * @short Returns true if the date is next week, month, or year.
     * @extra This method takes an optional locale code for `isNextWeek`, which is
     *        locale dependent. The default locale code is `en`, which places
     *        Sunday at the beginning of the week. You can pass `en-GB` as a quick
     *        way to force Monday as the beginning of the week.
     *
     * @set
     *   isNextWeek
     *   isNextMonth
     *   isNextYear
     *
     * @example
     *
     *   tomorrow.isNextWeek()  -> true or false?
     *   tomorrow.isNextMonth() -> probably not...
     *   tomorrow.isNextYear()  -> even less likely...
     *
     * @param {string} [localeCode]
     *
     ***
     * @method beginningOf[Unit]([localeCode])
     * @returns Date
     * @short Sets the date to the beginning of the appropriate unit.
     * @extra This method modifies the date! A locale code can be passed for
     *        `beginningOfWeek`, which is locale dependent. If consistency is
     *        needed, use `beginningOfISOWeek` instead.
     *
     * @set
     *   beginningOfDay
     *   beginningOfWeek
     *   beginningOfMonth
     *   beginningOfYear
     *
     * @example
     *
     *   new Date().beginningOfDay()   -> the beginning of today (resets the time)
     *   new Date().beginningOfWeek()  -> the beginning of the week
     *   new Date().beginningOfMonth() -> the beginning of the month
     *   new Date().beginningOfYear()  -> the beginning of the year
     *
     * @param {string} [localeCode]
     *
     ***
     * @method endOf[Unit]([localeCode])
     * @returns Date
     * @short Sets the date to the end of the appropriate unit.
     * @extra This method modifies the date! A locale code can be passed for
     *        `endOfWeek`, which is locale dependent. If consistency is needed, use
     *        `endOfISOWeek` instead.
     *
     * @set
     *   endOfDay
     *   endOfWeek
     *   endOfMonth
     *   endOfYear
     *
     * @example
     *
     *   new Date().endOfDay()   -> the end of today (sets the time to 23:59:59.999)
     *   new Date().endOfWeek()  -> the end of the week
     *   new Date().endOfMonth() -> the end of the month
     *   new Date().endOfYear()  -> the end of the year
     *
     * @param {string} [localeCode]
     *
     ***/
    function buildDateUnitMethods() {

        defineInstanceSimilar(sugarDate, DateUnits, function (methods, unit, index) {
            var name = unit.name, caps = simpleCapitalize(name);

            if (index > DAY_INDEX) {
                forEach(['Last', 'This', 'Next'], function (shift) {
                    methods['is' + shift + caps] = function (d, localeCode) {
                        return compareDate(d, shift + ' ' + name, 0, localeCode, {locale: 'en'});
                    };
                });
            }
            if (index > HOURS_INDEX) {
                methods['beginningOf' + caps] = function (d, localeCode) {
                    return moveToBeginningOfUnit(d, index, localeCode);
                };
                methods['endOf' + caps] = function (d, localeCode) {
                    return moveToEndOfUnit(d, index, localeCode);
                };
            }

            methods['add' + caps + 's'] = function (d, num, reset) {
                return advanceDate(d, name, num, reset);
            };

            var since = function (date, d, options) {
                return getTimeDistanceForUnit(date, createDateWithContext(date, d, options, true), unit);
            };
            var until = function (date, d, options) {
                return getTimeDistanceForUnit(createDateWithContext(date, d, options, true), date, unit);
            };

            methods[name + 'sAgo'] = methods[name + 'sUntil'] = until;
            methods[name + 'sSince'] = methods[name + 'sFromNow'] = since;

        });

    }

    /***
     * @method is[Day]()
     * @returns Boolean
     * @short Returns true if the date falls on the specified day.
     *
     * @set
     *   isToday
     *   isYesterday
     *   isTomorrow
     *   isWeekday
     *   isWeekend
     *   isSunday
     *   isMonday
     *   isTuesday
     *   isWednesday
     *   isThursday
     *   isFriday
     *   isSaturday
     *
     * @example
     *
     *   tomorrow.isToday() -> false
     *   thursday.isTomorrow() -> ?
     *   yesterday.isWednesday() -> ?
     *   today.isWeekend() -> ?
     *
     ***
     * @method isFuture()
     * @returns Boolean
     * @short Returns true if the date is in the future.
     *
     * @example
     *
     *   lastWeek.isFuture() -> false
     *   nextWeek.isFuture() -> true
     *
     ***
     * @method isPast()
     * @returns Boolean
     * @short Returns true if the date is in the past.
     *
     * @example
     *
     *   lastWeek.isPast() -> true
     *   nextWeek.isPast() -> false
     *
     ***/
    function buildRelativeAliases() {
        var special = spaceSplit('Today Yesterday Tomorrow Weekday Weekend Future Past');
        var weekdays = English.weekdays.slice(0, 7);
        var months = English.months.slice(0, 12);
        var together = special.concat(weekdays).concat(months);
        defineInstanceSimilar(sugarDate, together, function (methods, name) {
            methods['is' + name] = function (d) {
                return fullCompareDate(d, name);
            };
        });
    }

    defineStatic(sugarDate, {

        /***
         * @method create(d, [options])
         * @returns Date
         * @static
         * @short Alternate date constructor which accepts text formats, a timestamp,
         *        objects, or another date.
         * @extra If no argument is given, the date is assumed to be now. The second
         *        argument can either be an options object or a locale code as a
         *        shortcut. For more, see `date parsing`.
         *
         * @options
         *
         *   locale     A locale code to parse the date in. This can also be passed as
         *              the second argument to this method. Default is the current
         *              locale, which is `en` if none is set.
         *
         *   past       If `true`, ambiguous dates like `Sunday` will be parsed as
         *              `last Sunday`. Note that non-ambiguous dates are not
         *              guaranteed to be in the past.
         *              Default is `false`.
         *
         *   future     If `true`, ambiguous dates like `Sunday` will be parsed as
         *              `next Sunday`. Note that non-ambiguous dates are not
         *              guaranteed to be in the future.
         *              Default is `false`.
         *
         *   fromUTC    If `true`, dates with no timezone notation will be parsed as
         *              UTC (no timezone offset). This is useful for server
         *              timestamps, etc. Note that this flag is not required if the
         *              timezone is specified in the string, either as an explicit
         *              value (ex. +0900 or -09:00) or "Z", which is UTC time.
         *
         *   setUTC     If `true`, this will set a flag on the date that tells Sugar
         *              to internally use UTC methods like `getUTCHours` when handling
         *              it. This flag is the same as calling the `setUTC` method on
         *              the date after parsing is complete. Note that this is
         *              different from `fromUTC`, which parses a string as UTC, but
         *              does not set this flag.
         *
         *   clone      If `true` and `d` is a date, it will be cloned.
         *
         *   params     An optional object that is populated with properties that are
         *              parsed from string input. This option is useful when parsed
         *              properties need to be retained.
         *
         * @example
         *
         *   Date.create('July')                      -> July of this year
         *   Date.create('1776')                      -> 1776
         *   Date.create('today')                     -> today
         *   Date.create('Wednesday')                 -> This wednesday
         *   Date.create('next Friday')               -> Next friday
         *   Date.create('July 4, 1776')              -> July 4, 1776
         *   Date.create(-446806800000)               -> November 5, 1955
         *   Date.create('1776年07月04日', 'ja')      -> July 4, 1776
         *   Date.create('August', {past: true})      -> August of this or last year
         *   Date.create('August', {future: true})    -> August of this or next year
         *   Date.create('Thursday', {fromUTC: true}) -> Thursday at 12:00am UTC time
         *
         * @param {string|number|Date} d
         * @param {DateCreateOptions} [options]
         *
         * @option {string} [locale]
         * @option {boolean} [past]
         * @option {boolean} [future]
         * @option {boolean} [fromUTC]
         * @option {boolean} [setUTC]
         * @option {boolean} [clone]
         * @option {Object} [params]
         *
         ***/
        'create': function (d, options) {
            return createDate(d, options);
        },

        /***
         * @method getLocale([localeCode] = current)
         * @returns Locale
         * @static
         * @short Gets the locale object for the given code, or the current locale.
         * @extra The locale object has various properties that dictate how dates are
         *        parsed and formatted for that locale. The locale object is exposed
         *        here mostly for introspection - it should be uncommon to need to
         *        maniplate the object itself. For more, see `date locales`.
         *
         * @example
         *
         *   Date.getLocale()     -> Returns the current locale
         *   Date.getLocale('en') -> Returns the EN locale
         *
         * @param {string} [localeCode]
         *
         ***/
        'getLocale': function (code) {
            return localeManager.get(code, !code);
        },

        /***
         * @method getAllLocales()
         * @returns Array<Locale>
         * @static
         * @short Returns all available locales as an object.
         * @extra For more, see `date locales`.
         * @example
         *
         *   Date.getAllLocales()
         *
         ***/
        'getAllLocales': function () {
            return localeManager.getAll();
        },

        /***
         * @method getAllLocaleCodes()
         * @returns string[]
         * @static
         * @short Returns all available locale codes as an array of strings.
         * @extra For more, see `date locales`.
         * @example
         *
         *   Date.getAllLocaleCodes()
         *
         ***/
        'getAllLocaleCodes': function () {
            return getKeys(localeManager.getAll());
        },

        /***
         * @method setLocale(localeCode)
         * @returns Locale
         * @static
         * @short Sets the current locale to be used with dates.
         * @extra Sugar has native support for 17 major locales. In addition, you can
         *        define a new locale with `addLocale`. For more, see `date locales`.
         * @example
         *
         *   Date.setLocale('en')
         *
         * @param {string} localeCode
         *
         ***/
        'setLocale': function (code) {
            return localeManager.set(code);
        },

        /***
         * @method addLocale(localeCode, def)
         * @returns Locale
         * @static
         * @short Adds a locale definition to the locales understood by Sugar.
         * @extra This method should only be required for adding locale definitions
         *        that don't already exist. For more, see `date locales`.
         * @example
         *
         *   Date.addLocale('eo', {})
         *
         * @param {string} localeCode
         * @param {Object} def
         *
         ***/
        'addLocale': function (code, set) {
            return localeManager.add(code, set);
        },

        /***
         * @method removeLocale(localeCode)
         * @returns Locale
         * @static
         * @short Deletes the the locale by `localeCode` from Sugar's known locales.
         * @extra For more, see `date locales`.
         * @example
         *
         *   Date.removeLocale('foo')
         *
         * @param {string} localeCode
         *
         ***/
        'removeLocale': function (code) {
            return localeManager.remove(code);
        }

    });

    defineInstanceWithArguments(sugarDate, {

        /***
         * @method set(set, [reset] = false)
         * @returns Date
         * @short Sets the date object.
         * @extra This method accepts multiple formats including a single number as
         *        a timestamp, an object, or enumerated arguments. If [reset] is
         *        `true`, any units more specific than those passed will be reset.
         *
         * @example
         *
         *   new Date().set({year:2011,month:11,day:31}) -> December 31, 2011
         *   new Date().set(2011, 11, 31)                -> December 31, 2011
         *   new Date().set(86400000)                    -> 1 day after Jan 1, 1970
         *   new Date().set({year:2004,month:6}, true)   -> June 1, 2004, 00:00:00.000
         *
         * @signature set(milliseconds)
         * @signature set(year, month, [day], [hour], [minute], [second], [millliseconds])
         * @param {Object} set
         * @param {boolean} [reset]
         * @param {number} year
         * @param {number} month
         * @param {number} [day]
         * @param {number} [hour]
         * @param {number} [minute]
         * @param {number} [second]
         * @param {number} [milliseconds]
         *
         ***/
        'set': function (d, args) {
            args = collectDateArguments(args);
            return updateDate(d, args[0], args[1]);
        },

        /***
         * @method advance(set, [reset] = false)
         * @returns Date
         * @short Shifts the date forward.
         * @extra `set` accepts multiple formats including an object, a string in the
         *        format "3 days", a single number as milliseconds, or enumerated
         *        parameters (as with the Date constructor). If [reset] is `true`, any
         *        units more specific than those passed will be reset. This method
         *        modifies the date!
         *
         * @example
         *
         *   new Date().advance({ year: 2 }) -> 2 years in the future
         *   new Date().advance('2 hours')   -> 2 hours in the future
         *   new Date().advance(0, 2, 3)     -> 2 months 3 days in the future
         *   new Date().advance(86400000)    -> 1 day in the future
         *
         * @signature advance(milliseconds)
         * @signature advance(year, month, [day], [hour], [minute], [second], [millliseconds])
         * @param {string|Object} set
         * @param {boolean} [reset]
         * @param {number} year
         * @param {number} month
         * @param {number} [day]
         * @param {number} [hour]
         * @param {number} [minute]
         * @param {number} [second]
         * @param {number} [milliseconds]
         *
         ***/
        'advance': function (d, args) {
            return advanceDateWithArgs(d, args, 1);
        },

        /***
         * @method rewind(set, [reset] = false)
         * @returns Date
         * @short Shifts the date backward.
         * @extra [set] accepts multiple formats including an object, a string in the
         *        format "3 days", a single number as milliseconds, or enumerated
         *        parameters (as with the Date constructor). If [reset] is `true`, any
         *        units more specific than those passed will be reset. This method
         *        modifies the date!
         *
         * @example
         *
         *   new Date().rewind({ year: 2 }) -> 2 years in the past
         *   new Date().rewind('2 weeks')   -> 2 weeks in the past
         *   new Date().rewind(0, 2, 3)     -> 2 months 3 days in the past
         *   new Date().rewind(86400000)    -> 1 day in the past
         *
         * @signature advance(milliseconds)
         * @signature advance(year, month, [day], [hour], [minute], [second], [millliseconds])
         * @param {string|Object} set
         * @param {boolean} [reset]
         * @param {number} year
         * @param {number} month
         * @param {number} [day]
         * @param {number} [hour]
         * @param {number} [minute]
         * @param {number} [second]
         * @param {number} [milliseconds]
         *
         ***/
        'rewind': function (d, args) {
            return advanceDateWithArgs(d, args, -1);
        }

    });

    defineInstance(sugarDate, {

        /***
         * @method get(d, [options])
         * @returns Date
         * @short Gets a new date using the current one as a starting point.
         * @extra This method is identical to `Date.create`, except that relative
         *        formats like `next month` are relative to the date instance rather
         *        than the current date. Accepts a locale code as a string in place
         *        of [options]. See `create` for more.
         *
         * @example
         *
         *   nextYear.get('monday') -> monday of the week exactly 1 year from now
         *   millenium.get('2 years before') -> 2 years before Jan 1, 2000.
         *
         * @param {string|number|Date} d
         * @param {DateCreateOptions} options
         *
         ***/
        'get': function (date, d, options) {
            return createDateWithContext(date, d, options);
        },

        /***
         * @method setWeekday(dow)
         * @short Sets the weekday of the date, starting with Sunday at `0`.
         * @extra This method modifies the date!
         *
         * @example
         *
         *   d = new Date(); d.setWeekday(1); d; -> Monday of this week
         *   d = new Date(); d.setWeekday(6); d; -> Saturday of this week
         *
         * @param {number} dow
         *
         ***/
        'setWeekday': function (date, dow) {
            return setWeekday(date, dow);
        },

        /***
         * @method setISOWeek(num)
         * @short Sets the week (of the year) as defined by the ISO8601 standard.
         * @extra Note that this standard places Sunday at the end of the week (day 7).
         *        This method modifies the date!
         *
         * @example
         *
         *   d = new Date(); d.setISOWeek(15); d; -> 15th week of the year
         *
         * @param {number} num
         *
         ***/
        'setISOWeek': function (date, num) {
            return setISOWeekNumber(date, num);
        },

        /***
         * @method getISOWeek()
         * @returns Number
         * @short Gets the date's week (of the year) as defined by the ISO8601 standard.
         * @extra Note that this standard places Sunday at the end of the week (day 7).
         *        If `utc` is set on the date, the week will be according to UTC time.
         *
         * @example
         *
         *   new Date().getISOWeek() -> today's week of the year
         *
         ***/
        'getISOWeek': function (date) {
            return getWeekNumber(date, true);
        },

        /***
         * @method beginningOfISOWeek()
         * @returns Date
         * @short Set the date to the beginning of week as defined by ISO8601.
         * @extra Note that this standard places Monday at the start of the week.
         *        This method modifies the date!
         *
         * @example
         *
         *   new Date().beginningOfISOWeek() -> Monday
         *
         ***/
        'beginningOfISOWeek': function (date) {
            var day = getWeekday(date);
            if (day === 0) {
                day = -6;
            } else if (day !== 1) {
                day = 1;
            }
            setWeekday(date, day);
            return resetTime(date);
        },

        /***
         * @method endOfISOWeek()
         * @returns Date
         * @short Set the date to the end of week as defined by this ISO8601 standard.
         * @extra Note that this standard places Sunday at the end of the week.
         *        This method modifies the date!
         *
         * @example
         *
         *   new Date().endOfISOWeek() -> Sunday
         *
         ***/
        'endOfISOWeek': function (date) {
            if (getWeekday(date) !== 0) {
                setWeekday(date, 7);
            }
            return moveToEndOfUnit(date, DAY_INDEX);
        },

        /***
         * @method getUTCOffset([iso] = false)
         * @returns String
         * @short Returns a string representation of the offset from UTC time. If [iso]
         *        is true the offset will be in ISO8601 format.
         *
         * @example
         *
         *   new Date().getUTCOffset()     -> "+0900"
         *   new Date().getUTCOffset(true) -> "+09:00"
         *
         * @param {boolean} iso
         *
         ***/
        'getUTCOffset': function (date, iso) {
            return getUTCOffset(date, iso);
        },

        /***
         * @method setUTC([on] = false)
         * @returns Date
         * @short Controls a flag on the date that tells Sugar to internally use UTC
         *        methods like `getUTCHours`.
         * @extra This flag is most commonly used for output in UTC time with the
         *        `format` method. Note that this flag only governs which methods are
         *        called internally – date native methods like `setHours` will still
         *        return local non-UTC values. This method will modify the date!
         *
         * @example
         *
         *   new Date().setUTC(true).long()  -> formatted with UTC methods
         *   new Date().setUTC(false).long() -> formatted without UTC methods
         *
         * @param {boolean} on
         *
         ***/
        'setUTC': function (date, on) {
            return _utc(date, on);
        },

        /***
         * @method isUTC()
         * @returns Boolean
         * @short Returns true if the date has no timezone offset.
         * @extra This will also return true for dates whose internal utc flag is set
         *        with `setUTC`. Even if the utc flag is set, `getTimezoneOffset`
         *        will always report the same thing as Javascript always reports that
         *        based on the environment's locale.
         *
         * @example
         *
         *   new Date().isUTC() -> true or false (depends on the local offset)
         *   new Date().setUTC(true).isUTC() -> true
         *
         ***/
        'isUTC': function (date) {
            return isUTC(date);
        },

        /***
         * @method isValid()
         * @returns Boolean
         * @short Returns true if the date is valid.
         *
         * @example
         *
         *   new Date().isValid()         -> true
         *   new Date('flexor').isValid() -> false
         *
         ***/
        'isValid': function (date) {
            return dateIsValid(date);
        },

        /***
         * @method isAfter(d, [margin] = 0)
         * @returns Boolean
         * @short Returns true if the date is after `d`.
         * @extra [margin] is to allow extra margin of error in ms. `d` will accept
         *        a date object, timestamp, or string. If not specified, `d` is
         *        assumed to be now. See `create` for formats.
         *
         * @example
         *
         *   today.isAfter('tomorrow')  -> false
         *   today.isAfter('yesterday') -> true
         *
         * @param {string|number|Date} d
         * @param {number} [margin]
         *
         ***/
        'isAfter': function (date, d, margin) {
            return date.getTime() > createDate(d).getTime() - (margin || 0);
        },

        /***
         * @method isBefore(d, [margin] = 0)
         * @returns Boolean
         * @short Returns true if the date is before `d`.
         * @extra [margin] is to allow extra margin of error in ms. `d` will accept
         *        a date object, timestamp, or text format. If not specified, `d` is
         *        assumed to be now. See `create` for formats.
         *
         * @example
         *
         *   today.isBefore('tomorrow')  -> true
         *   today.isBefore('yesterday') -> false
         *
         * @param {string|number|Date} d
         * @param {number} [margin]
         *
         ***/
        'isBefore': function (date, d, margin) {
            return date.getTime() < createDate(d).getTime() + (margin || 0);
        },

        /***
         * @method isBetween(d1, d2, [margin] = 0)
         * @returns Boolean
         * @short Returns true if the date is later or equal to `d1` and before or
         *        equal to `d2`.
         * @extra [margin] is to allow extra margin of error in ms. `d1` and `d2` will
         *        accept a date object, timestamp, or text format. If not specified,
         *        they are assumed to be now.  See `create` for formats.
         *
         * @example
         *
         *   new Date().isBetween('yesterday', 'tomorrow')    -> true
         *   new Date().isBetween('last year', '2 years ago') -> false
         *
         * @param {string|number|Date} d1
         * @param {string|number|Date} d2
         * @param {number} [margin]
         *
         ***/
        'isBetween': function (date, d1, d2, margin) {
            var t = date.getTime();
            var t1 = createDate(d1).getTime();
            var t2 = createDate(d2).getTime();
            var lo = min(t1, t2);
            var hi = max(t1, t2);
            margin = margin || 0;
            return (lo - margin <= t) && (hi + margin >= t);
        },

        /***
         * @method isLeapYear()
         * @returns Boolean
         * @short Returns true if the date is a leap year.
         *
         * @example
         *
         *   millenium.isLeapYear() -> true
         *
         ***/
        'isLeapYear': function (date) {
            var year = getYear(date);
            return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        },

        /***
         * @method daysInMonth()
         * @returns Number
         * @short Returns the number of days in the date's month.
         *
         * @example
         *
         *   may.daysInMonth() -> 31
         *   feb.daysInMonth() -> 28 or 29
         *
         ***/
        'daysInMonth': function (date) {
            return getDaysInMonth(date);
        },

        /***
         * @method format([f], [localeCode] = currentLocaleCode)
         * @returns String
         * @short Returns the date as a string using the format `f`.
         * @extra `f` is a string that contains tokens in either LDML format using
         *        curly braces, or "strftime" format using a percent sign. If `f` is
         *        not specified, the locale specific `{long}` format is used. [localeCode]
         *        is a locale code to use (if not specified the current locale is
         *        used). For more, see `date formatting`.
         *
         * @example
         *
         *   new Date().format()                        -> ex. February 13, 2012 11:21 AM
         *   new Date().format('{Weekday} {d} {Month}') -> ex. Monday July 4
         *   new Date().format('{hh}:{mm}')             -> ex. 15:57
         *   new Date().format('%H:%M')                 -> ex. 15:57
         *   new Date().format('{12hr}:{mm}{tt}')       -> ex. 3:57pm
         *   new Date().format('ISO8601')               -> ex. 2011-07-05 12:24:55.528Z
         *   new Date().format('{Weekday}', 'ja')       -> ex. 先週
         *
         * @param {string} f
         * @param {string} [localeCode]
         *
         ***
         * @method short([localeCode] = currentLocaleCode)
         * @returns String
         * @short Outputs the date in the short format for the current locale.
         * @extra [localeCode] overrides the current locale code if passed.
         *
         * @example
         *
         *   new Date().short()     -> ex. 02/13/2016
         *   new Date().short('fi') -> ex. 13.2.2016
         *
         * @param {string} [localeCode]
         *
         ***
         * @method medium([localeCode] = currentLocaleCode)
         * @returns String
         * @short Outputs the date in the medium format for the current locale.
         * @extra [localeCode] overrides the current locale code if passed.
         *
         * @example
         *
         *   new Date().medium()     -> ex. February 13, 2016
         *   new Date().medium('ja') -> ex. 2016年2月13日
         *
         * @param {string} [localeCode]
         *
         ***
         * @method long([localeCode] = currentLocaleCode)
         * @returns String
         * @short Outputs the date in the long format for the current locale.
         * @extra [localeCode] overrides the current locale code if passed.
         *
         * @example
         *
         *   new Date().long()     -> ex. February 13, 2016 6:22 PM
         *   new Date().long('es') -> ex. 13 de febrero de 2016 18:22
         *
         * @param {string} [localeCode]
         *
         ***
         * @method full([localeCode] = currentLocaleCode)
         * @returns String
         * @short Outputs the date in the full format for the current locale.
         * @extra [localeCode] overrides the current locale code if passed.
         *
         * @example
         *
         *   new Date().full()     -> ex. Saturday, February 13, 2016 6:23 PM
         *   new Date().full('ru') -> ex. суббота, 13 февраля 2016 г., 18:23
         *
         * @param {string} [localeCode]
         *
         ***/
        'format': function (date, f, localeCode) {
            return dateFormat(date, f, localeCode);
        },

        /***
         * @method relative([localeCode] = currentLocaleCode, [fn])
         * @returns String
         * @short Returns the date in a text format relative to the current time,
         *        such as "5 minutes ago".
         * @extra [fn] is a function that can be passed to provide more granular
         *        control over the resulting string. Its return value will be passed
         *        to `format`. If nothing is returned, the relative format will be
         *        used. [fn] may be passed as the first argument in place of [locale].
         *        For more about formats, see `date formatting`.
         *
         * @callback relativeFn
         *
         *   num   The offset number in `unit`.
         *   unit  A numeric representation of the unit that `num` is in, starting at
         *         0 for ms.
         *   ms    The absolute offset in milliseconds.
         *   loc   The locale object, either specified by [locale] or default.
         *
         * @example
         *
         *   hourAgo.relative() -> 1 hour ago
         *   jan.relative()     -> ex. 5 months ago
         *   jan.relative('ja') -> 3ヶ月前
         *   jan.relative(function(num, unit, ms, loc) {
         *     // Return an absolute date for anything over 6 months.
         *     if(unit == 6 && num > 6 || unit > 6) {
         *       return '{Month} {d}, {yyyy}';
         *     }
         *   }); -> ex. 5 months ago
         *
         * @signature relative([fn])
         * @param {string} [localeCode]
         * @param {relativeFn} [fn]
         * @callbackParam {number} num
         * @callbackParam {number} unit
         * @callbackParam {number} ms
         * @callbackParam {Locale} loc
         * @callbackReturns {string} relativeFn
         *
         ***/
        'relative': function (date, localeCode, fn) {
            return dateRelative(date, null, localeCode, fn);
        },

        /***
         * @method relativeTo(d, [localeCode] = currentLocaleCode)
         * @returns String
         * @short Returns the date in a text format relative to `d`, such as
         *        "5 minutes".
         * @extra `d` will accept a date object, timestamp, or string. [localeCode]
         *        applies to the method output, not `d`.
         *
         * @example
         *
         *   jan.relativeTo(jul)                 -> 5 months
         *   yesterday.relativeTo('today', 'ja') -> 一日
         *
         * @param {string|number|Date} d
         * @param {string} localeCode
         *
         *
         ***/
        'relativeTo': function (date, d, localeCode) {
            return dateRelative(date, createDate(d), localeCode);
        },

        /***
         * @method is(d, [margin] = 0)
         * @returns Boolean
         * @short Returns true if the date matches `d`.
         * @extra `d` will accept a date object, timestamp, or text format. In the
         *        case of objects and text formats, `is` will additionally compare
         *        based on the precision implied in the input. In the case of text
         *        formats `d` will use the currently set locale. [margin] allows an
         *        extra margin of error in milliseconds. See `create` for formats.
         *
         * @example
         *
         *   new Date().is('July')               -> true or false?
         *   new Date().is('1776')               -> false
         *   new Date().is('today')              -> true
         *   new Date().is('weekday')            -> true or false?
         *   new Date().is('July 4, 1776')       -> false
         *   new Date().is(-6106093200000)       -> false
         *   new Date().is(new Date(1776, 6, 4)) -> false
         *
         * @param {string|number|Date} d
         * @param {number} [margin]
         *
         ***/
        'is': function (date, d, margin) {
            return fullCompareDate(date, d, margin);
        },

        /***
         * @method reset([unit] = 'day', [localeCode] = currentLocaleCode)
         * @returns Date
         * @short Resets the date to the beginning of [unit].
         * @extra This method effectively resets all smaller units, pushing the date
         *        to the beginning of [unit]. Default is `day`, which effectively
         *        resets the time. [localeCode] is provided for resetting weeks, which
         *        is locale dependent. This method modifies the date!
         *
         * @example
         *
         *   new Date().reset('day')   -> Beginning of the day
         *   new Date().reset('month') -> Beginning of the month
         *
         * @param {string} [unit]
         * @param {string} [localeCode]
         *
         ***/
        'reset': function (date, unit, localeCode) {
            var unitIndex = unit ? getUnitIndexForParamName(unit) : DAY_INDEX;
            moveToBeginningOfUnit(date, unitIndex, localeCode);
            return date;
        },

        /***
         * @method clone()
         * @returns Date
         * @short Clones the date.
         * @extra Note that the UTC flag will be preserved if set. This flag is
         *        set via the `setUTC` method or an option on `Date.create`.
         *
         * @example
         *
         *   new Date().clone() -> Copy of now
         *
         ***/
        'clone': function (date) {
            return cloneDate(date);
        },

        /***
         * @method iso()
         * @alias toISOString
         *
         ***/
        'iso': function (date) {
            return date.toISOString();
        },

        /***
         * @method getWeekday()
         * @returns Number
         * @short Alias for `getDay`.
         *
         * @example
         *
         *   new Date().getWeekday();    -> (ex.) 3
         *
         ***/
        'getWeekday': function (date) {
            return getWeekday(date);
        },

        /***
         * @method getUTCWeekday()
         * @returns Number
         * @short Alias for `getUTCDay`.
         *
         * @example
         *
         *   new Date().getUTCWeekday(); -> (ex.) 3
         *
         ***/
        'getUTCWeekday': function (date) {
            return date.getUTCDay();
        }

    });


    /*** @namespace Number ***/

    /***
     * @method [dateUnit]()
     * @returns Number
     * @short Takes the number as a unit of time and converts to milliseconds.
     * @extra Method names can be singular or plural.  Note that as "a month" is
     *        ambiguous as a unit of time, `months` will be equivalent to 30.4375
     *        days, the average number in a month. Be careful using `months` if you
     *        need exact precision.
     *
     * @set
     *   millisecond
     *   milliseconds
     *   second
     *   seconds
     *   minute
     *   minutes
     *   hour
     *   hours
     *   day
     *   days
     *   week
     *   weeks
     *   month
     *   months
     *   year
     *   years
     *
     * @example
     *
     *   (5).milliseconds() -> 5
     *   (10).hours()       -> 36000000
     *   (1).day()          -> 86400000
     *
     ***
     * @method [dateUnit]Before(d, [options])
     * @returns Date
     * @short Returns a date that is `n` units before [d], where `n` is the number.
     * @extra [d] will accept a date object, timestamp, or text format. Note that
     *        "months" is ambiguous as a unit of time. If the target date falls on a
     *        day that does not exist (i.e. August 31 -> February 31), the date will
     *        be shifted to the last day of the month. Be careful using
     *        `monthsBefore` if you need exact precision. [options] can be an object
     *        or a locale code as a string. See `create` for more.
     *
     *
     * @set
     *   millisecondBefore
     *   millisecondsBefore
     *   secondBefore
     *   secondsBefore
     *   minuteBefore
     *   minutesBefore
     *   hourBefore
     *   hoursBefore
     *   dayBefore
     *   daysBefore
     *   weekBefore
     *   weeksBefore
     *   monthBefore
     *   monthsBefore
     *   yearBefore
     *   yearsBefore
     *
     * @example
     *
     *   (5).daysBefore('tuesday')          -> 5 days before tuesday of this week
     *   (1).yearBefore('January 23, 1997') -> January 23, 1996
     *
     * @param {string|number|Date} d
     * @param {DateCreateOptions} options
     *
     ***
     * @method [dateUnit]Ago()
     * @returns Date
     * @short Returns a date that is `n` units ago.
     * @extra Note that "months" is ambiguous as a unit of time. If the target date
     *        falls on a day that does not exist (i.e. August 31 -> February 31), the
     *        date will be shifted to the last day of the month. Be careful using
     *        `monthsAgo` if you need exact precision.
     *
     * @set
     *   millisecondAgo
     *   millisecondsAgo
     *   secondAgo
     *   secondsAgo
     *   minuteAgo
     *   minutesAgo
     *   hourAgo
     *   hoursAgo
     *   dayAgo
     *   daysAgo
     *   weekAgo
     *   weeksAgo
     *   monthAgo
     *   monthsAgo
     *   yearAgo
     *   yearsAgo
     *
     * @example
     *
     *   (5).weeksAgo() -> 5 weeks ago
     *   (1).yearAgo()  -> January 23, 1996
     *
     ***
     * @method [dateUnit]After(d, [options])
     * @returns Date
     * @short Returns a date `n` units after [d], where `n` is the number.
     * @extra [d] will accept a date object, timestamp, or text format. Note that
     *        "months" is ambiguous as a unit of time. If the target date falls on a
     *        day that does not exist (i.e. August 31 -> February 31), the date will
     *        be shifted to the last day of the month. Be careful using
     *        `monthsAfter` if you need exact precision. [options] can be an object
     *        or a locale code as a string. See `create` for more.
     *
     * @set
     *   millisecondAfter
     *   millisecondsAfter
     *   secondAfter
     *   secondsAfter
     *   minuteAfter
     *   minutesAfter
     *   hourAfter
     *   hoursAfter
     *   dayAfter
     *   daysAfter
     *   weekAfter
     *   weeksAfter
     *   monthAfter
     *   monthsAfter
     *   yearAfter
     *   yearsAfter
     *
     * @example
     *
     *   (5).daysAfter('tuesday')          -> 5 days after tuesday of this week
     *   (1).yearAfter('January 23, 1997') -> January 23, 1998
     *
     * @param {string|number|Date} d
     * @param {DateCreateOptions} options
     *
     ***
     * @method [dateUnit]FromNow()
     * @returns Date
     * @short Returns a date `n` units from now.
     * @extra Note that "months" is ambiguous as a unit of time. If the target date
     *        falls on a day that does not exist (i.e. August 31 -> February 31), the
     *        date will be shifted to the last day of the month. Be careful using
     *        `monthsFromNow` if you need exact precision.
     *
     * @set
     *   millisecondFromNow
     *   millisecondsFromNow
     *   secondFromNow
     *   secondsFromNow
     *   minuteFromNow
     *   minutesFromNow
     *   hourFromNow
     *   hoursFromNow
     *   dayFromNow
     *   daysFromNow
     *   weekFromNow
     *   weeksFromNow
     *   monthFromNow
     *   monthsFromNow
     *   yearFromNow
     *   yearsFromNow
     *
     * @example
     *
     *   (5).weeksFromNow() -> 5 weeks ago
     *   (1).yearFromNow()  -> January 23, 1998
     *
     ***/
    function buildNumberUnitMethods() {
        defineInstanceSimilar(sugarNumber, DateUnits, function (methods, unit) {
            var name = unit.name, base, after, before;
            base = function (n) {
                return round(n * unit.multiplier);
            };
            after = function (n, d, options) {
                return advanceDate(createDate(d, options, true), name, n);
            };
            before = function (n, d, options) {
                return advanceDate(createDate(d, options, true), name, -n);
            };
            methods[name] = base;
            methods[name + 's'] = base;
            methods[name + 'Before'] = before;
            methods[name + 'sBefore'] = before;
            methods[name + 'Ago'] = before;
            methods[name + 'sAgo'] = before;
            methods[name + 'After'] = after;
            methods[name + 'sAfter'] = after;
            methods[name + 'FromNow'] = after;
            methods[name + 'sFromNow'] = after;
        });
    }

    defineInstance(sugarNumber, {

        /***
         * @method duration([localeCode] = currentLocaleCode)
         * @returns String
         * @short Takes the number as milliseconds and returns a localized string.
         * @extra This method is the same as `Date#relative` without the localized
         *        equivalent of "from now" or "ago". [localeCode] can be passed as the
         *        first (and only) parameter. Note that this method is only available
         *        when the dates module is included.
         *
         * @example
         *
         *   (500).duration() -> '500 milliseconds'
         *   (1200).duration() -> '1 second'
         *   (75).minutes().duration() -> '1 hour'
         *   (75).minutes().duration('es') -> '1 hora'
         *
         * @param {string} [localeCode]
         *
         ***/
        'duration': function (n, localeCode) {
            return localeManager.get(localeCode).getDuration(n);
        }

    });


    var EnglishLocaleBaseDefinition = {
        'code': 'en',
        'plural': true,
        'timeMarkers': 'at',
        'ampm': 'AM|A.M.|a,PM|P.M.|p',
        'units': 'millisecond:|s,second:|s,minute:|s,hour:|s,day:|s,week:|s,month:|s,year:|s',
        'months': 'Jan:uary|,Feb:ruary|,Mar:ch|,Apr:il|,May,Jun:e|,Jul:y|,Aug:ust|,Sep:tember|t|,Oct:ober|,Nov:ember|,Dec:ember|',
        'weekdays': 'Sun:day|,Mon:day|,Tue:sday|,Wed:nesday|,Thu:rsday|,Fri:day|,Sat:urday|+weekend',
        'numerals': 'zero,one|first,two|second,three|third,four:|th,five|fifth,six:|th,seven:|th,eight:|h,nin:e|th,ten:|th',
        'articles': 'a,an,the',
        'tokens': 'the,st|nd|rd|th,of|in,a|an,on',
        'time': '{H}:{mm}',
        'past': '{num} {unit} {sign}',
        'future': '{num} {unit} {sign}',
        'duration': '{num} {unit}',
        'modifiers': [
            {'name': 'half', 'src': 'half', 'value': .5},
            {'name': 'midday', 'src': 'noon', 'value': 12},
            {'name': 'midday', 'src': 'midnight', 'value': 24},
            {'name': 'day', 'src': 'yesterday', 'value': -1},
            {'name': 'day', 'src': 'today|tonight', 'value': 0},
            {'name': 'day', 'src': 'tomorrow', 'value': 1},
            {'name': 'sign', 'src': 'ago|before', 'value': -1},
            {'name': 'sign', 'src': 'from now|after|from|in|later', 'value': 1},
            {'name': 'edge', 'src': 'first day|first|beginning', 'value': -2},
            {'name': 'edge', 'src': 'last day', 'value': 1},
            {'name': 'edge', 'src': 'end|last', 'value': 2},
            {'name': 'shift', 'src': 'last', 'value': -1},
            {'name': 'shift', 'src': 'the|this', 'value': 0},
            {'name': 'shift', 'src': 'next', 'value': 1}
        ],
        'parse': [
            '(?:just)? now',
            '{shift} {unit:5-7}',
            "{months?} (?:{year}|'{yy})",
            '{midday} {4?} {day|weekday}',
            '{months},?(?:[-.\\/\\s]{year})?',
            '{edge} of (?:day)? {day|weekday}',
            '{0} {num}{1?} {weekday} {2} {months},? {year?}',
            '{shift?} {day?} {weekday?} {timeMarker?} {midday}',
            '{sign?} {3?} {half} {3?} {unit:3-4|unit:7} {sign?}',
            '{0?} {edge} {weekday?} {2} {shift?} {unit:4-7?} {months?},? {year?}'
        ],
        'timeParse': [
            '{day|weekday}',
            '{shift} {unit:5?} {weekday}',
            '{0?} {date}{1?} {2?} {months?}',
            '{weekday} {2?} {shift} {unit:5}',
            '{0?} {num} {2?} {months}\\.?,? {year?}',
            '{num?} {unit:4-5} {sign} {day|weekday}',
            '{year}[-.\\/\\s]{months}[-.\\/\\s]{date}',
            '{0|months} {date?}{1?} of {shift} {unit:6-7}',
            '{0?} {num}{1?} {weekday} of {shift} {unit:6}',
            "{date}[-.\\/\\s]{months}[-.\\/\\s](?:{year}|'?{yy})",
            "{weekday?}\\.?,? {months}\\.?,? {date}{1?},? (?:{year}|'{yy})?"
        ],
        'timeFrontParse': [
            '{sign} {num} {unit}',
            '{num} {unit} {sign}',
            '{4?} {day|weekday}'
        ]
    };

    var AmericanEnglishDefinition = getEnglishVariant({
        'mdy': true,
        'firstDayOfWeek': 0,
        'firstDayOfWeekYear': 1,
        'short': '{MM}/{dd}/{yyyy}',
        'medium': '{Month} {d}, {yyyy}',
        'long': '{Month} {d}, {yyyy} {time}',
        'full': '{Weekday}, {Month} {d}, {yyyy} {time}',
        'stamp': '{Dow} {Mon} {d} {yyyy} {time}',
        'time': '{h}:{mm} {TT}'
    });

    var BritishEnglishDefinition = getEnglishVariant({
        'short': '{dd}/{MM}/{yyyy}',
        'medium': '{d} {Month} {yyyy}',
        'long': '{d} {Month} {yyyy} {H}:{mm}',
        'full': '{Weekday}, {d} {Month}, {yyyy} {time}',
        'stamp': '{Dow} {d} {Mon} {yyyy} {time}'
    });

    var CanadianEnglishDefinition = getEnglishVariant({
        'short': '{yyyy}-{MM}-{dd}',
        'medium': '{d} {Month}, {yyyy}',
        'long': '{d} {Month}, {yyyy} {H}:{mm}',
        'full': '{Weekday}, {d} {Month}, {yyyy} {time}',
        'stamp': '{Dow} {d} {Mon} {yyyy} {time}'
    });

    var LazyLoadedLocales = {
        'en-US': AmericanEnglishDefinition,
        'en-GB': BritishEnglishDefinition,
        'en-AU': BritishEnglishDefinition,
        'en-CA': CanadianEnglishDefinition
    };

    buildLocales();
    buildDateFormatTokens();
    buildDateFormatMatcher();
    buildDateUnitMethods();
    buildNumberUnitMethods();
    buildRelativeAliases();
    setDateChainableConstructor();

    /***
     * @module Range
     * @description Date, Number, and String ranges that can be manipulated and compared,
     *              or enumerate over specific points within the range.
     *
     ***/

    var DURATION_UNITS = 'year|month|week|day|hour|minute|second|millisecond';
    var DURATION_REG = RegExp('(\\d+)?\\s*(' + DURATION_UNITS + ')s?', 'i');

    var MULTIPLIERS = {
        'Hours': 60 * 60 * 1000,
        'Minutes': 60 * 1000,
        'Seconds': 1000,
        'Milliseconds': 1
    };

    var PrimitiveRangeConstructor = function (start, end) {
        return new Range(start, end);
    };

    function Range(start, end) {
        this.start = cloneRangeMember(start);
        this.end = cloneRangeMember(end);
    }

    function getRangeMemberNumericValue(m) {
        return isString(m) ? m.charCodeAt(0) : m;
    }

    function getRangeMemberPrimitiveValue(m) {
        if (m == null) return m;
        return isDate(m) ? m.getTime() : m.valueOf();
    }

    function getPrecision(n) {
        var split = periodSplit(n.toString());
        return split[1] ? split[1].length : 0;
    }

    function getGreaterPrecision(n1, n2) {
        return max(getPrecision(n1), getPrecision(n2));
    }

    function cloneRangeMember(m) {
        if (isDate(m)) {
            return new Date(m.getTime());
        } else {
            return getRangeMemberPrimitiveValue(m);
        }
    }

    function isValidRangeMember(m) {
        var val = getRangeMemberPrimitiveValue(m);
        return (!!val || val === 0) && valueIsNotInfinite(m);
    }

    function valueIsNotInfinite(m) {
        return m !== -Infinity && m !== Infinity;
    }

    function rangeIsValid(range) {
        return isValidRangeMember(range.start) &&
            isValidRangeMember(range.end) &&
            typeof range.start === typeof range.end;
    }

    function rangeEvery(range, step, countOnly, fn) {
        var increment,
            precision,
            dio,
            unit,
            start = range.start,
            end = range.end,
            inverse = end < start,
            current = start,
            index = 0,
            result = [];

        if (!rangeIsValid(range)) {
            return countOnly ? NaN : [];
        }
        if (isFunction(step)) {
            fn = step;
            step = null;
        }
        step = step || 1;
        if (isNumber(start)) {
            precision = getGreaterPrecision(start, step);
            increment = function () {
                return incrementNumber(current, step, precision);
            };
        } else if (isString(start)) {
            increment = function () {
                return incrementString(current, step);
            };
        } else if (isDate(start)) {
            dio = getDateIncrementObject(step);
            step = dio[0];
            unit = dio[1];
            increment = function () {
                return incrementDate(current, step, unit);
            };
        }
        // Avoiding infinite loops
        if (inverse && step > 0) {
            step *= -1;
        }
        while (inverse ? current >= end : current <= end) {
            if (!countOnly) {
                result.push(current);
            }
            if (fn) {
                fn(current, index, range);
            }
            current = increment();
            index++;
        }
        return countOnly ? index - 1 : result;
    }

    function getDateIncrementObject(amt) {
        var match, val, unit;
        if (isNumber(amt)) {
            return [amt, 'Milliseconds'];
        }
        match = amt.match(DURATION_REG);
        val = +match[1] || 1;
        unit = simpleCapitalize(match[2].toLowerCase());
        if (unit.match(/hour|minute|second/i)) {
            unit += 's';
        } else if (unit === 'Year') {
            unit = 'FullYear';
        } else if (unit === 'Week') {
            unit = 'Date';
            val *= 7;
        } else if (unit === 'Day') {
            unit = 'Date';
        }
        return [val, unit];
    }

    function incrementDate(src, amount, unit) {
        var mult = MULTIPLIERS[unit], d;
        if (mult) {
            d = new Date(src.getTime() + (amount * mult));
        } else {
            d = new Date(src);
            callDateSet(d, unit, callDateGet(src, unit) + amount);
        }
        return d;
    }

    function incrementString(current, amount) {
        return chr(current.charCodeAt(0) + amount);
    }

    function incrementNumber(current, amount, precision) {
        return withPrecision(current + amount, precision);
    }

    function rangeClamp(range, obj) {
        var clamped,
            start = range.start,
            end = range.end,
            min = end < start ? end : start,
            max = start > end ? start : end;
        if (obj < min) {
            clamped = min;
        } else if (obj > max) {
            clamped = max;
        } else {
            clamped = obj;
        }
        return cloneRangeMember(clamped);
    }

    defineOnPrototype(Range, {

        /***
         * @method toString()
         * @returns String
         * @short Returns a string representation of the range.
         *
         * @example
         *
         *   Number.range(1, 5).toString() -> 1..5
         *   janToMay.toString()           -> January 1, xxxx..May 1, xxxx
         *
         ***/
        'toString': function () {
            return rangeIsValid(this) ? this.start + '..' + this.end : 'Invalid Range';
        },

        /***
         * @method isValid()
         * @returns Boolean
         * @short Returns true if the range is valid, false otherwise.
         *
         * @example
         *
         *   janToMay.isValid() -> true
         *   Number.range(NaN, NaN).isValid()                           -> false
         *
         ***/
        'isValid': function () {
            return rangeIsValid(this);
        },

        /***
         * @method span()
         * @returns Number
         * @short Returns the span of the range. If the range is a date range, the
         *        value is in milliseconds.
         * @extra The span includes both the start and the end.
         *
         * @example
         *
         *   Number.range(5, 10).span()  -> 6
         *   Number.range(40, 25).span() -> 16
         *   janToMay.span()             -> 10368000001 (or more depending on leap year)
         *
         ***/
        'span': function () {
            var n = getRangeMemberNumericValue(this.end) - getRangeMemberNumericValue(this.start);
            return rangeIsValid(this) ? abs(n) + 1 : NaN;
        },

        /***
         * @method contains(el)
         * @returns Boolean
         * @short Returns true if `el` is contained inside the range. `el` may be a
         *        value or another range.
         *
         * @example
         *
         *   Number.range(5, 10).contains(7)         -> true
         *   Number.range(5, 10).contains(2)         -> false
         *   janToMay.contains(mar)                  -> true
         *   janToMay.contains(marToAug)             -> false
         *   janToMay.contains(febToApr)             -> true
         *
         * @param {RangeElement} el
         *
         ***/
        'contains': function (el) {
            if (el == null) return false;
            if (el.start && el.end) {
                return el.start >= this.start && el.start <= this.end &&
                    el.end >= this.start && el.end <= this.end;
            } else {
                return el >= this.start && el <= this.end;
            }
        },

        /***
         * @method every(amount, [fn])
         * @returns Array
         * @short Iterates through the range by `amount`, calling [fn] for each step.
         * @extra Returns an array of each increment visited. For date ranges,
         *        `amount` can also be a string like `"2 days"`. This will step
         *        through the range by incrementing a date object by that specific
         *        unit, and so is generally preferable for vague units such as
         *        `"2 months"`.
         *
         * @callback rangeEveryFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   r    A reference to the range.
         *
         * @example
         *
         *   Number.range(2, 8).every(2) -> [2,4,6,8]
         *   janToMay.every('2 months')  -> [Jan 1, Mar 1, May 1]
         *
         *   sepToOct.every('week', function() {
         *     // Will be called every week from September to October
         *   })
         *
         * @param {string|number} amount
         * @param {rangeEveryFn} [fn]
         * @callbackParam {RangeElement} el
         * @callbackParam {number} i
         * @callbackParam {Range} r
         *
         ***/
        'every': function (amount, fn) {
            return rangeEvery(this, amount, false, fn);
        },

        /***
         * @method toArray()
         * @returns Array
         * @short Creates an array from the range.
         * @extra If the range is a date range, every millisecond between the start
         *        and end dates will be returned. To control this use `every` instead.
         *
         * @example
         *
         *   Number.range(1, 5).toArray() -> [1,2,3,4,5]
         *   Date.range('1 millisecond ago', 'now').toArray() -> [1ms ago, now]
         *
         ***/
        'toArray': function () {
            return rangeEvery(this);
        },

        /***
         * @method union(range)
         * @returns Range
         * @short Returns a new range with the earliest starting point as its start,
         *        and the latest ending point as its end. If the two ranges do not
         *        intersect this will effectively remove the "gap" between them.
         *
         * @example
         *
         *   oneToTen.union(fiveToTwenty) -> 1..20
         *   janToMay.union(marToAug)     -> Jan 1, xxxx..Aug 1, xxxx
         *
         * @param {Range} range
         *
         ***/
        'union': function (range) {
            return new Range(
                this.start < range.start ? this.start : range.start,
                this.end > range.end ? this.end : range.end
            );
        },

        /***
         * @method intersect(range)
         * @returns Range
         * @short Returns a new range with the latest starting point as its start,
         *        and the earliest ending point as its end. If the two ranges do not
         *        intersect this will effectively produce an invalid range.
         *
         * @example
         *
         *   oneToTen.intersect(fiveToTwenty) -> 5..10
         *   janToMay.intersect(marToAug)     -> Mar 1, xxxx..May 1, xxxx
         *
         * @param {Range} range
         *
         ***/
        'intersect': function (range) {
            if (range.start > this.end || range.end < this.start) {
                return new Range(NaN, NaN);
            }
            return new Range(
                this.start > range.start ? this.start : range.start,
                this.end < range.end ? this.end : range.end
            );
        },

        /***
         * @method clone()
         * @returns Range
         * @short Clones the range.
         * @extra Members of the range will also be cloned.
         *
         * @example
         *
         *   Number.range(1, 5).clone() -> Returns a copy of the range.
         *
         ***/
        'clone': function () {
            return new Range(this.start, this.end);
        },

        /***
         * @method clamp(el)
         * @returns Mixed
         * @short Clamps `el` to be within the range if it falls outside.
         *
         * @example
         *
         *   Number.range(1, 5).clamp(8)     -> 5
         *   janToMay.clamp(aug) -> May 1, xxxx
         *
         * @param {RangeElement} el
         *
         ***/
        'clamp': function (el) {
            return rangeClamp(this, el);
        }

    });


    /*** @namespace Number ***/

    defineStatic(sugarNumber, {

        /***
         * @method range([start], [end])
         * @returns Range
         * @static
         * @short Creates a new number range between [start] and [end]. See `ranges`
         *        for more.
         *
         * @example
         *
         *   Number.range(5, 10)
         *   Number.range(20, 15)
         *
         * @param {number} [start]
         * @param {number} [end]
         *
         ***/
        'range': PrimitiveRangeConstructor

    });

    defineInstance(sugarNumber, {

        /***
         * @method upto(num, [step] = 1, [fn])
         * @returns Array
         * @short Returns an array containing numbers from the number up to `num`.
         * @extra Optionally calls [fn] for each number in that array. [step] allows
         *        multiples other than 1. [fn] can be passed in place of [step].
         *
         * @callback rangeEveryFn
         *
         *   el   The element of the current iteration.
         *   i    The index of the current iteration.
         *   r    A reference to the range.
         *
         * @example
         *
         *   (2).upto(6) -> [2, 3, 4, 5, 6]
         *   (2).upto(6, function(n) {
         *     // This function is called 5 times receiving n as the value.
         *   });
         *   (2).upto(8, 2) -> [2, 4, 6, 8]
         *
         * @signature upto(num, [fn])
         * @param {number} num
         * @param {number} [step]
         * @param {rangeEveryFn} [fn]
         * @callbackParam {RangeElement} el
         * @callbackParam {number} i
         * @callbackParam {Range} r
         *
         ***/
        'upto': function (n, num, step, fn) {
            return rangeEvery(new Range(n, num), step, false, fn);
        },

        /***
         * @method clamp([start] = Infinity, [end] = Infinity)
         * @returns Number
         * @short Constrains the number so that it falls on or between [start] and
         *        [end].
         * @extra This will build a range object that has an equivalent `clamp` method.
         *
         * @example
         *
         *   (3).clamp(50, 100)  -> 50
         *   (85).clamp(50, 100) -> 85
         *
         * @param {number} [start]
         * @param {number} [end]
         *
         ***/
        'clamp': function (n, start, end) {
            return rangeClamp(new Range(start, end), n);
        },

        /***
         * @method cap([max] = Infinity)
         * @returns Number
         * @short Constrains the number so that it is no greater than [max].
         * @extra This will build a range object that has an equivalent `cap` method.
         *
         * @example
         *
         *   (100).cap(80) -> 80
         *
         * @param {number} [max]
         *
         ***/
        'cap': function (n, max) {
            return rangeClamp(new Range(undefined, max), n);
        }

    });

    /***
     * @method downto(num, [step] = 1, [fn])
     * @returns Array
     * @short Returns an array containing numbers from the number down to `num`.
     * @extra Optionally calls [fn] for each number in that array. [step] allows
     *        multiples other than 1. [fn] can be passed in place of [step].
     *
     * @callback rangeEveryFn
     *
     *   el   The element of the current iteration.
     *   i    The index of the current iteration.
     *   r    A reference to the range.
     *
     * @example
     *
     *   (8).downto(3) -> [8, 7, 6, 5, 4, 3]
     *   (8).downto(3, function(n) {
     *     // This function is called 6 times receiving n as the value.
     *   });
     *   (8).downto(2, 2) -> [8, 6, 4, 2]
     *
     * @signature upto(num, [fn])
     * @param {number} num
     * @param {number} [step]
     * @param {rangeEveryFn} [fn]
     * @callbackParam {RangeElement} el
     * @callbackParam {number} i
     * @callbackParam {Range} r
     *
     ***/
    alias(sugarNumber, 'downto', 'upto');


    /*** @namespace String ***/

    defineStatic(sugarString, {

        /***
         * @method range([start], [end])
         * @returns Range
         * @static
         * @short Creates a new string range between [start] and [end]. See `ranges`
         *        for more.
         *
         * @example
         *
         *   String.range('a', 'z')
         *   String.range('t', 'm')
         *
         * @param {string} [start]
         * @param {string} [end]
         *
         ***/
        'range': PrimitiveRangeConstructor

    });


    /*** @namespace Date ***/


    var FULL_CAPTURED_DURATION = '((?:\\d+)?\\s*(?:' + DURATION_UNITS + '))s?';

    // Duration text formats
    var RANGE_REG_FROM_TO = /(?:from)?\s*(.+)\s+(?:to|until)\s+(.+)$/i,
        RANGE_REG_REAR_DURATION = RegExp('(.+)\\s*for\\s*' + FULL_CAPTURED_DURATION, 'i'),
        RANGE_REG_FRONT_DURATION = RegExp('(?:for)?\\s*' + FULL_CAPTURED_DURATION + '\\s*(?:starting)?\\s(?:at\\s)?(.+)', 'i');

    var DateRangeConstructor = function (start, end) {
        if (arguments.length === 1 && isString(start)) {
            return createDateRangeFromString(start);
        }
        return new Range(getDateForRange(start), getDateForRange(end));
    };

    function createDateRangeFromString(str) {
        var match, datetime, duration, dio, start, end;
        if (sugarDate.get && (match = str.match(RANGE_REG_FROM_TO))) {
            start = getDateForRange(match[1].replace('from', 'at'));
            end = sugarDate.get(start, match[2]);
            return new Range(start, end);
        }
        if (match = str.match(RANGE_REG_FRONT_DURATION)) {
            duration = match[1];
            datetime = match[2];
        }
        if (match = str.match(RANGE_REG_REAR_DURATION)) {
            datetime = match[1];
            duration = match[2];
        }
        if (datetime && duration) {
            start = getDateForRange(datetime);
            dio = getDateIncrementObject(duration);
            end = incrementDate(start, dio[0], dio[1]);
        } else {
            start = str;
        }
        return new Range(getDateForRange(start), getDateForRange(end));
    }

    function getDateForRange(d) {
        if (isDate(d)) {
            return d;
        } else if (d == null) {
            return new Date();
        } else if (sugarDate.create) {
            return sugarDate.create(d);
        }
        return new Date(d);
    }

    /***
     * @method [dateUnit]()
     * @returns Number
     * @namespace Range
     * @short Returns the span of a date range in the given unit.
     * @extra Higher order units ("days" and greater) walk the date to avoid
     *        discrepancies with ambiguity. Lower order units simply subtract the
     *        start from the end.
     *
     * @set
     *   milliseconds
     *   seconds
     *   minutes
     *   hours
     *   days
     *   weeks
     *   months
     *   years
     *
     * @example
     *
     *   janToMay.months()  -> 4
     *   janToMay.days()    -> 121
     *   janToMay.hours()   -> 2904
     *   janToMay.minutes() -> 220320
     *
     ***/
    function buildDateRangeUnits() {
        var methods = {};
        forEach(DURATION_UNITS.split('|'), function (unit, i) {
            var name = unit + 's', mult, fn;
            if (i < 4) {
                fn = function () {
                    return rangeEvery(this, unit, true);
                };
            } else {
                mult = MULTIPLIERS[simpleCapitalize(name)];
                fn = function () {
                    return trunc((this.end - this.start) / mult);
                };
            }
            methods[name] = fn;
        });
        defineOnPrototype(Range, methods);
    }

    defineStatic(sugarDate, {

        /***
         * @method range([start], [end])
         * @returns Range
         * @namespace Date
         * @static
         * @short Creates a new date range between [start] and [end].
         * @extra Arguments may be either dates or strings which will be forwarded to
         *        the date constructor (`create` will be used if present in the build).
         *        If either [start] or [end] are undefined, they will default to the
         *        current date. This method also accepts an alternate syntax of a
         *        single string describing the range in natural language. See `ranges`
         *        for more.
         *
         * @example
         *
         *   Date.range(jan, may)
         *   Date.range('today', 'tomorrow')
         *   Date.range('now', '5 days ago')
         *   Date.range('last Monday')
         *   Date.range('Monday to Friday')
         *   Date.range('tomorrow from 3pm to 5pm')
         *   Date.range('1 hour starting at 5pm Tuesday')
         *
         * @param {string|Date} [start]
         * @param {string|Date} [end]
         *
         ***/
        'range': DateRangeConstructor

    });

    buildDateRangeUnits();

    /***
     * @module Function
     * @description Lazy, throttled, and memoized functions, delayed functions and
     *              handling of timers, argument currying.
     *
     ***/

    var _lock = privatePropertyAccessor('lock');
    var _timers = privatePropertyAccessor('timers');
    var _partial = privatePropertyAccessor('partial');
    var _canceled = privatePropertyAccessor('canceled');

    var createInstanceFromPrototype = Object.create || function (prototype) {
        var ctor = function () {
        };
        ctor.prototype = prototype;
        return new ctor;
    };

    function setDelay(fn, ms, after, scope, args) {
        // Delay of infinity is never called of course...
        ms = coercePositiveInteger(ms || 0);
        if (!_timers(fn)) {
            _timers(fn, []);
        }
        // This is a workaround for <= IE8, which apparently has the
        // ability to call timeouts in the queue on the same tick (ms?)
        // even if functionally they have already been cleared.
        _canceled(fn, false);
        _timers(fn).push(setTimeout(function () {
            if (!_canceled(fn)) {
                after.apply(scope, args || []);
            }
        }, ms));
    }

    function cancelFunction(fn) {
        var timers = _timers(fn), timer;
        if (isArray(timers)) {
            while (timer = timers.shift()) {
                clearTimeout(timer);
            }
        }
        _canceled(fn, true);
        return fn;
    }

    function createLazyFunction(fn, ms, immediate, limit) {
        var queue = [], locked = false, execute, rounded, perExecution, result;
        ms = ms || 1;
        limit = limit || Infinity;
        rounded = ceil(ms);
        perExecution = round(rounded / ms) || 1;
        execute = function () {
            var queueLength = queue.length, maxPerRound;
            if (queueLength == 0) return;
            // Allow fractions of a millisecond by calling
            // multiple times per actual timeout execution
            maxPerRound = max(queueLength - perExecution, 0);
            while (queueLength > maxPerRound) {
                // Getting uber-meta here...
                result = Function.prototype.apply.apply(fn, queue.shift());
                queueLength--;
            }
            setDelay(lazy, rounded, function () {
                locked = false;
                execute();
            });
        };

        function lazy() {
            // If the execution has locked and it's immediate, then
            // allow 1 less in the queue as 1 call has already taken place.
            if (queue.length < limit - (locked && immediate ? 1 : 0)) {
                // Optimized: no leaking arguments
                var args = [];
                for (var $i = 0, $len = arguments.length; $i < $len; $i++) args.push(arguments[$i]);
                queue.push([this, args]);
            }
            if (!locked) {
                locked = true;
                if (immediate) {
                    execute();
                } else {
                    setDelay(lazy, rounded, execute);
                }
            }
            // Return the memoized result
            return result;
        }

        return lazy;
    }

    // Collecting arguments in an array instead of
    // passing back the arguments object which will
    // deopt this function in V8.
    function collectArguments() {
        var args = arguments, i = args.length, arr = new Array(i);
        while (i--) {
            arr[i] = args[i];
        }
        return arr;
    }

    function createHashedMemoizeFunction(fn, hashFn, limit) {
        var map = {}, refs = [], counter = 0;
        return function () {
            var hashObj = hashFn.apply(this, arguments);
            var key = serializeInternal(hashObj, refs);
            if (hasOwn(map, key)) {
                return getOwn(map, key);
            }
            if (counter === limit) {
                map = {};
                refs = [];
                counter = 0;
            }
            counter++;
            return map[key] = fn.apply(this, arguments);
        };
    }

    defineInstance(sugarFunction, {

        /***
         * @method lazy([ms] = 1, [immediate] = false, [limit] = Infinity)
         * @returns Function
         * @short Creates a lazy function that, when called repeatedly, will queue
         *        execution and wait [ms] milliseconds to execute.
         * @extra If [immediate] is `true`, first execution will happen immediately,
         *        then lock. If [limit] is a fininte number, calls past [limit] will
         *        be ignored while execution is locked. Compare this to `throttle`,
         *        which will execute only once per [ms] milliseconds. Note that [ms]
         *        can also be a fraction. Calling `cancel` on a lazy function will
         *        clear the entire queue.
         *
         * @example
         *
         *   var fn = logHello.lazy(250);
         *   runTenTimes(fn); -> Logs 10 times each time 250ms later
         *
         *   var fn = logHello.lazy(250, false, 5);
         *   runTenTimes(fn); -> Logs 5 times each time 250ms later
         *
         * @param {number} [ms]
         * @param {number} [limit]
         * @param {boolean} [immediate]
         *
         ***/
        'lazy': function (fn, ms, immediate, limit) {
            return createLazyFunction(fn, ms, immediate, limit);
        },

        /***
         * @method throttle([ms] = 1)
         * @returns Function
         * @short Creates a "throttled" version of the function that will only be
         *        executed once per `ms` milliseconds.
         * @extra This is functionally equivalent to calling `lazy` with a [limit] of
         *        `1` and [immediate] as `true`. `throttle` is appropriate when you
         *        want to make sure a function is only executed at most once for a
         *        given duration.
         *
         * @example
         *
         *   var fn = logHello.throttle(50);
         *   runTenTimes(fn);
         *
         * @param {number} [ms]
         *
         ***/
        'throttle': function (fn, ms) {
            return createLazyFunction(fn, ms, true, 1);
        },

        /***
         * @method debounce([ms] = 1)
         * @returns Function
         * @short Creates a "debounced" function that postpones its execution until
         *        after `ms` milliseconds have passed.
         * @extra This method is useful to execute a function after things have
         *        "settled down". A good example of this is when a user tabs quickly
         *        through form fields, execution of a heavy operation should happen
         *        after a few milliseconds when they have "settled" on a field.
         *
         * @example
         *
         *   var fn = logHello.debounce(250)
         *   runTenTimes(fn); -> called once 250ms later
         *
         * @param {number} [ms]
         *
         ***/
        'debounce': function (fn, ms) {
            function debounced() {
                // Optimized: no leaking arguments
                var args = [];
                for (var $i = 0, $len = arguments.length; $i < $len; $i++) args.push(arguments[$i]);
                cancelFunction(debounced);
                setDelay(debounced, ms, fn, this, args);
            }

            return debounced;
        },

        /***
         * @method cancel()
         * @returns Function
         * @short Cancels a delayed function scheduled to be run.
         * @extra `delay`, `lazy`, `throttle`, and `debounce` can all set delays.
         *
         * @example
         *
         *   logHello.delay(500).cancel() -> never logs
         *
         ***/
        'cancel': function (fn) {
            return cancelFunction(fn);
        },

        /***
         * @method after(n)
         * @returns Function
         * @short Creates a function that will execute after `n` calls.
         * @extra `after` is useful for running a final callback after a specific
         *        number of operations, often when the order in which the operations
         *        will complete is unknown. The created function will be passed an
         *        array of the arguments that it has collected from each after `n`.
         *        Note that the function will execute on every call after `n`.
         *        Use `once` in conjunction with this method to prevent being
         *        triggered by subsequent calls.
         *
         * @example
         *
         *   var fn = logHello.after(5);
         *   runTenTimes(fn); -> logs 6 times
         *
         *   var fn = logHello.once().after(5)
         *   runTenTimes(fn); -> logs once
         *
         * @param {number} [n]
         *
         ***/
        'after': function (fn, num) {
            var count = 0, collectedArgs = [];
            num = coercePositiveInteger(num);
            return function () {
                // Optimized: no leaking arguments
                var args = [];
                for (var $i = 0, $len = arguments.length; $i < $len; $i++) args.push(arguments[$i]);
                collectedArgs.push(args);
                count++;
                if (count >= num) {
                    return fn.call(this, collectedArgs);
                }
            };
        },

        /***
         * @method once()
         * @returns Function
         * @short Creates a function that will execute only once and store the result.
         * @extra `once` is useful for creating functions that will cache the result
         *        of an expensive operation and use it on subsequent calls. Also it
         *        can be useful for creating initialization functions that only need
         *        to be run once.
         *
         * @example
         *
         *   var fn = logHello.once();
         *   runTenTimes(fn); -> logs once
         *
         ***/
        'once': function (fn) {
            var called = false, val;
            return function () {
                if (called) {
                    return val;
                }
                called = true;
                return val = fn.apply(this, arguments);
            };
        },

        /***
         * @method memoize([hashFn], [limit])
         * @returns Function
         * @short Creates a function that will memoize results for unique calls.
         * @extra `memoize` can be thought of as a more powerful `once`. Where `once`
         *        will only call a function once ever, memoized functions will be
         *        called once per unique call. A "unique call" is determined by the
         *        return value of [hashFn], which is passed the arguments of each call.
         *        If [hashFn] is undefined, it will deeply serialize all arguments,
         *        such that any different argument signature will result in a unique
         *        call. [hashFn] may be a string (allows `deep properties`) that acts
         *        as a shortcut to return a property of the first argument passed.
         *        [limit] sets an upper limit on memoized results. The default is no
         *        limit, meaning that unique calls will continue to memoize results.
         *        For most use cases this is fine, however [limit] is useful for more
         *        persistent (often server-side) applications for whom memory leaks
         *        are a concern.
         *
         * @example
         *
         *   var fn = logHello.memoize();
         *   fn(1); fn(1); fn(2); -> logs twice, memoizing once
         *
         *   var fn = calculateUserBalance.memoize('id');
         *   fn(Harry); fn(Mark); fn(Mark); -> logs twice, memoizing once
         *
         * @param {string|Function} [hashFn]
         * @param {number} [limit]
         *
         ***/
        'memoize': function (fn, arg1, arg2) {
            var hashFn, limit, prop;
            if (isNumber(arg1)) {
                limit = arg1;
            } else {
                hashFn = arg1;
                limit = arg2;
            }
            if (isString(hashFn)) {
                prop = hashFn;
                hashFn = function (obj) {
                    return deepGetProperty(obj, prop);
                };
            } else if (!hashFn) {
                hashFn = collectArguments;
            }
            return createHashedMemoizeFunction(fn, hashFn, limit);
        },

        /***
         * @method lock([n])
         * @returns Function
         * @short Locks the number of arguments accepted by the function.
         * @extra If not passed, [n] will be the length of the function. This method
         *        can be called on functions created by `partial`, in which case it
         *        will lock the total arguments during execution.
         *
         * @example
         *
         *   logArgs.lock(2)(1,2,3)      -> logs 1,2
         *
         * @param {number} [n]
         *
         ***/
        'lock': function (fn, n) {
            var lockedFn;
            if (_partial(fn)) {
                _lock(fn, isNumber(n) ? n : null);
                return fn;
            }
            lockedFn = function () {
                arguments.length = min(_lock(lockedFn), arguments.length);
                return fn.apply(this, arguments);
            };
            _lock(lockedFn, isNumber(n) ? n : fn.length);
            return lockedFn;
        }

    });

    defineInstanceWithArguments(sugarFunction, {

        /***
         * @method partial([arg1], [arg2], ...)
         * @returns Function
         * @short Returns a new version of the function which has part of its arguments
         *        pre-emptively filled in, also known as "currying".
         * @extra `undefined` can be passed as any argument, and is a placeholder that
         *        will be replaced with arguments passed when the function is executed.
         *        This allows currying of arguments even when they occur toward the end
         *        of an argument list (the example demonstrates this more clearly).
         *
         * @example
         *
         *   logArgs.partial(undefined, 'b')('a') -> logs a, b
         *
         * @param {any} [arg1]
         * @param {any} [arg2]
         *
         ***/
        'partial': function (fn, curriedArgs) {
            var curriedLen = curriedArgs.length;
            var partialFn = function () {
                var argIndex = 0, applyArgs = [], self = this, lock = _lock(partialFn), result, i;
                for (i = 0; i < curriedLen; i++) {
                    var arg = curriedArgs[i];
                    if (isDefined(arg)) {
                        applyArgs[i] = arg;
                    } else {
                        applyArgs[i] = arguments[argIndex++];
                    }
                }
                for (i = argIndex; i < arguments.length; i++) {
                    applyArgs.push(arguments[i]);
                }
                if (lock === null) {
                    lock = curriedLen;
                }
                if (isNumber(lock)) {
                    applyArgs.length = min(applyArgs.length, lock);
                }
                // If the bound "this" object is an instance of the partialed
                // function, then "new" was used, so preserve the prototype
                // so that constructor functions can also be partialed.
                if (self instanceof partialFn) {
                    self = createInstanceFromPrototype(fn.prototype);
                    result = fn.apply(self, applyArgs);
                    // An explicit return value is allowed from constructors
                    // as long as they are of "object" type, so return the
                    // correct result here accordingly.
                    return isObjectType(result) ? result : self;
                }
                return fn.apply(self, applyArgs);
            };
            _partial(partialFn, true);
            return partialFn;
        },

        /***
         * @method delay([ms] = 1, [arg1], [arg2], ...)
         * @returns Function
         * @short Executes the function after `ms` milliseconds.
         * @extra Returns a reference to itself. `delay` is also a way to execute non-
         *        blocking operations that will wait until the CPU is free. Delayed
         *        functions can be canceled using the `cancel` method. Can also curry
         *        arguments passed in after `ms`.
         *
         * @example
         *
         *   logHello.delay(500)     -> logs after 500ms
         *   logArgs.delay(500, 'a') -> logs "a" after 500ms
         *
         * @param {number} [ms]
         * @param {any} [arg1]
         * @param {any} [arg2]
         *
         ***/
        'delay': function (fn, ms, args) {
            setDelay(fn, ms, fn, fn, args);
            return fn;
        },

        /***
         * @method every([ms] = 1, [arg1], [arg2], ...)
         * @returns Function
         * @short Executes the function every `ms` milliseconds.
         * @extra Returns a reference to itself. `every` uses `setTimeout`, which
         *        means that you are guaranteed a period of idle time equal to [ms]
         *        after execution has finished. Compare this to `setInterval` which
         *        will try to run a function every [ms], even when execution itself
         *        takes up a portion of that time. In most cases avoiding `setInterval`
         *        is better as calls won't "back up" when the CPU is under strain,
         *        however this also means that calls are less likely to happen at
         *        exact intervals of [ms], so the use case here should be considered.
         *        Additionally, `every` can curry arguments passed in after [ms], and
         *        also be canceled with `cancel`.
         *
         * @example
         *
         *   logHello.every(1000)        -> logs every second
         *   logArgs.every(1000, 'Hola') -> logs 'hola' every second
         *
         * @param {number} [ms]
         * @param {any} [arg1]
         * @param {any} [arg2]
         *
         ***/
        'every': function (fn, ms, args) {
            function execute() {
                // Set the delay first here, so that cancel
                // can be called within the executing function.
                setDelay(fn, ms, execute);
                fn.apply(fn, args);
            }

            setDelay(fn, ms, execute);
            return fn;
        }

    });

    /***
     * @module RegExp
     * @description RegExp escaping and flag manipulation.
     *
     * Note here that methods on the RegExp class like .exec and .test will fail in
     * the current version of SpiderMonkey being used by CouchDB when using
     * shorthand regex notation like /foo/. This is the reason for the intermixed
     * use of shorthand and compiled regexes here. If you're using JS in CouchDB, it
     * is safer to ALWAYS compile your regexes from a string.
     *
     ***/

    defineStatic(sugarRegExp, {

        /***
         * @method escape([str] = '')
         * @returns String
         * @static
         * @short Escapes all RegExp tokens in a string.
         *
         * @example
         *
         *   RegExp.escape('really?')      -> 'really\?'
         *   RegExp.escape('yes.')         -> 'yes\.'
         *   RegExp.escape('(not really)') -> '\(not really\)'
         *
         * @param {string} str
         *
         ***/
        'escape': function (str) {
            return escapeRegExp(str);
        }

    });

    defineInstance(sugarRegExp, {

        /***
         * @method getFlags()
         * @returns String
         * @short Returns the flags of the regex as a string.
         *
         * @example
         *
         *   /texty/gim.getFlags() -> 'gim'
         *
         ***/
        'getFlags': function (r) {
            return getRegExpFlags(r);
        },

        /***
         * @method setFlags(flags)
         * @returns RegExp
         * @short Creates a copy of the regex with `flags` set.
         *
         * @example
         *
         *   /texty/.setFlags('gim') -> now has global, ignoreCase, and multiline set
         *
         * @param {string} flags
         *
         ***/
        'setFlags': function (r, flags) {
            return RegExp(r.source, flags);
        },

        /***
         * @method addFlags(flags)
         * @returns RegExp
         * @short Creates a copy of the regex with `flags` added.
         *
         * @example
         *
         *   /texty/.addFlags('g')  -> /texty/g
         *   /texty/.addFlags('im') -> /texty/im
         *
         * @param {string} flags
         *
         ***/
        'addFlags': function (r, flags) {
            return RegExp(r.source, getRegExpFlags(r, flags));
        },

        /***
         * @method removeFlags(flags)
         * @returns RegExp
         * @short Creates a copy of the regex with `flags` removed.
         *
         * @example
         *
         *   /texty/gim.removeFlags('g')  -> /texty/im
         *   /texty/gim.removeFlags('im') -> /texty/g
         *
         * @param {string} flags
         *
         ***/
        'removeFlags': function (r, flags) {
            var reg = allCharsReg(flags);
            return RegExp(r.source, getRegExpFlags(r).replace(reg, ''));
        }

    });

}).call(this);
/*!
	Zoom 1.7.21
	license: MIT
	http://www.jacklmoore.com/zoom
*/
(function ($) {
    var defaults = {
        url: false,
        callback: false,
        target: false,
        duration: 120,
        on: 'mouseover', // other options: grab, click, toggle
        touch: true, // enables a touch fallback
        onZoomIn: false,
        onZoomOut: false,
        magnify: 1
    };

    // Core Zoom Logic, independent of event listeners.
    $.zoom = function (target, source, img, magnify) {
        var targetHeight,
            targetWidth,
            sourceHeight,
            sourceWidth,
            xRatio,
            yRatio,
            offset,
            $target = $(target),
            position = $target.css('position'),
            $source = $(source);

        // The parent element needs positioning so that the zoomed element can be correctly positioned within.
        target.style.position = /(absolute|fixed)/.test(position) ? position : 'relative';
        target.style.overflow = 'hidden';
        img.style.width = img.style.height = '';

        $(img)
            .addClass('zoomImg')
            .css({
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: 0,
                width: img.width * magnify,
                height: img.height * magnify,
                border: 'none',
                maxWidth: 'none',
                maxHeight: 'none'
            })
            .appendTo(target);

        return {
            init: function () {
                targetWidth = $target.outerWidth();
                targetHeight = $target.outerHeight();

                if (source === target) {
                    sourceWidth = targetWidth;
                    sourceHeight = targetHeight;
                } else {
                    sourceWidth = $source.outerWidth();
                    sourceHeight = $source.outerHeight();
                }

                xRatio = (img.width - targetWidth) / sourceWidth;
                yRatio = (img.height - targetHeight) / sourceHeight;

                offset = $source.offset();
            },
            move: function (e) {
                var left = (e.pageX - offset.left),
                    top = (e.pageY - offset.top);

                top = Math.max(Math.min(top, sourceHeight), 0);
                left = Math.max(Math.min(left, sourceWidth), 0);

                img.style.left = (left * -xRatio) + 'px';
                img.style.top = (top * -yRatio) + 'px';
            }
        };
    };

    $.fn.zoom = function (options) {
        return this.each(function () {
            var
                settings = $.extend({}, defaults, options || {}),
                //target will display the zoomed image
                target = settings.target && $(settings.target)[0] || this,
                //source will provide zoom location info (thumbnail)
                source = this,
                $source = $(source),
                img = document.createElement('img'),
                $img = $(img),
                mousemove = 'mousemove.zoom',
                clicked = false,
                touched = false;

            // If a url wasn't specified, look for an image element.
            if (!settings.url) {
                var srcElement = source.querySelector('img');
                if (srcElement) {
                    settings.url = srcElement.getAttribute('data-src') || srcElement.currentSrc || srcElement.src;
                }
                if (!settings.url) {
                    return;
                }
            }

            $source.one('zoom.destroy', function (position, overflow) {
                $source.off(".zoom");
                target.style.position = position;
                target.style.overflow = overflow;
                img.onload = null;
                $img.remove();
            }.bind(this, target.style.position, target.style.overflow));

            img.onload = function () {
                var zoom = $.zoom(target, source, img, settings.magnify);

                function start(e) {
                    zoom.init();
                    zoom.move(e);

                    // Skip the fade-in for IE8 and lower since it chokes on fading-in
                    // and changing position based on mousemovement at the same time.
                    $img.stop()
                        .fadeTo($.support.opacity ? settings.duration : 0, 1, $.isFunction(settings.onZoomIn) ? settings.onZoomIn.call(img) : false);
                }

                function stop() {
                    $img.stop()
                        .fadeTo(settings.duration, 0, $.isFunction(settings.onZoomOut) ? settings.onZoomOut.call(img) : false);
                }

                // Mouse events
                if (settings.on === 'grab') {
                    $source
                        .on('mousedown.zoom',
                            function (e) {
                                if (e.which === 1) {
                                    $(document).one('mouseup.zoom',
                                        function () {
                                            stop();

                                            $(document).off(mousemove, zoom.move);
                                        }
                                    );

                                    start(e);

                                    $(document).on(mousemove, zoom.move);

                                    e.preventDefault();
                                }
                            }
                        );
                } else if (settings.on === 'click') {
                    $source.on('click.zoom',
                        function (e) {
                            if (clicked) {
                                // bubble the event up to the document to trigger the unbind.
                                return;
                            } else {
                                clicked = true;
                                start(e);
                                $(document).on(mousemove, zoom.move);
                                $(document).one('click.zoom',
                                    function () {
                                        stop();
                                        clicked = false;
                                        $(document).off(mousemove, zoom.move);
                                    }
                                );
                                return false;
                            }
                        }
                    );
                } else if (settings.on === 'toggle') {
                    $source.on('click.zoom',
                        function (e) {
                            if (clicked) {
                                stop();
                            } else {
                                start(e);
                            }
                            clicked = !clicked;
                        }
                    );
                } else if (settings.on === 'mouseover') {
                    zoom.init(); // Preemptively call init because IE7 will fire the mousemove handler before the hover handler.

                    $source
                        .on('mouseenter.zoom', start)
                        .on('mouseleave.zoom', stop)
                        .on(mousemove, zoom.move);
                }

                // Touch fallback
                if (settings.touch) {
                    $source
                        .on('touchstart.zoom', function (e) {
                            e.preventDefault();
                            if (touched) {
                                touched = false;
                                stop();
                            } else {
                                touched = true;
                                start(e.originalEvent.touches[0] || e.originalEvent.changedTouches[0]);
                            }
                        })
                        .on('touchmove.zoom', function (e) {
                            e.preventDefault();
                            zoom.move(e.originalEvent.touches[0] || e.originalEvent.changedTouches[0]);
                        })
                        .on('touchend.zoom', function (e) {
                            e.preventDefault();
                            if (touched) {
                                touched = false;
                                stop();
                            }
                        });
                }

                if ($.isFunction(settings.callback)) {
                    settings.callback.call(img);
                }
            };

            img.setAttribute('role', 'presentation');
            img.alt = '';
            img.src = settings.url;
        });
    };

    $.fn.zoom.defaults = defaults;
}(window.jQuery));


/*
     _ _      _       _
 ___| (_) ___| | __  (_)___
/ __| | |/ __| |/ /  | / __|
\__ \ | | (__|   < _ | \__ \
|___/_|_|\___|_|\_(_)/ |___/
                   |__/

 Version: 1.8.1
  Author: Ken Wheeler
 Website: http://kenwheeler.github.io
    Docs: http://kenwheeler.github.io/slick
    Repo: http://github.com/kenwheeler/slick
  Issues: http://github.com/kenwheeler/slick/issues

 */
/* global window, document, define, jQuery, setInterval, clearInterval */
;(function (factory) {
    'use strict';
    if (typeof define === 'function' && define.amd) {
        define(['jquery'], factory);
    } else if (typeof exports !== 'undefined') {
        module.exports = factory(require('jquery'));
    } else {
        factory(jQuery);
    }

}(function ($) {
    'use strict';
    var Slick = window.Slick || {};

    Slick = (function () {

        var instanceUid = 0;

        function Slick(element, settings) {

            var _ = this, dataSettings;

            _.defaults = {
                accessibility: true,
                adaptiveHeight: false,
                appendArrows: $(element),
                appendDots: $(element),
                arrows: true,
                asNavFor: null,
                prevArrow: '<button class="slick-prev" aria-label="Previous" type="button">Previous</button>',
                nextArrow: '<button class="slick-next" aria-label="Next" type="button">Next</button>',
                autoplay: false,
                autoplaySpeed: 3000,
                centerMode: false,
                centerPadding: '50px',
                cssEase: 'ease',
                customPaging: function (slider, i) {
                    return $('<button type="button" />').text(i + 1);
                },
                dots: false,
                dotsClass: 'slick-dots',
                draggable: true,
                easing: 'linear',
                edgeFriction: 0.35,
                fade: false,
                focusOnSelect: false,
                focusOnChange: false,
                infinite: true,
                initialSlide: 0,
                lazyLoad: 'ondemand',
                mobileFirst: false,
                pauseOnHover: true,
                pauseOnFocus: true,
                pauseOnDotsHover: false,
                respondTo: 'window',
                responsive: null,
                rows: 1,
                rtl: false,
                slide: '',
                slidesPerRow: 1,
                slidesToShow: 1,
                slidesToScroll: 1,
                speed: 500,
                swipe: true,
                swipeToSlide: false,
                touchMove: true,
                touchThreshold: 5,
                useCSS: true,
                useTransform: true,
                variableWidth: false,
                vertical: false,
                verticalSwiping: false,
                waitForAnimate: true,
                zIndex: 1000
            };

            _.initials = {
                animating: false,
                dragging: false,
                autoPlayTimer: null,
                currentDirection: 0,
                currentLeft: null,
                currentSlide: 0,
                direction: 1,
                $dots: null,
                listWidth: null,
                listHeight: null,
                loadIndex: 0,
                $nextArrow: null,
                $prevArrow: null,
                scrolling: false,
                slideCount: null,
                slideWidth: null,
                $slideTrack: null,
                $slides: null,
                sliding: false,
                slideOffset: 0,
                swipeLeft: null,
                swiping: false,
                $list: null,
                touchObject: {},
                transformsEnabled: false,
                unslicked: false
            };

            $.extend(_, _.initials);

            _.activeBreakpoint = null;
            _.animType = null;
            _.animProp = null;
            _.breakpoints = [];
            _.breakpointSettings = [];
            _.cssTransitions = false;
            _.focussed = false;
            _.interrupted = false;
            _.hidden = 'hidden';
            _.paused = true;
            _.positionProp = null;
            _.respondTo = null;
            _.rowCount = 1;
            _.shouldClick = true;
            _.$slider = $(element);
            _.$slidesCache = null;
            _.transformType = null;
            _.transitionType = null;
            _.visibilityChange = 'visibilitychange';
            _.windowWidth = 0;
            _.windowTimer = null;

            dataSettings = $(element).data('slick') || {};

            _.options = $.extend({}, _.defaults, settings, dataSettings);

            _.currentSlide = _.options.initialSlide;

            _.originalSettings = _.options;

            if (typeof document.mozHidden !== 'undefined') {
                _.hidden = 'mozHidden';
                _.visibilityChange = 'mozvisibilitychange';
            } else if (typeof document.webkitHidden !== 'undefined') {
                _.hidden = 'webkitHidden';
                _.visibilityChange = 'webkitvisibilitychange';
            }

            _.autoPlay = $.proxy(_.autoPlay, _);
            _.autoPlayClear = $.proxy(_.autoPlayClear, _);
            _.autoPlayIterator = $.proxy(_.autoPlayIterator, _);
            _.changeSlide = $.proxy(_.changeSlide, _);
            _.clickHandler = $.proxy(_.clickHandler, _);
            _.selectHandler = $.proxy(_.selectHandler, _);
            _.setPosition = $.proxy(_.setPosition, _);
            _.swipeHandler = $.proxy(_.swipeHandler, _);
            _.dragHandler = $.proxy(_.dragHandler, _);
            _.keyHandler = $.proxy(_.keyHandler, _);

            _.instanceUid = instanceUid++;

            // A simple way to check for HTML strings
            // Strict HTML recognition (must start with <)
            // Extracted from jQuery v1.11 source
            _.htmlExpr = /^(?:\s*(<[\w\W]+>)[^>]*)$/;


            _.registerBreakpoints();
            _.init(true);

        }

        return Slick;

    }());

    Slick.prototype.activateADA = function () {
        var _ = this;

        _.$slideTrack.find('.slick-active').attr({
            'aria-hidden': 'false'
        }).find('a, input, button, select').attr({
            'tabindex': '0'
        });

    };

    Slick.prototype.addSlide = Slick.prototype.slickAdd = function (markup, index, addBefore) {

        var _ = this;

        if (typeof (index) === 'boolean') {
            addBefore = index;
            index = null;
        } else if (index < 0 || (index >= _.slideCount)) {
            return false;
        }

        _.unload();

        if (typeof (index) === 'number') {
            if (index === 0 && _.$slides.length === 0) {
                $(markup).appendTo(_.$slideTrack);
            } else if (addBefore) {
                $(markup).insertBefore(_.$slides.eq(index));
            } else {
                $(markup).insertAfter(_.$slides.eq(index));
            }
        } else {
            if (addBefore === true) {
                $(markup).prependTo(_.$slideTrack);
            } else {
                $(markup).appendTo(_.$slideTrack);
            }
        }

        _.$slides = _.$slideTrack.children(this.options.slide);

        _.$slideTrack.children(this.options.slide).detach();

        _.$slideTrack.append(_.$slides);

        _.$slides.each(function (index, element) {
            $(element).attr('data-slick-index', index);
        });

        _.$slidesCache = _.$slides;

        _.reinit();

    };

    Slick.prototype.animateHeight = function () {
        var _ = this;
        if (_.options.slidesToShow === 1 && _.options.adaptiveHeight === true && _.options.vertical === false) {
            var targetHeight = _.$slides.eq(_.currentSlide).outerHeight(true);
            _.$list.animate({
                height: targetHeight
            }, _.options.speed);
        }
    };

    Slick.prototype.animateSlide = function (targetLeft, callback) {

        var animProps = {},
            _ = this;

        _.animateHeight();

        if (_.options.rtl === true && _.options.vertical === false) {
            targetLeft = -targetLeft;
        }
        if (_.transformsEnabled === false) {
            if (_.options.vertical === false) {
                _.$slideTrack.animate({
                    left: targetLeft
                }, _.options.speed, _.options.easing, callback);
            } else {
                _.$slideTrack.animate({
                    top: targetLeft
                }, _.options.speed, _.options.easing, callback);
            }

        } else {

            if (_.cssTransitions === false) {
                if (_.options.rtl === true) {
                    _.currentLeft = -(_.currentLeft);
                }
                $({
                    animStart: _.currentLeft
                }).animate({
                    animStart: targetLeft
                }, {
                    duration: _.options.speed,
                    easing: _.options.easing,
                    step: function (now) {
                        now = Math.ceil(now);
                        if (_.options.vertical === false) {
                            animProps[_.animType] = 'translate(' +
                                now + 'px, 0px)';
                            _.$slideTrack.css(animProps);
                        } else {
                            animProps[_.animType] = 'translate(0px,' +
                                now + 'px)';
                            _.$slideTrack.css(animProps);
                        }
                    },
                    complete: function () {
                        if (callback) {
                            callback.call();
                        }
                    }
                });

            } else {

                _.applyTransition();
                targetLeft = Math.ceil(targetLeft);

                if (_.options.vertical === false) {
                    animProps[_.animType] = 'translate3d(' + targetLeft + 'px, 0px, 0px)';
                } else {
                    animProps[_.animType] = 'translate3d(0px,' + targetLeft + 'px, 0px)';
                }
                _.$slideTrack.css(animProps);

                if (callback) {
                    setTimeout(function () {

                        _.disableTransition();

                        callback.call();
                    }, _.options.speed);
                }

            }

        }

    };

    Slick.prototype.getNavTarget = function () {

        var _ = this,
            asNavFor = _.options.asNavFor;

        if (asNavFor && asNavFor !== null) {
            asNavFor = $(asNavFor).not(_.$slider);
        }

        return asNavFor;

    };

    Slick.prototype.asNavFor = function (index) {

        var _ = this,
            asNavFor = _.getNavTarget();

        if (asNavFor !== null && typeof asNavFor === 'object') {
            asNavFor.each(function () {
                var target = $(this).slick('getSlick');
                if (!target.unslicked) {
                    target.slideHandler(index, true);
                }
            });
        }

    };

    Slick.prototype.applyTransition = function (slide) {

        var _ = this,
            transition = {};

        if (_.options.fade === false) {
            transition[_.transitionType] = _.transformType + ' ' + _.options.speed + 'ms ' + _.options.cssEase;
        } else {
            transition[_.transitionType] = 'opacity ' + _.options.speed + 'ms ' + _.options.cssEase;
        }

        if (_.options.fade === false) {
            _.$slideTrack.css(transition);
        } else {
            _.$slides.eq(slide).css(transition);
        }

    };

    Slick.prototype.autoPlay = function () {

        var _ = this;

        _.autoPlayClear();

        if (_.slideCount > _.options.slidesToShow) {
            _.autoPlayTimer = setInterval(_.autoPlayIterator, _.options.autoplaySpeed);
        }

    };

    Slick.prototype.autoPlayClear = function () {

        var _ = this;

        if (_.autoPlayTimer) {
            clearInterval(_.autoPlayTimer);
        }

    };

    Slick.prototype.autoPlayIterator = function () {

        var _ = this,
            slideTo = _.currentSlide + _.options.slidesToScroll;

        if (!_.paused && !_.interrupted && !_.focussed) {

            if (_.options.infinite === false) {

                if (_.direction === 1 && (_.currentSlide + 1) === (_.slideCount - 1)) {
                    _.direction = 0;
                } else if (_.direction === 0) {

                    slideTo = _.currentSlide - _.options.slidesToScroll;

                    if (_.currentSlide - 1 === 0) {
                        _.direction = 1;
                    }

                }

            }

            _.slideHandler(slideTo);

        }

    };

    Slick.prototype.buildArrows = function () {

        var _ = this;

        if (_.options.arrows === true) {

            _.$prevArrow = $(_.options.prevArrow).addClass('slick-arrow');
            _.$nextArrow = $(_.options.nextArrow).addClass('slick-arrow');

            if (_.slideCount > _.options.slidesToShow) {

                _.$prevArrow.removeClass('slick-hidden').removeAttr('aria-hidden tabindex');
                _.$nextArrow.removeClass('slick-hidden').removeAttr('aria-hidden tabindex');

                if (_.htmlExpr.test(_.options.prevArrow)) {
                    _.$prevArrow.prependTo(_.options.appendArrows);
                }

                if (_.htmlExpr.test(_.options.nextArrow)) {
                    _.$nextArrow.appendTo(_.options.appendArrows);
                }

                if (_.options.infinite !== true) {
                    _.$prevArrow
                        .addClass('slick-disabled')
                        .attr('aria-disabled', 'true');
                }

            } else {

                _.$prevArrow.add(_.$nextArrow)

                    .addClass('slick-hidden')
                    .attr({
                        'aria-disabled': 'true',
                        'tabindex': '-1'
                    });

            }

        }

    };

    Slick.prototype.buildDots = function () {

        var _ = this,
            i, dot;

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {

            _.$slider.addClass('slick-dotted');

            dot = $('<ul />').addClass(_.options.dotsClass);

            for (i = 0; i <= _.getDotCount(); i += 1) {
                dot.append($('<li />').append(_.options.customPaging.call(this, _, i)));
            }

            _.$dots = dot.appendTo(_.options.appendDots);

            _.$dots.find('li').first().addClass('slick-active');

        }

    };

    Slick.prototype.buildOut = function () {

        var _ = this;

        _.$slides =
            _.$slider
                .children(_.options.slide + ':not(.slick-cloned)')
                .addClass('slick-slide');

        _.slideCount = _.$slides.length;

        _.$slides.each(function (index, element) {
            $(element)
                .attr('data-slick-index', index)
                .data('originalStyling', $(element).attr('style') || '');
        });

        _.$slider.addClass('slick-slider');

        _.$slideTrack = (_.slideCount === 0) ?
            $('<div class="slick-track"/>').appendTo(_.$slider) :
            _.$slides.wrapAll('<div class="slick-track"/>').parent();

        _.$list = _.$slideTrack.wrap(
            '<div class="slick-list"/>').parent();
        _.$slideTrack.css('opacity', 0);

        if (_.options.centerMode === true || _.options.swipeToSlide === true) {
            _.options.slidesToScroll = 1;
        }

        $('img[data-lazy]', _.$slider).not('[src]').addClass('slick-loading');

        _.setupInfinite();

        _.buildArrows();

        _.buildDots();

        _.updateDots();


        _.setSlideClasses(typeof _.currentSlide === 'number' ? _.currentSlide : 0);

        if (_.options.draggable === true) {
            _.$list.addClass('draggable');
        }

    };

    Slick.prototype.buildRows = function () {

        var _ = this, a, b, c, newSlides, numOfSlides, originalSlides, slidesPerSection;

        newSlides = document.createDocumentFragment();
        originalSlides = _.$slider.children();

        if (_.options.rows > 0) {

            slidesPerSection = _.options.slidesPerRow * _.options.rows;
            numOfSlides = Math.ceil(
                originalSlides.length / slidesPerSection
            );

            for (a = 0; a < numOfSlides; a++) {
                var slide = document.createElement('div');
                for (b = 0; b < _.options.rows; b++) {
                    var row = document.createElement('div');
                    for (c = 0; c < _.options.slidesPerRow; c++) {
                        var target = (a * slidesPerSection + ((b * _.options.slidesPerRow) + c));
                        if (originalSlides.get(target)) {
                            row.appendChild(originalSlides.get(target));
                        }
                    }
                    slide.appendChild(row);
                }
                newSlides.appendChild(slide);
            }

            _.$slider.empty().append(newSlides);
            _.$slider.children().children().children()
                .css({
                    'width': (100 / _.options.slidesPerRow) + '%',
                    'display': 'inline-block'
                });

        }

    };

    Slick.prototype.checkResponsive = function (initial, forceUpdate) {

        var _ = this,
            breakpoint, targetBreakpoint, respondToWidth, triggerBreakpoint = false;
        var sliderWidth = _.$slider.width();
        var windowWidth = window.innerWidth || $(window).width();

        if (_.respondTo === 'window') {
            respondToWidth = windowWidth;
        } else if (_.respondTo === 'slider') {
            respondToWidth = sliderWidth;
        } else if (_.respondTo === 'min') {
            respondToWidth = Math.min(windowWidth, sliderWidth);
        }

        if (_.options.responsive &&
            _.options.responsive.length &&
            _.options.responsive !== null) {

            targetBreakpoint = null;

            for (breakpoint in _.breakpoints) {
                if (_.breakpoints.hasOwnProperty(breakpoint)) {
                    if (_.originalSettings.mobileFirst === false) {
                        if (respondToWidth < _.breakpoints[breakpoint]) {
                            targetBreakpoint = _.breakpoints[breakpoint];
                        }
                    } else {
                        if (respondToWidth > _.breakpoints[breakpoint]) {
                            targetBreakpoint = _.breakpoints[breakpoint];
                        }
                    }
                }
            }

            if (targetBreakpoint !== null) {
                if (_.activeBreakpoint !== null) {
                    if (targetBreakpoint !== _.activeBreakpoint || forceUpdate) {
                        _.activeBreakpoint =
                            targetBreakpoint;
                        if (_.breakpointSettings[targetBreakpoint] === 'unslick') {
                            _.unslick(targetBreakpoint);
                        } else {
                            _.options = $.extend({}, _.originalSettings,
                                _.breakpointSettings[
                                    targetBreakpoint]);
                            if (initial === true) {
                                _.currentSlide = _.options.initialSlide;
                            }
                            _.refresh(initial);
                        }
                        triggerBreakpoint = targetBreakpoint;
                    }
                } else {
                    _.activeBreakpoint = targetBreakpoint;
                    if (_.breakpointSettings[targetBreakpoint] === 'unslick') {
                        _.unslick(targetBreakpoint);
                    } else {
                        _.options = $.extend({}, _.originalSettings,
                            _.breakpointSettings[
                                targetBreakpoint]);
                        if (initial === true) {
                            _.currentSlide = _.options.initialSlide;
                        }
                        _.refresh(initial);
                    }
                    triggerBreakpoint = targetBreakpoint;
                }
            } else {
                if (_.activeBreakpoint !== null) {
                    _.activeBreakpoint = null;
                    _.options = _.originalSettings;
                    if (initial === true) {
                        _.currentSlide = _.options.initialSlide;
                    }
                    _.refresh(initial);
                    triggerBreakpoint = targetBreakpoint;
                }
            }

            // only trigger breakpoints during an actual break. not on initialize.
            if (!initial && triggerBreakpoint !== false) {
                _.$slider.trigger('breakpoint', [_, triggerBreakpoint]);
            }
        }

    };

    Slick.prototype.changeSlide = function (event, dontAnimate) {

        var _ = this,
            $target = $(event.currentTarget),
            indexOffset, slideOffset, unevenOffset;

        // If target is a link, prevent default action.
        if ($target.is('a')) {
            event.preventDefault();
        }

        // If target is not the <li> element (ie: a child), find the <li>.
        if (!$target.is('li')) {
            $target = $target.closest('li');
        }

        unevenOffset = (_.slideCount % _.options.slidesToScroll !== 0);
        indexOffset = unevenOffset ? 0 : (_.slideCount - _.currentSlide) % _.options.slidesToScroll;

        switch (event.data.message) {

            case 'previous':
                slideOffset = indexOffset === 0 ? _.options.slidesToScroll : _.options.slidesToShow - indexOffset;
                if (_.slideCount > _.options.slidesToShow) {
                    _.slideHandler(_.currentSlide - slideOffset, false, dontAnimate);
                }
                break;

            case 'next':
                slideOffset = indexOffset === 0 ? _.options.slidesToScroll : indexOffset;
                if (_.slideCount > _.options.slidesToShow) {
                    _.slideHandler(_.currentSlide + slideOffset, false, dontAnimate);
                }
                break;

            case 'index':
                var index = event.data.index === 0 ? 0 :
                    event.data.index || $target.index() * _.options.slidesToScroll;

                _.slideHandler(_.checkNavigable(index), false, dontAnimate);
                $target.children().trigger('focus');
                break;

            default:
                return;
        }

    };

    Slick.prototype.checkNavigable = function (index) {

        var _ = this,
            navigables, prevNavigable;

        navigables = _.getNavigableIndexes();
        prevNavigable = 0;
        if (index > navigables[navigables.length - 1]) {
            index = navigables[navigables.length - 1];
        } else {
            for (var n in navigables) {
                if (index < navigables[n]) {
                    index = prevNavigable;
                    break;
                }
                prevNavigable = navigables[n];
            }
        }

        return index;
    };

    Slick.prototype.cleanUpEvents = function () {

        var _ = this;

        if (_.options.dots && _.$dots !== null) {

            $('li', _.$dots)
                .off('click.slick', _.changeSlide)
                .off('mouseenter.slick', $.proxy(_.interrupt, _, true))
                .off('mouseleave.slick', $.proxy(_.interrupt, _, false));

            if (_.options.accessibility === true) {
                _.$dots.off('keydown.slick', _.keyHandler);
            }
        }

        _.$slider.off('focus.slick blur.slick');

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {
            _.$prevArrow && _.$prevArrow.off('click.slick', _.changeSlide);
            _.$nextArrow && _.$nextArrow.off('click.slick', _.changeSlide);

            if (_.options.accessibility === true) {
                _.$prevArrow && _.$prevArrow.off('keydown.slick', _.keyHandler);
                _.$nextArrow && _.$nextArrow.off('keydown.slick', _.keyHandler);
            }
        }

        _.$list.off('touchstart.slick mousedown.slick', _.swipeHandler);
        _.$list.off('touchmove.slick mousemove.slick', _.swipeHandler);
        _.$list.off('touchend.slick mouseup.slick', _.swipeHandler);
        _.$list.off('touchcancel.slick mouseleave.slick', _.swipeHandler);

        _.$list.off('click.slick', _.clickHandler);

        $(document).off(_.visibilityChange, _.visibility);

        _.cleanUpSlideEvents();

        if (_.options.accessibility === true) {
            _.$list.off('keydown.slick', _.keyHandler);
        }

        if (_.options.focusOnSelect === true) {
            $(_.$slideTrack).children().off('click.slick', _.selectHandler);
        }

        $(window).off('orientationchange.slick.slick-' + _.instanceUid, _.orientationChange);

        $(window).off('resize.slick.slick-' + _.instanceUid, _.resize);

        $('[draggable!=true]', _.$slideTrack).off('dragstart', _.preventDefault);

        $(window).off('load.slick.slick-' + _.instanceUid, _.setPosition);

    };

    Slick.prototype.cleanUpSlideEvents = function () {

        var _ = this;

        _.$list.off('mouseenter.slick', $.proxy(_.interrupt, _, true));
        _.$list.off('mouseleave.slick', $.proxy(_.interrupt, _, false));

    };

    Slick.prototype.cleanUpRows = function () {

        var _ = this, originalSlides;

        if (_.options.rows > 0) {
            originalSlides = _.$slides.children().children();
            originalSlides.removeAttr('style');
            _.$slider.empty().append(originalSlides);
        }

    };

    Slick.prototype.clickHandler = function (event) {

        var _ = this;

        if (_.shouldClick === false) {
            event.stopImmediatePropagation();
            event.stopPropagation();
            event.preventDefault();
        }

    };

    Slick.prototype.destroy = function (refresh) {

        var _ = this;

        _.autoPlayClear();

        _.touchObject = {};

        _.cleanUpEvents();

        $('.slick-cloned', _.$slider).detach();

        if (_.$dots) {
            _.$dots.remove();
        }

        if (_.$prevArrow && _.$prevArrow.length) {

            _.$prevArrow
                .removeClass('slick-disabled slick-arrow slick-hidden')
                .removeAttr('aria-hidden aria-disabled tabindex')
                .css('display', '');

            if (_.htmlExpr.test(_.options.prevArrow)) {
                _.$prevArrow.remove();
            }
        }

        if (_.$nextArrow && _.$nextArrow.length) {

            _.$nextArrow
                .removeClass('slick-disabled slick-arrow slick-hidden')
                .removeAttr('aria-hidden aria-disabled tabindex')
                .css('display', '');

            if (_.htmlExpr.test(_.options.nextArrow)) {
                _.$nextArrow.remove();
            }
        }


        if (_.$slides) {

            _.$slides
                .removeClass('slick-slide slick-active slick-center slick-visible slick-current')
                .removeAttr('aria-hidden')
                .removeAttr('data-slick-index')
                .each(function () {
                    $(this).attr('style', $(this).data('originalStyling'));
                });

            _.$slideTrack.children(this.options.slide).detach();

            _.$slideTrack.detach();

            _.$list.detach();

            _.$slider.append(_.$slides);
        }

        _.cleanUpRows();

        _.$slider.removeClass('slick-slider');
        _.$slider.removeClass('slick-initialized');
        _.$slider.removeClass('slick-dotted');

        _.unslicked = true;

        if (!refresh) {
            _.$slider.trigger('destroy', [_]);
        }

    };

    Slick.prototype.disableTransition = function (slide) {

        var _ = this,
            transition = {};

        transition[_.transitionType] = '';

        if (_.options.fade === false) {
            _.$slideTrack.css(transition);
        } else {
            _.$slides.eq(slide).css(transition);
        }

    };

    Slick.prototype.fadeSlide = function (slideIndex, callback) {

        var _ = this;

        if (_.cssTransitions === false) {

            _.$slides.eq(slideIndex).css({
                zIndex: _.options.zIndex
            });

            _.$slides.eq(slideIndex).animate({
                opacity: 1
            }, _.options.speed, _.options.easing, callback);

        } else {

            _.applyTransition(slideIndex);

            _.$slides.eq(slideIndex).css({
                opacity: 1,
                zIndex: _.options.zIndex
            });

            if (callback) {
                setTimeout(function () {

                    _.disableTransition(slideIndex);

                    callback.call();
                }, _.options.speed);
            }

        }

    };

    Slick.prototype.fadeSlideOut = function (slideIndex) {

        var _ = this;

        if (_.cssTransitions === false) {

            _.$slides.eq(slideIndex).animate({
                opacity: 0,
                zIndex: _.options.zIndex - 2
            }, _.options.speed, _.options.easing);

        } else {

            _.applyTransition(slideIndex);

            _.$slides.eq(slideIndex).css({
                opacity: 0,
                zIndex: _.options.zIndex - 2
            });

        }

    };

    Slick.prototype.filterSlides = Slick.prototype.slickFilter = function (filter) {

        var _ = this;

        if (filter !== null) {

            _.$slidesCache = _.$slides;

            _.unload();

            _.$slideTrack.children(this.options.slide).detach();

            _.$slidesCache.filter(filter).appendTo(_.$slideTrack);

            _.reinit();

        }

    };

    Slick.prototype.focusHandler = function () {

        var _ = this;

        _.$slider
            .off('focus.slick blur.slick')
            .on('focus.slick blur.slick', '*', function (event) {

                event.stopImmediatePropagation();
                var $sf = $(this);

                setTimeout(function () {

                    if (_.options.pauseOnFocus) {
                        _.focussed = $sf.is(':focus');
                        _.autoPlay();
                    }

                }, 0);

            });
    };

    Slick.prototype.getCurrent = Slick.prototype.slickCurrentSlide = function () {

        var _ = this;
        return _.currentSlide;

    };

    Slick.prototype.getDotCount = function () {

        var _ = this;

        var breakPoint = 0;
        var counter = 0;
        var pagerQty = 0;

        if (_.options.infinite === true) {
            if (_.slideCount <= _.options.slidesToShow) {
                ++pagerQty;
            } else {
                while (breakPoint < _.slideCount) {
                    ++pagerQty;
                    breakPoint = counter + _.options.slidesToScroll;
                    counter += _.options.slidesToScroll <= _.options.slidesToShow ? _.options.slidesToScroll : _.options.slidesToShow;
                }
            }
        } else if (_.options.centerMode === true) {
            pagerQty = _.slideCount;
        } else if (!_.options.asNavFor) {
            pagerQty = 1 + Math.ceil((_.slideCount - _.options.slidesToShow) / _.options.slidesToScroll);
        } else {
            while (breakPoint < _.slideCount) {
                ++pagerQty;
                breakPoint = counter + _.options.slidesToScroll;
                counter += _.options.slidesToScroll <= _.options.slidesToShow ? _.options.slidesToScroll : _.options.slidesToShow;
            }
        }

        return pagerQty - 1;

    };

    Slick.prototype.getLeft = function (slideIndex) {

        var _ = this,
            targetLeft,
            verticalHeight,
            verticalOffset = 0,
            targetSlide,
            coef;

        _.slideOffset = 0;
        verticalHeight = _.$slides.first().outerHeight(true);

        if (_.options.infinite === true) {
            if (_.slideCount > _.options.slidesToShow) {
                _.slideOffset = (_.slideWidth * _.options.slidesToShow) * -1;
                coef = -1

                if (_.options.vertical === true && _.options.centerMode === true) {
                    if (_.options.slidesToShow === 2) {
                        coef = -1.5;
                    } else if (_.options.slidesToShow === 1) {
                        coef = -2
                    }
                }
                verticalOffset = (verticalHeight * _.options.slidesToShow) * coef;
            }
            if (_.slideCount % _.options.slidesToScroll !== 0) {
                if (slideIndex + _.options.slidesToScroll > _.slideCount && _.slideCount > _.options.slidesToShow) {
                    if (slideIndex > _.slideCount) {
                        _.slideOffset = ((_.options.slidesToShow - (slideIndex - _.slideCount)) * _.slideWidth) * -1;
                        verticalOffset = ((_.options.slidesToShow - (slideIndex - _.slideCount)) * verticalHeight) * -1;
                    } else {
                        _.slideOffset = ((_.slideCount % _.options.slidesToScroll) * _.slideWidth) * -1;
                        verticalOffset = ((_.slideCount % _.options.slidesToScroll) * verticalHeight) * -1;
                    }
                }
            }
        } else {
            if (slideIndex + _.options.slidesToShow > _.slideCount) {
                _.slideOffset = ((slideIndex + _.options.slidesToShow) - _.slideCount) * _.slideWidth;
                verticalOffset = ((slideIndex + _.options.slidesToShow) - _.slideCount) * verticalHeight;
            }
        }

        if (_.slideCount <= _.options.slidesToShow) {
            _.slideOffset = 0;
            verticalOffset = 0;
        }

        if (_.options.centerMode === true && _.slideCount <= _.options.slidesToShow) {
            _.slideOffset = ((_.slideWidth * Math.floor(_.options.slidesToShow)) / 2) - ((_.slideWidth * _.slideCount) / 2);
        } else if (_.options.centerMode === true && _.options.infinite === true) {
            _.slideOffset += _.slideWidth * Math.floor(_.options.slidesToShow / 2) - _.slideWidth;
        } else if (_.options.centerMode === true) {
            _.slideOffset = 0;
            _.slideOffset += _.slideWidth * Math.floor(_.options.slidesToShow / 2);
        }

        if (_.options.vertical === false) {
            targetLeft = ((slideIndex * _.slideWidth) * -1) + _.slideOffset;
        } else {
            targetLeft = ((slideIndex * verticalHeight) * -1) + verticalOffset;
        }

        if (_.options.variableWidth === true) {

            if (_.slideCount <= _.options.slidesToShow || _.options.infinite === false) {
                targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex);
            } else {
                targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex + _.options.slidesToShow);
            }

            if (_.options.rtl === true) {
                if (targetSlide[0]) {
                    targetLeft = (_.$slideTrack.width() - targetSlide[0].offsetLeft - targetSlide.width()) * -1;
                } else {
                    targetLeft = 0;
                }
            } else {
                targetLeft = targetSlide[0] ? targetSlide[0].offsetLeft * -1 : 0;
            }

            if (_.options.centerMode === true) {
                if (_.slideCount <= _.options.slidesToShow || _.options.infinite === false) {
                    targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex);
                } else {
                    targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex + _.options.slidesToShow + 1);
                }

                if (_.options.rtl === true) {
                    if (targetSlide[0]) {
                        targetLeft = (_.$slideTrack.width() - targetSlide[0].offsetLeft - targetSlide.width()) * -1;
                    } else {
                        targetLeft = 0;
                    }
                } else {
                    targetLeft = targetSlide[0] ? targetSlide[0].offsetLeft * -1 : 0;
                }

                targetLeft += (_.$list.width() - targetSlide.outerWidth()) / 2;
            }
        }

        return targetLeft;

    };

    Slick.prototype.getOption = Slick.prototype.slickGetOption = function (option) {

        var _ = this;

        return _.options[option];

    };

    Slick.prototype.getNavigableIndexes = function () {

        var _ = this,
            breakPoint = 0,
            counter = 0,
            indexes = [],
            max;

        if (_.options.infinite === false) {
            max = _.slideCount;
        } else {
            breakPoint = _.options.slidesToScroll * -1;
            counter = _.options.slidesToScroll * -1;
            max = _.slideCount * 2;
        }

        while (breakPoint < max) {
            indexes.push(breakPoint);
            breakPoint = counter + _.options.slidesToScroll;
            counter += _.options.slidesToScroll <= _.options.slidesToShow ? _.options.slidesToScroll : _.options.slidesToShow;
        }

        return indexes;

    };

    Slick.prototype.getSlick = function () {

        return this;

    };

    Slick.prototype.getSlideCount = function () {

        var _ = this,
            slidesTraversed, swipedSlide, centerOffset;

        centerOffset = _.options.centerMode === true ? _.slideWidth * Math.floor(_.options.slidesToShow / 2) : 0;

        if (_.options.swipeToSlide === true) {
            _.$slideTrack.find('.slick-slide').each(function (index, slide) {
                if (slide.offsetLeft - centerOffset + ($(slide).outerWidth() / 2) > (_.swipeLeft * -1)) {
                    swipedSlide = slide;
                    return false;
                }
            });

            slidesTraversed = Math.abs($(swipedSlide).attr('data-slick-index') - _.currentSlide) || 1;

            return slidesTraversed;

        } else {
            return _.options.slidesToScroll;
        }

    };

    Slick.prototype.goTo = Slick.prototype.slickGoTo = function (slide, dontAnimate) {

        var _ = this;

        _.changeSlide({
            data: {
                message: 'index',
                index: parseInt(slide)
            }
        }, dontAnimate);

    };

    Slick.prototype.init = function (creation) {

        var _ = this;

        if (!$(_.$slider).hasClass('slick-initialized')) {

            $(_.$slider).addClass('slick-initialized');

            _.buildRows();
            _.buildOut();
            _.setProps();
            _.startLoad();
            _.loadSlider();
            _.initializeEvents();
            _.updateArrows();
            _.updateDots();
            _.checkResponsive(true);
            _.focusHandler();

        }

        if (creation) {
            _.$slider.trigger('init', [_]);
        }

        if (_.options.accessibility === true) {
            _.initADA();
        }

        if (_.options.autoplay) {

            _.paused = false;
            _.autoPlay();

        }

    };

    Slick.prototype.initADA = function () {
        var _ = this,
            numDotGroups = Math.ceil(_.slideCount / _.options.slidesToShow),
            tabControlIndexes = _.getNavigableIndexes().filter(function (val) {
                return (val >= 0) && (val < _.slideCount);
            });

        _.$slides.add(_.$slideTrack.find('.slick-cloned')).attr({
            'aria-hidden': 'true',
            'tabindex': '-1'
        }).find('a, input, button, select').attr({
            'tabindex': '-1'
        });

        if (_.$dots !== null) {
            _.$slides.not(_.$slideTrack.find('.slick-cloned')).each(function (i) {
                var slideControlIndex = tabControlIndexes.indexOf(i);

                $(this).attr({
                    'role': 'tabpanel',
                    'id': 'slick-slide' + _.instanceUid + i,
                    'tabindex': -1
                });

                if (slideControlIndex !== -1) {
                    var ariaButtonControl = 'slick-slide-control' + _.instanceUid + slideControlIndex
                    if ($('#' + ariaButtonControl).length) {
                        $(this).attr({
                            'aria-describedby': ariaButtonControl
                        });
                    }
                }
            });

            _.$dots.attr('role', 'tablist').find('li').each(function (i) {
                var mappedSlideIndex = tabControlIndexes[i];

                $(this).attr({
                    'role': 'presentation'
                });

                $(this).find('button').first().attr({
                    'role': 'tab',
                    'id': 'slick-slide-control' + _.instanceUid + i,
                    'aria-controls': 'slick-slide' + _.instanceUid + mappedSlideIndex,
                    'aria-label': (i + 1) + ' of ' + numDotGroups,
                    'aria-selected': null,
                    'tabindex': '-1'
                });

            }).eq(_.currentSlide).find('button').attr({
                'aria-selected': 'true',
                'tabindex': '0'
            }).end();
        }

        for (var i = _.currentSlide, max = i + _.options.slidesToShow; i < max; i++) {
            if (_.options.focusOnChange) {
                _.$slides.eq(i).attr({'tabindex': '0'});
            } else {
                _.$slides.eq(i).removeAttr('tabindex');
            }
        }

        _.activateADA();

    };

    Slick.prototype.initArrowEvents = function () {

        var _ = this;

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {
            _.$prevArrow
                .off('click.slick')
                .on('click.slick', {
                    message: 'previous'
                }, _.changeSlide);
            _.$nextArrow
                .off('click.slick')
                .on('click.slick', {
                    message: 'next'
                }, _.changeSlide);

            if (_.options.accessibility === true) {
                _.$prevArrow.on('keydown.slick', _.keyHandler);
                _.$nextArrow.on('keydown.slick', _.keyHandler);
            }
        }

    };

    Slick.prototype.initDotEvents = function () {

        var _ = this;

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {
            $('li', _.$dots).on('click.slick', {
                message: 'index'
            }, _.changeSlide);

            if (_.options.accessibility === true) {
                _.$dots.on('keydown.slick', _.keyHandler);
            }
        }

        if (_.options.dots === true && _.options.pauseOnDotsHover === true && _.slideCount > _.options.slidesToShow) {

            $('li', _.$dots)
                .on('mouseenter.slick', $.proxy(_.interrupt, _, true))
                .on('mouseleave.slick', $.proxy(_.interrupt, _, false));

        }

    };

    Slick.prototype.initSlideEvents = function () {

        var _ = this;

        if (_.options.pauseOnHover) {

            _.$list.on('mouseenter.slick', $.proxy(_.interrupt, _, true));
            _.$list.on('mouseleave.slick', $.proxy(_.interrupt, _, false));

        }

    };

    Slick.prototype.initializeEvents = function () {

        var _ = this;

        _.initArrowEvents();

        _.initDotEvents();
        _.initSlideEvents();

        _.$list.on('touchstart.slick mousedown.slick', {
            action: 'start'
        }, _.swipeHandler);
        _.$list.on('touchmove.slick mousemove.slick', {
            action: 'move'
        }, _.swipeHandler);
        _.$list.on('touchend.slick mouseup.slick', {
            action: 'end'
        }, _.swipeHandler);
        _.$list.on('touchcancel.slick mouseleave.slick', {
            action: 'end'
        }, _.swipeHandler);

        _.$list.on('click.slick', _.clickHandler);

        $(document).on(_.visibilityChange, $.proxy(_.visibility, _));

        if (_.options.accessibility === true) {
            _.$list.on('keydown.slick', _.keyHandler);
        }

        if (_.options.focusOnSelect === true) {
            $(_.$slideTrack).children().on('click.slick', _.selectHandler);
        }

        $(window).on('orientationchange.slick.slick-' + _.instanceUid, $.proxy(_.orientationChange, _));

        $(window).on('resize.slick.slick-' + _.instanceUid, $.proxy(_.resize, _));

        $('[draggable!=true]', _.$slideTrack).on('dragstart', _.preventDefault);

        $(window).on('load.slick.slick-' + _.instanceUid, _.setPosition);
        $(_.setPosition);

    };

    Slick.prototype.initUI = function () {

        var _ = this;

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {

            _.$prevArrow.show();
            _.$nextArrow.show();

        }

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {

            _.$dots.show();

        }

    };

    Slick.prototype.keyHandler = function (event) {

        var _ = this;
        //Dont slide if the cursor is inside the form fields and arrow keys are pressed
        if (!event.target.tagName.match('TEXTAREA|INPUT|SELECT')) {
            if (event.keyCode === 37 && _.options.accessibility === true) {
                _.changeSlide({
                    data: {
                        message: _.options.rtl === true ? 'next' : 'previous'
                    }
                });
            } else if (event.keyCode === 39 && _.options.accessibility === true) {
                _.changeSlide({
                    data: {
                        message: _.options.rtl === true ? 'previous' : 'next'
                    }
                });
            }
        }

    };

    Slick.prototype.lazyLoad = function () {

        var _ = this,
            loadRange, cloneRange, rangeStart, rangeEnd;

        function loadImages(imagesScope) {

            $('img[data-lazy]', imagesScope).each(function () {

                var image = $(this),
                    imageSource = $(this).attr('data-lazy'),
                    imageSrcSet = $(this).attr('data-srcset'),
                    imageSizes = $(this).attr('data-sizes') || _.$slider.attr('data-sizes'),
                    imageToLoad = document.createElement('img');

                imageToLoad.onload = function () {

                    image
                        .animate({opacity: 0}, 100, function () {

                            if (imageSrcSet) {
                                image
                                    .attr('srcset', imageSrcSet);

                                if (imageSizes) {
                                    image
                                        .attr('sizes', imageSizes);
                                }
                            }

                            image
                                .attr('src', imageSource)
                                .animate({opacity: 1}, 200, function () {
                                    image
                                        .removeAttr('data-lazy data-srcset data-sizes')
                                        .removeClass('slick-loading');
                                });
                            _.$slider.trigger('lazyLoaded', [_, image, imageSource]);
                        });

                };

                imageToLoad.onerror = function () {

                    image
                        .removeAttr('data-lazy')
                        .removeClass('slick-loading')
                        .addClass('slick-lazyload-error');

                    _.$slider.trigger('lazyLoadError', [_, image, imageSource]);

                };

                imageToLoad.src = imageSource;

            });

        }

        if (_.options.centerMode === true) {
            if (_.options.infinite === true) {
                rangeStart = _.currentSlide + (_.options.slidesToShow / 2 + 1);
                rangeEnd = rangeStart + _.options.slidesToShow + 2;
            } else {
                rangeStart = Math.max(0, _.currentSlide - (_.options.slidesToShow / 2 + 1));
                rangeEnd = 2 + (_.options.slidesToShow / 2 + 1) + _.currentSlide;
            }
        } else {
            rangeStart = _.options.infinite ? _.options.slidesToShow + _.currentSlide : _.currentSlide;
            rangeEnd = Math.ceil(rangeStart + _.options.slidesToShow);
            if (_.options.fade === true) {
                if (rangeStart > 0) rangeStart--;
                if (rangeEnd <= _.slideCount) rangeEnd++;
            }
        }

        loadRange = _.$slider.find('.slick-slide').slice(rangeStart, rangeEnd);

        if (_.options.lazyLoad === 'anticipated') {
            var prevSlide = rangeStart - 1,
                nextSlide = rangeEnd,
                $slides = _.$slider.find('.slick-slide');

            for (var i = 0; i < _.options.slidesToScroll; i++) {
                if (prevSlide < 0) prevSlide = _.slideCount - 1;
                loadRange = loadRange.add($slides.eq(prevSlide));
                loadRange = loadRange.add($slides.eq(nextSlide));
                prevSlide--;
                nextSlide++;
            }
        }

        loadImages(loadRange);

        if (_.slideCount <= _.options.slidesToShow) {
            cloneRange = _.$slider.find('.slick-slide');
            loadImages(cloneRange);
        } else if (_.currentSlide >= _.slideCount - _.options.slidesToShow) {
            cloneRange = _.$slider.find('.slick-cloned').slice(0, _.options.slidesToShow);
            loadImages(cloneRange);
        } else if (_.currentSlide === 0) {
            cloneRange = _.$slider.find('.slick-cloned').slice(_.options.slidesToShow * -1);
            loadImages(cloneRange);
        }

    };

    Slick.prototype.loadSlider = function () {

        var _ = this;

        _.setPosition();

        _.$slideTrack.css({
            opacity: 1
        });

        _.$slider.removeClass('slick-loading');

        _.initUI();

        if (_.options.lazyLoad === 'progressive') {
            _.progressiveLazyLoad();
        }

    };

    Slick.prototype.next = Slick.prototype.slickNext = function () {

        var _ = this;

        _.changeSlide({
            data: {
                message: 'next'
            }
        });

    };

    Slick.prototype.orientationChange = function () {

        var _ = this;

        _.checkResponsive();
        _.setPosition();

    };

    Slick.prototype.pause = Slick.prototype.slickPause = function () {

        var _ = this;

        _.autoPlayClear();
        _.paused = true;

    };

    Slick.prototype.play = Slick.prototype.slickPlay = function () {

        var _ = this;

        _.autoPlay();
        _.options.autoplay = true;
        _.paused = false;
        _.focussed = false;
        _.interrupted = false;

    };

    Slick.prototype.postSlide = function (index) {

        var _ = this;

        if (!_.unslicked) {

            _.$slider.trigger('afterChange', [_, index]);

            _.animating = false;

            if (_.slideCount > _.options.slidesToShow) {
                _.setPosition();
            }

            _.swipeLeft = null;

            if (_.options.autoplay) {
                _.autoPlay();
            }

            if (_.options.accessibility === true) {
                _.initADA();

                if (_.options.focusOnChange) {
                    var $currentSlide = $(_.$slides.get(_.currentSlide));
                    $currentSlide.attr('tabindex', 0).focus();
                }
            }

        }

    };

    Slick.prototype.prev = Slick.prototype.slickPrev = function () {

        var _ = this;

        _.changeSlide({
            data: {
                message: 'previous'
            }
        });

    };

    Slick.prototype.preventDefault = function (event) {

        event.preventDefault();

    };

    Slick.prototype.progressiveLazyLoad = function (tryCount) {

        tryCount = tryCount || 1;

        var _ = this,
            $imgsToLoad = $('img[data-lazy]', _.$slider),
            image,
            imageSource,
            imageSrcSet,
            imageSizes,
            imageToLoad;

        if ($imgsToLoad.length) {

            image = $imgsToLoad.first();
            imageSource = image.attr('data-lazy');
            imageSrcSet = image.attr('data-srcset');
            imageSizes = image.attr('data-sizes') || _.$slider.attr('data-sizes');
            imageToLoad = document.createElement('img');

            imageToLoad.onload = function () {

                if (imageSrcSet) {
                    image
                        .attr('srcset', imageSrcSet);

                    if (imageSizes) {
                        image
                            .attr('sizes', imageSizes);
                    }
                }

                image
                    .attr('src', imageSource)
                    .removeAttr('data-lazy data-srcset data-sizes')
                    .removeClass('slick-loading');

                if (_.options.adaptiveHeight === true) {
                    _.setPosition();
                }

                _.$slider.trigger('lazyLoaded', [_, image, imageSource]);
                _.progressiveLazyLoad();

            };

            imageToLoad.onerror = function () {

                if (tryCount < 3) {

                    /**
                     * try to load the image 3 times,
                     * leave a slight delay so we don't get
                     * servers blocking the request.
                     */
                    setTimeout(function () {
                        _.progressiveLazyLoad(tryCount + 1);
                    }, 500);

                } else {

                    image
                        .removeAttr('data-lazy')
                        .removeClass('slick-loading')
                        .addClass('slick-lazyload-error');

                    _.$slider.trigger('lazyLoadError', [_, image, imageSource]);

                    _.progressiveLazyLoad();

                }

            };

            imageToLoad.src = imageSource;

        } else {

            _.$slider.trigger('allImagesLoaded', [_]);

        }

    };

    Slick.prototype.refresh = function (initializing) {

        var _ = this, currentSlide, lastVisibleIndex;

        lastVisibleIndex = _.slideCount - _.options.slidesToShow;

        // in non-infinite sliders, we don't want to go past the
        // last visible index.
        if (!_.options.infinite && (_.currentSlide > lastVisibleIndex)) {
            _.currentSlide = lastVisibleIndex;
        }

        // if less slides than to show, go to start.
        if (_.slideCount <= _.options.slidesToShow) {
            _.currentSlide = 0;

        }

        currentSlide = _.currentSlide;

        _.destroy(true);

        $.extend(_, _.initials, {currentSlide: currentSlide});

        _.init();

        if (!initializing) {

            _.changeSlide({
                data: {
                    message: 'index',
                    index: currentSlide
                }
            }, false);

        }

    };

    Slick.prototype.registerBreakpoints = function () {

        var _ = this, breakpoint, currentBreakpoint, l,
            responsiveSettings = _.options.responsive || null;

        if ($.type(responsiveSettings) === 'array' && responsiveSettings.length) {

            _.respondTo = _.options.respondTo || 'window';

            for (breakpoint in responsiveSettings) {

                l = _.breakpoints.length - 1;

                if (responsiveSettings.hasOwnProperty(breakpoint)) {
                    currentBreakpoint = responsiveSettings[breakpoint].breakpoint;

                    // loop through the breakpoints and cut out any existing
                    // ones with the same breakpoint number, we don't want dupes.
                    while (l >= 0) {
                        if (_.breakpoints[l] && _.breakpoints[l] === currentBreakpoint) {
                            _.breakpoints.splice(l, 1);
                        }
                        l--;
                    }

                    _.breakpoints.push(currentBreakpoint);
                    _.breakpointSettings[currentBreakpoint] = responsiveSettings[breakpoint].settings;

                }

            }

            _.breakpoints.sort(function (a, b) {
                return (_.options.mobileFirst) ? a - b : b - a;
            });

        }

    };

    Slick.prototype.reinit = function () {

        var _ = this;

        _.$slides =
            _.$slideTrack
                .children(_.options.slide)
                .addClass('slick-slide');

        _.slideCount = _.$slides.length;

        if (_.currentSlide >= _.slideCount && _.currentSlide !== 0) {
            _.currentSlide = _.currentSlide - _.options.slidesToScroll;
        }

        if (_.slideCount <= _.options.slidesToShow) {
            _.currentSlide = 0;
        }

        _.registerBreakpoints();

        _.setProps();
        _.setupInfinite();
        _.buildArrows();
        _.updateArrows();
        _.initArrowEvents();
        _.buildDots();
        _.updateDots();
        _.initDotEvents();
        _.cleanUpSlideEvents();
        _.initSlideEvents();

        _.checkResponsive(false, true);

        if (_.options.focusOnSelect === true) {
            $(_.$slideTrack).children().on('click.slick', _.selectHandler);
        }

        _.setSlideClasses(typeof _.currentSlide === 'number' ? _.currentSlide : 0);

        _.setPosition();
        _.focusHandler();

        _.paused = !_.options.autoplay;
        _.autoPlay();

        _.$slider.trigger('reInit', [_]);

    };

    Slick.prototype.resize = function () {

        var _ = this;

        if ($(window).width() !== _.windowWidth) {
            clearTimeout(_.windowDelay);
            _.windowDelay = window.setTimeout(function () {
                _.windowWidth = $(window).width();
                _.checkResponsive();
                if (!_.unslicked) {
                    _.setPosition();
                }
            }, 50);
        }
    };

    Slick.prototype.removeSlide = Slick.prototype.slickRemove = function (index, removeBefore, removeAll) {

        var _ = this;

        if (typeof (index) === 'boolean') {
            removeBefore = index;
            index = removeBefore === true ? 0 : _.slideCount - 1;
        } else {
            index = removeBefore === true ? --index : index;
        }

        if (_.slideCount < 1 || index < 0 || index > _.slideCount - 1) {
            return false;
        }

        _.unload();

        if (removeAll === true) {
            _.$slideTrack.children().remove();
        } else {
            _.$slideTrack.children(this.options.slide).eq(index).remove();
        }

        _.$slides = _.$slideTrack.children(this.options.slide);

        _.$slideTrack.children(this.options.slide).detach();

        _.$slideTrack.append(_.$slides);

        _.$slidesCache = _.$slides;

        _.reinit();

    };

    Slick.prototype.setCSS = function (position) {

        var _ = this,
            positionProps = {},
            x, y;

        if (_.options.rtl === true) {
            position = -position;
        }
        x = _.positionProp == 'left' ? Math.ceil(position) + 'px' : '0px';
        y = _.positionProp == 'top' ? Math.ceil(position) + 'px' : '0px';

        positionProps[_.positionProp] = position;

        if (_.transformsEnabled === false) {
            _.$slideTrack.css(positionProps);
        } else {
            positionProps = {};
            if (_.cssTransitions === false) {
                positionProps[_.animType] = 'translate(' + x + ', ' + y + ')';
                _.$slideTrack.css(positionProps);
            } else {
                positionProps[_.animType] = 'translate3d(' + x + ', ' + y + ', 0px)';
                _.$slideTrack.css(positionProps);
            }
        }

    };

    Slick.prototype.setDimensions = function () {

        var _ = this;

        if (_.options.vertical === false) {
            if (_.options.centerMode === true) {
                _.$list.css({
                    padding: ('0px ' + _.options.centerPadding)
                });
            }
        } else {
            _.$list.height(_.$slides.first().outerHeight(true) * _.options.slidesToShow);
            if (_.options.centerMode === true) {
                _.$list.css({
                    padding: (_.options.centerPadding + ' 0px')
                });
            }
        }

        _.listWidth = _.$list.width();
        _.listHeight = _.$list.height();


        if (_.options.vertical === false && _.options.variableWidth === false) {
            _.slideWidth = Math.ceil(_.listWidth / _.options.slidesToShow);
            _.$slideTrack.width(Math.ceil((_.slideWidth * _.$slideTrack.children('.slick-slide').length)));

        } else if (_.options.variableWidth === true) {
            _.$slideTrack.width(5000 * _.slideCount);
        } else {
            _.slideWidth = Math.ceil(_.listWidth);
            _.$slideTrack.height(Math.ceil((_.$slides.first().outerHeight(true) * _.$slideTrack.children('.slick-slide').length)));
        }

        var offset = _.$slides.first().outerWidth(true) - _.$slides.first().width();
        if (_.options.variableWidth === false) _.$slideTrack.children('.slick-slide').width(_.slideWidth - offset);

    };

    Slick.prototype.setFade = function () {

        var _ = this,
            targetLeft;

        _.$slides.each(function (index, element) {
            targetLeft = (_.slideWidth * index) * -1;
            if (_.options.rtl === true) {
                $(element).css({
                    position: 'relative',
                    right: targetLeft,
                    top: 0,
                    zIndex: _.options.zIndex - 2,
                    opacity: 0
                });
            } else {
                $(element).css({
                    position: 'relative',
                    left: targetLeft,
                    top: 0,
                    zIndex: _.options.zIndex - 2,
                    opacity: 0
                });
            }
        });

        _.$slides.eq(_.currentSlide).css({
            zIndex: _.options.zIndex - 1,
            opacity: 1
        });

    };

    Slick.prototype.setHeight = function () {

        var _ = this;

        if (_.options.slidesToShow === 1 && _.options.adaptiveHeight === true && _.options.vertical === false) {
            var targetHeight = _.$slides.eq(_.currentSlide).outerHeight(true);
            _.$list.css('height', targetHeight);
        }

    };

    Slick.prototype.setOption =
        Slick.prototype.slickSetOption = function () {

            /**
             * accepts arguments in format of:
             *
             *  - for changing a single option's value:
             *     .slick("setOption", option, value, refresh )
             *
             *  - for changing a set of responsive options:
             *     .slick("setOption", 'responsive', [{}, ...], refresh )
             *
             *  - for updating multiple values at once (not responsive)
             *     .slick("setOption", { 'option': value, ... }, refresh )
             */

            var _ = this, l, item, option, value, refresh = false, type;

            if ($.type(arguments[0]) === 'object') {

                option = arguments[0];
                refresh = arguments[1];
                type = 'multiple';

            } else if ($.type(arguments[0]) === 'string') {

                option = arguments[0];
                value = arguments[1];
                refresh = arguments[2];

                if (arguments[0] === 'responsive' && $.type(arguments[1]) === 'array') {

                    type = 'responsive';

                } else if (typeof arguments[1] !== 'undefined') {

                    type = 'single';

                }

            }

            if (type === 'single') {

                _.options[option] = value;


            } else if (type === 'multiple') {

                $.each(option, function (opt, val) {

                    _.options[opt] = val;

                });


            } else if (type === 'responsive') {

                for (item in value) {

                    if ($.type(_.options.responsive) !== 'array') {

                        _.options.responsive = [value[item]];

                    } else {

                        l = _.options.responsive.length - 1;

                        // loop through the responsive object and splice out duplicates.
                        while (l >= 0) {

                            if (_.options.responsive[l].breakpoint === value[item].breakpoint) {

                                _.options.responsive.splice(l, 1);

                            }

                            l--;

                        }

                        _.options.responsive.push(value[item]);

                    }

                }

            }

            if (refresh) {

                _.unload();
                _.reinit();

            }

        };

    Slick.prototype.setPosition = function () {

        var _ = this;

        _.setDimensions();

        _.setHeight();

        if (_.options.fade === false) {
            _.setCSS(_.getLeft(_.currentSlide));
        } else {
            _.setFade();
        }

        _.$slider.trigger('setPosition', [_]);

    };

    Slick.prototype.setProps = function () {

        var _ = this,
            bodyStyle = document.body.style;

        _.positionProp = _.options.vertical === true ? 'top' : 'left';

        if (_.positionProp === 'top') {
            _.$slider.addClass('slick-vertical');
        } else {
            _.$slider.removeClass('slick-vertical');
        }

        if (bodyStyle.WebkitTransition !== undefined ||
            bodyStyle.MozTransition !== undefined ||
            bodyStyle.msTransition !== undefined) {
            if (_.options.useCSS === true) {
                _.cssTransitions = true;
            }
        }

        if (_.options.fade) {
            if (typeof _.options.zIndex === 'number') {
                if (_.options.zIndex < 3) {
                    _.options.zIndex = 3;
                }
            } else {
                _.options.zIndex = _.defaults.zIndex;
            }
        }

        if (bodyStyle.OTransform !== undefined) {
            _.animType = 'OTransform';
            _.transformType = '-o-transform';
            _.transitionType = 'OTransition';
            if (bodyStyle.perspectiveProperty === undefined && bodyStyle.webkitPerspective === undefined) _.animType = false;
        }
        if (bodyStyle.MozTransform !== undefined) {
            _.animType = 'MozTransform';
            _.transformType = '-moz-transform';
            _.transitionType = 'MozTransition';
            if (bodyStyle.perspectiveProperty === undefined && bodyStyle.MozPerspective === undefined) _.animType = false;
        }
        if (bodyStyle.webkitTransform !== undefined) {
            _.animType = 'webkitTransform';
            _.transformType = '-webkit-transform';
            _.transitionType = 'webkitTransition';
            if (bodyStyle.perspectiveProperty === undefined && bodyStyle.webkitPerspective === undefined) _.animType = false;
        }
        if (bodyStyle.msTransform !== undefined) {
            _.animType = 'msTransform';
            _.transformType = '-ms-transform';
            _.transitionType = 'msTransition';
            if (bodyStyle.msTransform === undefined) _.animType = false;
        }
        if (bodyStyle.transform !== undefined && _.animType !== false) {
            _.animType = 'transform';
            _.transformType = 'transform';
            _.transitionType = 'transition';
        }
        _.transformsEnabled = _.options.useTransform && (_.animType !== null && _.animType !== false);
    };


    Slick.prototype.setSlideClasses = function (index) {

        var _ = this,
            centerOffset, allSlides, indexOffset, remainder;

        allSlides = _.$slider
            .find('.slick-slide')
            .removeClass('slick-active slick-center slick-current')
            .attr('aria-hidden', 'true');

        _.$slides
            .eq(index)
            .addClass('slick-current');

        if (_.options.centerMode === true) {

            var evenCoef = _.options.slidesToShow % 2 === 0 ? 1 : 0;

            centerOffset = Math.floor(_.options.slidesToShow / 2);

            if (_.options.infinite === true) {

                if (index >= centerOffset && index <= (_.slideCount - 1) - centerOffset) {
                    _.$slides
                        .slice(index - centerOffset + evenCoef, index + centerOffset + 1)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                } else {

                    indexOffset = _.options.slidesToShow + index;
                    allSlides
                        .slice(indexOffset - centerOffset + 1 + evenCoef, indexOffset + centerOffset + 2)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                }

                if (index === 0) {

                    allSlides
                        .eq(allSlides.length - 1 - _.options.slidesToShow)
                        .addClass('slick-center');

                } else if (index === _.slideCount - 1) {

                    allSlides
                        .eq(_.options.slidesToShow)
                        .addClass('slick-center');

                }

            }

            _.$slides
                .eq(index)
                .addClass('slick-center');

        } else {

            if (index >= 0 && index <= (_.slideCount - _.options.slidesToShow)) {

                _.$slides
                    .slice(index, index + _.options.slidesToShow)
                    .addClass('slick-active')
                    .attr('aria-hidden', 'false');

            } else if (allSlides.length <= _.options.slidesToShow) {

                allSlides
                    .addClass('slick-active')
                    .attr('aria-hidden', 'false');

            } else {

                remainder = _.slideCount % _.options.slidesToShow;
                indexOffset = _.options.infinite === true ? _.options.slidesToShow + index : index;

                if (_.options.slidesToShow == _.options.slidesToScroll && (_.slideCount - index) < _.options.slidesToShow) {

                    allSlides
                        .slice(indexOffset - (_.options.slidesToShow - remainder), indexOffset + remainder)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                } else {

                    allSlides
                        .slice(indexOffset, indexOffset + _.options.slidesToShow)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                }

            }

        }

        if (_.options.lazyLoad === 'ondemand' || _.options.lazyLoad === 'anticipated') {
            _.lazyLoad();
        }
    };

    Slick.prototype.setupInfinite = function () {

        var _ = this,
            i, slideIndex, infiniteCount;

        if (_.options.fade === true) {
            _.options.centerMode = false;
        }

        if (_.options.infinite === true && _.options.fade === false) {

            slideIndex = null;

            if (_.slideCount > _.options.slidesToShow) {

                if (_.options.centerMode === true) {
                    infiniteCount = _.options.slidesToShow + 1;
                } else {
                    infiniteCount = _.options.slidesToShow;
                }

                for (i = _.slideCount; i > (_.slideCount -
                    infiniteCount); i -= 1) {
                    slideIndex = i - 1;
                    $(_.$slides[slideIndex]).clone(true).attr('id', '')
                        .attr('data-slick-index', slideIndex - _.slideCount)
                        .prependTo(_.$slideTrack).addClass('slick-cloned');
                }
                for (i = 0; i < infiniteCount + _.slideCount; i += 1) {
                    slideIndex = i;
                    $(_.$slides[slideIndex]).clone(true).attr('id', '')
                        .attr('data-slick-index', slideIndex + _.slideCount)
                        .appendTo(_.$slideTrack).addClass('slick-cloned');
                }
                _.$slideTrack.find('.slick-cloned').find('[id]').each(function () {
                    $(this).attr('id', '');
                });

            }

        }

    };

    Slick.prototype.interrupt = function (toggle) {

        var _ = this;

        if (!toggle) {
            _.autoPlay();
        }
        _.interrupted = toggle;

    };

    Slick.prototype.selectHandler = function (event) {

        var _ = this;

        var targetElement =
            $(event.target).is('.slick-slide') ?
                $(event.target) :
                $(event.target).parents('.slick-slide');

        var index = parseInt(targetElement.attr('data-slick-index'));

        if (!index) index = 0;

        if (_.slideCount <= _.options.slidesToShow) {

            _.slideHandler(index, false, true);
            return;

        }

        _.slideHandler(index);

    };

    Slick.prototype.slideHandler = function (index, sync, dontAnimate) {

        var targetSlide, animSlide, oldSlide, slideLeft, targetLeft = null,
            _ = this, navTarget;

        sync = sync || false;

        if (_.animating === true && _.options.waitForAnimate === true) {
            return;
        }

        if (_.options.fade === true && _.currentSlide === index) {
            return;
        }

        if (sync === false) {
            _.asNavFor(index);
        }

        targetSlide = index;
        targetLeft = _.getLeft(targetSlide);
        slideLeft = _.getLeft(_.currentSlide);

        _.currentLeft = _.swipeLeft === null ? slideLeft : _.swipeLeft;

        if (_.options.infinite === false && _.options.centerMode === false && (index < 0 || index > _.getDotCount() * _.options.slidesToScroll)) {
            if (_.options.fade === false) {
                targetSlide = _.currentSlide;
                if (dontAnimate !== true && _.slideCount > _.options.slidesToShow) {
                    _.animateSlide(slideLeft, function () {
                        _.postSlide(targetSlide);
                    });
                } else {
                    _.postSlide(targetSlide);
                }
            }
            return;
        } else if (_.options.infinite === false && _.options.centerMode === true && (index < 0 || index > (_.slideCount - _.options.slidesToScroll))) {
            if (_.options.fade === false) {
                targetSlide = _.currentSlide;
                if (dontAnimate !== true && _.slideCount > _.options.slidesToShow) {
                    _.animateSlide(slideLeft, function () {
                        _.postSlide(targetSlide);
                    });
                } else {
                    _.postSlide(targetSlide);
                }
            }
            return;
        }

        if (_.options.autoplay) {
            clearInterval(_.autoPlayTimer);
        }

        if (targetSlide < 0) {
            if (_.slideCount % _.options.slidesToScroll !== 0) {
                animSlide = _.slideCount - (_.slideCount % _.options.slidesToScroll);
            } else {
                animSlide = _.slideCount + targetSlide;
            }
        } else if (targetSlide >= _.slideCount) {
            if (_.slideCount % _.options.slidesToScroll !== 0) {
                animSlide = 0;
            } else {
                animSlide = targetSlide - _.slideCount;
            }
        } else {
            animSlide = targetSlide;
        }

        _.animating = true;

        _.$slider.trigger('beforeChange', [_, _.currentSlide, animSlide]);

        oldSlide = _.currentSlide;
        _.currentSlide = animSlide;

        _.setSlideClasses(_.currentSlide);

        if (_.options.asNavFor) {

            navTarget = _.getNavTarget();
            navTarget = navTarget.slick('getSlick');

            if (navTarget.slideCount <= navTarget.options.slidesToShow) {
                navTarget.setSlideClasses(_.currentSlide);
            }

        }

        _.updateDots();
        _.updateArrows();

        if (_.options.fade === true) {
            if (dontAnimate !== true) {

                _.fadeSlideOut(oldSlide);

                _.fadeSlide(animSlide, function () {
                    _.postSlide(animSlide);
                });

            } else {
                _.postSlide(animSlide);
            }
            _.animateHeight();
            return;
        }

        if (dontAnimate !== true && _.slideCount > _.options.slidesToShow) {
            _.animateSlide(targetLeft, function () {
                _.postSlide(animSlide);
            });
        } else {
            _.postSlide(animSlide);
        }

    };

    Slick.prototype.startLoad = function () {

        var _ = this;

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {

            _.$prevArrow.hide();
            _.$nextArrow.hide();

        }

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {

            _.$dots.hide();

        }

        _.$slider.addClass('slick-loading');

    };

    Slick.prototype.swipeDirection = function () {

        var xDist, yDist, r, swipeAngle, _ = this;

        xDist = _.touchObject.startX - _.touchObject.curX;
        yDist = _.touchObject.startY - _.touchObject.curY;
        r = Math.atan2(yDist, xDist);

        swipeAngle = Math.round(r * 180 / Math.PI);
        if (swipeAngle < 0) {
            swipeAngle = 360 - Math.abs(swipeAngle);
        }

        if ((swipeAngle <= 45) && (swipeAngle >= 0)) {
            return (_.options.rtl === false ? 'left' : 'right');
        }
        if ((swipeAngle <= 360) && (swipeAngle >= 315)) {
            return (_.options.rtl === false ? 'left' : 'right');
        }
        if ((swipeAngle >= 135) && (swipeAngle <= 225)) {
            return (_.options.rtl === false ? 'right' : 'left');
        }
        if (_.options.verticalSwiping === true) {
            if ((swipeAngle >= 35) && (swipeAngle <= 135)) {
                return 'down';
            } else {
                return 'up';
            }
        }

        return 'vertical';

    };

    Slick.prototype.swipeEnd = function (event) {

        var _ = this,
            slideCount,
            direction;

        _.dragging = false;
        _.swiping = false;

        if (_.scrolling) {
            _.scrolling = false;
            return false;
        }

        _.interrupted = false;
        _.shouldClick = (_.touchObject.swipeLength > 10) ? false : true;

        if (_.touchObject.curX === undefined) {
            return false;
        }

        if (_.touchObject.edgeHit === true) {
            _.$slider.trigger('edge', [_, _.swipeDirection()]);
        }

        if (_.touchObject.swipeLength >= _.touchObject.minSwipe) {

            direction = _.swipeDirection();

            switch (direction) {

                case 'left':
                case 'down':

                    slideCount =
                        _.options.swipeToSlide ?
                            _.checkNavigable(_.currentSlide + _.getSlideCount()) :
                            _.currentSlide + _.getSlideCount();

                    _.currentDirection = 0;

                    break;

                case 'right':
                case 'up':

                    slideCount =
                        _.options.swipeToSlide ?
                            _.checkNavigable(_.currentSlide - _.getSlideCount()) :
                            _.currentSlide - _.getSlideCount();

                    _.currentDirection = 1;

                    break;

                default:


            }

            if (direction != 'vertical') {

                _.slideHandler(slideCount);
                _.touchObject = {};
                _.$slider.trigger('swipe', [_, direction]);

            }

        } else {

            if (_.touchObject.startX !== _.touchObject.curX) {

                _.slideHandler(_.currentSlide);
                _.touchObject = {};

            }

        }

    };

    Slick.prototype.swipeHandler = function (event) {

        var _ = this;

        if ((_.options.swipe === false) || ('ontouchend' in document && _.options.swipe === false)) {
            return;
        } else if (_.options.draggable === false && event.type.indexOf('mouse') !== -1) {
            return;
        }

        _.touchObject.fingerCount = event.originalEvent && event.originalEvent.touches !== undefined ?
            event.originalEvent.touches.length : 1;

        _.touchObject.minSwipe = _.listWidth / _.options
            .touchThreshold;

        if (_.options.verticalSwiping === true) {
            _.touchObject.minSwipe = _.listHeight / _.options
                .touchThreshold;
        }

        switch (event.data.action) {

            case 'start':
                _.swipeStart(event);
                break;

            case 'move':
                _.swipeMove(event);
                break;

            case 'end':
                _.swipeEnd(event);
                break;

        }

    };

    Slick.prototype.swipeMove = function (event) {

        var _ = this,
            edgeWasHit = false,
            curLeft, swipeDirection, swipeLength, positionOffset, touches, verticalSwipeLength;

        touches = event.originalEvent !== undefined ? event.originalEvent.touches : null;

        if (!_.dragging || _.scrolling || touches && touches.length !== 1) {
            return false;
        }

        curLeft = _.getLeft(_.currentSlide);

        _.touchObject.curX = touches !== undefined ? touches[0].pageX : event.clientX;
        _.touchObject.curY = touches !== undefined ? touches[0].pageY : event.clientY;

        _.touchObject.swipeLength = Math.round(Math.sqrt(
            Math.pow(_.touchObject.curX - _.touchObject.startX, 2)));

        verticalSwipeLength = Math.round(Math.sqrt(
            Math.pow(_.touchObject.curY - _.touchObject.startY, 2)));

        if (!_.options.verticalSwiping && !_.swiping && verticalSwipeLength > 4) {
            _.scrolling = true;
            return false;
        }

        if (_.options.verticalSwiping === true) {
            _.touchObject.swipeLength = verticalSwipeLength;
        }

        swipeDirection = _.swipeDirection();

        if (event.originalEvent !== undefined && _.touchObject.swipeLength > 4) {
            _.swiping = true;
            event.preventDefault();
        }

        positionOffset = (_.options.rtl === false ? 1 : -1) * (_.touchObject.curX > _.touchObject.startX ? 1 : -1);
        if (_.options.verticalSwiping === true) {
            positionOffset = _.touchObject.curY > _.touchObject.startY ? 1 : -1;
        }


        swipeLength = _.touchObject.swipeLength;

        _.touchObject.edgeHit = false;

        if (_.options.infinite === false) {
            if ((_.currentSlide === 0 && swipeDirection === 'right') || (_.currentSlide >= _.getDotCount() && swipeDirection === 'left')) {
                swipeLength = _.touchObject.swipeLength * _.options.edgeFriction;
                _.touchObject.edgeHit = true;
            }
        }

        if (_.options.vertical === false) {
            _.swipeLeft = curLeft + swipeLength * positionOffset;
        } else {
            _.swipeLeft = curLeft + (swipeLength * (_.$list.height() / _.listWidth)) * positionOffset;
        }
        if (_.options.verticalSwiping === true) {
            _.swipeLeft = curLeft + swipeLength * positionOffset;
        }

        if (_.options.fade === true || _.options.touchMove === false) {
            return false;
        }

        if (_.animating === true) {
            _.swipeLeft = null;
            return false;
        }

        _.setCSS(_.swipeLeft);

    };

    Slick.prototype.swipeStart = function (event) {

        var _ = this,
            touches;

        _.interrupted = true;

        if (_.touchObject.fingerCount !== 1 || _.slideCount <= _.options.slidesToShow) {
            _.touchObject = {};
            return false;
        }

        if (event.originalEvent !== undefined && event.originalEvent.touches !== undefined) {
            touches = event.originalEvent.touches[0];
        }

        _.touchObject.startX = _.touchObject.curX = touches !== undefined ? touches.pageX : event.clientX;
        _.touchObject.startY = _.touchObject.curY = touches !== undefined ? touches.pageY : event.clientY;

        _.dragging = true;

    };

    Slick.prototype.unfilterSlides = Slick.prototype.slickUnfilter = function () {

        var _ = this;

        if (_.$slidesCache !== null) {

            _.unload();

            _.$slideTrack.children(this.options.slide).detach();

            _.$slidesCache.appendTo(_.$slideTrack);

            _.reinit();

        }

    };

    Slick.prototype.unload = function () {

        var _ = this;

        $('.slick-cloned', _.$slider).remove();

        if (_.$dots) {
            _.$dots.remove();
        }

        if (_.$prevArrow && _.htmlExpr.test(_.options.prevArrow)) {
            _.$prevArrow.remove();
        }

        if (_.$nextArrow && _.htmlExpr.test(_.options.nextArrow)) {
            _.$nextArrow.remove();
        }

        _.$slides
            .removeClass('slick-slide slick-active slick-visible slick-current')
            .attr('aria-hidden', 'true')
            .css('width', '');

    };

    Slick.prototype.unslick = function (fromBreakpoint) {

        var _ = this;
        _.$slider.trigger('unslick', [_, fromBreakpoint]);
        _.destroy();

    };

    Slick.prototype.updateArrows = function () {

        var _ = this,
            centerOffset;

        centerOffset = Math.floor(_.options.slidesToShow / 2);

        if (_.options.arrows === true &&
            _.slideCount > _.options.slidesToShow &&
            !_.options.infinite) {

            _.$prevArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');
            _.$nextArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            if (_.currentSlide === 0) {

                _.$prevArrow.addClass('slick-disabled').attr('aria-disabled', 'true');
                _.$nextArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            } else if (_.currentSlide >= _.slideCount - _.options.slidesToShow && _.options.centerMode === false) {

                _.$nextArrow.addClass('slick-disabled').attr('aria-disabled', 'true');
                _.$prevArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            } else if (_.currentSlide >= _.slideCount - 1 && _.options.centerMode === true) {

                _.$nextArrow.addClass('slick-disabled').attr('aria-disabled', 'true');
                _.$prevArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            }

        }

    };

    Slick.prototype.updateDots = function () {

        var _ = this;

        if (_.$dots !== null) {

            _.$dots
                .find('li')
                .removeClass('slick-active')
                .end();

            _.$dots
                .find('li')
                .eq(Math.floor(_.currentSlide / _.options.slidesToScroll))
                .addClass('slick-active');

        }

    };

    Slick.prototype.visibility = function () {

        var _ = this;

        if (_.options.autoplay) {

            if (document[_.hidden]) {

                _.interrupted = true;

            } else {

                _.interrupted = false;

            }

        }

    };

    $.fn.slick = function () {
        var _ = this,
            opt = arguments[0],
            args = Array.prototype.slice.call(arguments, 1),
            l = _.length,
            i,
            ret;
        for (i = 0; i < l; i++) {
            if (typeof opt == 'object' || typeof opt == 'undefined')
                _[i].slick = new Slick(_[i], opt);
            else
                ret = _[i].slick[opt].apply(_[i].slick, args);
            if (typeof ret != 'undefined') return ret;
        }
        return _;
    };

}));

/*!
Waypoints - 4.0.1
Copyright © 2011-2016 Caleb Troughton
Licensed under the MIT license.
https://github.com/imakewebthings/waypoints/blob/master/licenses.txt
*/
!function () {
    "use strict";

    function t(o) {
        if (!o) throw new Error("No options passed to Waypoint constructor");
        if (!o.element) throw new Error("No element option passed to Waypoint constructor");
        if (!o.handler) throw new Error("No handler option passed to Waypoint constructor");
        this.key = "waypoint-" + e, this.options = t.Adapter.extend({}, t.defaults, o), this.element = this.options.element, this.adapter = new t.Adapter(this.element), this.callback = o.handler, this.axis = this.options.horizontal ? "horizontal" : "vertical", this.enabled = this.options.enabled, this.triggerPoint = null, this.group = t.Group.findOrCreate({
            name: this.options.group,
            axis: this.axis
        }), this.context = t.Context.findOrCreateByElement(this.options.context), t.offsetAliases[this.options.offset] && (this.options.offset = t.offsetAliases[this.options.offset]), this.group.add(this), this.context.add(this), i[this.key] = this, e += 1
    }

    var e = 0, i = {};
    t.prototype.queueTrigger = function (t) {
        this.group.queueTrigger(this, t)
    }, t.prototype.trigger = function (t) {
        this.enabled && this.callback && this.callback.apply(this, t)
    }, t.prototype.destroy = function () {
        this.context.remove(this), this.group.remove(this), delete i[this.key]
    }, t.prototype.disable = function () {
        return this.enabled = !1, this
    }, t.prototype.enable = function () {
        return this.context.refresh(), this.enabled = !0, this
    }, t.prototype.next = function () {
        return this.group.next(this)
    }, t.prototype.previous = function () {
        return this.group.previous(this)
    }, t.invokeAll = function (t) {
        var e = [];
        for (var o in i) e.push(i[o]);
        for (var n = 0, r = e.length; r > n; n++) e[n][t]()
    }, t.destroyAll = function () {
        t.invokeAll("destroy")
    }, t.disableAll = function () {
        t.invokeAll("disable")
    }, t.enableAll = function () {
        t.Context.refreshAll();
        for (var e in i) i[e].enabled = !0;
        return this
    }, t.refreshAll = function () {
        t.Context.refreshAll()
    }, t.viewportHeight = function () {
        return window.innerHeight || document.documentElement.clientHeight
    }, t.viewportWidth = function () {
        return document.documentElement.clientWidth
    }, t.adapters = [], t.defaults = {
        context: window,
        continuous: !0,
        enabled: !0,
        group: "default",
        horizontal: !1,
        offset: 0
    }, t.offsetAliases = {
        "bottom-in-view": function () {
            return this.context.innerHeight() - this.adapter.outerHeight()
        }, "right-in-view": function () {
            return this.context.innerWidth() - this.adapter.outerWidth()
        }
    }, window.Waypoint = t
}(), function () {
    "use strict";

    function t(t) {
        window.setTimeout(t, 1e3 / 60)
    }

    function e(t) {
        this.element = t, this.Adapter = n.Adapter, this.adapter = new this.Adapter(t), this.key = "waypoint-context-" + i, this.didScroll = !1, this.didResize = !1, this.oldScroll = {
            x: this.adapter.scrollLeft(),
            y: this.adapter.scrollTop()
        }, this.waypoints = {
            vertical: {},
            horizontal: {}
        }, t.waypointContextKey = this.key, o[t.waypointContextKey] = this, i += 1, n.windowContext || (n.windowContext = !0, n.windowContext = new e(window)), this.createThrottledScrollHandler(), this.createThrottledResizeHandler()
    }

    var i = 0, o = {}, n = window.Waypoint, r = window.onload;
    e.prototype.add = function (t) {
        var e = t.options.horizontal ? "horizontal" : "vertical";
        this.waypoints[e][t.key] = t, this.refresh()
    }, e.prototype.checkEmpty = function () {
        var t = this.Adapter.isEmptyObject(this.waypoints.horizontal),
            e = this.Adapter.isEmptyObject(this.waypoints.vertical), i = this.element == this.element.window;
        t && e && !i && (this.adapter.off(".waypoints"), delete o[this.key])
    }, e.prototype.createThrottledResizeHandler = function () {
        function t() {
            e.handleResize(), e.didResize = !1
        }

        var e = this;
        this.adapter.on("resize.waypoints", function () {
            e.didResize || (e.didResize = !0, n.requestAnimationFrame(t))
        })
    }, e.prototype.createThrottledScrollHandler = function () {
        function t() {
            e.handleScroll(), e.didScroll = !1
        }

        var e = this;
        this.adapter.on("scroll.waypoints", function () {
            (!e.didScroll || n.isTouch) && (e.didScroll = !0, n.requestAnimationFrame(t))
        })
    }, e.prototype.handleResize = function () {
        n.Context.refreshAll()
    }, e.prototype.handleScroll = function () {
        var t = {}, e = {
            horizontal: {
                newScroll: this.adapter.scrollLeft(),
                oldScroll: this.oldScroll.x,
                forward: "right",
                backward: "left"
            },
            vertical: {
                newScroll: this.adapter.scrollTop(),
                oldScroll: this.oldScroll.y,
                forward: "down",
                backward: "up"
            }
        };
        for (var i in e) {
            var o = e[i], n = o.newScroll > o.oldScroll, r = n ? o.forward : o.backward;
            for (var s in this.waypoints[i]) {
                var a = this.waypoints[i][s];
                if (null !== a.triggerPoint) {
                    var l = o.oldScroll < a.triggerPoint, h = o.newScroll >= a.triggerPoint, p = l && h, u = !l && !h;
                    (p || u) && (a.queueTrigger(r), t[a.group.id] = a.group)
                }
            }
        }
        for (var c in t) t[c].flushTriggers();
        this.oldScroll = {x: e.horizontal.newScroll, y: e.vertical.newScroll}
    }, e.prototype.innerHeight = function () {
        return this.element == this.element.window ? n.viewportHeight() : this.adapter.innerHeight()
    }, e.prototype.remove = function (t) {
        delete this.waypoints[t.axis][t.key], this.checkEmpty()
    }, e.prototype.innerWidth = function () {
        return this.element == this.element.window ? n.viewportWidth() : this.adapter.innerWidth()
    }, e.prototype.destroy = function () {
        var t = [];
        for (var e in this.waypoints) for (var i in this.waypoints[e]) t.push(this.waypoints[e][i]);
        for (var o = 0, n = t.length; n > o; o++) t[o].destroy()
    }, e.prototype.refresh = function () {
        var t, e = this.element == this.element.window, i = e ? void 0 : this.adapter.offset(), o = {};
        this.handleScroll(), t = {
            horizontal: {
                contextOffset: e ? 0 : i.left,
                contextScroll: e ? 0 : this.oldScroll.x,
                contextDimension: this.innerWidth(),
                oldScroll: this.oldScroll.x,
                forward: "right",
                backward: "left",
                offsetProp: "left"
            },
            vertical: {
                contextOffset: e ? 0 : i.top,
                contextScroll: e ? 0 : this.oldScroll.y,
                contextDimension: this.innerHeight(),
                oldScroll: this.oldScroll.y,
                forward: "down",
                backward: "up",
                offsetProp: "top"
            }
        };
        for (var r in t) {
            var s = t[r];
            for (var a in this.waypoints[r]) {
                var l, h, p, u, c, d = this.waypoints[r][a], f = d.options.offset, w = d.triggerPoint, y = 0,
                    g = null == w;
                d.element !== d.element.window && (y = d.adapter.offset()[s.offsetProp]), "function" == typeof f ? f = f.apply(d) : "string" == typeof f && (f = parseFloat(f), d.options.offset.indexOf("%") > -1 && (f = Math.ceil(s.contextDimension * f / 100))), l = s.contextScroll - s.contextOffset, d.triggerPoint = Math.floor(y + l - f), h = w < s.oldScroll, p = d.triggerPoint >= s.oldScroll, u = h && p, c = !h && !p, !g && u ? (d.queueTrigger(s.backward), o[d.group.id] = d.group) : !g && c ? (d.queueTrigger(s.forward), o[d.group.id] = d.group) : g && s.oldScroll >= d.triggerPoint && (d.queueTrigger(s.forward), o[d.group.id] = d.group)
            }
        }
        return n.requestAnimationFrame(function () {
            for (var t in o) o[t].flushTriggers()
        }), this
    }, e.findOrCreateByElement = function (t) {
        return e.findByElement(t) || new e(t)
    }, e.refreshAll = function () {
        for (var t in o) o[t].refresh()
    }, e.findByElement = function (t) {
        return o[t.waypointContextKey]
    }, window.onload = function () {
        r && r(), e.refreshAll()
    }, n.requestAnimationFrame = function (e) {
        var i = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || t;
        i.call(window, e)
    }, n.Context = e
}(), function () {
    "use strict";

    function t(t, e) {
        return t.triggerPoint - e.triggerPoint
    }

    function e(t, e) {
        return e.triggerPoint - t.triggerPoint
    }

    function i(t) {
        this.name = t.name, this.axis = t.axis, this.id = this.name + "-" + this.axis, this.waypoints = [], this.clearTriggerQueues(), o[this.axis][this.name] = this
    }

    var o = {vertical: {}, horizontal: {}}, n = window.Waypoint;
    i.prototype.add = function (t) {
        this.waypoints.push(t)
    }, i.prototype.clearTriggerQueues = function () {
        this.triggerQueues = {up: [], down: [], left: [], right: []}
    }, i.prototype.flushTriggers = function () {
        for (var i in this.triggerQueues) {
            var o = this.triggerQueues[i], n = "up" === i || "left" === i;
            o.sort(n ? e : t);
            for (var r = 0, s = o.length; s > r; r += 1) {
                var a = o[r];
                (a.options.continuous || r === o.length - 1) && a.trigger([i])
            }
        }
        this.clearTriggerQueues()
    }, i.prototype.next = function (e) {
        this.waypoints.sort(t);
        var i = n.Adapter.inArray(e, this.waypoints), o = i === this.waypoints.length - 1;
        return o ? null : this.waypoints[i + 1]
    }, i.prototype.previous = function (e) {
        this.waypoints.sort(t);
        var i = n.Adapter.inArray(e, this.waypoints);
        return i ? this.waypoints[i - 1] : null
    }, i.prototype.queueTrigger = function (t, e) {
        this.triggerQueues[e].push(t)
    }, i.prototype.remove = function (t) {
        var e = n.Adapter.inArray(t, this.waypoints);
        e > -1 && this.waypoints.splice(e, 1)
    }, i.prototype.first = function () {
        return this.waypoints[0]
    }, i.prototype.last = function () {
        return this.waypoints[this.waypoints.length - 1]
    }, i.findOrCreate = function (t) {
        return o[t.axis][t.name] || new i(t)
    }, n.Group = i
}(), function () {
    "use strict";

    function t(t) {
        this.$element = e(t)
    }

    var e = window.jQuery, i = window.Waypoint;
    e.each(["innerHeight", "innerWidth", "off", "offset", "on", "outerHeight", "outerWidth", "scrollLeft", "scrollTop"], function (e, i) {
        t.prototype[i] = function () {
            var t = Array.prototype.slice.call(arguments);
            return this.$element[i].apply(this.$element, t)
        }
    }), e.each(["extend", "inArray", "isEmptyObject"], function (i, o) {
        t[o] = e[o]
    }), i.adapters.push({name: "jquery", Adapter: t}), i.Adapter = t
}(), function () {
    "use strict";

    function t(t) {
        return function () {
            var i = [], o = arguments[0];
            return t.isFunction(arguments[0]) && (o = t.extend({}, arguments[1]), o.handler = arguments[0]), this.each(function () {
                var n = t.extend({}, o, {element: this});
                "string" == typeof n.context && (n.context = t(this).closest(n.context)[0]), i.push(new e(n))
            }), i
        }
    }

    var e = window.Waypoint;
    window.jQuery && (window.jQuery.fn.waypoint = t(window.jQuery)), window.Zepto && (window.Zepto.fn.waypoint = t(window.Zepto))
}();


/*! iScroll v5.2.0 ~ (c) 2008-2016 Matteo Spinelli ~ http://cubiq.org/license */
(function (window, document, Math) {
    var rAF = window.requestAnimationFrame ||
        window.webkitRequestAnimationFrame ||
        window.mozRequestAnimationFrame ||
        window.oRequestAnimationFrame ||
        window.msRequestAnimationFrame ||
        function (callback) {
            window.setTimeout(callback, 1000 / 60);
        };

    var utils = (function () {
        var me = {};

        var _elementStyle = document.createElement('div').style;
        var _vendor = (function () {
            var vendors = ['t', 'webkitT', 'MozT', 'msT', 'OT'],
                transform,
                i = 0,
                l = vendors.length;

            for (; i < l; i++) {
                transform = vendors[i] + 'ransform';
                if (transform in _elementStyle) return vendors[i].substr(0, vendors[i].length - 1);
            }

            return false;
        })();

        function _prefixStyle(style) {
            if (_vendor === false) return false;
            if (_vendor === '') return style;
            return _vendor + style.charAt(0).toUpperCase() + style.substr(1);
        }

        me.getTime = Date.now || function getTime() {
            return new Date().getTime();
        };

        me.extend = function (target, obj) {
            for (var i in obj) {
                target[i] = obj[i];
            }
        };

        me.addEvent = function (el, type, fn, capture) {
            el.addEventListener(type, fn, !!capture);
        };

        me.removeEvent = function (el, type, fn, capture) {
            el.removeEventListener(type, fn, !!capture);
        };

        me.prefixPointerEvent = function (pointerEvent) {
            return window.MSPointerEvent ?
                'MSPointer' + pointerEvent.charAt(7).toUpperCase() + pointerEvent.substr(8) :
                pointerEvent;
        };

        me.momentum = function (current, start, time, lowerMargin, wrapperSize, deceleration) {
            var distance = current - start,
                speed = Math.abs(distance) / time,
                destination,
                duration;

            deceleration = deceleration === undefined ? 0.0006 : deceleration;

            destination = current + (speed * speed) / (2 * deceleration) * (distance < 0 ? -1 : 1);
            duration = speed / deceleration;

            if (destination < lowerMargin) {
                destination = wrapperSize ? lowerMargin - (wrapperSize / 2.5 * (speed / 8)) : lowerMargin;
                distance = Math.abs(destination - current);
                duration = distance / speed;
            } else if (destination > 0) {
                destination = wrapperSize ? wrapperSize / 2.5 * (speed / 8) : 0;
                distance = Math.abs(current) + destination;
                duration = distance / speed;
            }

            return {
                destination: Math.round(destination),
                duration: duration
            };
        };

        var _transform = _prefixStyle('transform');

        me.extend(me, {
            hasTransform: _transform !== false,
            hasPerspective: _prefixStyle('perspective') in _elementStyle,
            hasTouch: 'ontouchstart' in window,
            hasPointer: !!(window.PointerEvent || window.MSPointerEvent), // IE10 is prefixed
            hasTransition: _prefixStyle('transition') in _elementStyle
        });

        /*
	This should find all Android browsers lower than build 535.19 (both stock browser and webview)
	- galaxy S2 is ok
    - 2.3.6 : `AppleWebKit/533.1 (KHTML, like Gecko) Version/4.0 Mobile Safari/533.1`
    - 4.0.4 : `AppleWebKit/534.30 (KHTML, like Gecko) Version/4.0 Mobile Safari/534.30`
   - galaxy S3 is badAndroid (stock brower, webview)
     `AppleWebKit/534.30 (KHTML, like Gecko) Version/4.0 Mobile Safari/534.30`
   - galaxy S4 is badAndroid (stock brower, webview)
     `AppleWebKit/534.30 (KHTML, like Gecko) Version/4.0 Mobile Safari/534.30`
   - galaxy S5 is OK
     `AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Mobile Safari/537.36 (Chrome/)`
   - galaxy S6 is OK
     `AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Mobile Safari/537.36 (Chrome/)`
  */
        me.isBadAndroid = (function () {
            var appVersion = window.navigator.appVersion;
            // Android browser is not a chrome browser.
            if (/Android/.test(appVersion) && !(/Chrome\/\d/.test(appVersion))) {
                var safariVersion = appVersion.match(/Safari\/(\d+.\d)/);
                if (safariVersion && typeof safariVersion === "object" && safariVersion.length >= 2) {
                    return parseFloat(safariVersion[1]) < 535.19;
                } else {
                    return true;
                }
            } else {
                return false;
            }
        })();

        me.extend(me.style = {}, {
            transform: _transform,
            transitionTimingFunction: _prefixStyle('transitionTimingFunction'),
            transitionDuration: _prefixStyle('transitionDuration'),
            transitionDelay: _prefixStyle('transitionDelay'),
            transformOrigin: _prefixStyle('transformOrigin')
        });

        me.hasClass = function (e, c) {
            var re = new RegExp("(^|\\s)" + c + "(\\s|$)");
            return re.test(e.className);
        };

        me.addClass = function (e, c) {
            if (me.hasClass(e, c)) {
                return;
            }

            var newclass = e.className.split(' ');
            newclass.push(c);
            e.className = newclass.join(' ');
        };

        me.removeClass = function (e, c) {
            if (!me.hasClass(e, c)) {
                return;
            }

            var re = new RegExp("(^|\\s)" + c + "(\\s|$)", 'g');
            e.className = e.className.replace(re, ' ');
        };

        me.offset = function (el) {
            var left = -el.offsetLeft,
                top = -el.offsetTop;

            // jshint -W084
            while (el = el.offsetParent) {
                left -= el.offsetLeft;
                top -= el.offsetTop;
            }
            // jshint +W084

            return {
                left: left,
                top: top
            };
        };

        me.preventDefaultException = function (el, exceptions) {
            for (var i in exceptions) {
                if (exceptions[i].test(el[i])) {
                    return true;
                }
            }

            return false;
        };

        me.extend(me.eventType = {}, {
            touchstart: 1,
            touchmove: 1,
            touchend: 1,

            mousedown: 2,
            mousemove: 2,
            mouseup: 2,

            pointerdown: 3,
            pointermove: 3,
            pointerup: 3,

            MSPointerDown: 3,
            MSPointerMove: 3,
            MSPointerUp: 3
        });

        me.extend(me.ease = {}, {
            quadratic: {
                style: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                fn: function (k) {
                    return k * (2 - k);
                }
            },
            circular: {
                style: 'cubic-bezier(0.1, 0.57, 0.1, 1)',	// Not properly "circular" but this looks better, it should be (0.075, 0.82, 0.165, 1)
                fn: function (k) {
                    return Math.sqrt(1 - (--k * k));
                }
            },
            back: {
                style: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                fn: function (k) {
                    var b = 4;
                    return (k = k - 1) * k * ((b + 1) * k + b) + 1;
                }
            },
            bounce: {
                style: '',
                fn: function (k) {
                    if ((k /= 1) < (1 / 2.75)) {
                        return 7.5625 * k * k;
                    } else if (k < (2 / 2.75)) {
                        return 7.5625 * (k -= (1.5 / 2.75)) * k + 0.75;
                    } else if (k < (2.5 / 2.75)) {
                        return 7.5625 * (k -= (2.25 / 2.75)) * k + 0.9375;
                    } else {
                        return 7.5625 * (k -= (2.625 / 2.75)) * k + 0.984375;
                    }
                }
            },
            elastic: {
                style: '',
                fn: function (k) {
                    var f = 0.22,
                        e = 0.4;

                    if (k === 0) {
                        return 0;
                    }
                    if (k == 1) {
                        return 1;
                    }

                    return (e * Math.pow(2, -10 * k) * Math.sin((k - f / 4) * (2 * Math.PI) / f) + 1);
                }
            }
        });

        me.tap = function (e, eventName) {
            var ev = document.createEvent('Event');
            ev.initEvent(eventName, true, true);
            ev.pageX = e.pageX;
            ev.pageY = e.pageY;
            e.target.dispatchEvent(ev);
        };

        me.click = function (e) {
            var target = e.target,
                ev;

            if (!(/(SELECT|INPUT|TEXTAREA)/i).test(target.tagName)) {
                ev = document.createEvent('MouseEvents');
                ev.initMouseEvent('click', true, true, e.view, 1,
                    target.screenX, target.screenY, target.clientX, target.clientY,
                    e.ctrlKey, e.altKey, e.shiftKey, e.metaKey,
                    0, null);

                ev._constructed = true;
                target.dispatchEvent(ev);
            }
        };

        return me;
    })();

    function IScroll(el, options) {
        this.wrapper = typeof el == 'string' ? document.querySelector(el) : el;
        this.scroller = this.wrapper.children[0];
        this.scrollerStyle = this.scroller.style;		// cache style for better performance

        this.options = {

            resizeScrollbars: true,

            mouseWheelSpeed: 20,

            snapThreshold: 0.334,

// INSERT POINT: OPTIONS
            disablePointer: !utils.hasPointer,
            disableTouch: utils.hasPointer || !utils.hasTouch,
            disableMouse: utils.hasPointer || utils.hasTouch,
            startX: 0,
            startY: 0,
            scrollY: true,
            directionLockThreshold: 5,
            momentum: true,

            bounce: true,
            bounceTime: 600,
            bounceEasing: '',

            preventDefault: true,
            preventDefaultException: {tagName: /^(INPUT|TEXTAREA|BUTTON|SELECT)$/},

            HWCompositing: true,
            useTransition: true,
            useTransform: true,
            bindToWrapper: typeof window.onmousedown === "undefined"
        };

        for (var i in options) {
            this.options[i] = options[i];
        }

        // Normalize options
        this.translateZ = this.options.HWCompositing && utils.hasPerspective ? ' translateZ(0)' : '';

        this.options.useTransition = utils.hasTransition && this.options.useTransition;
        this.options.useTransform = utils.hasTransform && this.options.useTransform;

        this.options.eventPassthrough = this.options.eventPassthrough === true ? 'vertical' : this.options.eventPassthrough;
        this.options.preventDefault = !this.options.eventPassthrough && this.options.preventDefault;

        // If you want eventPassthrough I have to lock one of the axes
        this.options.scrollY = this.options.eventPassthrough == 'vertical' ? false : this.options.scrollY;
        this.options.scrollX = this.options.eventPassthrough == 'horizontal' ? false : this.options.scrollX;

        // With eventPassthrough we also need lockDirection mechanism
        this.options.freeScroll = this.options.freeScroll && !this.options.eventPassthrough;
        this.options.directionLockThreshold = this.options.eventPassthrough ? 0 : this.options.directionLockThreshold;

        this.options.bounceEasing = typeof this.options.bounceEasing == 'string' ? utils.ease[this.options.bounceEasing] || utils.ease.circular : this.options.bounceEasing;

        this.options.resizePolling = this.options.resizePolling === undefined ? 60 : this.options.resizePolling;

        if (this.options.tap === true) {
            this.options.tap = 'tap';
        }

        if (this.options.shrinkScrollbars == 'scale') {
            this.options.useTransition = false;
        }

        this.options.invertWheelDirection = this.options.invertWheelDirection ? -1 : 1;

// INSERT POINT: NORMALIZATION

        // Some defaults
        this.x = 0;
        this.y = 0;
        this.directionX = 0;
        this.directionY = 0;
        this._events = {};

// INSERT POINT: DEFAULTS

        this._init();
        this.refresh();

        this.scrollTo(this.options.startX, this.options.startY);
        this.enable();
    }

    IScroll.prototype = {
        version: '5.2.0',

        _init: function () {
            this._initEvents();

            if (this.options.scrollbars || this.options.indicators) {
                this._initIndicators();
            }

            if (this.options.mouseWheel) {
                this._initWheel();
            }

            if (this.options.snap) {
                this._initSnap();
            }

            if (this.options.keyBindings) {
                this._initKeys();
            }

// INSERT POINT: _init

        },

        destroy: function () {
            this._initEvents(true);
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
            this._execEvent('destroy');
        },

        _transitionEnd: function (e) {
            if (e.target != this.scroller || !this.isInTransition) {
                return;
            }

            this._transitionTime();
            if (!this.resetPosition(this.options.bounceTime)) {
                this.isInTransition = false;
                this._execEvent('scrollEnd');
            }
        },

        _start: function (e) {
            // React to left mouse button only
            if (utils.eventType[e.type] != 1) {
                // for button property
                // http://unixpapa.com/js/mouse.html
                var button;
                if (!e.which) {
                    /* IE case */
                    button = (e.button < 2) ? 0 :
                        ((e.button == 4) ? 1 : 2);
                } else {
                    /* All others */
                    button = e.button;
                }
                if (button !== 0) {
                    return;
                }
            }

            if (!this.enabled || (this.initiated && utils.eventType[e.type] !== this.initiated)) {
                return;
            }

            if (this.options.preventDefault && !utils.isBadAndroid && !utils.preventDefaultException(e.target, this.options.preventDefaultException)) {
                e.preventDefault();
            }

            var point = e.touches ? e.touches[0] : e,
                pos;

            this.initiated = utils.eventType[e.type];
            this.moved = false;
            this.distX = 0;
            this.distY = 0;
            this.directionX = 0;
            this.directionY = 0;
            this.directionLocked = 0;

            this.startTime = utils.getTime();

            if (this.options.useTransition && this.isInTransition) {
                this._transitionTime();
                this.isInTransition = false;
                pos = this.getComputedPosition();
                this._translate(Math.round(pos.x), Math.round(pos.y));
                this._execEvent('scrollEnd');
            } else if (!this.options.useTransition && this.isAnimating) {
                this.isAnimating = false;
                this._execEvent('scrollEnd');
            }

            this.startX = this.x;
            this.startY = this.y;
            this.absStartX = this.x;
            this.absStartY = this.y;
            this.pointX = point.pageX;
            this.pointY = point.pageY;

            this._execEvent('beforeScrollStart');
        },

        _move: function (e) {
            if (!this.enabled || utils.eventType[e.type] !== this.initiated) {
                return;
            }

            if (this.options.preventDefault) {	// increases performance on Android? TODO: check!
                e.preventDefault();
            }

            var point = e.touches ? e.touches[0] : e,
                deltaX = point.pageX - this.pointX,
                deltaY = point.pageY - this.pointY,
                timestamp = utils.getTime(),
                newX, newY,
                absDistX, absDistY;

            this.pointX = point.pageX;
            this.pointY = point.pageY;

            this.distX += deltaX;
            this.distY += deltaY;
            absDistX = Math.abs(this.distX);
            absDistY = Math.abs(this.distY);

            // We need to move at least 10 pixels for the scrolling to initiate
            if (timestamp - this.endTime > 300 && (absDistX < 10 && absDistY < 10)) {
                return;
            }

            // If you are scrolling in one direction lock the other
            if (!this.directionLocked && !this.options.freeScroll) {
                if (absDistX > absDistY + this.options.directionLockThreshold) {
                    this.directionLocked = 'h';		// lock horizontally
                } else if (absDistY >= absDistX + this.options.directionLockThreshold) {
                    this.directionLocked = 'v';		// lock vertically
                } else {
                    this.directionLocked = 'n';		// no lock
                }
            }

            if (this.directionLocked == 'h') {
                if (this.options.eventPassthrough == 'vertical') {
                    e.preventDefault();
                } else if (this.options.eventPassthrough == 'horizontal') {
                    this.initiated = false;
                    return;
                }

                deltaY = 0;
            } else if (this.directionLocked == 'v') {
                if (this.options.eventPassthrough == 'horizontal') {
                    e.preventDefault();
                } else if (this.options.eventPassthrough == 'vertical') {
                    this.initiated = false;
                    return;
                }

                deltaX = 0;
            }

            deltaX = this.hasHorizontalScroll ? deltaX : 0;
            deltaY = this.hasVerticalScroll ? deltaY : 0;

            newX = this.x + deltaX;
            newY = this.y + deltaY;

            // Slow down if outside of the boundaries
            if (newX > 0 || newX < this.maxScrollX) {
                newX = this.options.bounce ? this.x + deltaX / 3 : newX > 0 ? 0 : this.maxScrollX;
            }
            if (newY > 0 || newY < this.maxScrollY) {
                newY = this.options.bounce ? this.y + deltaY / 3 : newY > 0 ? 0 : this.maxScrollY;
            }

            this.directionX = deltaX > 0 ? -1 : deltaX < 0 ? 1 : 0;
            this.directionY = deltaY > 0 ? -1 : deltaY < 0 ? 1 : 0;

            if (!this.moved) {
                this._execEvent('scrollStart');
            }

            this.moved = true;

            this._translate(newX, newY);

            /* REPLACE START: _move */

            if (timestamp - this.startTime > 300) {
                this.startTime = timestamp;
                this.startX = this.x;
                this.startY = this.y;
            }

            /* REPLACE END: _move */

        },

        _end: function (e) {
            if (!this.enabled || utils.eventType[e.type] !== this.initiated) {
                return;
            }

            if (this.options.preventDefault && !utils.preventDefaultException(e.target, this.options.preventDefaultException)) {
                e.preventDefault();
            }

            var point = e.changedTouches ? e.changedTouches[0] : e,
                momentumX,
                momentumY,
                duration = utils.getTime() - this.startTime,
                newX = Math.round(this.x),
                newY = Math.round(this.y),
                distanceX = Math.abs(newX - this.startX),
                distanceY = Math.abs(newY - this.startY),
                time = 0,
                easing = '';

            this.isInTransition = 0;
            this.initiated = 0;
            this.endTime = utils.getTime();

            // reset if we are outside of the boundaries
            if (this.resetPosition(this.options.bounceTime)) {
                return;
            }

            this.scrollTo(newX, newY);	// ensures that the last position is rounded

            // we scrolled less than 10 pixels
            if (!this.moved) {
                if (this.options.tap) {
                    utils.tap(e, this.options.tap);
                }

                if (this.options.click) {
                    utils.click(e);
                }

                this._execEvent('scrollCancel');
                return;
            }

            if (this._events.flick && duration < 200 && distanceX < 100 && distanceY < 100) {
                this._execEvent('flick');
                return;
            }

            // start momentum animation if needed
            if (this.options.momentum && duration < 300) {
                momentumX = this.hasHorizontalScroll ? utils.momentum(this.x, this.startX, duration, this.maxScrollX, this.options.bounce ? this.wrapperWidth : 0, this.options.deceleration) : {
                    destination: newX,
                    duration: 0
                };
                momentumY = this.hasVerticalScroll ? utils.momentum(this.y, this.startY, duration, this.maxScrollY, this.options.bounce ? this.wrapperHeight : 0, this.options.deceleration) : {
                    destination: newY,
                    duration: 0
                };
                newX = momentumX.destination;
                newY = momentumY.destination;
                time = Math.max(momentumX.duration, momentumY.duration);
                this.isInTransition = 1;
            }


            if (this.options.snap) {
                var snap = this._nearestSnap(newX, newY);
                this.currentPage = snap;
                time = this.options.snapSpeed || Math.max(
                    Math.max(
                        Math.min(Math.abs(newX - snap.x), 1000),
                        Math.min(Math.abs(newY - snap.y), 1000)
                    ), 300);
                newX = snap.x;
                newY = snap.y;

                this.directionX = 0;
                this.directionY = 0;
                easing = this.options.bounceEasing;
            }

// INSERT POINT: _end

            if (newX != this.x || newY != this.y) {
                // change easing function when scroller goes out of the boundaries
                if (newX > 0 || newX < this.maxScrollX || newY > 0 || newY < this.maxScrollY) {
                    easing = utils.ease.quadratic;
                }

                this.scrollTo(newX, newY, time, easing);
                return;
            }

            this._execEvent('scrollEnd');
        },

        _resize: function () {
            var that = this;

            clearTimeout(this.resizeTimeout);

            this.resizeTimeout = setTimeout(function () {
                that.refresh();
            }, this.options.resizePolling);
        },

        resetPosition: function (time) {
            var x = this.x,
                y = this.y;

            time = time || 0;

            if (!this.hasHorizontalScroll || this.x > 0) {
                x = 0;
            } else if (this.x < this.maxScrollX) {
                x = this.maxScrollX;
            }

            if (!this.hasVerticalScroll || this.y > 0) {
                y = 0;
            } else if (this.y < this.maxScrollY) {
                y = this.maxScrollY;
            }

            if (x == this.x && y == this.y) {
                return false;
            }

            this.scrollTo(x, y, time, this.options.bounceEasing);

            return true;
        },

        disable: function () {
            this.enabled = false;
        },

        enable: function () {
            this.enabled = true;
        },

        refresh: function () {
            var rf = this.wrapper.offsetHeight;		// Force reflow

            this.wrapperWidth = this.wrapper.clientWidth;
            this.wrapperHeight = this.wrapper.clientHeight;

            /* REPLACE START: refresh */

            this.scrollerWidth = this.scroller.offsetWidth;
            this.scrollerHeight = this.scroller.offsetHeight;

            this.maxScrollX = this.wrapperWidth - this.scrollerWidth;
            this.maxScrollY = this.wrapperHeight - this.scrollerHeight;

            /* REPLACE END: refresh */

            this.hasHorizontalScroll = this.options.scrollX && this.maxScrollX < 0;
            this.hasVerticalScroll = this.options.scrollY && this.maxScrollY < 0;

            if (!this.hasHorizontalScroll) {
                this.maxScrollX = 0;
                this.scrollerWidth = this.wrapperWidth;
            }

            if (!this.hasVerticalScroll) {
                this.maxScrollY = 0;
                this.scrollerHeight = this.wrapperHeight;
            }

            this.endTime = 0;
            this.directionX = 0;
            this.directionY = 0;

            this.wrapperOffset = utils.offset(this.wrapper);

            this._execEvent('refresh');

            this.resetPosition();

// INSERT POINT: _refresh

        },

        on: function (type, fn) {
            if (!this._events[type]) {
                this._events[type] = [];
            }

            this._events[type].push(fn);
        },

        off: function (type, fn) {
            if (!this._events[type]) {
                return;
            }

            var index = this._events[type].indexOf(fn);

            if (index > -1) {
                this._events[type].splice(index, 1);
            }
        },

        _execEvent: function (type) {
            if (!this._events[type]) {
                return;
            }

            var i = 0,
                l = this._events[type].length;

            if (!l) {
                return;
            }

            for (; i < l; i++) {
                this._events[type][i].apply(this, [].slice.call(arguments, 1));
            }
        },

        scrollBy: function (x, y, time, easing) {
            x = this.x + x;
            y = this.y + y;
            time = time || 0;

            this.scrollTo(x, y, time, easing);
        },

        scrollTo: function (x, y, time, easing) {
            easing = easing || utils.ease.circular;

            this.isInTransition = this.options.useTransition && time > 0;
            var transitionType = this.options.useTransition && easing.style;
            if (!time || transitionType) {
                if (transitionType) {
                    this._transitionTimingFunction(easing.style);
                    this._transitionTime(time);
                }
                this._translate(x, y);
            } else {
                this._animate(x, y, time, easing.fn);
            }
        },

        scrollToElement: function (el, time, offsetX, offsetY, easing) {
            el = el.nodeType ? el : this.scroller.querySelector(el);

            if (!el) {
                return;
            }

            var pos = utils.offset(el);

            pos.left -= this.wrapperOffset.left;
            pos.top -= this.wrapperOffset.top;

            // if offsetX/Y are true we center the element to the screen
            if (offsetX === true) {
                offsetX = Math.round(el.offsetWidth / 2 - this.wrapper.offsetWidth / 2);
            }
            if (offsetY === true) {
                offsetY = Math.round(el.offsetHeight / 2 - this.wrapper.offsetHeight / 2);
            }

            pos.left -= offsetX || 0;
            pos.top -= offsetY || 0;

            pos.left = pos.left > 0 ? 0 : pos.left < this.maxScrollX ? this.maxScrollX : pos.left;
            pos.top = pos.top > 0 ? 0 : pos.top < this.maxScrollY ? this.maxScrollY : pos.top;

            time = time === undefined || time === null || time === 'auto' ? Math.max(Math.abs(this.x - pos.left), Math.abs(this.y - pos.top)) : time;

            this.scrollTo(pos.left, pos.top, time, easing);
        },

        _transitionTime: function (time) {
            time = time || 0;

            var durationProp = utils.style.transitionDuration;
            this.scrollerStyle[durationProp] = time + 'ms';

            if (!time && utils.isBadAndroid) {
                this.scrollerStyle[durationProp] = '0.0001ms';
                // remove 0.0001ms
                var self = this;
                rAF(function () {
                    if (self.scrollerStyle[durationProp] === '0.0001ms') {
                        self.scrollerStyle[durationProp] = '0s';
                    }
                });
            }


            if (this.indicators) {
                for (var i = this.indicators.length; i--;) {
                    this.indicators[i].transitionTime(time);
                }
            }


// INSERT POINT: _transitionTime

        },

        _transitionTimingFunction: function (easing) {
            this.scrollerStyle[utils.style.transitionTimingFunction] = easing;


            if (this.indicators) {
                for (var i = this.indicators.length; i--;) {
                    this.indicators[i].transitionTimingFunction(easing);
                }
            }


// INSERT POINT: _transitionTimingFunction

        },

        _translate: function (x, y) {
            if (this.options.useTransform) {

                /* REPLACE START: _translate */

                this.scrollerStyle[utils.style.transform] = 'translate(' + x + 'px,' + y + 'px)' + this.translateZ;

                /* REPLACE END: _translate */

            } else {
                x = Math.round(x);
                y = Math.round(y);
                this.scrollerStyle.left = x + 'px';
                this.scrollerStyle.top = y + 'px';
            }

            this.x = x;
            this.y = y;


            if (this.indicators) {
                for (var i = this.indicators.length; i--;) {
                    this.indicators[i].updatePosition();
                }
            }


// INSERT POINT: _translate

        },

        _initEvents: function (remove) {
            var eventType = remove ? utils.removeEvent : utils.addEvent,
                target = this.options.bindToWrapper ? this.wrapper : window;

            eventType(window, 'orientationchange', this);
            eventType(window, 'resize', this);

            if (this.options.click) {
                eventType(this.wrapper, 'click', this, true);
            }

            if (!this.options.disableMouse) {
                eventType(this.wrapper, 'mousedown', this);
                eventType(target, 'mousemove', this);
                eventType(target, 'mousecancel', this);
                eventType(target, 'mouseup', this);
            }

            if (utils.hasPointer && !this.options.disablePointer) {
                eventType(this.wrapper, utils.prefixPointerEvent('pointerdown'), this);
                eventType(target, utils.prefixPointerEvent('pointermove'), this);
                eventType(target, utils.prefixPointerEvent('pointercancel'), this);
                eventType(target, utils.prefixPointerEvent('pointerup'), this);
            }

            if (utils.hasTouch && !this.options.disableTouch) {
                eventType(this.wrapper, 'touchstart', this);
                eventType(target, 'touchmove', this);
                eventType(target, 'touchcancel', this);
                eventType(target, 'touchend', this);
            }

            eventType(this.scroller, 'transitionend', this);
            eventType(this.scroller, 'webkitTransitionEnd', this);
            eventType(this.scroller, 'oTransitionEnd', this);
            eventType(this.scroller, 'MSTransitionEnd', this);
        },

        getComputedPosition: function () {
            var matrix = window.getComputedStyle(this.scroller, null),
                x, y;

            if (this.options.useTransform) {
                matrix = matrix[utils.style.transform].split(')')[0].split(', ');
                x = +(matrix[12] || matrix[4]);
                y = +(matrix[13] || matrix[5]);
            } else {
                x = +matrix.left.replace(/[^-\d.]/g, '');
                y = +matrix.top.replace(/[^-\d.]/g, '');
            }

            return {x: x, y: y};
        },
        _initIndicators: function () {
            var interactive = this.options.interactiveScrollbars,
                customStyle = typeof this.options.scrollbars != 'string',
                indicators = [],
                indicator;

            var that = this;

            this.indicators = [];

            if (this.options.scrollbars) {
                // Vertical scrollbar
                if (this.options.scrollY) {
                    indicator = {
                        el: createDefaultScrollbar('v', interactive, this.options.scrollbars),
                        interactive: interactive,
                        defaultScrollbars: true,
                        customStyle: customStyle,
                        resize: this.options.resizeScrollbars,
                        shrink: this.options.shrinkScrollbars,
                        fade: this.options.fadeScrollbars,
                        listenX: false
                    };

                    this.wrapper.appendChild(indicator.el);
                    indicators.push(indicator);
                }

                // Horizontal scrollbar
                if (this.options.scrollX) {
                    indicator = {
                        el: createDefaultScrollbar('h', interactive, this.options.scrollbars),
                        interactive: interactive,
                        defaultScrollbars: true,
                        customStyle: customStyle,
                        resize: this.options.resizeScrollbars,
                        shrink: this.options.shrinkScrollbars,
                        fade: this.options.fadeScrollbars,
                        listenY: false
                    };

                    this.wrapper.appendChild(indicator.el);
                    indicators.push(indicator);
                }
            }

            if (this.options.indicators) {
                // TODO: check concat compatibility
                indicators = indicators.concat(this.options.indicators);
            }

            for (var i = indicators.length; i--;) {
                this.indicators.push(new Indicator(this, indicators[i]));
            }

            // TODO: check if we can use array.map (wide compatibility and performance issues)
            function _indicatorsMap(fn) {
                if (that.indicators) {
                    for (var i = that.indicators.length; i--;) {
                        fn.call(that.indicators[i]);
                    }
                }
            }

            if (this.options.fadeScrollbars) {
                this.on('scrollEnd', function () {
                    _indicatorsMap(function () {
                        this.fade();
                    });
                });

                this.on('scrollCancel', function () {
                    _indicatorsMap(function () {
                        this.fade();
                    });
                });

                this.on('scrollStart', function () {
                    _indicatorsMap(function () {
                        this.fade(1);
                    });
                });

                this.on('beforeScrollStart', function () {
                    _indicatorsMap(function () {
                        this.fade(1, true);
                    });
                });
            }


            this.on('refresh', function () {
                _indicatorsMap(function () {
                    this.refresh();
                });
            });

            this.on('destroy', function () {
                _indicatorsMap(function () {
                    this.destroy();
                });

                delete this.indicators;
            });
        },

        _initWheel: function () {
            utils.addEvent(this.wrapper, 'wheel', this);
            utils.addEvent(this.wrapper, 'mousewheel', this);
            utils.addEvent(this.wrapper, 'DOMMouseScroll', this);

            this.on('destroy', function () {
                clearTimeout(this.wheelTimeout);
                this.wheelTimeout = null;
                utils.removeEvent(this.wrapper, 'wheel', this);
                utils.removeEvent(this.wrapper, 'mousewheel', this);
                utils.removeEvent(this.wrapper, 'DOMMouseScroll', this);
            });
        },

        _wheel: function (e) {
            if (!this.enabled) {
                return;
            }

            e.preventDefault();

            var wheelDeltaX, wheelDeltaY,
                newX, newY,
                that = this;

            if (this.wheelTimeout === undefined) {
                that._execEvent('scrollStart');
            }

            // Execute the scrollEnd event after 400ms the wheel stopped scrolling
            clearTimeout(this.wheelTimeout);
            this.wheelTimeout = setTimeout(function () {
                if (!that.options.snap) {
                    that._execEvent('scrollEnd');
                }
                that.wheelTimeout = undefined;
            }, 400);

            if ('deltaX' in e) {
                if (e.deltaMode === 1) {
                    wheelDeltaX = -e.deltaX * this.options.mouseWheelSpeed;
                    wheelDeltaY = -e.deltaY * this.options.mouseWheelSpeed;
                } else {
                    wheelDeltaX = -e.deltaX;
                    wheelDeltaY = -e.deltaY;
                }
            } else if ('wheelDeltaX' in e) {
                wheelDeltaX = e.wheelDeltaX / 120 * this.options.mouseWheelSpeed;
                wheelDeltaY = e.wheelDeltaY / 120 * this.options.mouseWheelSpeed;
            } else if ('wheelDelta' in e) {
                wheelDeltaX = wheelDeltaY = e.wheelDelta / 120 * this.options.mouseWheelSpeed;
            } else if ('detail' in e) {
                wheelDeltaX = wheelDeltaY = -e.detail / 3 * this.options.mouseWheelSpeed;
            } else {
                return;
            }

            wheelDeltaX *= this.options.invertWheelDirection;
            wheelDeltaY *= this.options.invertWheelDirection;

            if (!this.hasVerticalScroll) {
                wheelDeltaX = wheelDeltaY;
                wheelDeltaY = 0;
            }

            if (this.options.snap) {
                newX = this.currentPage.pageX;
                newY = this.currentPage.pageY;

                if (wheelDeltaX > 0) {
                    newX--;
                } else if (wheelDeltaX < 0) {
                    newX++;
                }

                if (wheelDeltaY > 0) {
                    newY--;
                } else if (wheelDeltaY < 0) {
                    newY++;
                }

                this.goToPage(newX, newY);

                return;
            }

            newX = this.x + Math.round(this.hasHorizontalScroll ? wheelDeltaX : 0);
            newY = this.y + Math.round(this.hasVerticalScroll ? wheelDeltaY : 0);

            this.directionX = wheelDeltaX > 0 ? -1 : wheelDeltaX < 0 ? 1 : 0;
            this.directionY = wheelDeltaY > 0 ? -1 : wheelDeltaY < 0 ? 1 : 0;

            if (newX > 0) {
                newX = 0;
            } else if (newX < this.maxScrollX) {
                newX = this.maxScrollX;
            }

            if (newY > 0) {
                newY = 0;
            } else if (newY < this.maxScrollY) {
                newY = this.maxScrollY;
            }

            this.scrollTo(newX, newY, 0);

// INSERT POINT: _wheel
        },

        _initSnap: function () {
            this.currentPage = {};

            if (typeof this.options.snap == 'string') {
                this.options.snap = this.scroller.querySelectorAll(this.options.snap);
            }

            this.on('refresh', function () {
                var i = 0, l,
                    m = 0, n,
                    cx, cy,
                    x = 0, y,
                    stepX = this.options.snapStepX || this.wrapperWidth,
                    stepY = this.options.snapStepY || this.wrapperHeight,
                    el;

                this.pages = [];

                if (!this.wrapperWidth || !this.wrapperHeight || !this.scrollerWidth || !this.scrollerHeight) {
                    return;
                }

                if (this.options.snap === true) {
                    cx = Math.round(stepX / 2);
                    cy = Math.round(stepY / 2);

                    while (x > -this.scrollerWidth) {
                        this.pages[i] = [];
                        l = 0;
                        y = 0;

                        while (y > -this.scrollerHeight) {
                            this.pages[i][l] = {
                                x: Math.max(x, this.maxScrollX),
                                y: Math.max(y, this.maxScrollY),
                                width: stepX,
                                height: stepY,
                                cx: x - cx,
                                cy: y - cy
                            };

                            y -= stepY;
                            l++;
                        }

                        x -= stepX;
                        i++;
                    }
                } else {
                    el = this.options.snap;
                    l = el.length;
                    n = -1;

                    for (; i < l; i++) {
                        if (i === 0 || el[i].offsetLeft <= el[i - 1].offsetLeft) {
                            m = 0;
                            n++;
                        }

                        if (!this.pages[m]) {
                            this.pages[m] = [];
                        }

                        x = Math.max(-el[i].offsetLeft, this.maxScrollX);
                        y = Math.max(-el[i].offsetTop, this.maxScrollY);
                        cx = x - Math.round(el[i].offsetWidth / 2);
                        cy = y - Math.round(el[i].offsetHeight / 2);

                        this.pages[m][n] = {
                            x: x,
                            y: y,
                            width: el[i].offsetWidth,
                            height: el[i].offsetHeight,
                            cx: cx,
                            cy: cy
                        };

                        if (x > this.maxScrollX) {
                            m++;
                        }
                    }
                }

                this.goToPage(this.currentPage.pageX || 0, this.currentPage.pageY || 0, 0);

                // Update snap threshold if needed
                if (this.options.snapThreshold % 1 === 0) {
                    this.snapThresholdX = this.options.snapThreshold;
                    this.snapThresholdY = this.options.snapThreshold;
                } else {
                    this.snapThresholdX = Math.round(this.pages[this.currentPage.pageX][this.currentPage.pageY].width * this.options.snapThreshold);
                    this.snapThresholdY = Math.round(this.pages[this.currentPage.pageX][this.currentPage.pageY].height * this.options.snapThreshold);
                }
            });

            this.on('flick', function () {
                var time = this.options.snapSpeed || Math.max(
                    Math.max(
                        Math.min(Math.abs(this.x - this.startX), 1000),
                        Math.min(Math.abs(this.y - this.startY), 1000)
                    ), 300);

                this.goToPage(
                    this.currentPage.pageX + this.directionX,
                    this.currentPage.pageY + this.directionY,
                    time
                );
            });
        },

        _nearestSnap: function (x, y) {
            if (!this.pages.length) {
                return {x: 0, y: 0, pageX: 0, pageY: 0};
            }

            var i = 0,
                l = this.pages.length,
                m = 0;

            // Check if we exceeded the snap threshold
            if (Math.abs(x - this.absStartX) < this.snapThresholdX &&
                Math.abs(y - this.absStartY) < this.snapThresholdY) {
                return this.currentPage;
            }

            if (x > 0) {
                x = 0;
            } else if (x < this.maxScrollX) {
                x = this.maxScrollX;
            }

            if (y > 0) {
                y = 0;
            } else if (y < this.maxScrollY) {
                y = this.maxScrollY;
            }

            for (; i < l; i++) {
                if (x >= this.pages[i][0].cx) {
                    x = this.pages[i][0].x;
                    break;
                }
            }

            l = this.pages[i].length;

            for (; m < l; m++) {
                if (y >= this.pages[0][m].cy) {
                    y = this.pages[0][m].y;
                    break;
                }
            }

            if (i == this.currentPage.pageX) {
                i += this.directionX;

                if (i < 0) {
                    i = 0;
                } else if (i >= this.pages.length) {
                    i = this.pages.length - 1;
                }

                x = this.pages[i][0].x;
            }

            if (m == this.currentPage.pageY) {
                m += this.directionY;

                if (m < 0) {
                    m = 0;
                } else if (m >= this.pages[0].length) {
                    m = this.pages[0].length - 1;
                }

                y = this.pages[0][m].y;
            }

            return {
                x: x,
                y: y,
                pageX: i,
                pageY: m
            };
        },

        goToPage: function (x, y, time, easing) {
            easing = easing || this.options.bounceEasing;

            if (x >= this.pages.length) {
                x = this.pages.length - 1;
            } else if (x < 0) {
                x = 0;
            }

            if (y >= this.pages[x].length) {
                y = this.pages[x].length - 1;
            } else if (y < 0) {
                y = 0;
            }

            var posX = this.pages[x][y].x,
                posY = this.pages[x][y].y;

            time = time === undefined ? this.options.snapSpeed || Math.max(
                Math.max(
                    Math.min(Math.abs(posX - this.x), 1000),
                    Math.min(Math.abs(posY - this.y), 1000)
                ), 300) : time;

            this.currentPage = {
                x: posX,
                y: posY,
                pageX: x,
                pageY: y
            };

            this.scrollTo(posX, posY, time, easing);
        },

        next: function (time, easing) {
            var x = this.currentPage.pageX,
                y = this.currentPage.pageY;

            x++;

            if (x >= this.pages.length && this.hasVerticalScroll) {
                x = 0;
                y++;
            }

            this.goToPage(x, y, time, easing);
        },

        prev: function (time, easing) {
            var x = this.currentPage.pageX,
                y = this.currentPage.pageY;

            x--;

            if (x < 0 && this.hasVerticalScroll) {
                x = 0;
                y--;
            }

            this.goToPage(x, y, time, easing);
        },

        _initKeys: function (e) {
            // default key bindings
            var keys = {
                pageUp: 33,
                pageDown: 34,
                end: 35,
                home: 36,
                left: 37,
                up: 38,
                right: 39,
                down: 40
            };
            var i;

            // if you give me characters I give you keycode
            if (typeof this.options.keyBindings == 'object') {
                for (i in this.options.keyBindings) {
                    if (typeof this.options.keyBindings[i] == 'string') {
                        this.options.keyBindings[i] = this.options.keyBindings[i].toUpperCase().charCodeAt(0);
                    }
                }
            } else {
                this.options.keyBindings = {};
            }

            for (i in keys) {
                this.options.keyBindings[i] = this.options.keyBindings[i] || keys[i];
            }

            utils.addEvent(window, 'keydown', this);

            this.on('destroy', function () {
                utils.removeEvent(window, 'keydown', this);
            });
        },

        _key: function (e) {
            if (!this.enabled) {
                return;
            }

            var snap = this.options.snap,	// we are using this alot, better to cache it
                newX = snap ? this.currentPage.pageX : this.x,
                newY = snap ? this.currentPage.pageY : this.y,
                now = utils.getTime(),
                prevTime = this.keyTime || 0,
                acceleration = 0.250,
                pos;

            if (this.options.useTransition && this.isInTransition) {
                pos = this.getComputedPosition();

                this._translate(Math.round(pos.x), Math.round(pos.y));
                this.isInTransition = false;
            }

            this.keyAcceleration = now - prevTime < 200 ? Math.min(this.keyAcceleration + acceleration, 50) : 0;

            switch (e.keyCode) {
                case this.options.keyBindings.pageUp:
                    if (this.hasHorizontalScroll && !this.hasVerticalScroll) {
                        newX += snap ? 1 : this.wrapperWidth;
                    } else {
                        newY += snap ? 1 : this.wrapperHeight;
                    }
                    break;
                case this.options.keyBindings.pageDown:
                    if (this.hasHorizontalScroll && !this.hasVerticalScroll) {
                        newX -= snap ? 1 : this.wrapperWidth;
                    } else {
                        newY -= snap ? 1 : this.wrapperHeight;
                    }
                    break;
                case this.options.keyBindings.end:
                    newX = snap ? this.pages.length - 1 : this.maxScrollX;
                    newY = snap ? this.pages[0].length - 1 : this.maxScrollY;
                    break;
                case this.options.keyBindings.home:
                    newX = 0;
                    newY = 0;
                    break;
                case this.options.keyBindings.left:
                    newX += snap ? -1 : 5 + this.keyAcceleration >> 0;
                    break;
                case this.options.keyBindings.up:
                    newY += snap ? 1 : 5 + this.keyAcceleration >> 0;
                    break;
                case this.options.keyBindings.right:
                    newX -= snap ? -1 : 5 + this.keyAcceleration >> 0;
                    break;
                case this.options.keyBindings.down:
                    newY -= snap ? 1 : 5 + this.keyAcceleration >> 0;
                    break;
                default:
                    return;
            }

            if (snap) {
                this.goToPage(newX, newY);
                return;
            }

            if (newX > 0) {
                newX = 0;
                this.keyAcceleration = 0;
            } else if (newX < this.maxScrollX) {
                newX = this.maxScrollX;
                this.keyAcceleration = 0;
            }

            if (newY > 0) {
                newY = 0;
                this.keyAcceleration = 0;
            } else if (newY < this.maxScrollY) {
                newY = this.maxScrollY;
                this.keyAcceleration = 0;
            }

            this.scrollTo(newX, newY, 0);

            this.keyTime = now;
        },

        _animate: function (destX, destY, duration, easingFn) {
            var that = this,
                startX = this.x,
                startY = this.y,
                startTime = utils.getTime(),
                destTime = startTime + duration;

            function step() {
                var now = utils.getTime(),
                    newX, newY,
                    easing;

                if (now >= destTime) {
                    that.isAnimating = false;
                    that._translate(destX, destY);

                    if (!that.resetPosition(that.options.bounceTime)) {
                        that._execEvent('scrollEnd');
                    }

                    return;
                }

                now = (now - startTime) / duration;
                easing = easingFn(now);
                newX = (destX - startX) * easing + startX;
                newY = (destY - startY) * easing + startY;
                that._translate(newX, newY);

                if (that.isAnimating) {
                    rAF(step);
                }
            }

            this.isAnimating = true;
            step();
        },
        handleEvent: function (e) {
            switch (e.type) {
                case 'touchstart':
                case 'pointerdown':
                case 'MSPointerDown':
                case 'mousedown':
                    this._start(e);
                    break;
                case 'touchmove':
                case 'pointermove':
                case 'MSPointerMove':
                case 'mousemove':
                    this._move(e);
                    break;
                case 'touchend':
                case 'pointerup':
                case 'MSPointerUp':
                case 'mouseup':
                case 'touchcancel':
                case 'pointercancel':
                case 'MSPointerCancel':
                case 'mousecancel':
                    this._end(e);
                    break;
                case 'orientationchange':
                case 'resize':
                    this._resize();
                    break;
                case 'transitionend':
                case 'webkitTransitionEnd':
                case 'oTransitionEnd':
                case 'MSTransitionEnd':
                    this._transitionEnd(e);
                    break;
                case 'wheel':
                case 'DOMMouseScroll':
                case 'mousewheel':
                    this._wheel(e);
                    break;
                case 'keydown':
                    this._key(e);
                    break;
                case 'click':
                    if (this.enabled && !e._constructed) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    break;
            }
        }
    };

    function createDefaultScrollbar(direction, interactive, type) {
        var scrollbar = document.createElement('div'),
            indicator = document.createElement('div');

        if (type === true) {
            scrollbar.style.cssText = 'position:absolute;z-index:9999';
            indicator.style.cssText = '-webkit-box-sizing:border-box;-moz-box-sizing:border-box;box-sizing:border-box;position:absolute;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.9);border-radius:3px';
        }

        indicator.className = 'iScrollIndicator';

        if (direction == 'h') {
            if (type === true) {
                scrollbar.style.cssText += ';height:7px;left:2px;right:2px;bottom:0';
                indicator.style.height = '100%';
            }
            scrollbar.className = 'iScrollHorizontalScrollbar';
        } else {
            if (type === true) {
                scrollbar.style.cssText += ';width:7px;bottom:2px;top:2px;right:1px';
                indicator.style.width = '100%';
            }
            scrollbar.className = 'iScrollVerticalScrollbar';
        }

        scrollbar.style.cssText += ';overflow:hidden';

        if (!interactive) {
            scrollbar.style.pointerEvents = 'none';
        }

        scrollbar.appendChild(indicator);

        return scrollbar;
    }

    function Indicator(scroller, options) {
        this.wrapper = typeof options.el == 'string' ? document.querySelector(options.el) : options.el;
        this.wrapperStyle = this.wrapper.style;
        this.indicator = this.wrapper.children[0];
        this.indicatorStyle = this.indicator.style;
        this.scroller = scroller;

        this.options = {
            listenX: true,
            listenY: true,
            interactive: false,
            resize: true,
            defaultScrollbars: false,
            shrink: false,
            fade: false,
            speedRatioX: 0,
            speedRatioY: 0
        };

        for (var i in options) {
            this.options[i] = options[i];
        }

        this.sizeRatioX = 1;
        this.sizeRatioY = 1;
        this.maxPosX = 0;
        this.maxPosY = 0;

        if (this.options.interactive) {
            if (!this.options.disableTouch) {
                utils.addEvent(this.indicator, 'touchstart', this);
                utils.addEvent(window, 'touchend', this);
            }
            if (!this.options.disablePointer) {
                utils.addEvent(this.indicator, utils.prefixPointerEvent('pointerdown'), this);
                utils.addEvent(window, utils.prefixPointerEvent('pointerup'), this);
            }
            if (!this.options.disableMouse) {
                utils.addEvent(this.indicator, 'mousedown', this);
                utils.addEvent(window, 'mouseup', this);
            }
        }

        if (this.options.fade) {
            this.wrapperStyle[utils.style.transform] = this.scroller.translateZ;
            var durationProp = utils.style.transitionDuration;
            this.wrapperStyle[durationProp] = utils.isBadAndroid ? '0.0001ms' : '0ms';
            // remove 0.0001ms
            var self = this;
            if (utils.isBadAndroid) {
                rAF(function () {
                    if (self.wrapperStyle[durationProp] === '0.0001ms') {
                        self.wrapperStyle[durationProp] = '0s';
                    }
                });
            }
            this.wrapperStyle.opacity = '0';
        }
    }

    Indicator.prototype = {
        handleEvent: function (e) {
            switch (e.type) {
                case 'touchstart':
                case 'pointerdown':
                case 'MSPointerDown':
                case 'mousedown':
                    this._start(e);
                    break;
                case 'touchmove':
                case 'pointermove':
                case 'MSPointerMove':
                case 'mousemove':
                    this._move(e);
                    break;
                case 'touchend':
                case 'pointerup':
                case 'MSPointerUp':
                case 'mouseup':
                case 'touchcancel':
                case 'pointercancel':
                case 'MSPointerCancel':
                case 'mousecancel':
                    this._end(e);
                    break;
            }
        },

        destroy: function () {
            if (this.options.fadeScrollbars) {
                clearTimeout(this.fadeTimeout);
                this.fadeTimeout = null;
            }
            if (this.options.interactive) {
                utils.removeEvent(this.indicator, 'touchstart', this);
                utils.removeEvent(this.indicator, utils.prefixPointerEvent('pointerdown'), this);
                utils.removeEvent(this.indicator, 'mousedown', this);

                utils.removeEvent(window, 'touchmove', this);
                utils.removeEvent(window, utils.prefixPointerEvent('pointermove'), this);
                utils.removeEvent(window, 'mousemove', this);

                utils.removeEvent(window, 'touchend', this);
                utils.removeEvent(window, utils.prefixPointerEvent('pointerup'), this);
                utils.removeEvent(window, 'mouseup', this);
            }

            if (this.options.defaultScrollbars) {
                this.wrapper.parentNode.removeChild(this.wrapper);
            }
        },

        _start: function (e) {
            var point = e.touches ? e.touches[0] : e;

            e.preventDefault();
            e.stopPropagation();

            this.transitionTime();

            this.initiated = true;
            this.moved = false;
            this.lastPointX = point.pageX;
            this.lastPointY = point.pageY;

            this.startTime = utils.getTime();

            if (!this.options.disableTouch) {
                utils.addEvent(window, 'touchmove', this);
            }
            if (!this.options.disablePointer) {
                utils.addEvent(window, utils.prefixPointerEvent('pointermove'), this);
            }
            if (!this.options.disableMouse) {
                utils.addEvent(window, 'mousemove', this);
            }

            this.scroller._execEvent('beforeScrollStart');
        },

        _move: function (e) {
            var point = e.touches ? e.touches[0] : e,
                deltaX, deltaY,
                newX, newY,
                timestamp = utils.getTime();

            if (!this.moved) {
                this.scroller._execEvent('scrollStart');
            }

            this.moved = true;

            deltaX = point.pageX - this.lastPointX;
            this.lastPointX = point.pageX;

            deltaY = point.pageY - this.lastPointY;
            this.lastPointY = point.pageY;

            newX = this.x + deltaX;
            newY = this.y + deltaY;

            this._pos(newX, newY);

// INSERT POINT: indicator._move

            e.preventDefault();
            e.stopPropagation();
        },

        _end: function (e) {
            if (!this.initiated) {
                return;
            }

            this.initiated = false;

            e.preventDefault();
            e.stopPropagation();

            utils.removeEvent(window, 'touchmove', this);
            utils.removeEvent(window, utils.prefixPointerEvent('pointermove'), this);
            utils.removeEvent(window, 'mousemove', this);

            if (this.scroller.options.snap) {
                var snap = this.scroller._nearestSnap(this.scroller.x, this.scroller.y);

                var time = this.options.snapSpeed || Math.max(
                    Math.max(
                        Math.min(Math.abs(this.scroller.x - snap.x), 1000),
                        Math.min(Math.abs(this.scroller.y - snap.y), 1000)
                    ), 300);

                if (this.scroller.x != snap.x || this.scroller.y != snap.y) {
                    this.scroller.directionX = 0;
                    this.scroller.directionY = 0;
                    this.scroller.currentPage = snap;
                    this.scroller.scrollTo(snap.x, snap.y, time, this.scroller.options.bounceEasing);
                }
            }

            if (this.moved) {
                this.scroller._execEvent('scrollEnd');
            }
        },

        transitionTime: function (time) {
            time = time || 0;
            var durationProp = utils.style.transitionDuration;
            this.indicatorStyle[durationProp] = time + 'ms';

            if (!time && utils.isBadAndroid) {
                this.indicatorStyle[durationProp] = '0.0001ms';
                // remove 0.0001ms
                var self = this;
                rAF(function () {
                    if (self.indicatorStyle[durationProp] === '0.0001ms') {
                        self.indicatorStyle[durationProp] = '0s';
                    }
                });
            }
        },

        transitionTimingFunction: function (easing) {
            this.indicatorStyle[utils.style.transitionTimingFunction] = easing;
        },

        refresh: function () {
            this.transitionTime();

            if (this.options.listenX && !this.options.listenY) {
                this.indicatorStyle.display = this.scroller.hasHorizontalScroll ? 'block' : 'none';
            } else if (this.options.listenY && !this.options.listenX) {
                this.indicatorStyle.display = this.scroller.hasVerticalScroll ? 'block' : 'none';
            } else {
                this.indicatorStyle.display = this.scroller.hasHorizontalScroll || this.scroller.hasVerticalScroll ? 'block' : 'none';
            }

            if (this.scroller.hasHorizontalScroll && this.scroller.hasVerticalScroll) {
                utils.addClass(this.wrapper, 'iScrollBothScrollbars');
                utils.removeClass(this.wrapper, 'iScrollLoneScrollbar');

                if (this.options.defaultScrollbars && this.options.customStyle) {
                    if (this.options.listenX) {
                        this.wrapper.style.right = '8px';
                    } else {
                        this.wrapper.style.bottom = '8px';
                    }
                }
            } else {
                utils.removeClass(this.wrapper, 'iScrollBothScrollbars');
                utils.addClass(this.wrapper, 'iScrollLoneScrollbar');

                if (this.options.defaultScrollbars && this.options.customStyle) {
                    if (this.options.listenX) {
                        this.wrapper.style.right = '2px';
                    } else {
                        this.wrapper.style.bottom = '2px';
                    }
                }
            }

            var r = this.wrapper.offsetHeight;	// force refresh

            if (this.options.listenX) {
                this.wrapperWidth = this.wrapper.clientWidth;
                if (this.options.resize) {
                    this.indicatorWidth = Math.max(Math.round(this.wrapperWidth * this.wrapperWidth / (this.scroller.scrollerWidth || this.wrapperWidth || 1)), 8);
                    this.indicatorStyle.width = this.indicatorWidth + 'px';
                } else {
                    this.indicatorWidth = this.indicator.clientWidth;
                }

                this.maxPosX = this.wrapperWidth - this.indicatorWidth;

                if (this.options.shrink == 'clip') {
                    this.minBoundaryX = -this.indicatorWidth + 8;
                    this.maxBoundaryX = this.wrapperWidth - 8;
                } else {
                    this.minBoundaryX = 0;
                    this.maxBoundaryX = this.maxPosX;
                }

                this.sizeRatioX = this.options.speedRatioX || (this.scroller.maxScrollX && (this.maxPosX / this.scroller.maxScrollX));
            }

            if (this.options.listenY) {
                this.wrapperHeight = this.wrapper.clientHeight;
                if (this.options.resize) {
                    this.indicatorHeight = Math.max(Math.round(this.wrapperHeight * this.wrapperHeight / (this.scroller.scrollerHeight || this.wrapperHeight || 1)), 8);
                    this.indicatorStyle.height = this.indicatorHeight + 'px';
                } else {
                    this.indicatorHeight = this.indicator.clientHeight;
                }

                this.maxPosY = this.wrapperHeight - this.indicatorHeight;

                if (this.options.shrink == 'clip') {
                    this.minBoundaryY = -this.indicatorHeight + 8;
                    this.maxBoundaryY = this.wrapperHeight - 8;
                } else {
                    this.minBoundaryY = 0;
                    this.maxBoundaryY = this.maxPosY;
                }

                this.maxPosY = this.wrapperHeight - this.indicatorHeight;
                this.sizeRatioY = this.options.speedRatioY || (this.scroller.maxScrollY && (this.maxPosY / this.scroller.maxScrollY));
            }

            this.updatePosition();
        },

        updatePosition: function () {
            var x = this.options.listenX && Math.round(this.sizeRatioX * this.scroller.x) || 0,
                y = this.options.listenY && Math.round(this.sizeRatioY * this.scroller.y) || 0;

            if (!this.options.ignoreBoundaries) {
                if (x < this.minBoundaryX) {
                    if (this.options.shrink == 'scale') {
                        this.width = Math.max(this.indicatorWidth + x, 8);
                        this.indicatorStyle.width = this.width + 'px';
                    }
                    x = this.minBoundaryX;
                } else if (x > this.maxBoundaryX) {
                    if (this.options.shrink == 'scale') {
                        this.width = Math.max(this.indicatorWidth - (x - this.maxPosX), 8);
                        this.indicatorStyle.width = this.width + 'px';
                        x = this.maxPosX + this.indicatorWidth - this.width;
                    } else {
                        x = this.maxBoundaryX;
                    }
                } else if (this.options.shrink == 'scale' && this.width != this.indicatorWidth) {
                    this.width = this.indicatorWidth;
                    this.indicatorStyle.width = this.width + 'px';
                }

                if (y < this.minBoundaryY) {
                    if (this.options.shrink == 'scale') {
                        this.height = Math.max(this.indicatorHeight + y * 3, 8);
                        this.indicatorStyle.height = this.height + 'px';
                    }
                    y = this.minBoundaryY;
                } else if (y > this.maxBoundaryY) {
                    if (this.options.shrink == 'scale') {
                        this.height = Math.max(this.indicatorHeight - (y - this.maxPosY) * 3, 8);
                        this.indicatorStyle.height = this.height + 'px';
                        y = this.maxPosY + this.indicatorHeight - this.height;
                    } else {
                        y = this.maxBoundaryY;
                    }
                } else if (this.options.shrink == 'scale' && this.height != this.indicatorHeight) {
                    this.height = this.indicatorHeight;
                    this.indicatorStyle.height = this.height + 'px';
                }
            }

            this.x = x;
            this.y = y;

            if (this.scroller.options.useTransform) {
                this.indicatorStyle[utils.style.transform] = 'translate(' + x + 'px,' + y + 'px)' + this.scroller.translateZ;
            } else {
                this.indicatorStyle.left = x + 'px';
                this.indicatorStyle.top = y + 'px';
            }
        },

        _pos: function (x, y) {
            if (x < 0) {
                x = 0;
            } else if (x > this.maxPosX) {
                x = this.maxPosX;
            }

            if (y < 0) {
                y = 0;
            } else if (y > this.maxPosY) {
                y = this.maxPosY;
            }

            x = this.options.listenX ? Math.round(x / this.sizeRatioX) : this.scroller.x;
            y = this.options.listenY ? Math.round(y / this.sizeRatioY) : this.scroller.y;

            this.scroller.scrollTo(x, y);
        },

        fade: function (val, hold) {
            if (hold && !this.visible) {
                return;
            }

            clearTimeout(this.fadeTimeout);
            this.fadeTimeout = null;

            var time = val ? 250 : 500,
                delay = val ? 0 : 300;

            val = val ? '1' : '0';

            this.wrapperStyle[utils.style.transitionDuration] = time + 'ms';

            this.fadeTimeout = setTimeout((function (val) {
                this.wrapperStyle.opacity = val;
                this.visible = +val;
            }).bind(this, val), delay);
        }
    };

    IScroll.utils = utils;

    if (typeof module != 'undefined' && module.exports) {
        module.exports = IScroll;
    } else if (typeof define == 'function' && define.amd) {
        define(function () {
            return IScroll;
        });
    } else {
        window.IScroll = IScroll;
    }

})(window, document, Math);


/* ========================================================================
 * Bootstrap: tooltip.js v3.3.6
 * http://getbootstrap.com/javascript/#tooltip
 * Inspired by the original jQuery.tipsy by Jason Frame
 * ========================================================================
 * Copyright 2011-2016 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
    'use strict';

    // TOOLTIP PUBLIC CLASS DEFINITION
    // ===============================

    var Tooltip = function (element, options) {
        this.type = null
        this.options = null
        this.enabled = null
        this.timeout = null
        this.hoverState = null
        this.$element = null
        this.inState = null

        this.init('tooltip', element, options)
    }

    Tooltip.VERSION = '3.3.6'

    Tooltip.TRANSITION_DURATION = 150

    Tooltip.DEFAULTS = {
        animation: true,
        placement: 'top',
        selector: false,
        template: '<div class="tooltip" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
        trigger: 'hover focus',
        title: '',
        delay: 0,
        html: false,
        container: false,
        viewport: {
            selector: 'body',
            padding: 0
        }
    }

    Tooltip.prototype.init = function (type, element, options) {
        this.enabled = true
        this.type = type
        this.$element = $(element)
        this.options = this.getOptions(options)
        this.$viewport = this.options.viewport && $($.isFunction(this.options.viewport) ? this.options.viewport.call(this, this.$element) : (this.options.viewport.selector || this.options.viewport))
        this.inState = {click: false, hover: false, focus: false}

        if (this.$element[0] instanceof document.constructor && !this.options.selector) {
            throw new Error('`selector` option must be specified when initializing ' + this.type + ' on the window.document object!')
        }

        var triggers = this.options.trigger.split(' ')

        for (var i = triggers.length; i--;) {
            var trigger = triggers[i]

            if (trigger == 'click') {
                this.$element.on('click.' + this.type, this.options.selector, $.proxy(this.toggle, this))
            } else if (trigger != 'manual') {
                var eventIn = trigger == 'hover' ? 'mouseenter' : 'focusin'
                var eventOut = trigger == 'hover' ? 'mouseleave' : 'focusout'

                this.$element.on(eventIn + '.' + this.type, this.options.selector, $.proxy(this.enter, this))
                this.$element.on(eventOut + '.' + this.type, this.options.selector, $.proxy(this.leave, this))
            }
        }

        this.options.selector ?
            (this._options = $.extend({}, this.options, {trigger: 'manual', selector: ''})) :
            this.fixTitle()
    }

    Tooltip.prototype.getDefaults = function () {
        return Tooltip.DEFAULTS
    }

    Tooltip.prototype.getOptions = function (options) {
        options = $.extend({}, this.getDefaults(), this.$element.data(), options)

        if (options.delay && typeof options.delay == 'number') {
            options.delay = {
                show: options.delay,
                hide: options.delay
            }
        }

        return options
    }

    Tooltip.prototype.getDelegateOptions = function () {
        var options = {}
        var defaults = this.getDefaults()

        this._options && $.each(this._options, function (key, value) {
            if (defaults[key] != value) options[key] = value
        })

        return options
    }

    Tooltip.prototype.enter = function (obj) {
        var self = obj instanceof this.constructor ?
            obj : $(obj.currentTarget).data('bs.' + this.type)

        if (!self) {
            self = new this.constructor(obj.currentTarget, this.getDelegateOptions())
            $(obj.currentTarget).data('bs.' + this.type, self)
        }

        if (obj instanceof $.Event) {
            self.inState[obj.type == 'focusin' ? 'focus' : 'hover'] = true
        }

        if (self.tip().hasClass('in') || self.hoverState == 'in') {
            self.hoverState = 'in'
            return
        }

        clearTimeout(self.timeout)

        self.hoverState = 'in'

        if (!self.options.delay || !self.options.delay.show) return self.show()

        self.timeout = setTimeout(function () {
            if (self.hoverState == 'in') self.show()
        }, self.options.delay.show)
    }

    Tooltip.prototype.isInStateTrue = function () {
        for (var key in this.inState) {
            if (this.inState[key]) return true
        }

        return false
    }

    Tooltip.prototype.leave = function (obj) {
        var self = obj instanceof this.constructor ?
            obj : $(obj.currentTarget).data('bs.' + this.type)

        if (!self) {
            self = new this.constructor(obj.currentTarget, this.getDelegateOptions())
            $(obj.currentTarget).data('bs.' + this.type, self)
        }

        if (obj instanceof $.Event) {
            self.inState[obj.type == 'focusout' ? 'focus' : 'hover'] = false
        }

        if (self.isInStateTrue()) return

        clearTimeout(self.timeout)

        self.hoverState = 'out'

        if (!self.options.delay || !self.options.delay.hide) return self.hide()

        self.timeout = setTimeout(function () {
            if (self.hoverState == 'out') self.hide()
        }, self.options.delay.hide)
    }

    Tooltip.prototype.show = function () {
        var e = $.Event('show.bs.' + this.type)

        if (this.hasContent() && this.enabled) {
            this.$element.trigger(e)

            var inDom = $.contains(this.$element[0].ownerDocument.documentElement, this.$element[0])
            if (e.isDefaultPrevented() || !inDom) return
            var that = this

            var $tip = this.tip()

            var tipId = this.getUID(this.type)

            this.setContent()
            $tip.attr('id', tipId)
            this.$element.attr('aria-describedby', tipId)

            if (this.options.animation) $tip.addClass('fade')

            var placement = typeof this.options.placement == 'function' ?
                this.options.placement.call(this, $tip[0], this.$element[0]) :
                this.options.placement

            var autoToken = /\s?auto?\s?/i
            var autoPlace = autoToken.test(placement)
            if (autoPlace) placement = placement.replace(autoToken, '') || 'top'

            $tip
                .detach()
                .css({top: 0, left: 0, display: 'block'})
                .addClass(placement)
                .data('bs.' + this.type, this)

            this.options.container ? $tip.appendTo(this.options.container) : $tip.insertAfter(this.$element)
            this.$element.trigger('inserted.bs.' + this.type)

            var pos = this.getPosition()
            var actualWidth = $tip[0].offsetWidth
            var actualHeight = $tip[0].offsetHeight

            if (autoPlace) {
                var orgPlacement = placement
                var viewportDim = this.getPosition(this.$viewport)

                placement = placement == 'bottom' && pos.bottom + actualHeight > viewportDim.bottom ? 'top' :
                    placement == 'top' && pos.top - actualHeight < viewportDim.top ? 'bottom' :
                        placement == 'right' && pos.right + actualWidth > viewportDim.width ? 'left' :
                            placement == 'left' && pos.left - actualWidth < viewportDim.left ? 'right' :
                                placement

                $tip
                    .removeClass(orgPlacement)
                    .addClass(placement)
            }

            var calculatedOffset = this.getCalculatedOffset(placement, pos, actualWidth, actualHeight)

            this.applyPlacement(calculatedOffset, placement)

            var complete = function () {
                var prevHoverState = that.hoverState
                that.$element.trigger('shown.bs.' + that.type)
                that.hoverState = null

                if (prevHoverState == 'out') that.leave(that)
            }

            $.support.transition && this.$tip.hasClass('fade') ?
                $tip
                    .one('bsTransitionEnd', complete)
                    .emulateTransitionEnd(Tooltip.TRANSITION_DURATION) :
                complete()
        }
    }

    Tooltip.prototype.applyPlacement = function (offset, placement) {
        var $tip = this.tip()
        var width = $tip[0].offsetWidth
        var height = $tip[0].offsetHeight

        // manually read margins because getBoundingClientRect includes difference
        var marginTop = parseInt($tip.css('margin-top'), 10)
        var marginLeft = parseInt($tip.css('margin-left'), 10)

        // we must check for NaN for ie 8/9
        if (isNaN(marginTop)) marginTop = 0
        if (isNaN(marginLeft)) marginLeft = 0

        offset.top += marginTop
        offset.left += marginLeft

        // $.fn.offset doesn't round pixel values
        // so we use setOffset directly with our own function B-0
        $.offset.setOffset($tip[0], $.extend({
            using: function (props) {
                $tip.css({
                    top: Math.round(props.top),
                    left: Math.round(props.left)
                })
            }
        }, offset), 0)

        $tip.addClass('in')

        // check to see if placing tip in new offset caused the tip to resize itself
        var actualWidth = $tip[0].offsetWidth
        var actualHeight = $tip[0].offsetHeight

        if (placement == 'top' && actualHeight != height) {
            offset.top = offset.top + height - actualHeight
        }

        var delta = this.getViewportAdjustedDelta(placement, offset, actualWidth, actualHeight)

        if (delta.left) offset.left += delta.left
        else offset.top += delta.top

        var isVertical = /top|bottom/.test(placement)
        var arrowDelta = isVertical ? delta.left * 2 - width + actualWidth : delta.top * 2 - height + actualHeight
        var arrowOffsetPosition = isVertical ? 'offsetWidth' : 'offsetHeight'

        $tip.offset(offset)
        this.replaceArrow(arrowDelta, $tip[0][arrowOffsetPosition], isVertical)
    }

    Tooltip.prototype.replaceArrow = function (delta, dimension, isVertical) {
        this.arrow()
            .css(isVertical ? 'left' : 'top', 50 * (1 - delta / dimension) + '%')
            .css(isVertical ? 'top' : 'left', '')
    }

    Tooltip.prototype.setContent = function () {
        var $tip = this.tip()
        var title = this.getTitle()

        $tip.find('.tooltip-inner')[this.options.html ? 'html' : 'text'](title)
        $tip.removeClass('fade in top bottom left right')
    }

    Tooltip.prototype.hide = function (callback) {
        var that = this
        var $tip = $(this.$tip)
        var e = $.Event('hide.bs.' + this.type)

        function complete() {
            if (that.hoverState != 'in') $tip.detach()
            that.$element
                .removeAttr('aria-describedby')
                .trigger('hidden.bs.' + that.type)
            callback && callback()
        }

        this.$element.trigger(e)

        if (e.isDefaultPrevented()) return

        $tip.removeClass('in')

        $.support.transition && $tip.hasClass('fade') ?
            $tip
                .one('bsTransitionEnd', complete)
                .emulateTransitionEnd(Tooltip.TRANSITION_DURATION) :
            complete()

        this.hoverState = null

        return this
    }

    Tooltip.prototype.fixTitle = function () {
        var $e = this.$element
        if ($e.attr('title') || typeof $e.attr('data-original-title') != 'string') {
            $e.attr('data-original-title', $e.attr('title') || '').attr('title', '')
        }
    }

    Tooltip.prototype.hasContent = function () {
        return this.getTitle()
    }

    Tooltip.prototype.getPosition = function ($element) {
        $element = $element || this.$element

        var el = $element[0]
        var isBody = el.tagName == 'BODY'

        var elRect = el.getBoundingClientRect()
        if (elRect.width == null) {
            // width and height are missing in IE8, so compute them manually; see https://github.com/twbs/bootstrap/issues/14093
            elRect = $.extend({}, elRect, {width: elRect.right - elRect.left, height: elRect.bottom - elRect.top})
        }
        var elOffset = isBody ? {top: 0, left: 0} : $element.offset()
        var scroll = {scroll: isBody ? document.documentElement.scrollTop || document.body.scrollTop : $element.scrollTop()}
        var outerDims = isBody ? {width: $(window).width(), height: $(window).height()} : null

        return $.extend({}, elRect, scroll, outerDims, elOffset)
    }

    Tooltip.prototype.getCalculatedOffset = function (placement, pos, actualWidth, actualHeight) {
        return placement == 'bottom' ? {top: pos.top + pos.height, left: pos.left + pos.width / 2 - actualWidth / 2} :
            placement == 'top' ? {top: pos.top - actualHeight, left: pos.left + pos.width / 2 - actualWidth / 2} :
                placement == 'left' ? {top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left - actualWidth} :
                    /* placement == 'right' */ {
                        top: pos.top + pos.height / 2 - actualHeight / 2,
                        left: pos.left + pos.width
                    }

    }

    Tooltip.prototype.getViewportAdjustedDelta = function (placement, pos, actualWidth, actualHeight) {
        var delta = {top: 0, left: 0}
        if (!this.$viewport) return delta

        var viewportPadding = this.options.viewport && this.options.viewport.padding || 0
        var viewportDimensions = this.getPosition(this.$viewport)

        if (/right|left/.test(placement)) {
            var topEdgeOffset = pos.top - viewportPadding - viewportDimensions.scroll
            var bottomEdgeOffset = pos.top + viewportPadding - viewportDimensions.scroll + actualHeight
            if (topEdgeOffset < viewportDimensions.top) { // top overflow
                delta.top = viewportDimensions.top - topEdgeOffset
            } else if (bottomEdgeOffset > viewportDimensions.top + viewportDimensions.height) { // bottom overflow
                delta.top = viewportDimensions.top + viewportDimensions.height - bottomEdgeOffset
            }
        } else {
            var leftEdgeOffset = pos.left - viewportPadding
            var rightEdgeOffset = pos.left + viewportPadding + actualWidth
            if (leftEdgeOffset < viewportDimensions.left) { // left overflow
                delta.left = viewportDimensions.left - leftEdgeOffset
            } else if (rightEdgeOffset > viewportDimensions.right) { // right overflow
                delta.left = viewportDimensions.left + viewportDimensions.width - rightEdgeOffset
            }
        }

        return delta
    }

    Tooltip.prototype.getTitle = function () {
        var title
        var $e = this.$element
        var o = this.options

        title = $e.attr('data-original-title')
            || (typeof o.title == 'function' ? o.title.call($e[0]) : o.title)

        return title
    }

    Tooltip.prototype.getUID = function (prefix) {
        do prefix += ~~(Math.random() * 1000000)
        while (document.getElementById(prefix))
        return prefix
    }

    Tooltip.prototype.tip = function () {
        if (!this.$tip) {
            this.$tip = $(this.options.template)
            if (this.$tip.length != 1) {
                throw new Error(this.type + ' `template` option must consist of exactly 1 top-level element!')
            }
        }
        return this.$tip
    }

    Tooltip.prototype.arrow = function () {
        return (this.$arrow = this.$arrow || this.tip().find('.tooltip-arrow'))
    }

    Tooltip.prototype.enable = function () {
        this.enabled = true
    }

    Tooltip.prototype.disable = function () {
        this.enabled = false
    }

    Tooltip.prototype.toggleEnabled = function () {
        this.enabled = !this.enabled
    }

    Tooltip.prototype.toggle = function (e) {
        var self = this
        if (e) {
            self = $(e.currentTarget).data('bs.' + this.type)
            if (!self) {
                self = new this.constructor(e.currentTarget, this.getDelegateOptions())
                $(e.currentTarget).data('bs.' + this.type, self)
            }
        }

        if (e) {
            self.inState.click = !self.inState.click
            if (self.isInStateTrue()) self.enter(self)
            else self.leave(self)
        } else {
            self.tip().hasClass('in') ? self.leave(self) : self.enter(self)
        }
    }

    Tooltip.prototype.destroy = function () {
        var that = this
        clearTimeout(this.timeout)
        this.hide(function () {
            that.$element.off('.' + that.type).removeData('bs.' + that.type)
            if (that.$tip) {
                that.$tip.detach()
            }
            that.$tip = null
            that.$arrow = null
            that.$viewport = null
        })
    }


    // TOOLTIP PLUGIN DEFINITION
    // =========================

    function Plugin(option) {
        return this.each(function () {
            var $this = $(this)
            var data = $this.data('bs.tooltip')
            var options = typeof option == 'object' && option

            if (!data && /destroy|hide/.test(option)) return
            if (!data) $this.data('bs.tooltip', (data = new Tooltip(this, options)))
            if (typeof option == 'string') data[option]()
        })
    }

    var old = $.fn.tooltip

    $.fn.tooltip = Plugin
    $.fn.tooltip.Constructor = Tooltip


    // TOOLTIP NO CONFLICT
    // ===================

    $.fn.tooltip.noConflict = function () {
        $.fn.tooltip = old
        return this
    }

}(jQuery);
(function (a) {
    a.fn.validationEngineLanguage = function () {
    };
    a.validationEngineLanguage = {
        newLang: function () {
            a.validationEngineLanguage.allRules = {
                required: {
                    regex: "none",
                    alertText: "* This field is required",
                    alertTextCheckboxMultiple: "* Please select an option",
                    alertTextCheckboxe: "* This checkbox is required",
                    alertTextDateRange: "* Both date range fields are required"
                },
                requiredInFunction: {
                    func: function (d, e, c, b) {
                        return (d.val() == "test") ? true : false
                    }, alertText: "* Field must equal test"
                },
                dateRange: {regex: "none", alertText: "* Invalid ", alertText2: "Date Range"},
                dateTimeRange: {regex: "none", alertText: "* Invalid ", alertText2: "Date Time Range"},
                minSize: {regex: "none", alertText: "* Minimum ", alertText2: " characters required"},
                maxSize: {regex: "none", alertText: "* Maximum ", alertText2: " characters allowed"},
                groupRequired: {regex: "none", alertText: "* You must fill one of the following fields"},
                min: {regex: "none", alertText: "* Minimum value is "},
                max: {regex: "none", alertText: "* Maximum value is "},
                past: {regex: "none", alertText: "* Date prior to "},
                future: {regex: "none", alertText: "* Date past "},
                maxCheckbox: {regex: "none", alertText: "* Maximum ", alertText2: " options allowed"},
                minCheckbox: {regex: "none", alertText: "* Please select ", alertText2: " options"},
                equals: {regex: "none", alertText: "* Fields do not match"},
                creditCard: {regex: "none", alertText: "* Invalid credit card number"},
                phone: {
                    regex: /^([\+][0-9]{1,3}[\ \.\-])?([\(]{1}[0-9]{2,6}[\)])?([0-9\ \.\-\/]{3,20})((x|ext|extension)[\ ]?[0-9]{1,4})?$/,
                    alertText: "* Invalid phone number"
                },
                email: {
                    regex: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    alertText: "* Invalid email address"
                },
                integer: {regex: /^[\-\+]?\d+$/, alertText: "* Not a valid integer"},
                number: {
                    regex: /^[\-\+]?((([0-9]{1,3})([,][0-9]{3})*)|([0-9]+))?([\.]([0-9]+))?$/,
                    alertText: "* Invalid floating decimal number"
                },
                date: {
                    func: function (h) {
                        var f = new RegExp(/^(\d{4})[\/\-\.](0?[1-9]|1[012])[\/\-\.](0?[1-9]|[12][0-9]|3[01])$/);
                        var d = f.exec(h.val());
                        if (d == null) {
                            return false
                        }
                        var e = d[1];
                        var g = d[2] * 1;
                        var b = d[3] * 1;
                        var c = new Date(e, g - 1, b);
                        return (c.getFullYear() == e && c.getMonth() == (g - 1) && c.getDate() == b)
                    }, alertText: "* Invalid date, must be in YYYY-MM-DD format"
                },
                ipv4: {
                    regex: /^((([01]?[0-9]{1,2})|(2[0-4][0-9])|(25[0-5]))[.]){3}(([0-1]?[0-9]{1,2})|(2[0-4][0-9])|(25[0-5]))$/,
                    alertText: "* Invalid IP address"
                },
                url: {
                    regex: /^(https?|ftp):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i,
                    alertText: "* Invalid URL"
                },
                onlyNumberSp: {regex: /^[0-9\ ]+$/, alertText: "* Numbers only"},
                onlyLetterSp: {regex: /^[a-zA-Z\ \']+$/, alertText: "* Letters only"},
                onlyLetterNumber: {regex: /^[0-9a-zA-Z]+$/, alertText: "* No special characters allowed"},
                ajaxUserCall: {
                    url: "ajaxValidateFieldUser",
                    extraData: "name=eric",
                    alertText: "* This user is already taken",
                    alertTextLoad: "* Validating, please wait"
                },
                ajaxUserCallPhp: {
                    url: "phpajax/ajaxValidateFieldUser.php",
                    extraData: "name=eric",
                    alertTextOk: "* This username is available",
                    alertText: "* This user is already taken",
                    alertTextLoad: "* Validating, please wait"
                },
                ajaxNameCall: {
                    url: "ajaxValidateFieldName",
                    alertText: "* This name is already taken",
                    alertTextOk: "* This name is available",
                    alertTextLoad: "* Validating, please wait"
                },
                ajaxNameCallPhp: {
                    url: "phpajax/ajaxValidateFieldName.php",
                    alertText: "* This name is already taken",
                    alertTextLoad: "* Validating, please wait"
                },
                validate2fields: {alertText: "* Please input HELLO"},
                dateFormat: {
                    regex: /^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$|^(?:(?:(?:0?[13578]|1[02])(\/|-)31)|(?:(?:0?[1,3-9]|1[0-2])(\/|-)(?:29|30)))(\/|-)(?:[1-9]\d\d\d|\d[1-9]\d\d|\d\d[1-9]\d|\d\d\d[1-9])$|^(?:(?:0?[1-9]|1[0-2])(\/|-)(?:0?[1-9]|1\d|2[0-8]))(\/|-)(?:[1-9]\d\d\d|\d[1-9]\d\d|\d\d[1-9]\d|\d\d\d[1-9])$|^(0?2(\/|-)29)(\/|-)(?:(?:0[48]00|[13579][26]00|[2468][048]00)|(?:\d\d)?(?:0[48]|[2468][048]|[13579][26]))$/,
                    alertText: "* Invalid Date"
                },
                dateTimeFormat: {
                    regex: /^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])\s+(1[012]|0?[1-9]){1}:(0?[1-5]|[0-6][0-9]){1}:(0?[0-6]|[0-6][0-9]){1}\s+(am|pm|AM|PM){1}$|^(?:(?:(?:0?[13578]|1[02])(\/|-)31)|(?:(?:0?[1,3-9]|1[0-2])(\/|-)(?:29|30)))(\/|-)(?:[1-9]\d\d\d|\d[1-9]\d\d|\d\d[1-9]\d|\d\d\d[1-9])$|^((1[012]|0?[1-9]){1}\/(0?[1-9]|[12][0-9]|3[01]){1}\/\d{2,4}\s+(1[012]|0?[1-9]){1}:(0?[1-5]|[0-6][0-9]){1}:(0?[0-6]|[0-6][0-9]){1}\s+(am|pm|AM|PM){1})$/,
                    alertText: "* Invalid Date or Date Format",
                    alertText2: "Expected Format: ",
                    alertText3: "mm/dd/yyyy hh:mm:ss AM|PM or ",
                    alertText4: "yyyy-mm-dd hh:mm:ss AM|PM"
                }
            }
        }
    };
    a.validationEngineLanguage.newLang()
})(jQuery);
(function (b) {
    var a = {
        init: function (c) {
            var d = this;
            if (!d.data("jqv") || d.data("jqv") == null) {
                c = a._saveOptions(d, c);
                b(document).on("click", ".formError", function () {
                    b(this).fadeOut(150, function () {
                        b(this).parent(".formErrorOuter").remove();
                        b(this).remove()
                    })
                })
            }
            return this
        },
        attach: function (e) {
            var d = this;
            var c;
            if (e) {
                c = a._saveOptions(d, e)
            } else {
                c = d.data("jqv")
            }
            c.validateAttribute = (d.find("[data-validation-engine*=validate]").length) ? "data-validation-engine" : "class";
            if (c.binded) {
                d.on(c.validationEventTrigger, "[" + c.validateAttribute + "*=validate]:not([type=checkbox]):not([type=radio]):not(.datepicker)", a._onFieldEvent);
                d.on("click", "[" + c.validateAttribute + "*=validate][type=checkbox],[" + c.validateAttribute + "*=validate][type=radio]", a._onFieldEvent);
                d.on(c.validationEventTrigger, "[" + c.validateAttribute + "*=validate][class*=datepicker]", {delay: 300}, a._onFieldEvent)
            }
            if (c.autoPositionUpdate) {
                b(window).bind("resize", {noAnimation: true, formElem: d}, a.updatePromptsPosition)
            }
            d.on("click", "a[data-validation-engine-skip], a[class*='validate-skip'], button[data-validation-engine-skip], button[class*='validate-skip'], input[data-validation-engine-skip], input[class*='validate-skip']", a._submitButtonClick);
            d.removeData("jqv_submitButton");
            d.on("submit", a._onSubmitEvent);
            return this
        },
        detach: function () {
            var d = this;
            var c = d.data("jqv");
            d.find("[" + c.validateAttribute + "*=validate]").not("[type=checkbox]").off(c.validationEventTrigger, a._onFieldEvent);
            d.find("[" + c.validateAttribute + "*=validate][type=checkbox],[class*=validate][type=radio]").off("click", a._onFieldEvent);
            d.off("submit", a.onAjaxFormComplete);
            d.off("submit", a.onAjaxFormComplete);
            d.removeData("jqv");
            d.off("click", "a[data-validation-engine-skip], a[class*='validate-skip'], button[data-validation-engine-skip], button[class*='validate-skip'], input[data-validation-engine-skip], input[class*='validate-skip']", a._submitButtonClick);
            d.removeData("jqv_submitButton");
            if (c.autoPositionUpdate) {
                b(window).unbind("resize", a.updatePromptsPosition)
            }
            return this
        },
        validate: function () {
            var d = b(this);
            var f = null;
            if (d.is("form") || d.hasClass("validationEngineContainer")) {
                if (d.hasClass("validating")) {
                    return false
                } else {
                    d.addClass("validating");
                    var c = d.data("jqv");
                    var f = a._validateFields(this);
                    setTimeout(function () {
                        d.removeClass("validating")
                    }, 100);
                    if (f && c.onSuccess) {
                        c.onSuccess()
                    } else {
                        if (!f && c.onFailure) {
                            c.onFailure()
                        }
                    }
                }
            } else {
                if (d.is("form") || d.hasClass("validationEngineContainer")) {
                    d.removeClass("validating")
                } else {
                    var e = d.closest("form, .validationEngineContainer"),
                        c = (e.data("jqv")) ? e.data("jqv") : b.validationEngine.defaults, f = a._validateField(d, c);
                    if (f && c.onFieldSuccess) {
                        c.onFieldSuccess()
                    } else {
                        if (c.onFieldFailure && c.InvalidFields.length > 0) {
                            c.onFieldFailure()
                        }
                    }
                }
            }
            if (c.onValidationComplete) {
                return !!c.onValidationComplete(e, f)
            }
            return f
        },
        updatePromptsPosition: function (f) {
            if (f && this == window) {
                var e = f.data.formElem;
                var c = f.data.noAnimation
            } else {
                var e = b(this.closest("form, .validationEngineContainer"))
            }
            var d = e.data("jqv");
            e.find("[" + d.validateAttribute + "*=validate]").not(":disabled").each(function () {
                var i = b(this);
                if (d.prettySelect && i.is(":hidden")) {
                    i = e.find("#" + d.usePrefix + i.attr("id") + d.useSuffix)
                }
                var g = a._getPrompt(i);
                var h = b(g).find(".formErrorContent").html();
                if (g) {
                    a._updatePrompt(i, b(g), h, undefined, false, d, c)
                }
            });
            return this
        },
        showPrompt: function (d, f, h, e) {
            var g = this.closest("form, .validationEngineContainer");
            var c = g.data("jqv");
            if (!c) {
                c = a._saveOptions(this, c)
            }
            if (h) {
                c.promptPosition = h
            }
            c.showArrow = e == true;
            a._showPrompt(this, d, f, false, c);
            return this
        },
        hide: function () {
            var f = b(this).closest("form, .validationEngineContainer");
            var d = f.data("jqv");
            var e = (d && d.fadeDuration) ? d.fadeDuration : 0.3;
            var c;
            if (b(this).is("form") || b(this).hasClass("validationEngineContainer")) {
                c = "parentForm" + a._getClassName(b(this).attr("id"))
            } else {
                c = a._getClassName(b(this).attr("id")) + "formError"
            }
            b("." + c).fadeTo(e, 0.3, function () {
                b(this).parent(".formErrorOuter").remove();
                b(this).remove()
            });
            return this
        },
        hideAll: function () {
            var d = this;
            var c = d.data("jqv");
            var e = c ? c.fadeDuration : 300;
            b(".formError").fadeTo(e, 300, function () {
                b(this).parent(".formErrorOuter").remove();
                b(this).remove()
            });
            return this
        },
        _onFieldEvent: function (e) {
            var f = b(this);
            var d = f.closest("form, .validationEngineContainer");
            var c = d.data("jqv");
            c.eventTrigger = "field";
            window.setTimeout(function () {
                a._validateField(f, c);
                if (c.InvalidFields.length == 0 && c.onFieldSuccess) {
                    c.onFieldSuccess()
                } else {
                    if (c.InvalidFields.length > 0 && c.onFieldFailure) {
                        c.onFieldFailure()
                    }
                }
            }, (e.data) ? e.data.delay : 0)
        },
        _onSubmitEvent: function () {
            var f = b(this);
            var c = f.data("jqv");
            if (f.data("jqv_submitButton")) {
                var d = b("#" + f.data("jqv_submitButton"));
                if (d) {
                    if (d.length > 0) {
                        if (d.hasClass("validate-skip") || d.attr("data-validation-engine-skip") == "true") {
                            return true
                        }
                    }
                }
            }
            c.eventTrigger = "submit";
            var e = a._validateFields(f);
            if (e && c.ajaxFormValidation) {
                a._validateFormWithAjax(f, c);
                return false
            }
            if (c.onValidationComplete) {
                return !!c.onValidationComplete(f, e)
            }
            return e
        },
        _checkAjaxStatus: function (d) {
            var c = true;
            b.each(d.ajaxValidCache, function (e, f) {
                if (!f) {
                    c = false;
                    return false
                }
            });
            return c
        },
        _checkAjaxFieldStatus: function (c, d) {
            return d.ajaxValidCache[c] == true
        },
        _validateFields: function (e) {
            var m = e.data("jqv");
            var f = false;
            e.trigger("jqv.form.validating");
            var n = null;
            e.find("[" + m.validateAttribute + "*=validate]").not(":disabled").each(function () {
                var p = b(this);
                var o = [];
                if (b.inArray(p.attr("name"), o) < 0) {
                    f |= a._validateField(p, m);
                    if (f && n == null) {
                        if (p.is(":hidden") && m.prettySelect) {
                            n = p = e.find("#" + m.usePrefix + a._jqSelector(p.attr("id")) + m.useSuffix)
                        } else {
                            n = p
                        }
                    }
                    if (m.doNotShowAllErrosOnSubmit) {
                        return false
                    }
                    o.push(p.attr("name"));
                    if (m.showOneMessage == true && f) {
                        return false
                    }
                }
            });
            e.trigger("jqv.form.result", [f]);
            if (f) {
                if (m.scroll) {
                    var l = n.offset().top;
                    var h = n.offset().left;
                    var j = m.promptPosition;
                    if (typeof (j) == "string" && j.indexOf(":") != -1) {
                        j = j.substring(0, j.indexOf(":"))
                    }
                    if (j != "bottomRight" && j != "bottomLeft") {
                        var i = a._getPrompt(n);
                        if (i) {
                            l = i.offset().top
                        }
                    }
                    if (m.scrollOffset) {
                        l -= m.scrollOffset
                    }
                    if (m.isOverflown) {
                        var c = b(m.overflownDIV);
                        if (!c.length) {
                            return false
                        }
                        var d = c.scrollTop();
                        var g = -parseInt(c.offset().top);
                        l += d + g - 5;
                        var k = b(m.overflownDIV + ":not(:animated)");
                        k.animate({scrollTop: l}, 1100, function () {
                            if (m.focusFirstField) {
                                n.focus()
                            }
                        })
                    } else {
                        b("html, body").animate({scrollTop: l}, 1100, function () {
                            if (m.focusFirstField) {
                                n.focus()
                            }
                        });
                        b("html, body").animate({scrollLeft: h}, 1100)
                    }
                } else {
                    if (m.focusFirstField) {
                        n.focus()
                    }
                }
                return false
            }
            return true
        },
        _validateFormWithAjax: function (g, e) {
            var h = g.serialize();
            var f = (e.ajaxFormValidationMethod) ? e.ajaxFormValidationMethod : "GET";
            var d = (e.ajaxFormValidationURL) ? e.ajaxFormValidationURL : g.attr("action");
            var c = (e.dataType) ? e.dataType : "json";
            b.ajax({
                type: f,
                url: d,
                cache: false,
                dataType: c,
                data: h,
                form: g,
                methods: a,
                options: e,
                beforeSend: function () {
                    return e.onBeforeAjaxFormValidation(g, e)
                },
                error: function (i, j) {
                    a._ajaxError(i, j)
                },
                success: function (n) {
                    if ((c == "json") && (n !== true)) {
                        var l = false;
                        for (var m = 0; m < n.length; m++) {
                            var o = n[m];
                            var q = o[0];
                            var k = b(b("#" + q)[0]);
                            if (k.length == 1) {
                                var p = o[2];
                                if (o[1] == true) {
                                    if (p == "" || !p) {
                                        a._closePrompt(k)
                                    } else {
                                        if (e.allrules[p]) {
                                            var j = e.allrules[p].alertTextOk;
                                            if (j) {
                                                p = j
                                            }
                                        }
                                        if (e.showPrompts) {
                                            a._showPrompt(k, p, "pass", false, e, true)
                                        }
                                    }
                                } else {
                                    l |= true;
                                    if (e.allrules[p]) {
                                        var j = e.allrules[p].alertText;
                                        if (j) {
                                            p = j
                                        }
                                    }
                                    if (e.showPrompts) {
                                        a._showPrompt(k, p, "", false, e, true)
                                    }
                                }
                            }
                        }
                        e.onAjaxFormComplete(!l, g, n, e)
                    } else {
                        e.onAjaxFormComplete(true, g, n, e)
                    }
                }
            })
        },
        _validateField: function (d, l, s) {
            if (!d.attr("id")) {
                d.attr("id", "form-validation-field-" + b.validationEngine.fieldIdCounter);
                ++b.validationEngine.fieldIdCounter
            }
            if (!l.validateNonVisibleFields && (d.is(":hidden") && !l.prettySelect || d.parent().is(":hidden"))) {
                return false
            }
            var u = d.attr(l.validateAttribute);
            var z = /validate\[(.*)\]/.exec(u);
            if (!z) {
                return false
            }
            var v = z[1];
            var r = v.split(/\[|,|\]/);
            var o = false;
            var j = d.attr("name");
            var h = "";
            var y = "";
            var t = false;
            var A = false;
            l.isError = false;
            l.showArrow = true;
            if (l.maxErrorsPerField > 0) {
                A = true
            }
            var e = b(d.closest("form, .validationEngineContainer"));
            for (var x = 0; x < r.length; x++) {
                r[x] = r[x].replace(" ", "");
                if (r[x] === "") {
                    delete r[x]
                }
            }
            for (var x = 0, n = 0; x < r.length; x++) {
                if (A && n >= l.maxErrorsPerField) {
                    if (!t) {
                        var k = b.inArray("required", r);
                        t = (k != -1 && k >= x)
                    }
                    break
                }
                var g = undefined;
                switch (r[x]) {
                    case"required":
                        t = true;
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._required);
                        break;
                    case"custom":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._custom);
                        break;
                    case"groupRequired":
                        var w = "[" + l.validateAttribute + "*=" + r[x + 1] + "]";
                        var f = e.find(w).eq(0);
                        if (f[0] != d[0]) {
                            a._validateField(f, l, s);
                            l.showArrow = true
                        }
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._groupRequired);
                        if (g) {
                            t = true
                        }
                        l.showArrow = false;
                        break;
                    case"ajax":
                        g = a._ajax(d, r, x, l);
                        if (g) {
                            y = "load"
                        }
                        break;
                    case"minSize":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._minSize);
                        break;
                    case"maxSize":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._maxSize);
                        break;
                    case"min":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._min);
                        break;
                    case"max":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._max);
                        break;
                    case"past":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._past);
                        break;
                    case"future":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._future);
                        break;
                    case"dateRange":
                        var w = "[" + l.validateAttribute + "*=" + r[x + 1] + "]";
                        l.firstOfGroup = e.find(w).eq(0);
                        l.secondOfGroup = e.find(w).eq(1);
                        if (l.firstOfGroup[0].value || l.secondOfGroup[0].value) {
                            g = a._getErrorMessage(e, d, r[x], r, x, l, a._dateRange)
                        }
                        if (g) {
                            t = true
                        }
                        l.showArrow = false;
                        break;
                    case"dateTimeRange":
                        var w = "[" + l.validateAttribute + "*=" + r[x + 1] + "]";
                        l.firstOfGroup = e.find(w).eq(0);
                        l.secondOfGroup = e.find(w).eq(1);
                        if (l.firstOfGroup[0].value || l.secondOfGroup[0].value) {
                            g = a._getErrorMessage(e, d, r[x], r, x, l, a._dateTimeRange)
                        }
                        if (g) {
                            t = true
                        }
                        l.showArrow = false;
                        break;
                    case"maxCheckbox":
                        d = b(e.find("input[name='" + j + "']"));
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._maxCheckbox);
                        break;
                    case"minCheckbox":
                        d = b(e.find("input[name='" + j + "']"));
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._minCheckbox);
                        break;
                    case"equals":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._equals);
                        break;
                    case"funcCall":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._funcCall);
                        break;
                    case"creditCard":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._creditCard);
                        break;
                    case"condRequired":
                        g = a._getErrorMessage(e, d, r[x], r, x, l, a._condRequired);
                        if (g !== undefined) {
                            t = true
                        }
                        break;
                    default:
                }
                var m = false;
                if (typeof g == "object") {
                    switch (g.status) {
                        case"_break":
                            m = true;
                            break;
                        case"_error":
                            g = g.message;
                            break;
                        case"_error_no_prompt":
                            return true;
                            break;
                        default:
                            break
                    }
                }
                if (m) {
                    break
                }
                if (typeof g == "string") {
                    h += g + "<br/>";
                    l.isError = true;
                    n++
                }
            }
            if (!t && !(d.val()) && d.val().length < 1) {
                l.isError = false
            }
            var p = d.prop("type");
            var c = d.data("promptPosition") || l.promptPosition;
            if ((p == "radio" || p == "checkbox") && e.find("input[name='" + j + "']").size() > 1) {
                if (c === "inline") {
                    d = b(e.find("input[name='" + j + "'][type!=hidden]:last"))
                } else {
                    d = b(e.find("input[name='" + j + "'][type!=hidden]:first"))
                }
                l.showArrow = false
            }
            if (d.is(":hidden") && l.prettySelect) {
                d = e.find("#" + l.usePrefix + a._jqSelector(d.attr("id")) + l.useSuffix)
            }
            if (l.isError && l.showPrompts) {
                a._showPrompt(d, h, y, false, l)
            } else {
                if (!o) {
                    a._closePrompt(d)
                }
            }
            if (!o) {
                d.trigger("jqv.field.result", [d, l.isError, h])
            }
            var q = b.inArray(d[0], l.InvalidFields);
            if (q == -1) {
                if (l.isError) {
                    l.InvalidFields.push(d[0])
                }
            } else {
                if (!l.isError) {
                    l.InvalidFields.splice(q, 1)
                }
            }
            a._handleStatusCssClasses(d, l);
            if (l.isError && l.onFieldFailure) {
                l.onFieldFailure(d)
            }
            if (!l.isError && l.onFieldSuccess) {
                l.onFieldSuccess(d)
            }
            return l.isError
        },
        _handleStatusCssClasses: function (d, c) {
            if (c.addSuccessCssClassToField) {
                d.removeClass(c.addSuccessCssClassToField)
            }
            if (c.addFailureCssClassToField) {
                d.removeClass(c.addFailureCssClassToField)
            }
            if (c.addSuccessCssClassToField && !c.isError) {
                d.addClass(c.addSuccessCssClassToField)
            }
            if (c.addFailureCssClassToField && c.isError) {
                d.addClass(c.addFailureCssClassToField)
            }
        },
        _getErrorMessage: function (c, n, l, p, g, q, m) {
            var j = jQuery.inArray(l, p);
            if (l === "custom" || l === "funcCall") {
                var o = p[j + 1];
                l = l + "[" + o + "]";
                delete (p[j])
            }
            var d = l;
            var e = (n.attr("data-validation-engine")) ? n.attr("data-validation-engine") : n.attr("class");
            var h = e.split(" ");
            var k;
            if (l == "future" || l == "past" || l == "maxCheckbox" || l == "minCheckbox") {
                k = m(c, n, p, g, q)
            } else {
                k = m(n, p, g, q)
            }
            if (k != undefined) {
                var f = a._getCustomErrorMessage(b(n), h, d, q);
                if (f) {
                    k = f
                }
            }
            return k
        },
        _getCustomErrorMessage: function (j, e, h, l) {
            var f = false;
            var d = /^custom\[.*\]$/.test(h) ? a._validityProp.custom : a._validityProp[h];
            if (d != undefined) {
                f = j.attr("data-errormessage-" + d);
                if (f != undefined) {
                    return f
                }
            }
            f = j.attr("data-errormessage");
            if (f != undefined) {
                return f
            }
            var c = "#" + j.attr("id");
            if (typeof l.custom_error_messages[c] != "undefined" && typeof l.custom_error_messages[c][h] != "undefined") {
                f = l.custom_error_messages[c][h]["message"]
            } else {
                if (e.length > 0) {
                    for (var g = 0; g < e.length && e.length > 0; g++) {
                        var k = "." + e[g];
                        if (typeof l.custom_error_messages[k] != "undefined" && typeof l.custom_error_messages[k][h] != "undefined") {
                            f = l.custom_error_messages[k][h]["message"];
                            break
                        }
                    }
                }
            }
            if (!f && typeof l.custom_error_messages[h] != "undefined" && typeof l.custom_error_messages[h]["message"] != "undefined") {
                f = l.custom_error_messages[h]["message"]
            }
            return f
        },
        _validityProp: {
            required: "value-missing",
            custom: "custom-error",
            groupRequired: "value-missing",
            ajax: "custom-error",
            minSize: "range-underflow",
            maxSize: "range-overflow",
            min: "range-underflow",
            max: "range-overflow",
            past: "type-mismatch",
            future: "type-mismatch",
            dateRange: "type-mismatch",
            dateTimeRange: "type-mismatch",
            maxCheckbox: "range-overflow",
            minCheckbox: "range-underflow",
            equals: "pattern-mismatch",
            funcCall: "custom-error",
            creditCard: "pattern-mismatch",
            condRequired: "value-missing"
        },
        _required: function (h, k, f, m, g) {
            switch (h.prop("type")) {
                case"text":
                case"password":
                case"textarea":
                case"file":
                case"select-one":
                case"select-multiple":
                default:
                    var l = b.trim(h.val());
                    var e = b.trim(h.attr("data-validation-placeholder"));
                    var j = b.trim(h.attr("placeholder"));
                    if ((!l) || (e && l == e) || (j && l == j)) {
                        return m.allrules[k[f]].alertText
                    }
                    break;
                case"radio":
                case"checkbox":
                    if (g) {
                        if (!h.attr("checked")) {
                            return m.allrules[k[f]].alertTextCheckboxMultiple
                        }
                        break
                    }
                    var d = h.closest("form, .validationEngineContainer");
                    var c = h.attr("name");
                    if (d.find("input[name='" + c + "']:checked").size() == 0) {
                        if (d.find("input[name='" + c + "']:visible").size() == 1) {
                            return m.allrules[k[f]].alertTextCheckboxe
                        } else {
                            return m.allrules[k[f]].alertTextCheckboxMultiple
                        }
                    }
                    break
            }
        },
        _groupRequired: function (f, h, d, c) {
            var g = "[" + c.validateAttribute + "*=" + h[d + 1] + "]";
            var e = false;
            f.closest("form, .validationEngineContainer").find(g).each(function () {
                if (!a._required(b(this), h, d, c)) {
                    e = true;
                    return false
                }
            });
            if (!e) {
                return c.allrules[h[d]].alertText
            }
        },
        _custom: function (j, k, d, l) {
            var c = k[d + 1];
            var g = l.allrules[c];
            var h;
            if (!g) {
                alert("jqv:custom rule not found - " + c);
                return
            }
            if (g.regex) {
                var f = g.regex;
                if (!f) {
                    alert("jqv:custom regex not found - " + c);
                    return
                }
                var e = new RegExp(f);
                if (!e.test(j.val())) {
                    return l.allrules[c].alertText
                }
            } else {
                if (g.func) {
                    h = g.func;
                    if (typeof (h) !== "function") {
                        alert("jqv:custom parameter 'function' is no function - " + c);
                        return
                    }
                    if (!h(j, k, d, l)) {
                        return l.allrules[c].alertText
                    }
                } else {
                    alert("jqv:custom type not allowed " + c);
                    return
                }
            }
        },
        _funcCall: function (j, k, d, c) {
            var h = k[d + 1];
            var f;
            if (h.indexOf(".") > -1) {
                var g = h.split(".");
                var e = window;
                while (g.length) {
                    e = e[g.shift()]
                }
                f = e
            } else {
                f = window[h] || c.customFunctions[h]
            }
            if (typeof (f) == "function") {
                return f(j, k, d, c)
            }
        },
        _equals: function (f, g, e, d) {
            var c = g[e + 1];
            if (f.val() != b("#" + c).val()) {
                return d.allrules.equals.alertText
            }
        },
        _maxSize: function (h, j, f, e) {
            var d = j[f + 1];
            var c = h.val().length;
            if (c > d) {
                var g = e.allrules.maxSize;
                return g.alertText + d + g.alertText2
            }
        },
        _minSize: function (h, j, f, d) {
            var e = j[f + 1];
            var c = h.val().length;
            if (c < e) {
                var g = d.allrules.minSize;
                return g.alertText + e + g.alertText2
            }
        },
        _min: function (h, j, f, d) {
            var e = parseFloat(j[f + 1]);
            var c = parseFloat(h.val());
            if (c < e) {
                var g = d.allrules.min;
                if (g.alertText2) {
                    return g.alertText + e + g.alertText2
                }
                return g.alertText + e
            }
        },
        _max: function (h, j, f, e) {
            var d = parseFloat(j[f + 1]);
            var c = parseFloat(h.val());
            if (c > d) {
                var g = e.allrules.max;
                if (g.alertText2) {
                    return g.alertText + d + g.alertText2
                }
                return g.alertText + d
            }
        },
        _past: function (d, j, k, e, m) {
            var c = k[e + 1];
            var g = b(d.find("input[name='" + c.replace(/^#+/, "") + "']"));
            var f;
            if (c.toLowerCase() == "now") {
                f = new Date()
            } else {
                if (undefined != g.val()) {
                    if (g.is(":disabled")) {
                        return
                    }
                    f = a._parseDate(g.val())
                } else {
                    f = a._parseDate(c)
                }
            }
            var l = a._parseDate(j.val());
            if (l > f) {
                var h = m.allrules.past;
                if (h.alertText2) {
                    return h.alertText + a._dateToString(f) + h.alertText2
                }
                return h.alertText + a._dateToString(f)
            }
        },
        _future: function (d, j, k, e, m) {
            var c = k[e + 1];
            var g = b(d.find("input[name='" + c.replace(/^#+/, "") + "']"));
            var f;
            if (c.toLowerCase() == "now") {
                f = new Date()
            } else {
                if (undefined != g.val()) {
                    if (g.is(":disabled")) {
                        return
                    }
                    f = a._parseDate(g.val())
                } else {
                    f = a._parseDate(c)
                }
            }
            var l = a._parseDate(j.val());
            if (l < f) {
                var h = m.allrules.future;
                if (h.alertText2) {
                    return h.alertText + a._dateToString(f) + h.alertText2
                }
                return h.alertText + a._dateToString(f)
            }
        },
        _isDate: function (d) {
            var c = new RegExp(/^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$|^(?:(?:(?:0?[13578]|1[02])(\/|-)31)|(?:(?:0?[1,3-9]|1[0-2])(\/|-)(?:29|30)))(\/|-)(?:[1-9]\d\d\d|\d[1-9]\d\d|\d\d[1-9]\d|\d\d\d[1-9])$|^(?:(?:0?[1-9]|1[0-2])(\/|-)(?:0?[1-9]|1\d|2[0-8]))(\/|-)(?:[1-9]\d\d\d|\d[1-9]\d\d|\d\d[1-9]\d|\d\d\d[1-9])$|^(0?2(\/|-)29)(\/|-)(?:(?:0[48]00|[13579][26]00|[2468][048]00)|(?:\d\d)?(?:0[48]|[2468][048]|[13579][26]))$/);
            return c.test(d)
        },
        _isDateTime: function (d) {
            var c = new RegExp(/^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])\s+(1[012]|0?[1-9]){1}:(0?[1-5]|[0-6][0-9]){1}:(0?[0-6]|[0-6][0-9]){1}\s+(am|pm|AM|PM){1}$|^(?:(?:(?:0?[13578]|1[02])(\/|-)31)|(?:(?:0?[1,3-9]|1[0-2])(\/|-)(?:29|30)))(\/|-)(?:[1-9]\d\d\d|\d[1-9]\d\d|\d\d[1-9]\d|\d\d\d[1-9])$|^((1[012]|0?[1-9]){1}\/(0?[1-9]|[12][0-9]|3[01]){1}\/\d{2,4}\s+(1[012]|0?[1-9]){1}:(0?[1-5]|[0-6][0-9]){1}:(0?[0-6]|[0-6][0-9]){1}\s+(am|pm|AM|PM){1})$/);
            return c.test(d)
        },
        _dateCompare: function (d, c) {
            return (new Date(d.toString()) < new Date(c.toString()))
        },
        _dateRange: function (e, f, d, c) {
            if ((!c.firstOfGroup[0].value && c.secondOfGroup[0].value) || (c.firstOfGroup[0].value && !c.secondOfGroup[0].value)) {
                return c.allrules[f[d]].alertText + c.allrules[f[d]].alertText2
            }
            if (!a._isDate(c.firstOfGroup[0].value) || !a._isDate(c.secondOfGroup[0].value)) {
                return c.allrules[f[d]].alertText + c.allrules[f[d]].alertText2
            }
            if (!a._dateCompare(c.firstOfGroup[0].value, c.secondOfGroup[0].value)) {
                return c.allrules[f[d]].alertText + c.allrules[f[d]].alertText2
            }
        },
        _dateTimeRange: function (e, f, d, c) {
            if ((!c.firstOfGroup[0].value && c.secondOfGroup[0].value) || (c.firstOfGroup[0].value && !c.secondOfGroup[0].value)) {
                return c.allrules[f[d]].alertText + c.allrules[f[d]].alertText2
            }
            if (!a._isDateTime(c.firstOfGroup[0].value) || !a._isDateTime(c.secondOfGroup[0].value)) {
                return c.allrules[f[d]].alertText + c.allrules[f[d]].alertText2
            }
            if (!a._dateCompare(c.firstOfGroup[0].value, c.secondOfGroup[0].value)) {
                return c.allrules[f[d]].alertText + c.allrules[f[d]].alertText2
            }
        },
        _maxCheckbox: function (h, j, k, g, f) {
            var d = k[g + 1];
            var e = j.attr("name");
            var c = h.find("input[name='" + e + "']:checked").size();
            if (c > d) {
                f.showArrow = false;
                if (f.allrules.maxCheckbox.alertText2) {
                    return f.allrules.maxCheckbox.alertText + " " + d + " " + f.allrules.maxCheckbox.alertText2
                }
                return f.allrules.maxCheckbox.alertText
            }
        },
        _minCheckbox: function (h, j, k, g, f) {
            var d = k[g + 1];
            var e = j.attr("name");
            var c = h.find("input[name='" + e + "']:checked").size();
            if (c < d) {
                f.showArrow = false;
                return f.allrules.minCheckbox.alertText + " " + d + " " + f.allrules.minCheckbox.alertText2
            }
        },
        _creditCard: function (k, l, f, n) {
            var d = false, m = k.val().replace(/ +/g, "").replace(/-+/g, "");
            var c = m.length;
            if (c >= 14 && c <= 16 && parseInt(m) > 0) {
                var g = 0, f = c - 1, j = 1, h, e = new String();
                do {
                    h = parseInt(m.charAt(f));
                    e += (j++ % 2 == 0) ? h * 2 : h
                } while (--f >= 0);
                for (f = 0; f < e.length; f++) {
                    g += parseInt(e.charAt(f))
                }
                d = g % 10 == 0
            }
            if (!d) {
                return n.allrules.creditCard.alertText
            }
        },
        _ajax: function (o, r, j, s) {
            var q = r[j + 1];
            var n = s.allrules[q];
            var e = n.extraData;
            var l = n.extraDataDynamic;
            var h = {fieldId: o.attr("id"), fieldValue: o.val()};
            if (typeof e === "object") {
                b.extend(h, e)
            } else {
                if (typeof e === "string") {
                    var k = e.split("&");
                    for (var j = 0; j < k.length; j++) {
                        var p = k[j].split("=");
                        if (p[0] && p[0]) {
                            h[p[0]] = p[1]
                        }
                    }
                }
            }
            if (l) {
                var g = [];
                var m = String(l).split(",");
                for (var j = 0; j < m.length; j++) {
                    var c = m[j];
                    if (b(c).length) {
                        var d = o.closest("form, .validationEngineContainer").find(c).val();
                        var f = c.replace("#", "") + "=" + escape(d);
                        h[c.replace("#", "")] = d
                    }
                }
            }
            if (s.eventTrigger == "field") {
                delete (s.ajaxValidCache[o.attr("id")])
            }
            if (!s.isError && !a._checkAjaxFieldStatus(o.attr("id"), s)) {
                b.ajax({
                    type: s.ajaxFormValidationMethod,
                    url: n.url,
                    cache: false,
                    dataType: "json",
                    data: h,
                    field: o,
                    rule: n,
                    methods: a,
                    options: s,
                    beforeSend: function () {
                    },
                    error: function (i, t) {
                        a._ajaxError(i, t)
                    },
                    success: function (v) {
                        var x = v[0];
                        var u = b("#" + x).eq(0);
                        if (u.length == 1) {
                            var t = v[1];
                            var w = v[2];
                            if (!t) {
                                s.ajaxValidCache[x] = false;
                                s.isError = true;
                                if (w) {
                                    if (s.allrules[w]) {
                                        var i = s.allrules[w].alertText;
                                        if (i) {
                                            w = i
                                        }
                                    }
                                } else {
                                    w = n.alertText
                                }
                                if (s.showPrompts) {
                                    a._showPrompt(u, w, "", true, s)
                                }
                            } else {
                                s.ajaxValidCache[x] = true;
                                if (w) {
                                    if (s.allrules[w]) {
                                        var i = s.allrules[w].alertTextOk;
                                        if (i) {
                                            w = i
                                        }
                                    }
                                } else {
                                    w = n.alertTextOk
                                }
                                if (s.showPrompts) {
                                    if (w) {
                                        a._showPrompt(u, w, "pass", true, s)
                                    } else {
                                        a._closePrompt(u)
                                    }
                                }
                                if (s.eventTrigger == "submit") {
                                    o.closest("form").submit()
                                }
                            }
                        }
                        u.trigger("jqv.field.result", [u, s.isError, w])
                    }
                });
                return n.alertTextLoad
            }
        },
        _ajaxError: function (c, d) {
            if (c.status == 0 && d == null) {
                alert("The page is not served from a server! ajax call failed")
            } else {
                if (typeof console != "undefined") {
                    console.log("Ajax error: " + c.status + " " + d)
                }
            }
        },
        _dateToString: function (c) {
            return c.getFullYear() + "-" + (c.getMonth() + 1) + "-" + c.getDate()
        },
        _parseDate: function (e) {
            var c = e.split("-");
            if (c == e) {
                c = e.split("/")
            }
            if (c == e) {
                c = e.split(".");
                return new Date(c[2], (c[1] - 1), c[0])
            }
            return new Date(c[0], (c[1] - 1), c[2])
        },
        _showPrompt: function (i, g, h, f, e, d) {
            var c = a._getPrompt(i);
            if (d) {
                c = false
            }
            if (b.trim(g)) {
                if (c) {
                    a._updatePrompt(i, c, g, h, f, e)
                } else {
                    a._buildPrompt(i, g, h, f, e)
                }
            }
        },
        _buildPrompt: function (i, c, g, k, m) {
            var d = b("<div>");
            d.addClass(a._getClassName(i.attr("id")) + "formError");
            d.addClass("parentForm" + a._getClassName(i.closest("form, .validationEngineContainer").attr("id")));
            d.addClass("formError");
            switch (g) {
                case"pass":
                    d.addClass("greenPopup");
                    break;
                case"load":
                    d.addClass("blackPopup");
                    break;
                default:
            }
            if (k) {
                d.addClass("ajaxed")
            }
            var n = b("<div>").addClass("formErrorContent").html(c).appendTo(d);
            var f = i.data("promptPosition") || m.promptPosition;
            if (m.showArrow) {
                var j = b("<div>").addClass("formErrorArrow");
                if (typeof (f) == "string") {
                    var h = f.indexOf(":");
                    if (h != -1) {
                        f = f.substring(0, h)
                    }
                }
                switch (f) {
                    case"bottomLeft":
                    case"bottomRight":
                        d.find(".formErrorContent").before(j);
                        j.addClass("formErrorArrowBottom").html('<div class="line1"><!-- --></div><div class="line2"><!-- --></div><div class="line3"><!-- --></div><div class="line4"><!-- --></div><div class="line5"><!-- --></div><div class="line6"><!-- --></div><div class="line7"><!-- --></div><div class="line8"><!-- --></div><div class="line9"><!-- --></div><div class="line10"><!-- --></div>');
                        break;
                    case"topLeft":
                    case"topRight":
                        j.html('<div class="line10"><!-- --></div><div class="line9"><!-- --></div><div class="line8"><!-- --></div><div class="line7"><!-- --></div><div class="line6"><!-- --></div><div class="line5"><!-- --></div><div class="line4"><!-- --></div><div class="line3"><!-- --></div><div class="line2"><!-- --></div><div class="line1"><!-- --></div>');
                        d.append(j);
                        break
                }
            }
            if (m.addPromptClass) {
                d.addClass(m.addPromptClass)
            }
            var l = i.attr("data-required-class");
            if (l !== undefined) {
                d.addClass(l)
            } else {
                if (m.prettySelect) {
                    if (b("#" + i.attr("id")).next().is("select")) {
                        var e = b("#" + i.attr("id").substr(m.usePrefix.length).substring(m.useSuffix.length)).attr("data-required-class");
                        if (e !== undefined) {
                            d.addClass(e)
                        }
                    }
                }
            }
            d.css({opacity: 0});
            if (f === "inline") {
                d.addClass("inline");
                if (typeof i.attr("data-prompt-target") !== "undefined" && b("#" + i.attr("data-prompt-target")).length > 0) {
                    d.appendTo(b("#" + i.attr("data-prompt-target")))
                } else {
                    i.after(d)
                }
            } else {
                i.before(d)
            }
            var h = a._calculatePosition(i, d, m);
            d.css({
                position: f === "inline" ? "relative" : "absolute",
                top: h.callerTopPosition,
                left: h.callerleftPosition,
                marginTop: h.marginTopSize,
                opacity: 0
            }).data("callerField", i);
            if (m.autoHidePrompt) {
                setTimeout(function () {
                    d.animate({opacity: 0}, function () {
                        d.closest(".formErrorOuter").remove();
                        d.remove()
                    })
                }, m.autoHideDelay)
            }
            return d.animate({opacity: 0.87})
        },
        _updatePrompt: function (i, d, c, g, j, k, e) {
            if (d) {
                if (typeof g !== "undefined") {
                    if (g == "pass") {
                        d.addClass("greenPopup")
                    } else {
                        d.removeClass("greenPopup")
                    }
                    if (g == "load") {
                        d.addClass("blackPopup")
                    } else {
                        d.removeClass("blackPopup")
                    }
                }
                if (j) {
                    d.addClass("ajaxed")
                } else {
                    d.removeClass("ajaxed")
                }
                d.find(".formErrorContent").html(c);
                var h = a._calculatePosition(i, d, k);
                var f = {top: h.callerTopPosition, left: h.callerleftPosition, marginTop: h.marginTopSize};
                if (e) {
                    d.css(f)
                } else {
                    d.animate(f)
                }
            }
        },
        _closePrompt: function (d) {
            var c = a._getPrompt(d);
            if (c) {
                c.fadeTo("fast", 0, function () {
                    c.parent(".formErrorOuter").remove();
                    c.remove()
                })
            }
        },
        closePrompt: function (c) {
            return a._closePrompt(c)
        },
        _getPrompt: function (e) {
            var f = b(e).closest("form, .validationEngineContainer").attr("id");
            var d = a._getClassName(e.attr("id")) + "formError";
            var c = b("." + a._escapeExpression(d) + ".parentForm" + f)[0];
            if (c) {
                return b(c)
            }
        },
        _escapeExpression: function (c) {
            return c.replace(/([#;&,\.\+\*\~':"\!\^$\[\]\(\)=>\|])/g, "\\$1")
        },
        isRTL: function (e) {
            var f = b(document);
            var c = b("body");
            var d = (e && e.hasClass("rtl")) || (e && (e.attr("dir") || "").toLowerCase() === "rtl") || f.hasClass("rtl") || (f.attr("dir") || "").toLowerCase() === "rtl" || c.hasClass("rtl") || (c.attr("dir") || "").toLowerCase() === "rtl";
            return Boolean(d)
        },
        _calculatePosition: function (m, f, r) {
            var e, n, k;
            var g = m.width();
            var c = m.position().left;
            var p = m.position().top;
            var d = m.height();
            var q = f.height();
            e = n = 0;
            k = -q;
            var j = m.data("promptPosition") || r.promptPosition;
            var i = "";
            var h = "";
            var o = 0;
            var l = 0;
            if (typeof (j) == "string") {
                if (j.indexOf(":") != -1) {
                    i = j.substring(j.indexOf(":") + 1);
                    j = j.substring(0, j.indexOf(":"));
                    if (i.indexOf(",") != -1) {
                        h = i.substring(i.indexOf(",") + 1);
                        i = i.substring(0, i.indexOf(","));
                        l = parseInt(h);
                        if (isNaN(l)) {
                            l = 0
                        }
                    }
                    o = parseInt(i);
                    if (isNaN(i)) {
                        i = 0
                    }
                }
            }
            switch (j) {
                default:
                case"topRight":
                    n += c + g - 30;
                    e += p;
                    break;
                case"topLeft":
                    e += p;
                    n += c;
                    break;
                case"centerRight":
                    e = p + 4;
                    k = 0;
                    n = c + m.outerWidth(true) + 5;
                    break;
                case"centerLeft":
                    n = c - (f.width() + 2);
                    e = p + 4;
                    k = 0;
                    break;
                case"bottomLeft":
                    e = p + m.height() + 5;
                    k = 0;
                    n = c;
                    break;
                case"bottomRight":
                    n = c + g - 30;
                    e = p + m.height() + 5;
                    k = 0;
                    break;
                case"inline":
                    n = 0;
                    e = 0;
                    k = 0
            }
            n += o;
            e += l;
            return {callerTopPosition: e + "px", callerleftPosition: n + "px", marginTopSize: k + "px"}
        },
        _saveOptions: function (e, d) {
            if (b.validationEngineLanguage) {
                var c = b.validationEngineLanguage.allRules
            } else {
                b.error("jQuery.validationEngine rules are not loaded, plz add localization files to the page")
            }
            b.validationEngine.defaults.allrules = c;
            var f = b.extend(true, {}, b.validationEngine.defaults, d);
            e.data("jqv", f);
            return f
        },
        _getClassName: function (c) {
            if (c) {
                return c.replace(/:/g, "_").replace(/\./g, "_")
            }
        },
        _jqSelector: function (c) {
            return c.replace(/([;&,\.\+\*\~':"\!\^#$%@\[\]\(\)=>\|])/g, "\\$1")
        },
        _condRequired: function (g, h, e, d) {
            var c, f;
            for (c = (e + 1); c < h.length; c++) {
                f = jQuery("#" + h[c]).first();
                if (f.length && a._required(f, ["required"], 0, d, true) == undefined) {
                    return a._required(g, ["required"], 0, d)
                }
            }
        },
        _submitButtonClick: function (e) {
            var c = b(this);
            var d = c.closest("form, .validationEngineContainer");
            d.data("jqv_submitButton", c.attr("id"))
        }
    };
    b.fn.validationEngine = function (d) {
        var c = b(this);
        if (!c[0]) {
            return c
        }
        if (typeof (d) == "string" && d.charAt(0) != "_" && a[d]) {
            if (d != "showPrompt" && d != "hide" && d != "hideAll") {
                a.init.apply(c)
            }
            return a[d].apply(c, Array.prototype.slice.call(arguments, 1))
        } else {
            if (typeof d == "object" || !d) {
                a.init.apply(c, arguments);
                return a.attach.apply(c)
            } else {
                b.error("Method " + d + " does not exist in jQuery.validationEngine")
            }
        }
    };
    b.validationEngine = {
        fieldIdCounter: 0,
        defaults: {
            validationEventTrigger: "blur",
            scroll: true,
            focusFirstField: true,
            showPrompts: true,
            validateNonVisibleFields: false,
            promptPosition: "topRight",
            bindMethod: "bind",
            inlineAjax: false,
            ajaxFormValidation: false,
            ajaxFormValidationURL: false,
            ajaxFormValidationMethod: "get",
            onAjaxFormComplete: b.noop,
            onBeforeAjaxFormValidation: b.noop,
            onValidationComplete: false,
            doNotShowAllErrosOnSubmit: false,
            custom_error_messages: {},
            binded: true,
            showArrow: true,
            isError: false,
            maxErrorsPerField: false,
            ajaxValidCache: {},
            autoPositionUpdate: false,
            InvalidFields: [],
            onFieldSuccess: false,
            onFieldFailure: false,
            onSuccess: false,
            onFailure: false,
            validateAttribute: "class",
            addSuccessCssClassToField: "",
            addFailureCssClassToField: "",
            autoHidePrompt: false,
            autoHideDelay: 10000,
            fadeDuration: 0.3,
            prettySelect: false,
            addPromptClass: "",
            usePrefix: "",
            useSuffix: "",
            showOneMessage: false
        }
    };
    b(function () {
        b.validationEngine.defaults.promptPosition = a.isRTL() ? "topLeft" : "topRight"
    })
})(jQuery);
/* Jonathan Snook - MIT License - https://github.com/snookca/prepareTransition */
(function (a) {
    a.fn.prepareTransition = function () {
        return this.each(function () {
            var b = a(this);
            b.one("TransitionEnd webkitTransitionEnd transitionend oTransitionEnd", function () {
                b.removeClass("is-transitioning")
            });
            var c = ["transition-duration", "-moz-transition-duration", "-webkit-transition-duration", "-o-transition-duration"];
            var d = 0;
            a.each(c, function (a, c) {
                d = parseFloat(b.css(c)) || d
            });
            if (d != 0) {
                b.addClass("is-transitioning");
                b[0].offsetWidth
            }
        })
    }
})(jQuery);

/* replaceUrlParam - http://stackoverflow.com/questions/7171099/how-to-replace-url-parameter-with-javascript-jquery */
function replaceUrlParam(e, r, a) {
    var n = new RegExp("(" + r + "=).*?(&|$)"), c = e;
    return c = e.search(n) >= 0 ? e.replace(n, "$1" + a + "$2") : c + (c.indexOf("?") > 0 ? "&" : "?") + r + "=" + a
};

/*============================================================================
  Money Format
  - Shopify.format money is defined in option_selection.js.
    If that file is not included, it is redefined here.
==============================================================================*/
if ((typeof Shopify) === 'undefined') {
    Shopify = {};
}
if (!Shopify.formatMoney) {
    Shopify.formatMoney = function (cents, format) {
        var value = '',
            placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
            formatString = (format || this.money_format);

        if (typeof cents == 'string') {
            cents = cents.replace('.', '');
        }

        function defaultOption(opt, def) {
            return (typeof opt == 'undefined' ? def : opt);
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
            precision = defaultOption(precision, 2);
            thousands = defaultOption(thousands, ',');
            decimal = defaultOption(decimal, '.');

            if (isNaN(number) || number == null) {
                return 0;
            }

            number = (number / 100.0).toFixed(precision);

            var parts = number.split('.'),
                dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
                cents = parts[1] ? (decimal + parts[1]) : '';

            return dollars + cents;
        }

        switch (formatString.match(placeholderRegex)[1]) {
            case 'amount':
                value = formatWithDelimiters(cents, 2);
                break;
            case 'amount_no_decimals':
                value = formatWithDelimiters(cents, 0);
                break;
            case 'amount_with_comma_separator':
                value = formatWithDelimiters(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = formatWithDelimiters(cents, 0, '.', ',');
                break;
        }

        return formatString.replace(placeholderRegex, value);
    };
}

const productIdRestrict = '40703376064586';

// Timber functions
window.timber = window.timber || {};

timber.cacheSelectors = function () {
    timber.cache = {
        // General
        $html: $('html'),
        $body: $('body'),

        // Navigation
        $navigation: $('#AccessibleNav'),
        $mobileSubNavToggle: $('.mobile-nav__toggle'),

        // Collection Pages
        $changeView: $('.change-view'),

        // Product Page
        $productImage: $('#ProductPhotoImg'),
        $thumbImages: $('#ProductThumbs').find('a.product-single__thumbnail'),

        // Customer Pages
        $recoverPasswordLink: $('#RecoverPassword'),
        $hideRecoverPasswordLink: $('#HideRecoverPasswordLink'),
        $recoverPasswordForm: $('#RecoverPasswordForm'),
        $customerLoginForm: $('#CustomerLoginForm'),
        $passwordResetSuccess: $('#ResetSuccess')
    };
};

timber.init = function () {
    /*FastClick.attach(document.body);*/
    timber.cacheSelectors();
    /*timber.accessibleNav();*/
    timber.drawersInit();
    timber.mobileNavToggle();
    timber.productImageSwitch();
    timber.responsiveVideos();
    timber.collectionViews();
    timber.loginForms();
};
/*
timber.accessibleNav = function () {
    var $nav = timber.cache.$navigation,
        $allLinks = $nav.find('a'),
        $topLevel = $nav.children('li').find('a'),
        $parents = $nav.find('.site-nav--has-dropdown'),
        $subMenuLinks = $nav.find('.site-nav__dropdown').find('a'),
        activeClass = 'nav-hover',
        focusClass = 'nav-focus';

    // Mouseenter
    $parents.on('mouseenter touchstart', function (evt) {
        var $el = $(this);

        if (!$el.hasClass(activeClass)) {
            evt.preventDefault();
        }

        showDropdown($el);
    });

    // Mouseout
    $parents.on('mouseleave', function () {
        hideDropdown($(this));
    });

    $subMenuLinks.on('touchstart', function (evt) {
        // Prevent touchstart on body from firing instead of link
        evt.stopImmediatePropagation();
    });

    $allLinks.focus(function () {
        handleFocus($(this));
    });

    $allLinks.blur(function () {
        removeFocus($topLevel);
    });

    // accessibleNav private methods
    function handleFocus($el) {
        var $subMenu = $el.next('ul'),
            hasSubMenu = $subMenu.hasClass('sub-nav') ? true : false,
            isSubItem = $('.site-nav__dropdown').has($el).length,
            $newFocus = null;

        // Add focus class for top level items, or keep menu shown
        if (!isSubItem) {
            removeFocus($topLevel);
            addFocus($el);
        } else {
            $newFocus = $el.closest('.site-nav--has-dropdown').find('a');
            addFocus($newFocus);
        }
    }

    function showDropdown($el) {
        $el.addClass(activeClass);

        setTimeout(function () {
            timber.cache.$body.on('touchstart', function () {
                hideDropdown($el);
            });
        }, 250);
    }

    function hideDropdown($el) {
        $el.removeClass(activeClass);
        timber.cache.$body.off('touchstart');
    }

    function addFocus($el) {
        $el.addClass(focusClass);
    }

    function removeFocus($el) {
        $el.removeClass(focusClass);
    }
};
*/

timber.drawersInit = function () {
    timber.LeftDrawer = new timber.Drawers('NavDrawer', 'left');
    timber.RightDrawer = new timber.Drawers('CartDrawer', 'right', {
        'onDrawerOpen': eval('ajaxCart.load')
    });
};

timber.mobileNavToggle = function () {
    timber.cache.$mobileSubNavToggle.on('click', function () {
        $(this).parent().toggleClass('mobile-nav--expanded');
    });
};

timber.getHash = function () {
    return window.location.hash;
};

timber.updateHash = function (hash) {
    window.location.hash = '#' + hash;
    $('#' + hash).attr('tabindex', -1).focus();
};

timber.productPage = function (options) {
    var moneyFormat = options.money_format,
        variant = options.variant,
        selector = options.selector;
    availableSizes = options.availableSizes;

    // Selectors
    var $productImage = $('#ProductPhotoImg'),
        $addToCart = $('#AddToCart'),
        $productPrice = $('#ProductPrice'),
        $comparePrice = $('#ComparePrice'),
        $quantityElements = $('.quantity-selector, label + .js-qty'),
        $addToCartText = $('#AddToCartText');

    if (variant) {
        // Update variant image, if one is set
        if (variant.featured_image) {
            var newImg = variant.featured_image,
                el = $productImage[0];
            Shopify.Image.switchImage(newImg, el, timber.switchImage);
        }

        // Select a valid variant if available
        if (availableSizes > 0 || variant.id == productIdRestrict) {
            // Available, enable the submit button, change text, show quantity elements
            $addToCart.removeClass('disabled').prop('disabled', false);
            $addToCartText.html("Add to Cart");
            $quantityElements.show();
        } else {
            // Sold out, disable the submit button, change text, hide quantity elements
            $addToCart.addClass('disabled').prop('disabled', true);
            $addToCartText.html("Sold Out");
            $quantityElements.hide();
        }

        // Regardless of stock, update the product price
        $productPrice.html(Shopify.formatMoney(variant.price, moneyFormat));

        // Also update and show the product's compare price if necessary
        if (variant.compare_at_price > variant.price) {
            // $comparePrice
            //   .html("Compare at" + ' ' + Shopify.formatMoney(variant.compare_at_price, moneyFormat))
            //   .show();
            $comparePrice
                .html(Shopify.formatMoney(variant.compare_at_price, moneyFormat))
                .show();
        } else {
            $comparePrice.hide();
        }

    } else {
        // The variant doesn't exist, disable submit button.
        // This may be an error or notice that a specific variant is not available.
        // To only show available variants, implement linked product options:
        //   - http://docs.shopify.com/manual/configuration/store-customization/advanced-navigation/linked-product-options
        $addToCart.addClass('disabled').prop('disabled', true);
        $addToCartText.html("Unavailable");
        $quantityElements.hide();
    }
};

timber.productImageSwitch = function () {
    if (timber.cache.$thumbImages.length) {
        // Switch the main image with one of the thumbnails
        // Note: this does not change the variant selected, just the image
        timber.cache.$thumbImages.on('click', function (evt) {
            evt.preventDefault();
            var newImage = $(this).attr('href');
            var newSrcset = $(this).attr('data-srcset');
            timber.switchImage(newImage, null, timber.cache.$productImage, newSrcset);
        });
    }
};

timber.switchImage = function (src, imgObject, el, srcset) {
    // Make sure element is a jquery object
    var $el = $(el);
    $el.attr('src', src);
    if (srcset !== undefined) {
        $el.attr('srcset', srcset);
    }
};

timber.responsiveVideos = function () {
    $('iframe[src*="youtube.com/embed"]').wrap('<div class="video-wrapper"></div>');
    $('iframe[src*="player.vimeo"]').wrap('<div class="video-wrapper"></div>');
};

timber.collectionViews = function () {
    if (timber.cache.$changeView.length) {
        timber.cache.$changeView.on('click', function () {
            var view = $(this).data('view'),
                url = document.URL,
                hasParams = url.indexOf('?') > -1;

            if (hasParams) {
                window.location = replaceUrlParam(url, 'view', view);
            } else {
                window.location = url + '?view=' + view;
            }
        });
    }
};

timber.loginForms = function () {
    function showRecoverPasswordForm() {
        timber.cache.$recoverPasswordForm.show();
        timber.cache.$customerLoginForm.hide();
    }

    function hideRecoverPasswordForm() {
        timber.cache.$recoverPasswordForm.hide();
        timber.cache.$customerLoginForm.show();
    }

    timber.cache.$recoverPasswordLink.on('click', function (evt) {
        evt.preventDefault();
        showRecoverPasswordForm();
    });

    timber.cache.$hideRecoverPasswordLink.on('click', function (evt) {
        evt.preventDefault();
        hideRecoverPasswordForm();
    });

    // Allow deep linking to recover password form
    if (timber.getHash() == '#recover') {
        showRecoverPasswordForm();
    }
};

timber.resetPasswordSuccess = function () {
    timber.cache.$passwordResetSuccess.show();
};

/*============================================================================
  Drawer modules
  - Docs http://shopify.github.io/Timber/#drawers
==============================================================================*/
timber.Drawers = (function () {
    var Drawer = function (id, position, options) {
        var defaults = {
            close: '.js-drawer-close',
            open: '.js-drawer-open-' + position,
            openClass: 'js-drawer-open',
            dirOpenClass: 'js-drawer-open-' + position
        };

        this.$nodes = {
            parent: $('body, html'),
            page: $('#PageContainer'),
            moved: $('.is-moved-by-drawer')
        };

        this.config = $.extend(defaults, options);
        this.position = position;

        this.$drawer = $('#' + id);

        if (!this.$drawer.length) {
            return false;
        }

        this.drawerIsOpen = false;
        this.init();
    };

    Drawer.prototype.init = function () {
        $(this.config.open).on('click', $.proxy(this.open, this));
        this.$drawer.find(this.config.close).on('click', $.proxy(this.close, this));
    };

    Drawer.prototype.open = function (evt) {
        // Keep track if drawer was opened from a click, or called by another function
        var externalCall = false;

        // Prevent following href if link is clicked
        if (evt) {
            evt.preventDefault();
        } else {
            externalCall = true;
        }

        // Without this, the drawer opens, the click event bubbles up to $nodes.page
        // which closes the drawer.
        if (evt && evt.stopPropagation) {
            evt.stopPropagation();
            // save the source of the click, we'll focus to this on close
            this.$activeSource = $(evt.currentTarget);
        }

        if (this.drawerIsOpen && !externalCall) {
            return this.close();
        }

        // Add is-transitioning class to moved elements on open so drawer can have
        // transition for close animation
        this.$nodes.moved.addClass('is-transitioning');
        this.$drawer.prepareTransition();

        this.$nodes.parent.addClass(this.config.openClass + ' ' + this.config.dirOpenClass);
        this.drawerIsOpen = true;

        // Run function when draw opens if set
        if (this.config.onDrawerOpen && typeof (this.config.onDrawerOpen) == 'function') {
            if (!externalCall) {
                this.config.onDrawerOpen();
            }
        }

        if (this.$activeSource && this.$activeSource.attr('aria-expanded')) {
            this.$activeSource.attr('aria-expanded', 'true');
        }

        // Lock scrolling on mobile
        this.$nodes.page.on('touchmove.drawer', function () {
            return false;
        });

        this.$nodes.page.on('click.drawer', $.proxy(function () {
            this.close();
            return false;
        }, this));
    };

    Drawer.prototype.close = function () {
        if (!this.drawerIsOpen) { // don't close a closed drawer
            return;
        }

        // deselect any focused form elements
        $(document.activeElement).trigger('blur');

        // Ensure closing transition is applied to moved elements, like the nav
        this.$nodes.moved.prepareTransition({disableExisting: true});
        this.$drawer.prepareTransition({disableExisting: true});

        this.$nodes.parent.removeClass(this.config.dirOpenClass + ' ' + this.config.openClass);

        this.drawerIsOpen = false;

        this.$nodes.page.off('.drawer');
    };

    return Drawer;
})();

// Initialize Timber's JS on docready
$(timber.init);

/*============================================================================
  Ajax the add to bag for Marine Layer shop
  This file includes:
    - Basic Shopify Ajax API calls
    - Ajax cart plugin

  This requires:
    - jQuery 1.8+
    - handlebars.min.js (for bag template)
    - modernizer.min.js
    - ajax-cart.js.liquid (timber ajax cart)
    - snippet/ajax-bag-template-ml.liquid

 Auth: Duc Nguyen
==============================================================================*/

/*============================================================================
  Ajax Shopify Add To bag
==============================================================================*/
var ajaxBag = (function (module, $) {

    'use strict';

    // Public functions
    var init, loadCart;

    // Private general variables
    var settings, $body, $niceScroll;


    var cartIsOpened = false;


    // Private plugin variables
    var $formContainer, $addToCart, $cartCountSelector, $cartCostSelector, $bagContainer, $mobileCartCountSelector;

    // Private functions
    var updateCountPrice,
        formOverride,
        itemAddedCallback,
        itemErrorCallback,
        cartUpdateCallback,
        buildCart,
        cartCallback,
        adjustCart,
        adjustCartCallback,
        createQtySelectors,
        qtySelectors,
        validateQty,
        truncateString,
        removeItem;

    /*============================================================================
    Initialise the plugin and define global options
  ==============================================================================*/
    init = function (options) {

        // Default settings
        settings = {
            bagContainer: '#bag-hover',
            addToCartSelector: 'button[type="submit"]',
            cartCountSelector: '#CartCount',
            mobileCartCountSelector: '#MobileCartCount',
            cartCostSelector: null,
            moneyFormat: '$',
            formSelector: '#mlAddToCartForm'
        };
        // Override defaults with arguments
        $.extend(settings, options);

        // Select DOM elements
        $formContainer = $(settings.formSelector);
        $bagContainer = $(settings.bagContainer);
        $addToCart = $formContainer.find(settings.addToCartSelector);
        $cartCountSelector = $(settings.cartCountSelector);
        $mobileCartCountSelector = $(settings.mobileCartCountSelector);
        //$cartCostSelector  = $(settings.cartCostSelector);

        // General Selectors
        $body = $('body');

        //overide form

        formOverride();

    };

    itemAddedCallback = function (product) {
        if ($addToCart) {
            $addToCart.removeClass('is-adding').addClass('is-added');
        }
        ShopifyAPI.getCart(cartUpdateCallback);
        if ($(window).width() < 780) {
            setTimeout(function () {
                jQuery('.utility-nav').removeClass('show-bag');
            }, 2000);
        }
    };

    itemErrorCallback = function (XMLHttpRequest, textStatus) {
        var data = eval('(' + XMLHttpRequest.responseText + ')');
        $addToCart.removeClass('is-adding is-added');

        if (!!data.message) {
            if (data.status == 422) {
                var errorMess = data.description;
                var newErroMess = 'No can do. All of these are currently in YOUR bag.';
                if ($formContainer) {
                    $formContainer.after('<div class="errors qty-error">' + newErroMess + '</div>')
                }
            }
        }
    };

    updateCountPrice = function (cart) {
        if ($cartCountSelector) {
            $cartCountSelector.html(cart.item_count).removeClass('hidden-count');

            if (cart.item_count === 0) {
                $cartCountSelector.addClass('hidden-count');
            }
        }

        if ($mobileCartCountSelector) {
            $mobileCartCountSelector.html('(' + cart.item_count + ')');
        }

        if ($cartCostSelector) {
            $cartCostSelector.html(Shopify.formatMoney(cart.total_price, settings.moneyFormat));
        }
    };

    cartUpdateCallback = function (cart) {
        // Update quantity and price
        updateCountPrice(cart);
        buildCart(cart);
    };

    buildCart = function (cart) {
        // Start with a fresh cart div
        $bagContainer.empty();
        // Show empty cart
        if (cart.item_count === 0) {
            $bagContainer
                .append('<p>' + "This empty bag would look a lot better with some shirts in it." + '</p>');
            cartCallback(cart);
            return;
        }

        // Handlebars.js cart layout
        var items = [],
            item = {},
            data = {},
            source = $("#bagTemplate").html(),
            template = Handlebars.compile(source);

        // Add each item to our handlebars.js data
        $.each(cart.items, function (index, cartItem) {

            /* Hack to get product image thumbnail
       *   - If image is not null
       *     - Remove file extension, add _small, and re-add extension
       *     - Create server relative link
       *   - A hard-coded url of no-image
      */
            if (cartItem.image != null) {
                var prodImg = cartItem.image.replace(/(\.[^.]*)$/, "_compact$1").replace('http:', '');
            } else {
                var prodImg = "//cdn.shopify.com/s/assets/admin/no-image-medium-cc9732cb976dd349a0df1d39816fbcc7.gif";
            }

            // Create item's data object and add to 'items' array
            item = {
                id: cartItem.variant_id,
                line: index + 1, // Shopify uses a 1+ index in the API
                url: cartItem.url,
                img: prodImg,
                name: cartItem.product_title,
                size: cartItem.variant_options[0].remove("3+"),
                variation: cartItem.variant_title,
                properties: cartItem.properties,
                itemAdd: cartItem.quantity + 1,
                itemMinus: cartItem.quantity - 1,
                itemQty: cartItem.quantity,
                // Divide line price by quantity to get the final unit price taking into
                // account any line item discounts that may have been applied.
                price: Shopify.formatMoney(cartItem.line_price / cartItem.quantity, settings.moneyFormat),
                vendor: cartItem.vendor,
                gift_card: cartItem.gift_card,
                color: cartItem.variant_options[1]
            };

            items.push(item);
        });

        // Gather all cart data and add to DOM
        data = {
            items: items,
            note: cart.note,
            totalPrice: Shopify.formatMoney(cart.total_price, settings.moneyFormat)
        }

        $bagContainer.append(template(data));

        cartCallback(cart);
    };


    cartCallback = function (cart) {
        jQuery('.utility-nav').addClass('show-bag');
        jQuery.fancybox.close();

        if ($('#bag-hover .bag-list-wrapper').length) {
            setTimeout(function () {
                $niceScroll = new IScroll('#bag-hover .bag-list-wrapper', {
                    mouseWheel: true,
                    scrollbars: true
                });

                // console.dir($niceScroll);
            })
        }

        /*setTimeout(function () {
            jQuery('.utility-nav').removeClass('show-bag');
        }, 3000);*/

        if ($('#bag-hover').length > 0) {

            $('#bag-hover').mouseleave(function () {
                /*setTimeout(function () {*/
                jQuery('.utility-nav').removeClass('show-bag');
                /*}, 1000);*/
            });
        }
    };

    if ($('#cart-bag').length > 0) {
        $('#cart-bag').mouseleave(function () {
            setTimeout(function () {
                if ($('#bag-hover:hover').length == 0) {
                    jQuery('.utility-nav').removeClass('show-bag');
                }
            }, 100);
        })
    }

    loadCart = function () {
        ShopifyAPI.getCart(cartUpdateCallback);
    };


    formOverride = function () {

        const getCountForProduct = function (cart, productId) {
            let countProducts = 0;
            if (parseInt(cart.item_count) > 0) {
                $.each(cart.items, function (key, cartItem) {
                    if (cartItem["id"] == productId) {
                        countProducts += cartItem["quantity"]
                    }
                });
            }
            return countProducts;
        }

        if ($formContainer.length) {

            $formContainer.on('submit', function (evt) {
                evt.preventDefault();
                const productId = $formContainer.find('#productSelect').val();
                if (productId === productIdRestrict) {
                    $.ajax({
                        type: 'GET',
                        url: '/cart.js',
                        cache: false,
                        dataType: 'json',
                        success: function (cart) {
                            const countProductsRestrict = getCountForProduct(cart, productIdRestrict);
                            if (countProductsRestrict >= 3) {
                                $addToCart.removeClass('is-added').addClass('is-adding');
                                $('.qty-error').remove();
                                return false;
                            } else {
                                $addToCart.removeClass('is-added').addClass('is-adding');
                                $('.qty-error').remove();
                                ShopifyAPI.addItemFromForm(evt.target, itemAddedCallback, itemErrorCallback);
                            }
                        }
                    });
                } else {
                    // Add class to be styled if desired
                    $addToCart.removeClass('is-added').addClass('is-adding');

                    // Remove any previous quantity errors
                    $('.qty-error').remove();

                    ShopifyAPI.addItemFromForm(evt.target, itemAddedCallback, itemErrorCallback);
                }


            });
        }
        if ($('.quick-cart-add-form').length) {
            $('.quick-cart-add-form').on('submit', function (evt) {
                evt.preventDefault();
                const productId = $(this).find('.product-select').val();
                if (productId === productIdRestrict) {
                    $.ajax({
                        type: 'GET',
                        url: '/cart.js',
                        cache: false,
                        dataType: 'json',
                        success: function (cart) {
                            const countProductsRestrict = getCountForProduct(cart, productIdRestrict);
                            if (countProductsRestrict >= 3) {
                                return false;
                            } else {
                                ShopifyAPI.addItemFromForm(evt.target, itemAddedCallback, itemErrorCallback);
                            }
                        }
                    });
                } else {
                    ShopifyAPI.addItemFromForm(evt.target, itemAddedCallback, itemErrorCallback);
                }
            })
        }
    };

    truncateString = function (text, length, wordSplit) {
        var shortText = jQuery.trim(text).substring(0, length);

        if (wordSplit) {
            return shortText.split(" ").slice(0, -1).join(" ") + "...";
        } else {
            return shortText + '...';
        }

    };

    qtySelectors = function () {
        // Change number inputs to JS ones, similar to ajax cart but without API integration.
        // Make sure to add the existing name and id to the new input element
        var numInputs = $('input[type="number"]').not('.js-qty__num');

        if (numInputs.length) {
            numInputs.each(function () {
                var $el = $(this),
                    currentQty = $el.val(),
                    inputName = $el.attr('name'),
                    inputId = $el.attr('id');

                var itemAdd = currentQty + 1,
                    itemMinus = currentQty - 1,
                    itemQty = currentQty;

                var source = $("#JsQty").html(),
                    template = Handlebars.compile(source),
                    data = {
                        id: $el.data('id'),
                        itemQty: itemQty,
                        itemAdd: itemAdd,
                        itemMinus: itemMinus,
                        inputName: inputName,
                        inputId: inputId
                    };

                // Append new quantity selector then remove original
                $el.after(template(data)).remove();
            });

            // Setup listeners to add/subtract from the input
            $('.js-qty__adjust').off('click').on('click', function () {
                var $el = $(this),
                    id = $el.data('id'),
                    $qtySelector = $el.siblings('.js-qty__num'),
                    qty = parseInt($qtySelector.val().replace(/\D/g, ''));
                //console.log($qtySelector.val());
                var qty = validateQty(qty);

                // Add or subtract from the current quantity
                if ($el.hasClass('js-qty__adjust--plus')) {
                    qty += 1;
                } else {
                    qty -= 1;
                    if (qty <= 1) qty = 1;
                }

                // Update the input's number
                $qtySelector.val(qty);
            });
        }
    };

    validateQty = function (qty) {
        if ((parseFloat(qty) == parseInt(qty)) && !isNaN(qty)) {
            // We have a valid number!
        } else {
            // Not a number. Default to 1.
            qty = 1;
        }
        return qty;
    };

    removeItem = function (line) {
        ShopifyAPI.changeItem(line, 0, function (cart) {
            cartUpdateCallback(cart);
        });
    };

    module = {
        init: init,
        load: loadCart,
        numInput: qtySelectors,
        removeItem: removeItem
    };

    return module;

}(ajaxBag || {}, jQuery));

var hamburgerIcon = (function (module, $) {
    'use strict';
    // Constants

    // Private plugin variables
    var state = false;
    var $icon;

    // Private functions
    var init, load, setActive, setInactive;

    load = function () {
        $icon = $('.icon-hamburger--animation');

        return $icon.length;
    };

    init = function () {
        if (!load()) {
            console.log('WARNING: no hamburger element by .icon-hamburger--animation class.');
            return;
        }

        $icon.on('click', function (event) {
            event.preventDefault();
            $(this).toggleClass('active');
        });

        state = true;
    };

    setActive = function () {
        if (!state) {
            init();
            return;
        }

        $icon.addClass('active');
    };

    setInactive = function () {
        if (!state) {
            init();
            return;
        }

        $icon.removeClass('active');
    };

    return {
        init: init,
        active: setActive,
        inactive: setInactive
    };

}(hamburgerIcon || {}, jQuery));
var mobileMenu = (function (module, $) {
    'use strict';
    // Constants
    var MOBILE_NAV_HEIGHT = 55;

    // Private plugin variables
    var $navContainer, $navTitle, $menuBtn;

    // Private functions
    var init, load, handleEvents, updateContainerHeight;

    init = function () {
        if (!load()) {
            console.log('WARNING: not found nav-mobile ID.');
            return;
        }
        updateContainerHeight();
        handleEvents();
    };

    load = function () {
        $navContainer = $('#NavMobile');
        if (!$navContainer.length) {
            return false;
        }
        $navTitle = $navContainer.find('.title');
        $menuBtn = $('#nav-bar').find('.js-drawer-open-down');

        return true;
    };

    updateContainerHeight = function () {
        var $height = window.innerHeight;
        $navContainer.css('max-height', $height - MOBILE_NAV_HEIGHT + 'px');
    };

    handleEvents = function () {
        $navTitle.off().on('click', function (event) {
            var $currentRow = $(this).closest('.has-dropdown');
            var $rowHasDropdown = $currentRow.find('.has-dropdown');
            var $menu = $currentRow.find('.nav-mobile__menu');

            if ($(this).closest('.nav-mobile__row').hasClass('has-dropdown')) {
                event.preventDefault();
            }

            if ($currentRow.hasClass('expand')) {
                $currentRow.removeClass('expand');
                $rowHasDropdown.removeClass('expand').find('.nav-mobile__menu').hide();
                $menu.first().slideUp('fast');
            } else {
                $currentRow.addClass('expand').find('.nav-mobile__menu').first().slideDown('fast');
            }
        });

        $menuBtn.off().on('click', function (event) {
            event.preventDefault();
            if ($navContainer.hasClass('nav-mobile--open')) {
                $navContainer.removeClass('nav-mobile--open')
                    .slideUp('fast')
                    // close sub-menu and remove its classes
                    .find('.has-dropdown')
                    .removeClass('expand')
                    .find('.nav-mobile__menu')
                    .hide();
                $('body').removeClass('show-nav-mobile');
            } else {
                $navContainer.addClass('nav-mobile--open').slideDown('fast');
                $('body').addClass('show-nav-mobile');
            }
        });

        $(window).on('resize', function (event) {
            event.preventDefault();
            updateContainerHeight();
        });
    };

    return {
        init: init
    };

}(mobileMenu || {}, jQuery));
var productDetail = (function (module, $) {
    'use strict';
    // Constants

    // Private plugin variables

    // Private functions
    var init, smoothScrolling, magicScroll, scrollToFixedIndicators, carouselProductImages;

    smoothScrolling = function () {
        var offset = $('#nav-bar').height() + $('#product-single-header').height() + 25;
        $('.scroll-indicator-bullets a[href*="#"]')
            // Remove links that don't actually link to anything
            .not('[href="#"]')
            .not('[href="#0"]')
            .click(function (event) {
                // On-page links
                if (
                    location.pathname.replace(/^\//, '') == this.pathname.replace(/^\//, '')
                    &&
                    location.hostname == this.hostname
                ) {
                    // Figure out element to scroll to
                    var target = $(this.hash);
                    target = target.length ? target : $('[name=' + this.hash.slice(1) + ']') + $('#product-single-header').height();
                    // Does a scroll target exist?
                    if (target.length) {
                        // Only prevent default if animation is actually gonna happen
                        event.preventDefault();
                        $('html, body').animate({
                            scrollTop: target.offset().top - offset
                        }, 1000, function () {
                            // Callback after animation
                            // Must change focus!
                            var $target = $(target);
                            $target.focus();
                            if ($target.is(":focus")) { // Checking if the target was focused
                                return false;
                            } else {
                                $target.attr('tabindex', '-1'); // Adding tabindex for elements not focusable
                                $target.focus(); // Set focus again
                            }
                        });
                    }
                }
            });
    };

    scrollToFixedIndicators = function () {
        var $indicators = $('.scroll-indicator-bullets');
        var $slideImages = $('.slideshow-wrap .slide img');

        if ($indicators.length) {
            var setupScrollToFixed = function () {
                var INDICATOR_OFFSET_TOP = 300;
                var $firstSlide = $('.slideshow-wrap .slide').first();
                var $lastSlide = $('.slideshow-wrap .slide').last();

                var topLimit = $firstSlide.offset().top + ($firstSlide.height() / 2) - ($indicators.height() / 2);
                var bottomLimit = $lastSlide.offset().top + ($lastSlide.height() / 2) + ($indicators.height() / 2);

                var checkPosition = function () {
                    var currentPos = $(window).scrollTop() + INDICATOR_OFFSET_TOP + $indicators.height();

                    if (currentPos <= topLimit) {
                        $indicators.css('position', 'absolute').css('bottom', '').css('top', INDICATOR_OFFSET_TOP + 'px');
                    }

                    if ((currentPos - $indicators.height()) >= bottomLimit) {
                        $indicators.css('position', 'absolute').css('top', '').css('bottom', INDICATOR_OFFSET_TOP + 'px');
                    }

                    if (currentPos > topLimit && (currentPos - $indicators.height()) < bottomLimit) {
                        $indicators.css('position', 'fixed').css('bottom', '').css('top', INDICATOR_OFFSET_TOP + 'px');
                    }
                };

                checkPosition();

                $(window).scroll(function () {
                    checkPosition();
                });

                if ($indicators.css('display') == 'none') {
                    $indicators.show();
                }
            };

            // check images loaded in the cache
            if ($slideImages.prop('complete')) {
                setupScrollToFixed();
            } else {
                $slideImages.on('load', function () {
                    setupScrollToFixed();
                });
            }
        }
    };

    magicScroll = function () {
        var $indicators = $('.scroll-indicator-bullets');
        var $slideshow = $('.slideshow-wrap');
        var $slideImages = $slideshow.find('img');
        if ($indicators.length) {
            /*var setupMagicScroll = function () {
        var slide = $slideshow.find('.slide');
        var controller = new ScrollMagic.Controller();
        slide.each(function(index, el) {
          var item = $(el);
          var src = '#' + item.attr('id');
          var target = '#target_' + item.attr('id');
          new ScrollMagic.Scene({triggerElement: src, duration: item.height()})
            .setClassToggle(target, 'active')
            .addTo(controller);
        });
      };

      // check images loaded in cached
      if ($slideImages.prop('complete')) {
        setupMagicScroll();
      } else {
        $slideImages.on('load', function() {
          setupMagicScroll();
        });
      }*/
        }
    };

    carouselProductImages = function () {
        var $carousel = $('.carousel-product-images');

        $carousel.on('init', function (ev, el) {
            $('video').each(function () {
                this.play();
            });
        });

        if ($carousel.length) {
            $carousel.slick({
                dots: true,
                arrows: false,
                slidesToShow: 1,
                adaptiveHeight: true,
                infinite: false
            });
        }
    };

    init = function () {
        smoothScrolling();
        scrollToFixedIndicators();
        magicScroll();
        carouselProductImages();

        $(window).resize(function (event) {
            magicScroll();
        });
    };


    return {
        init: init
    };

}(productDetail || {}, jQuery));

/* globals $ marinelayer jQuery timber  ajaxBag Sugar */

// marinelayer functions
window.marinelayer = window.marinelayer || {};

marinelayer.init = function () {
    marinelayer.initSugar();
    marinelayer.initsSlickSlider();
    // marinelayer.initFancybox();
    // marinelayer.initTooltip();
    marinelayer.productImageSwitch();
    marinelayer.initMobileFilters();
    marinelayer.initFormGetTheScoop();
    marinelayer.initAccordion();
    marinelayer.initBagHover();
    marinelayer.initTeamPage();
    marinelayer.productZoom();
    marinelayer.initTopSearch();
    marinelayer.overideMobileMenuDropdown();
    marinelayer.createImageCarousel();
    // marinelayer.shirtStoryPage();
    marinelayer.busPage();
    marinelayer.initIndex();
    marinelayer.storesPage();
    marinelayer.storesPageNews();
    marinelayer.numberGift();
    marinelayer.hamburgerIcon();
    marinelayer.mobileMenu();
    marinelayer.productDetail();
    marinelayer.removeCartItem();
};

marinelayer.initSugar = function () {
    Sugar.extend();
};

marinelayer.storesPage = function () {
    if (!$('.template-page-store').length) {
        return;
    }

    $('[rel=accordion]').each(function (index, item) {
        $(item).off('click').on('click', function () {
            var action = $(this).data('action');

            if (action == 'fade-out') {
                $(this).fadeOut();
            }

            $(this).toggleClass('open');
            $(this).next().slideToggle(200);
            var that = $(this);
            $('html, body').animate({scrollTop: that.offset().top - 60}, 500);
        });
    });
};

marinelayer.storesPageNews = function () {
    if (!$('.template-page-stores').length) {
        return;
    }

    $('[rel=accordion]').each(function (index, item) {
        $(item).off('click').on('click', function () {
            var action = $(this).data('action');

            if (action == 'fade-out') {
                $(this).fadeOut();
            }

            $(this).toggleClass('open');
            $(this).next().slideToggle(200);
            var that = $(this);
            $('html, body').animate({scrollTop: that.offset().top - 60}, 500);
        });
    });
};

marinelayer.initIndex = function () {
    if (!$('.template-index').length) {
        return;
    }

    if ($(window).width() <= 768) {
        return;
    }
    $('.promo__item').each(function (index, element) {
        if (index == 1 && $(element).width() <= $(element).find('.promo__item__content').height()) {
            $('.promo__item .promo__item__content').css('zoom', '75%');
        }
        $(element).height($(element).width());
    });


    $(window).resize(function () {
        if ($(window).width() <= 768) {
            return;
        }
        if ($(window).width() < 1080) {
            $('.promo__item .promo__item__content').css('zoom', '75%');
        } else {
            $('.promo__item .promo__item__content').css('zoom', '100%');
        }

        $('.promo__item').each(function (index, element) {
            $(element).height($(element).width());
        });
    });
};

// marinelayer.initTooltip = function() {
//   if($('.swatch').length) {
//     $('.swatch').tooltip();
//   }
// };

marinelayer.createImageCarousel = function () {
    if (!$('.product-single__carousel').length || $('.product-single__carousel-item').length < 2) {
        return;
    }

    $('.product-single__carousel').slick({
        arrows: false
        , dots: true
        , adaptiveHeight: true
    });
};

marinelayer.overideMobileMenuDropdown = function () {
    $('.mobile-nav__has-sublist > .mobile-nav__link--ml').on('click', function (e) {
        e.preventDefault();
        $(this).parents('.mobile-nav__has-sublist').find('.mobile-nav__toggle').click();
    });
};

marinelayer.productZoom = function () {

    var width = $(window).width();

    if (width > 1024) {
        $('.inner-zoom').zoom({on: 'click', magnify: 1.25});

        $('.zoom-img-selector').click(function () {
            // var imgSrc = $(this).closest('a').attr('href');
            $('.inner-zoom .zoomImg').remove();
            //$('.inner-zoom').zoom({ on:'click'});
            setTimeout(function () {
                $('.inner-zoom').zoom({on: 'click', magnify: 1.25});
            }, 200);

        });

        $('.inner-zoom').click(function (e) {
            e.preventDefault();
            $(this).toggleClass('in');
            var zoomImg = $('.inner-zoom .zoomImg');
            if (zoomImg.length > 1) {
                $(zoomImg[0]).remove();
            }
        });
    }
};

marinelayer.initsSlickSlider = function () {
    $('#slick-slider').slick({
        autoplay: true
        // , adaptiveHeight: true
        , arrows: false
        // , centerMode: true
        // , centerPadding: '100px'
        , dots: true
        , fade: true
        , pauseOnHover: true
    });
};

/*
marinelayer.initFancybox = function() {

  jQuery('.close-fancybox').on('click', function() { jQuery.fancybox.close(); });

  if(jQuery('[rel=fancybox-guys]').length) {
    jQuery('[rel=fancybox-guys]').fancybox({
      width: 550,
      height: 700,
      autoSize: false,
      closeBtn: false,
      arrows: false,
      hideOnContentClick: true,
      padding: [10, 20, 0, 20]
    });
  }

  if(jQuery('[rel=fancybox-gals]').length) {
    // jQuery('.close-fancybox').on('click', function() { jQuery.fancybox.close(); });
    jQuery('[rel=fancybox-gals]').fancybox({
      width: 550,
      // height: 700,
      autoSize: false,
      closeBtn: false,
      arrows: false,
      hideOnContentClick: true,
      padding: [10, 20, 0, 20]
    });
  }

  if(jQuery('[rel=fancybox-gals-swim]').length) {
    // jQuery('.close-fancybox').on('click', function() { jQuery.fancybox.close(); });
    jQuery('[rel=fancybox-gals-swim]').fancybox({
      width: 550,
      height: 700,
      autoSize: false,
      closeBtn: false,
      arrows: false,
      hideOnContentClick: true,
      padding: [10, 20, 0, 20]
    });
  }

  if(jQuery('.quick-shop').length) {
    jQuery('.product-quick-view [rel=AddToCart]').on('click', function() {
      var addBtn = jQuery(this);
      if(jQuery(this).closest('#mlAddToCartForm').find('.size-select-section .size-item').hasClass('active')) {
        setTimeout(function() {
          addBtn.removeClass('is-added');
        }, 2500);
      } else {
        addBtn.find('#mlAddToCartText').hide();
        addBtn.find('#mlSelectSizeText').show();
        setTimeout(function() {
          addBtn.find('#mlSelectSizeText').hide();
          addBtn.find('#mlAddToCartText').show();
        }, 2500);


        return false;
      }

    });

    jQuery('.quick-shop').fancybox({
      width: '1080px'
      , height: 'auto'
      , autoSize: false
      , closeBtn: false
      , scrolling: 'no'
      , fitToView: false
      , autoCenter: true
      , padding: [0, 20, 0, 20]
      , afterClose: function() {
        jQuery('.product-quick-view .errors').remove();
        jQuery('.product-quick-view .gift-card-section input, .product-quick-view .gift-card-section textarea').val('');
        // jQuery('.product-quick-view .gift-card-section textarea').val('');
      }
    });
  }
};
*/


marinelayer.productImageSwitch = function () {
    var thumbImages = jQuery('.product-quick-view').find('a.product-single__thumbnail');
    if (thumbImages.length) {
        // Switch the main image with one of the thumbnails
        // Note: this does not change the variant selected, just the image
        thumbImages.on('click', function (evt) {
            evt.preventDefault();
            var newImage = $(this).attr('href');
            var newSrcset = $(this).attr('data-srcset');
            timber.switchImage(
                newImage
                , null
                , jQuery(this).parents('.product-quick-view')
                    .find('.product-single__photos img'), newSrcset);
        });
    }
};

marinelayer.initMobileFilters = function () {
    jQuery('body').on('click', '.collection-filters--mobile .filter-name', function () {
        jQuery('.collection-filters--mobile .filter-name').not(jQuery(this)).parent().removeClass('active');
        jQuery(this).parent().toggleClass('active');


        if (jQuery(this).parent().hasClass('active')) {
            jQuery('.collection-filters--mobile').addClass('show-choices');
        } else {
            jQuery('.collection-filters--mobile').removeClass('show-choices');
        }

        var target = jQuery(this).data('target');
        jQuery('.collection-filters--mobile__choices').not(target).hide();
        jQuery(target).slideToggle();
    });
};

marinelayer.initAccordion = function () {
    if (jQuery('[rel=accordion]').length) {
        jQuery('[rel=accordion]').each(function (index, item) {

            var defaultOpen = jQuery(item).data('default-open');

            if (defaultOpen) {
                jQuery(item).closest('[rel=accordion]').find(defaultOpen).find('.content').show();
                jQuery(item).closest('[rel=accordion]').find(defaultOpen).addClass('open');
            }

            jQuery(item).off('click').on('click', '.title:not(.no-handle)', function () {
                jQuery(this).parent().toggleClass('open');
                jQuery(this).next().slideToggle(200, function () {
                    if (jQuery(this).parents('.product-quick-view').length) {
                        // var quickView = jQuery(this).parents('.product-quick-view');
                        // quickView.niceScroll();
                        // quickView.getNiceScroll().resize();
                    }
                });
            });
        });
    }
};

marinelayer.initFormGetTheScoop = function () {
    $('#email-subscription').validationEngine();
    $('#form-unsubscribe').validationEngine();

    var listIdInput = $('#list_id');
    //DucNguyen
    //List id of klaviyo
    var defaultListId = 'rf2hVx'
        , supperfanListId = 'rf2hVx';

    listIdInput.val(defaultListId);

    $('#form-unsubscribe').on('submit', function () {
        // alert('aaaa');
        var email = $('#email').val();

        if (email == '') {
            email = 'fakeemail' + Math.random() + '@marinelayer.com';
        }

        $('#hidden_email').val(email);
    });

    $('#checkbox--catalogue').on('change', function () {
        $(this).is(':checked') ? $('.form--catalogue-wrapper').fadeIn() : $('.form--catalogue-wrapper').fadeOut();

        if ($(this).is(':checked')) {
            $('#sign-up-source').val('Super Fan Address Sign-up');
            listIdInput.val(supperfanListId);
        } else {
            $('#sign-up-source').val('Web Footer Sign-up');
            listIdInput.val(defaultListId);
        }
    });
};

marinelayer.initBagHover = function () {
    // var self = this;
    //jQuery('body').on('mouseover', '#cart-bag', function () {
    $('#cart-bag').on('mouseenter', function () {
        ajaxBag.load();
        // $('.search-form-dropdown').slideUp();
        // jQuery('.utility-links').addClass('show-bag');
    });
};

marinelayer.removeCartItem = function () {
    $('#bag-hover').on('click', '.remove-item', function () {
        var line = parseInt($(this).data('line')) + 1;
        ajaxBag.removeItem(line);
    });

    $('#CartContainer').on('click', '.remove-item', function () {
        var line = parseInt($(this).data('line')) + 1;
        ajaxCart.removeItem(line);
    });
};

marinelayer.initTeamPage = function () {
    if ($('.job_header').length) {
        $('.job_header').on('click', function () {
            $(this).parent().find('.job_body').slideToggle();
        });
    }
};

marinelayer.initTopSearch = function () {
    var $searchFormDropdown = $('.search-form-dropdown');
    var $topSearch = $('#top-search');
    $topSearch.click(function (e) {
        if ($(window).width() <= 780) {
            if ($('.nav-list-container').css('display') == 'block') {
                $('#mobile-nav-button').trigger('click');
                $('#mobile-nav-button .icon-hamburger--animation').removeClass('active');
            }
        }
        e.preventDefault(), $searchFormDropdown.slideToggle('fast', function () {
            $searchFormDropdown.is(':visible') && ($searchFormDropdown.find('input').value = '', $searchFormDropdown.find('pre').html(''), $searchFormDropdown.find('input').focus());
            if ($searchFormDropdown.css('display') == 'block') {
                $('#ml-global-search-input').focus();
                $('body').addClass('overflow-hidden');
            } else {
                $('body').removeClass('overflow-hidden');
            }

        });
        $('body').on('click', function (event) {
            //console.log(event.target);
            const parentId = $($(event.target).parent()).attr('id');
            const thisId = $($(event.target)).attr('id');
            if (!$(event.target).is($searchFormDropdown) && thisId !== 'top-search' && parentId !== 'top-search' && !$($(event.target)).hasClass('input-group-field')) {
                $searchFormDropdown.slideUp('fast');
                $('body').removeClass('overflow-hidden');
            }
        });
    }), $('.clear-btn').click(function () {
        $searchFormDropdown.slideUp('fast');
        $('body').removeClass('overflow-hidden');
    });

    $searchFormDropdown.find('.close-btn').on('click', function (event) {
        event.preventDefault();
        $searchFormDropdown.slideUp('fast');
        $('body').removeClass('overflow-hidden')
    });

    // $searchFormDropdown.mouseleave(function(){
    // 	$('.search-form-dropdown').slideUp();
    // });

    // $('.clear-btn').click(function() {
    // $('.search-form-dropdown').slideUp();
    // });
};

marinelayer.isMobile = function () {
    return navigator.userAgent.match(/Android/i) || navigator.userAgent.match(/webOS/i) || navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPod/i) || navigator.userAgent.match(/BlackBerry/i) || navigator.userAgent.match(/Windows Phone/i) ? !0 : !1;
};

/*marinelayer.shirtStoryPage = function() {
  if(!$('#shirt-story').length) return;

  var stickyHeaders = (function() {

    var $window = $(window)
      , $stickies;

    var load = function(stickies) {

      if(typeof stickies === 'object' && stickies instanceof jQuery && stickies.length > 0) {

        $stickies = stickies.each(function() {

          var $thisSticky = $(this).wrap('<div class="followWrap" />');

          $thisSticky
            .data('originalPosition', $thisSticky.offset().top)
            .data('originalHeight', $thisSticky.outerHeight())
            .parent()
            .height($thisSticky.outerHeight());
        });

        $window.off('scroll.stickies').on('scroll.stickies', function() {
          _whenScrolling();
        });
      }
    };

    var _whenScrolling = function() {

      $stickies.each(function(i) {

        var $thisSticky = $(this)
          , $stickyPosition = $thisSticky.data('originalPosition')
          , offset = 60;

        if(i > 0) {
          offset = 185;
        }

        if($stickyPosition <= ($window.scrollTop() + offset)) {

          var $nextSticky = $stickies.eq(i + 1)
            , $nextStickyPosition = $nextSticky.data('originalPosition') - $thisSticky.data('originalHeight');

          $('.mini-nav').hide();

          $thisSticky.show().addClass('fixed');

          if($nextSticky.length > 0 && $thisSticky.offset().top >= $nextStickyPosition) {

            $thisSticky.addClass('absolute').css('top', $nextStickyPosition);
          }

        } else {

          var $prevSticky = $stickies.eq(i - 1);

          $thisSticky.removeClass('fixed');

          if($prevSticky.length > 0 && $window.scrollTop() <= $thisSticky.data('originalPosition') - $thisSticky.data('originalHeight')) {

            $prevSticky.removeClass('absolute').removeAttr('style');
          }
        }
      });
    };

    return {
      load: load
    };
  })();

  $(window).load(function() {
    $('#top .banner').height($(window).height());
    $('.parallax').height($(window).height());
    $('.parallax').css('background-position-y', $('#sticky-wrapper').height());
    $('#top .intro').css('margin-top', $(window).height());

    $('html, body').animate({ scrollTop: 0 }, 500, function() {
      $('.template-page-shirt-story').addClass('loaded');
    });

    var offset = $('#sticky-wrapper').height();

    $('#body').waypoint({
      handler: function() {
        if(!$('.followWrap').length && $(window).width() > 768) {
          stickyHeaders.load($('.mini-nav'));
        }
      },

      offset: offset

    });

    $('#sourcing').waypoint({
      handler: function() {
        $('.mini-nav li').removeClass('active');
        $('.mini-nav li[rel=sourcing]').addClass('active');
      }
      , offset: 40
    });

    $('#fabric').waypoint({
      handler: function() {
        $('.mini-nav li').removeClass('active');
        $('.mini-nav li[rel=fabric]').addClass('active');

      }
      , offset: 130
    });

    $('#construction').waypoint({
      handler: function() {
        $('.mini-nav li').removeClass('active');
        $('.mini-nav li[rel=construction]').addClass('active');
      }
      , offset: 136
    });

    $('#factories').waypoint({
      handler: function() {
        $('.mini-nav li').removeClass('active');
        $('.mini-nav li[rel=factories]').addClass('active');
      }
      , offset: 136
    });

    $('#top .intro--text h2').waypoint({
      handler: function() {
        $(this.element).addClass('animated fadeIn');
      }
      , offset: '90%'
    });

    $('.content__textbox h3').waypoint({
      handler: function() {
        $(this.element).addClass('animated fadeIn');
      }
      , offset: '90%'
    });

    $('.content__textbox p').waypoint({
      handler: function() {
        $(this.element).addClass('animated fadeIn');
      }
      , offset: '90%'
    });

    $('.content__textbox .arrow').waypoint({
      handler: function() {
        $(this.element).addClass('animated fadeIn');
      }
      , offset: '90%'
    });

    $('#body-banner .text').waypoint({
      handler: function() {
        $(this.element).addClass('animated fadeIn');
      }
      , offset: '90%'
    });

  });

  $(window).scroll(function() {
    var opacity = 0
      , scrollTop = $(document).scrollTop()
      , viewportHeight = $(window).height();
    if(scrollTop < viewportHeight) {
      opacity = scrollTop / viewportHeight;

      $('#top .veil').css({
        opacity: opacity
      });
    }
  });

  $('.mini-nav a').click(function(e) {
    e.stopPropagation();
    e.preventDefault();
    var idOfDiv = $(this).attr('href');
    var top = $(idOfDiv).offset().top - $('#sticky-wrapper').outerHeight() - $('.mini-nav').outerHeight();

    $('html, body').animate({
      scrollTop: top - 5
    }, 1000);
    setTimeout(function() {
      $('html, body').animate({
        scrollTop: top
      }, 1000);
    }, 500);
  });

  $('.banner a').click(function(e) {
    e.stopPropagation();
    e.preventDefault();
    var idOfDiv = $(this).attr('href');
    var top = $(idOfDiv).offset().top - $('#sticky-wrapper').outerHeight();

    $('html, body').animate({
      scrollTop: top
    }, 1000);
  });
};
*/

marinelayer.busPage = function () {
    if (!$('#bus-content').length) {
        return;
    }

    $('#bus-content-row--first').waypoint({
        handler: function () {
            if (!$(this.element).hasClass('loaded-bus')) {
                $(this.element).find('.bus-content__item img').each(function (index, item) {
                    if (!$(item).hasClass('loaded')) {
                        $(item).addClass('loaded');
                    }
                });
                $(this.element).addClass('loaded-bus');

                $(this.element).find('.bus-content__item .text').each(function (index, item) {
                    if (!$(item).hasClass('loaded')) {
                        $(item).addClass('loaded');
                    }
                });
            }
        }
        , offset: '95%'
    });

    /*$('#bus-content-row--first .text').waypoint({
    handler: function(direction) {
      $(this.element).parents('#bus-content-row--first').find('.text').each(function(index, item) {
        if(!$(item).hasClass('loaded')) {
          $(item).addClass('loaded');
        }
      });
    },
    offset: '95%'
  });*/

    $('#bus-content-row--second').waypoint({
        handler: function () {
            if (!$(this.element).hasClass('loaded-bus')) {
                $(this.element).find('.bus-content__item img').each(function (index, item) {
                    if (!$(item).hasClass('loaded')) {
                        $(item).addClass('loaded');
                    }
                });
                $(this.element).addClass('loaded-bus');

                $(this.element).find('.bus-content__item .text').each(function (index, item) {
                    if (!$(item).hasClass('loaded')) {
                        $(item).addClass('loaded');
                    }
                });
            }
        }
        , offset: '95%'
    });


    /*$('#bus-content-row--second .text').waypoint({
    handler: function(direction) {
      $(this.element).parents('#bus-content-row--second').find('.text').each(function(index, item) {
        if(!$(item).hasClass('loaded')) {
          $(item).addClass('loaded');
        }
      });
    },
    offset: '95%'
  });*/

};

marinelayer.numberGift = function () {
    $(".product-item-number a").hover(
        function () {
            var mainObj = $(this).closest(".block-item-gift");
            var numberShow = $(this).attr("data-number");
            var activeObj = mainObj.find(".gift-menu-item");

            mainObj.find(".product-item-number a").removeClass("active").addClass("inactive");
            mainObj.find(".product-item--block").removeClass("active").addClass("inactive");
            activeObj.find(".product-item--block[data-number='" + numberShow + "']").addClass("active").removeClass("inactive");
            $(this).addClass("active").removeClass("inactive");
        }
        , function () {
            var mainObj = $(this).closest(".block-item-gift");
            var numberShow = $(this).attr("data-number");
            var activeObj = mainObj.find(".gift-menu-item");

            mainObj.find(".product-item-number a").addClass("inactive").removeClass("active");
            mainObj.find(".product-item--block").addClass("active").removeClass("inactive");
            activeObj.find(".product-item--block[data-number='" + numberShow + "']").addClass("active").removeClass("inactive");
            $(this).addClass("active").removeClass("active");
        }
    );

    $(".product-item--block").hover(
        function () {
            var mainObj = $(this).closest(".block-item-gift");
            var numberShow = $(this).attr("data-number");
            var activeObj = mainObj.find(".gift-image-item");

            mainObj.find(".product-item-number a").removeClass("active").addClass("inactive");
            mainObj.find(".product-item--block").removeClass("active").addClass("inactive");
            activeObj.find(".product-item-number a[data-number='" + numberShow + "']").addClass("active");
            $(this).addClass("active").removeClass("inactive");
        }
        , function () {
            var mainObj = $(this).closest(".block-item-gift");
            var numberShow = $(this).attr("data-number");
            var activeObj = mainObj.find(".gift-image-item");

            mainObj.find(".product-item-number a").addClass("inactive").removeClass("active");
            mainObj.find(".product-item--block").addClass("active").removeClass("inactive");
            activeObj.find(".product-item-number a[data-number='" + numberShow + "']").removeClass("active").addClass("inactive");
            $(this).addClass("active").removeClass("active");
        }
    );
};

marinelayer.hamburgerIcon = function () {
    hamburgerIcon.init();
};

marinelayer.mobileMenu = function () {
    mobileMenu.init();
};

marinelayer.productDetail = function () {
    productDetail.init();
};

// Initialize JS on docready
$(marinelayer.init);

